import React, { useState, useEffect, useRef, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import dashboardIcon from "../assets/icons/dashboard.svg";
import projectsIcon from "../assets/icons/projects.svg";
import sourcesIcon from "../assets/icons/sources.svg";
import newsIcon from "../assets/icons/news.svg";
import adsIcon from "../assets/icons/badge-ad.svg";
import usersIcon from "../assets/icons/user.svg";
import reportsIcon from "../assets/icons/logs.svg";
import settingsIcon from "../assets/icons/settings.svg";
import logoutIcon from "../assets/icons/logout.svg";
import allProjectsStatIcon from "../assets/icons/allprojects.svg";
import sendStatIcon from "../assets/icons/send.svg";
import receiveStatIcon from "../assets/icons/recevie.svg";
import resourcesStatIcon from "../assets/icons/res.svg";
import topbarUserIcon from "../assets/icons/topbaruser.svg";
import alertIcon from "../assets/icons/alert.svg";
import {
  authApi,
  authStorage,
  buildAbsoluteFileUrl,
  dashboardApi,
  newsApi,
  processApi,
  projectsApi,
  reportsApi,
  settingsApi,
  sourcesApi,
  usersApi,
  priceTemplatesApi,
} from "../api/api";
import "./Dashboard.css";

const TIME_PICKER_HOURS = Array.from({ length: 24 }, (_, index) => String(index).padStart(2, "0"));
const TIME_PICKER_MINUTES = Array.from({ length: 60 }, (_, index) => String(index).padStart(2, "0"));

const FIXED_SOURCE_PLATFORM_OPTIONS = [
  { value: "شبکه های اجتماعی", label: "شبکه های اجتماعی" },
];

const FIXED_SOURCE_PLATFORM_TYPE_OPTIONS = [
  { value: "rubika", label: "روبیکا" },
  { value: "bale", label: "بله" },
  { value: "eita with video", label: "ایتا با ویدئو (نیازمند اکانت ایتا)" },
];

const FIXED_SOURCE_LINK_OPTIONS = [
  { value: "https://www.irna.ir/rss", label: "https://www.irna.ir/rss" },
];

const FIXED_PUBLISH_PLATFORM_NAME_OPTIONS = [
  { value: "rubika", label: "روبیکا" },
  { value: "bale", label: "بله" },
  { value: "telegram", label: "تلگرام" },
  { value: "eita", label: "ایتا" },
  { value: "igap", label: "آیگپ" },
];

const FIXED_PUBLISH_PLATFORM_TYPE_OPTIONS = [
  { value: "کانال انتشار خبر", label: "کانال انتشار خبر" },
];

const FIXED_PUBLISH_LINK_OPTIONS = [
  { value: "https://rubika.ir/news.publish", label: "https://rubika.ir/news.publish" },
];

const PROJECT_OUTSIDE_WINDOW_MODE_OPTIONS = [
  {
    value: "publish_only",
    label: "فقط انتشار خارج از بازه متوقف شود",
  },
  {
    value: "publish_and_receive",
    label: "هم انتشار و هم دریافت خارج از بازه متوقف شوند",
  },
];

const SIDEBAR_ITEMS = [
  { key: "dashboard", label: "داشبورد", icon: dashboardIcon },
  { key: "projects", label: "پروژه ها", icon: projectsIcon },
  { key: "sources", label: "منابع", icon: sourcesIcon },
  { key: "news", label: "اخبار", icon: newsIcon },
  { key: "ads", label: "تبلیغات", icon: adsIcon },
  { key: "users", label: "کاربران", icon: usersIcon },
  { key: "reports", label: "گزارشات", icon: reportsIcon },
  { key: "settings", label: "تنظیمات", icon: settingsIcon },
];

const MODULE_KEY_TO_TAB_KEY = {
  dashboards: "dashboard",
  dashboard: "dashboard",
  projects: "projects",
  sources: "sources",
  news: "news",
  ads: "ads",
  users: "users",
  reports: "reports",
  settings: "settings",
};

const TAB_KEY_TO_MODULE_KEY = {
  dashboard: "dashboards",
  projects: "projects",
  sources: "sources",
  news: "news",
  ads: "ads",
  users: "users",
  reports: "reports",
  settings: "settings",
};

const TAB_TO_MODULE_ACCESS_KEY = {
  dashboard: "dashboards",
  projects: "projects",
  "projects-view": "projects",
  sources: "sources",
  news: "news",
  "news-view": "news",
  "news-create": "news",
  ads: "ads",
  "ads-view": "ads",
  "ads-create": "ads",
  users: "users",
  "users-create": "users",
  "users-edit": "users",
  reports: "reports",
  "reports-realtime": "reports",
  settings: "settings",
};

const DASHBOARD_CHART_POINTS = [42, 58, 64, 96, 122, 84, 36, 30, 142, 138, 126, 112, 54];
const DASHBOARD_CHART_LABELS = [
  "شنبه",
  "یکشنبه",
  "دوشنبه",
  "سه شنبه",
  "چهارشنبه",
  "پنج شنبه",
  "جمعه",
];
const PERSIAN_MONTH_LABELS = [
  "فروردین",
  "اردیبهشت",
  "خرداد",
  "تیر",
  "مرداد",
  "شهریور",
  "مهر",
  "آبان",
  "آذر",
  "دی",
  "بهمن",
  "اسفند",
];

const getDashboardActivityDate = (activityDateTime = "") => (
  String(activityDateTime).trim().split(" ")[0] || ""
);
const formatTimeFromDateTime = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "--:--:--";
  }
  return date.toLocaleTimeString("fa-IR", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
};
const DASHBOARD_ACTIVITY_ROWS_PER_PAGE = 10;
const SYSTEM_USAGE_FAKE_MIN = 60;
const SYSTEM_USAGE_FAKE_MAX = 70;
const SYSTEM_USAGE_UPDATE_INTERVAL_MS = 2 * 60 * 1000;
const getRandomSystemUsagePercent = () => (
  Math.random() * (SYSTEM_USAGE_FAKE_MAX - SYSTEM_USAGE_FAKE_MIN) + SYSTEM_USAGE_FAKE_MIN
);
const buildFakeSystemUsage = () => ({
  cpuPercent: getRandomSystemUsagePercent(),
  ramPercent: getRandomSystemUsagePercent(),
});

const PERSIAN_MONTH_NAMES = [
  "فروردین",
  "اردیبهشت",
  "خرداد",
  "تیر",
  "مرداد",
  "شهریور",
  "مهر",
  "آبان",
  "آذر",
  "دی",
  "بهمن",
  "اسفند",
];

const PERSIAN_WEEKDAY_SHORT = ["ش", "ی", "د", "س", "چ", "پ", "ج"];
const JALALI_BREAKS = [-61, 9, 38, 199, 426, 686, 756, 818, 1111, 1181, 1210, 1635, 2060, 2097, 2192, 2262, 2324, 2394, 2456, 3178];

const PROJECT_ROWS_PER_PAGE_OPTIONS = [20, 50, 100];
const NEWS_ROWS_PER_PAGE_OPTIONS = [5, 10, 15];

const NEWS_STATUS_FILTER_OPTIONS = [
  { value: "all", label: "همه وضعیت‌ها" },
  { value: "published", label: "منتشر شده" },
  { value: "received", label: "دریافت شده" },
  { value: "failed", label: "ناموفق" },
];

const USER_ROWS_PER_PAGE_OPTIONS = [5, 10, 15];

const USER_STATUS_FILTER_OPTIONS = [
  { value: "all", label: "همه وضعیت‌ها" },
  { value: "active", label: "فعال" },
  { value: "inactive", label: "غیرفعال" },
];

const USER_ACTIVITY_ROWS_PER_PAGE_OPTIONS = [10];
const SECURITY_LOGIN_ATTEMPTS_ROWS_PER_PAGE = 10;
const SETTINGS_BACKUP_ROWS_PER_PAGE = 10;

const USER_CREATE_ROLE_OPTIONS = ["مالک", "مدیر ارشد", "مدیر پروژه", "مشاهده گر"];
const USER_ROLE_TO_API = {
  مالک: "owner",
  "مدیر ارشد": "senior_manager",
  "مدیر پروژه": "project_manager",
  "مشاهده گر": "viewer",
};
const USER_ROLE_FROM_API = {
  owner: "مالک",
  senior_manager: "مدیر ارشد",
  project_manager: "مدیر پروژه",
  viewer: "مشاهده گر",
};
const USER_MODULE_TO_API = {
  داشبورد: "dashboards",
  "پروژه ها": "projects",
  منابع: "sources",
  اخبار: "news",
  تبلیغات: "ads",
  کاربران: "users",
  گزارشات: "reports",
  تنظیمات: "settings",
};
const USER_MODULE_FROM_API = {
  projects: "پروژه ها",
  sources: "منابع",
  news: "اخبار",
  ads: "تبلیغات",
  users: "کاربران",
  reports: "گزارشات",
  settings: "تنظیمات",
  dashboards: "داشبورد",
};
const USER_ACCESS_SECTION_OPTIONS = SIDEBAR_ITEMS.map((item) => item.label);
const TOPBAR_USER_INFO = {
  role: "مدیر پروژه",
  name: "محسن مرادی",
  profileImage: "",
};
const TOPBAR_SYSTEM_MESSAGES = [];

const PROJECT_VIEW_NEWS_TABS = [
  { key: "all", label: "خبر های دریافت شده" },
  { key: "published", label: "خبر های منتشر شده" },
];

const SOURCE_ROWS_PER_PAGE_OPTIONS = [5, 10, 15];
const PROJECT_VIEW_NEWS_ROWS_PER_PAGE = 10;

const SOURCE_STATUS_FILTER_OPTIONS = [
  { value: "all", label: "همه وضعیت‌ها" },
  { value: "active", label: "فعال" },
  { value: "inactive", label: "غیرفعال" },
];
const ANTI_AD_RULE_FLAG_OPTIONS = [
  { value: "is_ad", label: "تبلیغ" },
  { value: "is_news", label: "خبر" },
  { value: "is_trash", label: "زباله" },
  { value: "is_normal_post", label: "پست عادی" },
];
// "contains" is the classic blacklist. "not_contains" inverts the rule into a
// whitelist: anything without one of these phrases gets the selected label.
const ANTI_AD_RULE_MATCH_MODE_OPTIONS = [
  { value: "contains", label: "اگر شامل باشد" },
  { value: "not_contains", label: "اگر شامل نباشد" },
];
const NEWS_TYPE_BADGE_OPTIONS = [
  { key: "is_news", label: "خبر" },
  { key: "is_ad", label: "تبلیغ" },
  { key: "is_normal_post", label: "پست عادی" },
  { key: "is_trash", label: "زباله" },
];

const isNewsTypeFlagEnabled = (value) => {
  if (typeof value === "boolean") {
    return value;
  }
  if (typeof value === "number") {
    return value === 1;
  }
  const normalized = String(value || "").trim().toLowerCase();
  return normalized === "1" || normalized === "true" || normalized === "yes";
};
const getNewsTypeBadges = (newsItem = {}) => (
  NEWS_TYPE_BADGE_OPTIONS
    .filter((badgeItem) => isNewsTypeFlagEnabled(newsItem?.[badgeItem.key]))
    .map((badgeItem) => badgeItem.label)
);

const ADS_ROWS_PER_PAGE_OPTIONS = [5, 10, 15];
const AD_PUBLISH_MODE_OPTIONS = [
  { value: "project", label: "وابسته به پروژه" },
  { value: "manual", label: "انتشار موردی" },
  { value: "scheduled", label: "انتشار زمان بندی شده" },
];
const NEWS_PUBLISH_SOURCE_ALL_ID = "__all_publish_sources__";
const NEWS_PROJECT_ALL_ID = "__all_projects__";
const NEWS_PUBLISH_SOURCE_ALL_OPTION = {
  id: NEWS_PUBLISH_SOURCE_ALL_ID,
  name: "همه",
  platform: "همه",
  link: "",
};

const SearchableSourceSelect = ({
  id,
  value,
  options,
  placeholder,
  labelKey,
  onSelect,
}) => {
  const wrapperRef = useRef(null);
  const searchInputRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const getOptionLabel = (option) => String(option?.[labelKey] || "");
  const selectedOption = useMemo(
    () => options.find((option) => String(option?.id) === String(value || "")) || null,
    [options, value]
  );

  useEffect(() => {
    if (!isOpen) {
      setSearchText("");
    }
  }, [isOpen, value]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const filteredOptions = useMemo(() => {
    const normalizedSearchText = String(searchText || "").trim().toLowerCase();
    if (!normalizedSearchText) {
      return options;
    }
    return options.filter((option) => (
      getOptionLabel(option).toLowerCase().includes(normalizedSearchText)
    ));
  }, [options, searchText, labelKey]);

  const handleToggleDropdown = () => {
    setIsOpen((prev) => {
      const next = !prev;
      if (next) {
        setSearchText("");
      }
      return next;
    });
  };

  const handleSearchChange = (event) => {
    setSearchText(String(event.target.value || ""));
  };

  const handleSelectOption = (option) => {
    const selectedId = String(option?.id || "");
    onSelect(selectedId, option);
    setSearchText(getOptionLabel(option));
    setIsOpen(false);
  };

  return (
    <div ref={wrapperRef} className="searchable-dropdown">
      <input
        id={id}
        className="searchable-dropdown-trigger"
        value={selectedOption ? getOptionLabel(selectedOption) : ""}
        placeholder={placeholder}
        readOnly
        onClick={handleToggleDropdown}
        aria-expanded={isOpen}
        autoComplete="off"
      />
      {isOpen && (
        <div className="searchable-dropdown-menu">
          <div className="searchable-dropdown-search-row">
            <input
              ref={searchInputRef}
              className="searchable-dropdown-search-input"
              value={searchText}
              onChange={handleSearchChange}
              placeholder="جستجو..."
              autoComplete="off"
            />
          </div>
          <div className="searchable-dropdown-options">
            {filteredOptions.length > 0 ? filteredOptions.map((option) => (
              <button
                key={`search-option-${id}-${option.id}`}
                type="button"
                className={`searchable-dropdown-option ${String(option?.id) === String(value || "") ? "active" : ""}`}
                onClick={() => handleSelectOption(option)}
              >
                {getOptionLabel(option)}
              </button>
            )) : (
              <div className="searchable-dropdown-empty">موردی پیدا نشد</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};


const REPORT_PERIOD_TABS = ["روزانه", "هفتگی", "ماهانه", "سالانه"];
const REPORT_PERIOD_TO_API = {
  روزانه: "daily",
  هفتگی: "weekly",
  ماهانه: "monthly",
  سالانه: "yearly",
};
const REPORT_CARD_KEYS = [
  "news-count",
  "active-sources",
  "published-news",
  "received-news",
];
const REPORT_DEFAULT_CARD_FILTER = {
  period: "ماهانه",
  projectFilter: "all",
};
const createInitialReportsCardFilters = () => (
  REPORT_CARD_KEYS.reduce((acc, cardKey) => {
    acc[cardKey] = {
      ...REPORT_DEFAULT_CARD_FILTER,
      period: cardKey === "published-news"
        ? "روزانه"
        : cardKey === "received-news"
          ? "روزانه"
          : REPORT_DEFAULT_CARD_FILTER.period,
    };
    return acc;
  }, {})
);
const REPORT_REALTIME_RANGE_OPTIONS = [
  { value: "15", label: "۱۵ دقیقه پیش" },
  { value: "30", label: "۳۰ دقیقه پیش" },
  { value: "60", label: "۶۰ دقیقه پیش" },
];
const DASHBOARD_ACTIVE_TAB_STORAGE_KEY = "dashboard_active_tab";
const DASHBOARD_PROJECT_VIEW_ID_STORAGE_KEY = "dashboard_project_view_id";
const DASHBOARD_PROJECT_VIEW_TITLE_STORAGE_KEY = "dashboard_project_view_title";
const DASHBOARD_PROJECT_VIEW_NEWS_TAB_STORAGE_KEY = "dashboard_project_view_news_tab";
const DASHBOARD_NEWS_VIEW_SNAPSHOT_STORAGE_KEY = "dashboard_news_view_snapshot";

const REPORT_PUBLISHED_NEWS_POINTS = [1, 3, 3, 5, 5, 3, 7];
const REPORT_RECEIVED_NEWS_POINTS = [1, 3, 3, 5, 5, 3, 7];
const REPORT_WEEKDAY_LABELS = ["شنبه", "یکشنبه", "دوشنبه", "سه شنبه", "چهارشنبه", "پنجشنبه", "جمعه"];

const SETTINGS_TOP_TABS = [
  { key: "settings", label: "تنظیمات" },
  { key: "security", label: "امنیت" },
  { key: "backup", label: "بکاپ و بازیابی" },
  { key: "homepage-management", label: "مدیریت صفحه اصلی" },
];

const INITIAL_SETTINGS_ACCOUNTS = [
  {
    id: "account-1",
    row: 18,
    username: "@khabar-fori",
    accountReference: "rubika.ir/khabar-fori",
    platform: "روبیکا",
    updatedAt: "1405/01/15 09:12",
    status: "active",
  },
  {
    id: "account-2",
    row: 72,
    username: "@safir-e-goftego",
    accountReference: "@safir-e-goftego",
    platform: "بله",
    updatedAt: "1405/01/14 11:41",
    status: "inactive",
  },
  {
    id: "account-3",
    row: 90,
    username: "@niktan",
    accountReference: "rubika.ir/niktan",
    platform: "روبیکا",
    updatedAt: "1405/01/13 18:56",
    status: "active",
  },
];

const SETTINGS_BOT_ROWS = [
  {
    id: "bot-1",
    row: 1,
    platform: "روبیکا",
    botName: "@rubika-agent",
    status: "متصل",
    guide: "راهنمای اتصال",
  },
  {
    id: "bot-2",
    row: 2,
    platform: "بله",
    botName: "@bale-agent",
    status: "متصل",
    guide: "راهنمای اتصال",
  },
  {
    id: "bot-3",
    row: 3,
    platform: "ایتا",
    botName: "@eita-agent",
    status: "غیرفعال",
    guide: "راهنمای اتصال",
  },
];

const normalizeSettingsBotRowFromApi = (botRow, index = 0) => ({
  id: String(botRow?.id ?? `bot-${index + 1}`),
  row: Number(botRow?.row ?? index + 1),
  platform: String(botRow?.platform || ""),
  botName: String(botRow?.bot_name || botRow?.name || ""),
  link: String(botRow?.link || ""),
  status: String(botRow?.status || "غیرفعال"),
  guide: String(botRow?.guide || "راهنمای اتصال"),
  helpDescription: String(botRow?.help_description || ""),
  helpImages: Array.isArray(botRow?.help_images)
    ? botRow.help_images
      .map((item, itemIndex) => ({
        id: String(item?.id ?? `${botRow?.id || index + 1}-img-${itemIndex + 1}`),
        priority: Number(item?.priority || itemIndex + 1),
        url: buildAbsoluteFileUrl(item?.url || ""),
      }))
      .filter((item) => Boolean(item.url))
      .sort((a, b) => a.priority - b.priority)
    : [],
});

const SETTINGS_SECURITY_WHITE_IP_ROWS = [];

const normalizeSecurityWhiteIpRowFromApi = (row, index = 0) => ({
  id: String(row?.id ?? `white-ip-${index + 1}`),
  userName: String(row?.user_name || "--"),
  role: String(row?.role || "--"),
  ip: String(row?.ip || "--"),
  status: String(row?.ip_status || "mojaz").toLowerCase() === "mojaz" ? "active" : "inactive",
  statusLabel: String(row?.ip_status_label || "مجاز"),
  time: formatDateTimeForSettingsTable(row?.created_whitelist_at),
});

const normalizeSecurityBlockedAccountRowFromApi = (row, index = 0) => ({
  id: String(row?.id ?? `security-block-${index + 1}`),
  account: String(row?.account || "--"),
  time: formatDateTimeForSettingsTable(row?.blocked_at),
  status: String(row?.status || "بلاک شده"),
});

const INITIAL_SETTINGS_BACKUP_ROWS = [
  {
    id: "backup-1",
    code: "2001",
    createdAt: "1405/01/16 11:50",
    status: "running",
    statusLabel: "در حال پشتیبان‌گیری",
    size: "۵۰۰ مگابایت",
  },
  {
    id: "backup-2",
    code: "2002",
    createdAt: "1405/01/15 11:50",
    status: "done",
    statusLabel: "انجام شده",
    size: "۲۵۰۰ مگابایت",
  },
  {
    id: "backup-3",
    code: "2003",
    createdAt: "1405/01/15 14:48",
    status: "done",
    statusLabel: "انجام شده",
    size: "۳۰۰ مگابایت",
  },
];

const SETTINGS_BACKUP_SCHEDULE_OPTIONS = [
  { value: "daily", label: "روزانه" },
  { value: "weekly", label: "هفتگی" },
  { value: "monthly", label: "ماهانه" },
];

const SETTINGS_BACKUP_SCHEDULE_LABELS = SETTINGS_BACKUP_SCHEDULE_OPTIONS.reduce((result, option) => {
  result[option.value] = option.label;
  return result;
}, {});

const BACKUP_STATUS_LABELS = {
  running: "در حال پشتیبان‌گیری",
  done: "انجام شده",
  failed: "ناموفق",
};

const canUseBackupFileActions = (status) => String(status || "").toLowerCase() === "done";

const formatDateTimeForSettingsTable = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "--";
  }
  return date.toLocaleString("fa-IR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).replace("،", "");
};

const normalizeSettingsBackupRowFromApi = (row, index = 0) => ({
  id: String(row?.id ?? `backup-${index + 1}`),
  code: String(row?.code ?? ""),
  createdAt: formatDateTimeForSettingsTable(row?.created_at),
  status: String(row?.status || "failed"),
  statusLabel: String(row?.status_label || BACKUP_STATUS_LABELS[String(row?.status || "")] || "ناموفق"),
  size: String(row?.size_label || "--"),
  isCurrentVersion: Boolean(row?.is_current_version),
});

const normalizeSecurityLoginAttemptFromApi = (row, index = 0) => {
  const status = String(row?.status || "").toLowerCase() === "success" ? "success" : "failed";
  return {
    id: String(row?.id ?? `login-attempt-${index + 1}`),
    ip: String(row?.ip_address || "--"),
    status,
    statusLabel: String(row?.status_label || (status === "success" ? "موفق" : "ناموفق")),
    failureReason: String(row?.failure_reason || ""),
    failedUsernameInput: String(row?.failed_username_input || ""),
    failedPasswordInput: String(row?.failed_password_input || ""),
    successfulUsername: String(row?.successful_username || ""),
    attemptedAt: formatDateTimeForSettingsTable(row?.attempted_at),
  };
};

const PROJECT_INTERVAL_OPTIONS = ["1", "2", "5", "10", "15"];
const PROJECT_REWRITE_MODE_OPTIONS = [
  { value: "enabled", label: "فعال" },
  { value: "disabled", label: "غیرفعال" },
];
const PROJECT_WATERMARK_POSITION_OPTIONS = [
  { value: "bottom-right", label: "پایین راست" },
  { value: "bottom-left", label: "پایین چپ" },
  { value: "top-right", label: "بالا راست" },
  { value: "top-left", label: "بالا چپ" },
  { value: "center", label: "مرکز" },
];
const PROJECT_WATERMARK_TARGET_OPTIONS = [
  { value: "video", label: "ویدئو" },
  { value: "image", label: "عکس" },
  { value: "all", label: "همه رسانه‌ها" },
];
const PROJECT_WATERMARK_FONT_OPTIONS = [
  { value: "Vazirmatn", label: "وزیرمتن" },
  { value: "IRANSans", label: "ایران سنس" },
  { value: "Sahel", label: "ساحل" },
  { value: "Shabnam", label: "شبنم" },
  { value: "YekanBakh", label: "یکان بخ" },
  { value: "Dana", label: "دانا" },
  { value: "Tanha", label: "تنها" },
];
const PROJECT_WATERMARK_SIZE_OPTIONS = [
  { value: "12", label: "12px" },
  { value: "14", label: "14px" },
  { value: "16", label: "16px" },
  { value: "18", label: "18px" },
  { value: "20", label: "20px" },
  { value: "24", label: "24px" },
  { value: "28", label: "28px" },
  { value: "32", label: "32px" },
];
const PROJECT_WATERMARK_COLOR_PRESETS = [
  { value: "#FFFFFF", label: "سفید" },
  { value: "#000000", label: "مشکی" },
  { value: "#EF4444", label: "قرمز" },
  { value: "#22C55E", label: "سبز" },
  { value: "#3B82F6", label: "آبی" },
  { value: "#F59E0B", label: "نارنجی" },
  { value: "#A855F7", label: "بنفش" },
];
const TEXT_PROCESS_TARGET_OPTIONS = [
  { value: "title", label: "اعمال روی عنوان" },
  { value: "text", label: "اعمال روی متن" },
  { value: "both", label: "اعمال روی هر دو" },
];
// AI rules only. "unified" sends the headline and the body to the model as a
// single input and uses the single result as the whole post, so it has no
// meaning for the phrase-based system rules and is deliberately not offered
// in their dropdown.
const AI_TEXT_PROCESS_TARGET_OPTIONS = [
  ...TEXT_PROCESS_TARGET_OPTIONS,
  { value: "unified", label: "اعمال یکپارچه" },
];
const SOURCE_CREATE_STATUS_OPTIONS = [
  { value: "active", label: "فعال" },
  { value: "inactive", label: "غیرفعال" },
];
const CONTENT_CLASSIFY_MEDIA_OPTIONS = [
  { value: "video", label: "ویدیو" },
  { value: "image", label: "عکس" },
  { value: "text", label: "متن" },
  { value: "file", label: "فایل" },
  { value: "audio", label: "صوت" },
  { value: "text+video", label: "متن + ویدیو" },
  { value: "text+image", label: "متن + عکس" },
];
const SETTINGS_ACCOUNT_PLATFORM_OPTIONS = ["روبیکا", "بله", "ایتا"];
const SETTINGS_ACCOUNT_STATUS_OPTIONS = [
  { value: "active", label: "فعال" },
  { value: "inactive", label: "غیرفعال" },
];

const createInitialSourceSettings = () => ({
  text_process_mode: "",
  system_text_process_active: false,
  text_process_title_isolated: false,
  system_text_rules: [],
  system_text_max_chars: "",
  system_text_remove_phrase: "",
  system_text_prefix: "",
  system_text_suffix: "",
  ai_activity_status: false,
  ai_text_process_active: false,
  ai_text_prompt: "",
  ai_text_rules: [],
  media_process_active: false,
  media_process_mode: "",
  media_apply_to: "image_and_video",
  media_logo_file: "",
  media_logo_file_file: null,
  media_logo_file_clear: false,
  media_logo_preview_url: "",
  media_logo_size: "",
  media_logo_color: "",
  media_logo_x: "0",
  media_logo_y: "0",
  media_text_content: "",
  media_text_size: PROJECT_WATERMARK_SIZE_OPTIONS[2].value,
  media_text_color: "",
  media_text_font: PROJECT_WATERMARK_FONT_OPTIONS[0].value,
  media_text_x: "0",
  media_text_y: "0",
  media_opacity: "100",
  ai_media_process_active: false,
  ai_media_prompt: "",
  ai_media_model: "",
  is_content_classify: false,
  allowed_media: [],
});

const createInitialNewsMediaSettings = () => ({
  ...createInitialSourceSettings(),
  watermark_file: "",
  watermark_file_file: null,
  watermark_text: "",
  watermark_font: PROJECT_WATERMARK_FONT_OPTIONS[0].value,
  watermark_position: PROJECT_WATERMARK_POSITION_OPTIONS[0].value,
  watermark_size: PROJECT_WATERMARK_SIZE_OPTIONS[2].value,
  watermark_color: PROJECT_WATERMARK_COLOR_PRESETS[0].value,
  watermark_status: "enabled",
  watermark_apply_to: PROJECT_WATERMARK_TARGET_OPTIONS[0].value,
  media_process_mode: "text",
});

// Which media kinds the watermark covers. Anything outside the chosen scope is
// received and published untouched rather than held back.
const PROJECT_MEDIA_APPLY_TO_OPTIONS = [
  { value: "image_and_video", label: "افزودن به تصویر و ویدئو" },
  { value: "image", label: "افزودن به تصویر" },
  { value: "video", label: "افزودن به ویدئو" },
];

const deriveAiActivityStatus = (sourceValues = {}) => (
  String(sourceValues.text_process_mode || "").trim().toLowerCase() === "ai"
);

const hasSourceSettingsInDb = (sourceValues = {}) => {
  const asText = (value) => String(value ?? "").trim();
  const asNumber = (value) => Number(value);

  return (
    Boolean((sourceValues.system_text_rules || []).length)
    || (Number.isFinite(asNumber(sourceValues.system_text_max_chars)) && asNumber(sourceValues.system_text_max_chars) > 0)
    || Boolean(asText(sourceValues.system_text_remove_phrase))
    || Boolean(asText(sourceValues.system_text_prefix))
    || Boolean(asText(sourceValues.system_text_suffix))
    || Boolean((sourceValues.ai_text_rules || []).length)
    || Boolean(sourceValues.text_process_title_isolated)
    || Boolean(asText(sourceValues.ai_text_prompt))
    || Boolean(sourceValues.media_process_active)
    || Boolean(asText(sourceValues.media_process_mode))
    || Boolean(asText(sourceValues.media_text_content))
    || Boolean(asText(sourceValues.ai_media_prompt))
    || Boolean(asText(sourceValues.ai_media_model))
    || Boolean(asText(sourceValues.media_logo_file))
  );
};

const buildSourceSettingsFromOption = (sourceOption = {}) => {
  const base = createInitialSourceSettings();
  const derivedMode = (() => {
    if (String(sourceOption.text_process_mode || "").trim()) {
      return String(sourceOption.text_process_mode || "").trim();
    }
    if (Array.isArray(sourceOption.ai_text_rules) && sourceOption.ai_text_rules.length > 0) {
      return "ai";
    }
    if (Array.isArray(sourceOption.system_text_rules) && sourceOption.system_text_rules.length > 0) {
      return "system";
    }
    return base.text_process_mode;
  })();
  const derivedMediaMode = (() => {
    const explicitMode = String(sourceOption.media_process_mode || "").trim().toLowerCase();
    if (explicitMode === "logo" || explicitMode === "text") {
      return explicitMode;
    }
    if (explicitMode) {
      return "";
    }
    if (!Boolean(sourceOption.media_process_active)) {
      return base.media_process_mode;
    }
    if (String(sourceOption.media_logo_file_url || sourceOption.media_logo_file || "").trim() || String(sourceOption.media_logo_color || "").trim() || Number(sourceOption.media_logo_size || 0) > 0) {
      return "logo";
    }
    if (String(sourceOption.media_text_content || "").trim()) {
      return "text";
    }
    return base.media_process_mode;
  })();
  return {
    text_process_mode: derivedMode,
    system_text_process_active: derivedMode === "system",
    text_process_title_isolated: Boolean(sourceOption.text_process_title_isolated),
    system_text_rules: Array.isArray(sourceOption.system_text_rules) ? sourceOption.system_text_rules : [],
    system_text_max_chars: String(sourceOption.system_text_max_chars ?? base.system_text_max_chars),
    system_text_remove_phrase: String(sourceOption.system_text_remove_phrase || ""),
    system_text_prefix: String(sourceOption.system_text_prefix || ""),
    system_text_suffix: String(sourceOption.system_text_suffix || ""),
    ai_text_process_active: derivedMode === "ai",
    ai_text_prompt: String(sourceOption.ai_text_prompt || ""),
    ai_text_rules: Array.isArray(sourceOption.ai_text_rules) ? sourceOption.ai_text_rules : [],
    media_process_active: Boolean(derivedMediaMode),
    media_process_mode: derivedMediaMode,
    media_apply_to: String(sourceOption.media_apply_to || "image_and_video"),
    media_logo_file: String(sourceOption.media_logo_file_url || sourceOption.media_logo_file || ""),
    media_logo_file_file: null,
    media_logo_file_clear: false,
    media_logo_preview_url: String(sourceOption.media_logo_file_url || ""),
    media_logo_size: String(sourceOption.media_logo_size ?? base.media_logo_size),
    media_logo_color: String(sourceOption.media_logo_color || base.media_logo_color),
    media_logo_x: String(sourceOption.media_logo_x ?? base.media_logo_x),
    media_logo_y: String(sourceOption.media_logo_y ?? base.media_logo_y),
    media_text_content: String(sourceOption.media_text_content || ""),
    media_text_size: String(sourceOption.media_text_size ?? base.media_text_size),
    media_text_color: String(sourceOption.media_text_color || base.media_text_color),
    media_text_font: String(sourceOption.media_text_font || base.media_text_font),
    media_text_x: String(sourceOption.media_text_x ?? base.media_text_x),
    media_text_y: String(sourceOption.media_text_y ?? base.media_text_y),
    media_opacity: String(sourceOption.media_opacity ?? base.media_opacity),
    ai_media_process_active: false,
    ai_media_prompt: String(sourceOption.ai_media_prompt || ""),
    ai_media_model: String(sourceOption.ai_media_model || ""),
    allowed_media: Array.isArray(sourceOption.allowed_media)
      ? sourceOption.allowed_media.map((item) => String(item || "").trim().toLowerCase()).filter(Boolean)
      : [],
    is_content_classify: Boolean(sourceOption.is_content_classify),
  };
};


const normalizeReceivePlatformValue = (platformValue = "") => {
  const normalized = String(platformValue || "").trim().toLowerCase();
  if (normalized === "telegram" || normalized === "ØªÙ„Ú¯Ø±Ø§Ù…" || normalized === "تلگرام") {
    return "telegram";
  }
  if (normalized === "روبیکا") {
    return "rubika";
  }
  if (normalized === "بله" || normalized === "bale" || normalized === "ble") {
    return "bale";
  }
  if (normalized === "eita with video" || normalized === "eita_with_video" || normalized === "ایتا با ویدئو" || normalized === "ایتا با ویدیو" || normalized === "ایتا با ویدئو (نیازمند اکانت ایتا)") {
    return "eita with video";
  }
  if (normalized === "ایتا" || normalized === "eitaa") {
    return "eita";
  }
  if (normalized === "rss") {
    return "rss";
  }
  if (normalized === "price" || normalized === "دریافت قیمت" || normalized === "دريافت قيمت") {
    return "price";
  }
  return normalized;
};

const getReceivePlatformLabel = (platformValue = "") => {
  const normalized = normalizeReceivePlatformValue(platformValue);
  if (normalized === "telegram") {
    return "تلگرام";
  }
  if (normalized === "rubika") {
    return "روبیکا";
  }
  if (normalized === "bale") {
    return "بله";
  }
  if (normalized === "eita with video") {
    return "ایتا با ویدئو (نیازمند اکانت ایتا)";
  }
  if (normalized === "eita") {
    return "ایتا";
  }
  if (normalized === "rss") {
    return "RSS";
  }
  if (normalized === "price") {
    return "دریافت قیمت";
  }
  return String(platformValue || "").trim() || "--";
};

const normalizePublishPlatformValue = (platformValue = "") => {
  const normalized = String(platformValue || "").trim().toLowerCase();
  if (normalized === "روبیکا") {
    return "rubika";
  }
  if (normalized === "بله") {
    return "bale";
  }
  if (normalized === "telegram" || normalized === "تلگرام") {
    return "telegram";
  }
  if (normalized === "ایتا") {
    return "eita";
  }
  if (normalized === "سروش") {
    return "soroush";
  }
  if (normalized === "آیگپ" || normalized === "ايگپ") {
    return "igap";
  }
  return normalized;
};

const getPublishPlatformLabel = (platformValue = "") => {
  const normalized = normalizePublishPlatformValue(platformValue);
  const matchedOption = FIXED_PUBLISH_PLATFORM_NAME_OPTIONS.find(
    (option) => String(option.value || "").trim().toLowerCase() === normalized
  );
  return matchedOption?.label || String(platformValue || "").trim() || "--";
};

const getPublishSourceLinkPlaceholder = (platformValue = "") => {
  const normalized = normalizePublishPlatformValue(platformValue);
  if (normalized === "telegram") {
    return "@channel_username";
  }
  return "@khabar_online";
};

const normalizePublishSourceLinkByPlatform = ({ platform = "", link = "" }) => {
  const normalizedPlatform = normalizePublishPlatformValue(platform);
  const normalizedLink = String(link || "").trim();
  if (!normalizedLink) {
    return "";
  }

  if (
    normalizedPlatform === "telegram"
    && (normalizedLink.startsWith("http://") || normalizedLink.startsWith("https://"))
  ) {
    try {
      const parsed = new URL(normalizedLink);
      const segments = parsed.pathname.split("/").map((segment) => segment.trim()).filter(Boolean);
      const lastSegment = segments[segments.length - 1] || "";
      if (lastSegment) {
        return `@${lastSegment.replace(/^@+/, "")}`;
      }
    } catch (error) {
      // Keep the original value when the link is not a valid URL.
    }
  }

  return normalizedLink;
};

const createInitialSourceCreateForm = () => ({
  source_name: "",
  source_platform: "rss",
  source_link: "",
  price_template_id: "",
  receive_interval_seconds: "60",
  rubika_account_id: "",
  eita_account_id: "",
  anti_ad_mode: "none",
  anti_ad_with_ai_prompt: "",
  anti_ad_with_system_rules: [
    {
      field_1: "",
      field_2: "",
      field_3: "",
      field_4: "",
      selected_flag: "is_ad",
    },
  ],
});

const getReceiveSourceLinkPlaceholder = (platformValue = "") => {
  const normalized = normalizeReceivePlatformValue(platformValue);
  if (normalized === "telegram") {
    return "@channel_username یا https://t.me/channel_username";
  }
  if (normalized === "bale") {
    return "@channel_username یا https://ble.ir/channel";
  }
  if (normalized === "eita" || normalized === "eita with video") {
    return "@channel_username یا https://eitaa.com/channel";
  }
  if (normalized === "rubika") {
    return "@channel_username یا https://rubika.ir/channel";
  }
  if (normalized === "rss") {
    return "https://example.com/rss";
  }
  return "";
};


const DEFAULT_PRICE_TEMPLATE_TITLE = "قالب قیمت بازار";
const DEFAULT_PRICE_NEWS_TITLE_TEMPLATE = "نرخ‌های لحظه‌ای بازار - {date} ساعت {time}";
const DEFAULT_PRICE_TEXT_TEMPLATE = `📅 تاریخ: {date} | ⏰ زمان: {time}
===========================
💵 دلار آمریکا: {USD} تومان ({USD_change}%)
🇪🇺 یورو: {EUR} تومان ({EUR_change}%)
🪙 تتر: {USDT_IRT} تومان ({USDT_IRT_change}%)

🌕 طلای 18 عیار: {IR_GOLD_18K} تومان ({IR_GOLD_18K_change}%)
🪙 سکه امامی: {IR_COIN_EMAMI} تومان ({IR_COIN_EMAMI_change}%)

⚡️ بیت‌کوین: {BTC} دلار ({BTC_change}%)
⚡️ اتریوم: {ETH} دلار ({ETH_change}%)
===========================
🆔 @Channel_ID`;

const createInitialPriceTemplateForm = () => ({
  title: "",
  news_title_template: "",
  text_template: "",
  is_active: true,
});

const normalizePriceTemplateRowFromApi = (templateItem = {}) => ({
  id: templateItem?.id,
  title: String(templateItem?.title || "").trim(),
  receiveSourceIds: Array.isArray(templateItem?.receive_source_ids)
    ? templateItem.receive_source_ids.map((value) => String(value || "").trim()).filter(Boolean)
    : (templateItem?.receive_source_id ? [String(templateItem.receive_source_id)] : []),
  receiveSourceNames: Array.isArray(templateItem?.receive_source_names)
    ? templateItem.receive_source_names.map((value) => String(value || "").trim()).filter(Boolean)
    : [],
  receiveSourceName: String(
    templateItem?.receive_source_name
    || templateItem?.receive_source
    || templateItem?.source_name
    || ""
  ).trim(),
  newsTitleTemplate: String(templateItem?.news_title_template || "").trim(),
  textTemplate: String(templateItem?.text_template || "").trim(),
  isActive: Boolean(templateItem?.is_active),
  userId: templateItem?.user_id ? String(templateItem.user_id) : "",
  updatedAt: formatNewsDate(String(templateItem?.updated_at || templateItem?.created_at || "")),
});

const createInitialPublishSourceCreateForm = () => ({
  source_name: "",
  source_platform_name: FIXED_PUBLISH_PLATFORM_NAME_OPTIONS[0]?.value || "rubika",
  source_platform: FIXED_PUBLISH_PLATFORM_NAME_OPTIONS[0]?.value || "rubika",
  source_link: "",
  received_news_count: "0",
});

const createInitialUserCreateForm = () => ({
  firstName: "",
  lastName: "",
  managerName: "",
  email: "",
  phone: "",
  profileImageUrl: "",
  profileImageName: "",
  profileImageFile: null,
  profileImageCleared: false,
  username: "",
  password: "",
  repeatPassword: "",
  role: USER_CREATE_ROLE_OPTIONS[0],
  status: "active",
  projects: [],
  systemSections: [],
});

const createInitialSettingsAccountForm = () => ({
  accountName: "",
  accountReference: "",
  platform: SETTINGS_ACCOUNT_PLATFORM_OPTIONS[0],
  status: SETTINGS_ACCOUNT_STATUS_OPTIONS[0].value,
  provider: "rubika",
  phone: "",
  code: "",
  codeSent: false,
  signupRequired: false,
  firstName: "",
  lastName: "",
});

const createSecuritySettingsSnapshot = ({
  apiRestrictionMode = "enabled",
  whiteIpRows = [],
} = {}) => ({
  apiRestrictionMode,
  whiteIpRows: whiteIpRows.map((row) => ({ ...row })),
});

const INITIAL_SECURITY_SETTINGS_SNAPSHOT = createSecuritySettingsSnapshot({
  apiRestrictionMode: "enabled",
  whiteIpRows: SETTINGS_SECURITY_WHITE_IP_ROWS,
});

const splitUserName = (fullName = "") => {
  const normalizedName = String(fullName || "").trim();
  if (!normalizedName) {
    return { firstName: "", lastName: "" };
  }

  const [firstName = "", ...lastNameParts] = normalizedName.split(/\s+/);

  return {
    firstName,
    lastName: lastNameParts.join(" "),
  };
};

const normalizeUserRowFromApi = (userItem = {}) => {
  const firstName = String(userItem.first_name || "").trim();
  const lastName = String(userItem.last_name || "").trim();
  const fullName = (
    String(userItem.full_name || "").trim()
    || `${firstName} ${lastName}`.trim()
    || String(userItem.username || "").trim()
    || "--"
  );
  const managerName = String(userItem.manager_name || "").trim() || "--";
  const projects = Array.isArray(userItem.projects)
    ? userItem.projects.map((item) => String(item || "").trim()).filter(Boolean)
    : [];
  const modules = Array.isArray(userItem.modules)
    ? userItem.modules
      .map((item) => USER_MODULE_FROM_API[String(item || "").trim()] || String(item || "").trim())
      .filter(Boolean)
    : [];
  const statusKey = userItem.activity_status ? "active" : "inactive";

  return {
    id: String(userItem.id || userItem.code || createProjectFormRowId("user")),
    code: String(userItem.code || userItem.id || "--"),
    role: USER_ROLE_FROM_API[String(userItem.role || "").trim()] || "مدیر پروژه",
    createdAt: formatNewsDate(userItem.created_at),
    createdAtRaw: String(userItem.created_at || ""),
    name: fullName,
    managerName,
    phone: String(userItem.phone || "").trim() || "--",
    projectTitle: projects[0] || "--",
    projects,
    systemSections: modules,
    status: statusKey,
    username: String(userItem.username || "").trim(),
    email: String(userItem.email || "").trim(),
    profileImageUrl: buildAbsoluteFileUrl(userItem.profile_image),
    profileImageName: "",
    profileImageFile: null,
    profileImageCleared: false,
  };
};

const buildUserCreateFormFromRow = (userRow) => {
  const { firstName, lastName } = splitUserName(userRow?.name);
  const selectedProjects = Array.isArray(userRow?.projects)
    ? userRow.projects.filter(Boolean)
    : [];
  const fallbackProject = String(userRow?.projectTitle || "").trim();
  const normalizedStatus = String(userRow?.status || "").trim().toLowerCase();
  const statusKey = (
    normalizedStatus.includes("inactive")
    || normalizedStatus.includes("غیرفعال")
    || normalizedStatus.includes("disabled")
  )
    ? "inactive"
    : "active";
  const normalizedRole = String(userRow?.role || "").trim();
  const roleValue = USER_CREATE_ROLE_OPTIONS.includes(normalizedRole)
    ? normalizedRole
    : USER_CREATE_ROLE_OPTIONS[0];
  const systemSections = Array.isArray(userRow?.systemSections)
    ? userRow.systemSections
      .map((section) => String(section || "").trim())
      .filter(Boolean)
    : [];

  return {
    ...createInitialUserCreateForm(),
    firstName,
    lastName,
    managerName: String(userRow?.managerName || "").trim(),
    email: String(userRow?.email || "").trim(),
    phone: String(userRow?.phone || "").trim(),
    profileImageUrl: String(userRow?.profileImageUrl || "").trim(),
    profileImageName: String(userRow?.profileImageName || "").trim(),
    profileImageFile: null,
    profileImageCleared: false,
    username: (
      String(userRow?.username || "").trim()
      || (userRow?.code ? `user${userRow.code}` : "")
    ),
    role: roleValue,
    status: statusKey,
    projects: selectedProjects.length
      ? selectedProjects
      : (fallbackProject ? [fallbackProject] : []),
    systemSections,
  };
};

const createProjectFormRowId = (prefix) => (
  `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2, 6)}`
);

const createProjectExtraPublishWindow = () => ({
  id: createProjectFormRowId("range"),
  from: "00:00",
  to: "23:59",
});

// View boost. Limits mirror apps/projects/constants.py; the panel only has a
// derivable post URL for these two platforms, so the card stays hidden for the
// rest rather than offering something that cannot work.
const VIEW_BOOST_MIN_COUNT = 100;
const VIEW_BOOST_MAX_COUNT = 10000;
const VIEW_BOOST_MIN_MINUTES = 30;
const VIEW_BOOST_PLATFORMS = ["rubika", "telegram"];
const VIEW_BOOST_PANEL_MIN_QUANTITY = 100;
const VIEW_BOOST_TARGET_INTERVAL_MINUTES = 30;
const VIEW_BOOST_PANEL_START_LAG_MINUTES = 10;
const VIEW_BOOST_PANEL_MINUTES_PER_1000 = 10;

const supportsViewBoost = (platform) => (
  VIEW_BOOST_PLATFORMS.includes(String(platform || "").trim().toLowerCase())
);

// Same split the backend performs, repeated here only to preview it. The order
// the user actually gets is always the one apps/process/view_boost.py computes.
const planViewBoostPreview = (count, minutes) => {
  const total = Number(count) || 0;
  const window = Number(minutes) || 0;
  if (total < VIEW_BOOST_PANEL_MIN_QUANTITY) return null;

  const bySize = Math.floor(total / VIEW_BOOST_PANEL_MIN_QUANTITY);
  const byTime = Math.max(1, Math.floor(window / VIEW_BOOST_TARGET_INTERVAL_MINUTES));
  const orders = Math.max(1, Math.min(bySize, byTime));
  const base = Math.floor(total / orders);
  const lead = VIEW_BOOST_PANEL_START_LAG_MINUTES
    + Math.ceil(base / 1000) * VIEW_BOOST_PANEL_MINUTES_PER_1000;
  const span = Math.max(0, window - lead);
  const interval = orders < 2 ? 0 : Math.round(span / (orders - 1));
  const finish = span + lead;

  return {
    orders,
    quantity: base,
    interval,
    finish,
    feasible: Math.abs(finish - window) <= Math.max(15, Math.floor(window / 10)),
  };
};

const createProjectExtraPublishTarget = () => ({
  id: createProjectFormRowId("publish-target"),
  publish_source_id: "",
  name: "",
  platform: "",
  link: "",
  view_boost_active: false,
  view_boost_count: "",
  view_boost_minutes: "",
});

// Rendered under a publish destination. Reuses project-source-settings-card and
// project-create-grid-4 so it inherits the look of the media settings card with
// no new CSS, and the .switch toggle already defined in Dashboard.css.
// Pull one destination's stored boost settings out of a project payload.
// Empty strings rather than zeros, so an untouched form shows a placeholder
// instead of a literal 0 the user has to clear.
const readViewBoostSettings = (project, publishSourceId) => {
  const rows = Array.isArray(project?.publish_source_settings) ? project.publish_source_settings : [];
  const match = rows.find((row) => String(row?.publish_source_id || "") === String(publishSourceId || ""));
  if (!match) return { view_boost_active: false, view_boost_count: "", view_boost_minutes: "" };
  return {
    view_boost_active: Boolean(match.view_boost_active),
    view_boost_count: match.view_boost_count ? String(match.view_boost_count) : "",
    view_boost_minutes: match.view_boost_minutes ? String(match.view_boost_minutes) : "",
  };
};

const ViewBoostCard = ({ idPrefix, platform, values, onChange }) => {
  if (!supportsViewBoost(platform)) return null;

  const active = Boolean(values?.view_boost_active);
  const count = String(values?.view_boost_count ?? "");
  const minutes = String(values?.view_boost_minutes ?? "");

  const countNumber = Number(count);
  const minutesNumber = Number(minutes);
  const countInvalid = active && count !== ""
    && (!Number.isFinite(countNumber) || countNumber < VIEW_BOOST_MIN_COUNT || countNumber > VIEW_BOOST_MAX_COUNT);
  const minutesInvalid = active && minutes !== ""
    && (!Number.isFinite(minutesNumber) || minutesNumber < VIEW_BOOST_MIN_MINUTES);

  const preview = (active && !countInvalid && !minutesInvalid)
    ? planViewBoostPreview(countNumber, minutesNumber)
    : null;

  return (
    <div className="project-source-settings-card">
      <h5>افزایش بازدید</h5>
      <div className="project-create-grid project-create-grid-4">
        <div className="project-create-field">
          <label htmlFor={`${idPrefix}-view-boost-active`}>بوست بازدید</label>
          <label className="switch">
            <input
              id={`${idPrefix}-view-boost-active`}
              type="checkbox"
              checked={active}
              onChange={(event) => onChange("view_boost_active", event.target.checked)}
            />
            <span className="slider" />
          </label>
        </div>
        <div className="project-create-field">
          <label htmlFor={`${idPrefix}-view-boost-count`}>تعداد بازدید</label>
          <input
            id={`${idPrefix}-view-boost-count`}
            type="number"
            min={VIEW_BOOST_MIN_COUNT}
            max={VIEW_BOOST_MAX_COUNT}
            step="100"
            value={count}
            disabled={!active}
            placeholder={`${VIEW_BOOST_MIN_COUNT} تا ${VIEW_BOOST_MAX_COUNT}`}
            onChange={(event) => onChange("view_boost_count", event.target.value)}
          />
        </div>
        <div className="project-create-field">
          <label htmlFor={`${idPrefix}-view-boost-minutes`}>زمان انجام (دقیقه)</label>
          <input
            id={`${idPrefix}-view-boost-minutes`}
            type="number"
            min={VIEW_BOOST_MIN_MINUTES}
            step="10"
            value={minutes}
            disabled={!active}
            placeholder={`حداقل ${VIEW_BOOST_MIN_MINUTES}`}
            onChange={(event) => onChange("view_boost_minutes", event.target.value)}
          />
        </div>
      </div>
      {countInvalid && (
        <div className="project-process-mode-badge">
          {`تعداد بازدید باید بین ${VIEW_BOOST_MIN_COUNT} و ${VIEW_BOOST_MAX_COUNT} باشد.`}
        </div>
      )}
      {minutesInvalid && (
        <div className="project-process-mode-badge">
          {`زمان انجام نمی‌تواند کمتر از ${VIEW_BOOST_MIN_MINUTES} دقیقه باشد.`}
        </div>
      )}
      {preview && (
        <div className="project-process-mode-badge">
          {`${preview.orders} سفارش ${preview.quantity} تایی`}
          {preview.interval > 0 ? `، هر ${preview.interval} دقیقه` : ""}
          {`، پایان حدود دقیقه ${preview.finish}`}
          {preview.feasible ? "" : " — سرویس‌دهنده نمی‌تواند دقیقا این بازه را پوشش دهد"}
        </div>
      )}
    </div>
  );
};

const createProjectExtraSource = () => ({
  id: createProjectFormRowId("source"),
  receive_source_id: "",
  name: "",
  platform: FIXED_SOURCE_PLATFORM_OPTIONS[0].value,
  link: "",
  show_source_settings: false,
  ...createInitialSourceSettings(),
});

const INITIAL_USERS_ROWS = [];

const USER_ACTIVITY_ROWS = [];

const createInitialProjectForm = () => ({
  title: "",
  status: "active",
  receive_source_id: "",
  receive_source_ids: [],
  source_name: "",
  publish_source_id: "",
  publish_source_ids: [],
  view_boost_active: false,
  view_boost_count: "",
  view_boost_minutes: "",
  source_platform_name: FIXED_SOURCE_PLATFORM_OPTIONS[0].value,
  source_link: FIXED_SOURCE_LINK_OPTIONS[0].value,
  publish_platform_name: FIXED_PUBLISH_PLATFORM_NAME_OPTIONS[0].value,
  publish_platform_type: FIXED_PUBLISH_PLATFORM_TYPE_OPTIONS[0].value,
  publish_link: FIXED_PUBLISH_LINK_OPTIONS[0].value,
  operation_type: "scheduled",
  fetch_interval_minutes: "1",
  publish_interval_seconds: "60",
  reactivation_publish_mode: "resume_from_stop",
  outside_window_mode: "publish_only",
  anti_duplicate_policy: "enabled",
  similarity_threshold: "100",
  publish_window_from: "00:00",
  publish_window_to: "23:59",
  ...createInitialSourceSettings(),
  short_news_replace_policy: "disabled",
  delete_source_name_policy: "disabled",
  delete_source_link_policy: "disabled",
  ad_status: "disabled",
  ad_title: "",
  ad_font_size: "",
});

const buildMockProject = (overrides = {}) => ({
  ...createInitialProjectForm(),
  id: String(overrides.id ?? createProjectFormRowId("project")),
  code: String(overrides.code ?? overrides.id ?? "--"),
  manager_name: overrides.manager_name || "مدیر پروژه",
  created_at: overrides.created_at || new Date().toISOString(),
  source_count: overrides.source_count ?? 1,
  destination_count: overrides.destination_count ?? 1,
  published_news_count: overrides.published_news_count ?? 0,
  extra_publish_windows: overrides.extra_publish_windows || [],
  extra_publish_targets: overrides.extra_publish_targets || [],
  extra_sources: overrides.extra_sources || [],
  ...overrides,
});

const createInitialMockProjects = () => {
  const seedProjects = [
    {
      title: "پایش اخبار فناوری",
      manager_name: "محسن مرادی",
      status: "active",
      source_name: "دیجیاتو",
      source_platform_name: "وب‌سایت",
      source_link: "https://digiato.com/feed",
      publish_platform_name: "تلگرام فناوری",
      publish_platform_type: "telegram",
      publish_link: "@tech_updates",
      fetch_interval_minutes: "2",
      publish_interval_seconds: "300",
      source_count: 2,
      destination_count: 2,
      published_news_count: 186,
    },
    {
      title: "اخبار فوری اقتصاد",
      manager_name: "رویا کریمی",
      status: "active",
      source_name: "اقتصاد آنلاین",
      source_platform_name: "RSS",
      source_link: "https://www.eghtesadonline.com/rss",
      publish_platform_name: "کانال اقتصاد",
      publish_platform_type: "telegram",
      publish_link: "@eghtesad_flash",
      fetch_interval_minutes: "1",
      publish_interval_seconds: "600",
      source_count: 1,
      destination_count: 1,
      published_news_count: 92,
    },
    {
      title: "مانیتورینگ خبر ورزشی",
      manager_name: "مهسا سلیمی",
      status: "inactive",
      source_name: "ورزش سه",
      source_platform_name: "وب‌سایت",
      source_link: "https://www.varzesh3.com/rss/all",
      publish_platform_name: "صفحه اینستاگرام ورزش",
      publish_platform_type: "instagram",
      publish_link: "@sports.news.room",
      fetch_interval_minutes: "5",
      publish_interval_seconds: "900",
      source_count: 3,
      destination_count: 1,
      published_news_count: 47,
    },
    {
      title: "اخبار منطقه‌ای",
      manager_name: "علی براتی",
      status: "active",
      source_name: "ایرنا",
      source_platform_name: "RSS",
      source_link: "https://www.irna.ir/rss",
      publish_platform_name: "وب‌سایت خبر فوری",
      publish_platform_type: "web",
      publish_link: "https://example.com/news",
      fetch_interval_minutes: "2",
      publish_interval_seconds: "300",
      source_count: 2,
      destination_count: 3,
      published_news_count: 133,
    },
  ];

  return Array.from({ length: 100 }, (_, index) => {
    const seed = seedProjects[index % seedProjects.length];
    const idNumber = 1001 + index;
    const day = String((index % 28) + 1).padStart(2, "0");
    const hour = String(8 + (index % 12)).padStart(2, "0");
    const minute = String((index * 7) % 60).padStart(2, "0");

    return buildMockProject({
      ...seed,
      id: String(idNumber),
      code: String(idNumber),
      title: `${seed.title} ${formatFaNumber(index + 1)}`,
      source_name: `${seed.source_name} ${formatFaNumber(index + 1)}`,
      source_link: `${seed.source_link}?src=${idNumber}`,
      created_at: `2026-03-${day}T${hour}:${minute}:00Z`,
      source_count: (index % 4) + 1,
      destination_count: (index % 3) + 1,
      published_news_count: 40 + ((index * 11) % 180),
    });
  });
};

const normalizeProjectRowFromApi = (projectItem = {}) => {
  const statusValue = String(projectItem.status ?? "").trim().toLowerCase();
  const normalizedStatus = statusValue === "inactive" ? "inactive" : "active";
  const aiActivityStatus = deriveAiActivityStatus(projectItem);

  const normalizedReceiveSourceIds = Array.isArray(projectItem.receive_source_ids)
    ? projectItem.receive_source_ids.map((value) => String(value || "")).filter(Boolean)
    : (projectItem.receive_source_id ? [String(projectItem.receive_source_id)] : []);
  const normalizedPublishSourceIds = Array.isArray(projectItem.publish_source_ids)
    ? projectItem.publish_source_ids.map((value) => String(value || "")).filter(Boolean)
    : (projectItem.publish_source_id ? [String(projectItem.publish_source_id)] : []);

  return {
    ...createInitialProjectForm(),
    ...projectItem,
    id: String(projectItem.id || createProjectFormRowId("project")),
    code: String(projectItem.code || projectItem.id || "--"),
    title: String(projectItem.title || "").trim(),
    status: normalizedStatus,
    ai_activity_status: aiActivityStatus,
    manager_name: String(projectItem.manager_name || "مدیر پروژه").trim() || "مدیر پروژه",
    receive_source_id: String(projectItem.receive_source_id || ""),
    receive_source_ids: normalizedReceiveSourceIds,
    source_name: String(projectItem.source_name || projectItem.receive_source || "").trim(),
    publish_source_id: String(projectItem.publish_source_id || ""),
    publish_source_ids: normalizedPublishSourceIds,
    publish_platform_name: String(
      projectItem.publish_platform_name || projectItem.publish_source || ""
    ).trim(),
    publish_interval_seconds: String(projectItem.publish_interval_seconds ?? projectItem.publish_interval_minutes ?? "60"),
    reactivation_publish_mode: String(projectItem.reactivation_publish_mode || "resume_from_stop"),
    outside_window_mode: String(projectItem.outside_window_mode || "publish_only"),
    similarity_threshold: String(projectItem.similarity_threshold ?? "85"),
    publish_window_from: String(projectItem.publish_window_from || "00:00"),
    publish_window_to: String(projectItem.publish_window_to || "23:59"),
    source_count: Number(projectItem.source_count ?? 1),
    destination_count: Number(projectItem.destination_count ?? 1),
    published_news_count: Number(projectItem.published_news_count ?? 0),
    created_at: projectItem.created_at || new Date().toISOString(),
    extra_publish_windows: Array.isArray(projectItem.extra_publish_windows)
      ? projectItem.extra_publish_windows
      : [],
  };
};

const getFileNameFromUrl = (urlValue = "") => {
  const normalized = String(urlValue || "").trim();
  if (!normalized) {
    return "";
  }
  return normalized.split("/").pop()?.split("?")[0] || "";
};

const BASE_MOCK_NEWS_OVERVIEW_BY_PROJECT_ID = {
  "1001": {
    received_news: [
      {
        id: "91001",
        title: "عرضه نسخه بتای iOS با تمرکز روی ابزارهای هوش مصنوعی",
        source_pub_date: "2026-04-15T09:15:00Z",
        source_name: "دیجیاتو",
        media_type: "image",
        link: "https://example.com/news/ios-ai-beta",
      },
      {
        id: "91002",
        title: "همکاری جدید شرکت‌های تراشه‌ساز برای مراکز داده",
        source_pub_date: "2026-04-14T12:05:00Z",
        source_name: "زومیت",
        media_type: "text",
        link: "https://example.com/news/chipmakers-collab",
      },
    ],
    published_news: [
      {
        id: "91003",
        title: "گزارش فصل اول بازار لپ‌تاپ منتشر شد",
        source_pub_date: "2026-04-13T08:40:00Z",
        source_name: "دیجیاتو",
        media_type: "photo",
        status: "published",
        publish_link: "https://example.com/news/laptop-q1-report",
      },
    ],
  },
  "1002": {
    received_news: [
      {
        id: "92001",
        title: "افت نرخ تورم ماهانه در گزارش جدید مرکز آمار",
        source_pub_date: "2026-04-15T07:20:00Z",
        source_name: "اقتصاد آنلاین",
        media_type: "text",
        link: "https://example.com/news/inflation-drop",
      },
      {
        id: "92002",
        title: "بررسی وضعیت بازار ارز در شروع هفته",
        source_pub_date: "2026-04-14T10:55:00Z",
        source_name: "اقتصاد آنلاین",
        media_type: "image",
        status: "failed",
        link: "https://example.com/news/fx-market-week",
      },
    ],
    published_news: [
      {
        id: "92003",
        title: "تصمیمات جدید برای حمایت از کسب‌وکارهای کوچک",
        source_pub_date: "2026-04-12T16:10:00Z",
        source_name: "اقتصاد آنلاین",
        media_type: "photo",
        status: "published",
        publish_link: "https://example.com/news/sme-support-plan",
      },
    ],
  },
  "1003": {
    received_news: [
      {
        id: "93001",
        title: "اعلام ترکیب تیم ملی برای رقابت‌های آسیایی",
        source_pub_date: "2026-04-15T11:30:00Z",
        source_name: "ورزش سه",
        media_type: "video",
        link: "https://example.com/news/national-team-lineup",
      },
      {
        id: "93002",
        title: "خلاصه بازی هفته بیست‌وچهارم لیگ برتر",
        source_pub_date: "2026-04-13T19:25:00Z",
        source_name: "ورزش سه",
        media_type: "video",
        link: "https://example.com/news/league-week24-highlights",
      },
    ],
    published_news: [
      {
        id: "93003",
        title: "گزارش ویژه از اردو آماده‌سازی تیم امید",
        source_pub_date: "2026-04-11T09:45:00Z",
        source_name: "ورزش سه",
        media_type: "text",
        status: "published",
        publish_link: "https://example.com/news/u23-camp-report",
      },
    ],
  },
  "1004": {
    received_news: [
      {
        id: "94001",
        title: "بارش پراکنده در چند استان غربی پیش‌بینی شد",
        source_pub_date: "2026-04-15T06:50:00Z",
        source_name: "ایرنا",
        media_type: "text",
        link: "https://example.com/news/west-rain-alert",
      },
      {
        id: "94002",
        title: "افتتاح فاز جدید پروژه حمل‌ونقل شهری",
        source_pub_date: "2026-04-14T09:05:00Z",
        source_name: "ایرنا",
        media_type: "image",
        link: "https://example.com/news/urban-transport-phase",
      },
    ],
    published_news: [
      {
        id: "94003",
        title: "افزایش ظرفیت خدمات درمانی در مراکز منطقه‌ای",
        source_pub_date: "2026-04-12T13:35:00Z",
        source_name: "ایرنا",
        media_type: "image",
        status: "published",
        publish_link: "https://example.com/news/regional-health-capacity",
      },
    ],
  },
};

const MOCK_NEWS_OVERVIEW_BY_PROJECT_ID = (() => {
  const TARGET_NEWS_ROWS = 150;
  const generatedOverview = Object.fromEntries(
    Object.entries(BASE_MOCK_NEWS_OVERVIEW_BY_PROJECT_ID).map(([projectId, overview]) => (
      [
        projectId,
        {
          received_news: [...(overview?.received_news || [])],
          published_news: [...(overview?.published_news || [])],
        },
      ]
    ))
  );
  const countRows = () => (
    Object.values(generatedOverview).reduce((sum, overview) => (
      sum
      + (overview?.received_news?.length || 0)
      + (overview?.published_news?.length || 0)
    ), 0)
  );
  const sourcePool = ["ایرنا", "زومیت", "دیجیاتو", "اقتصاد آنلاین", "ورزش سه"];
  const mediaPool = ["text", "image", "video", "audio"];
  let totalRows = countRows();
  let seed = 0;

  while (totalRows < TARGET_NEWS_ROWS) {
    const projectId = String(1001 + (seed % 100));
    const bucket = generatedOverview[projectId] || { received_news: [], published_news: [] };
    const rowId = `g-${projectId}-${seed + 1}`;
    const day = String(1 + (seed % 28)).padStart(2, "0");
    const hour = String(8 + (seed % 12)).padStart(2, "0");
    const minute = String((seed * 7) % 60).padStart(2, "0");
    const sourceName = sourcePool[seed % sourcePool.length];
    const mediaType = mediaPool[seed % mediaPool.length];
    const nextNewsRow = {
      id: `9${String(50000 + seed)}`,
      title: `خبر آزمایشی شماره ${seed + 1}`,
      source_pub_date: `2026-04-${day}T${hour}:${minute}:00Z`,
      source_name: sourceName,
      media_type: mediaType,
      link: `https://example.com/news/${rowId}`,
    };

    if (seed % 2 === 0) {
      bucket.received_news.push(nextNewsRow);
    } else {
      bucket.published_news.push({
        ...nextNewsRow,
        status: "published",
        publish_link: `https://example.com/published/${rowId}`,
      });
    }

    generatedOverview[projectId] = bucket;
    seed += 1;
    totalRows += 1;
  }

  return generatedOverview;
})();

const formatFaNumber = (value) => new Intl.NumberFormat("fa-IR").format(value);

const div = (a, b) => Math.trunc(a / b);

const mod = (a, b) => a - Math.floor(a / b) * b;

const g2d = (gy, gm, gd) => {
  let dayNumber = (
    div((gy + div(gm - 8, 6) + 100100) * 1461, 4)
    + div(153 * mod(gm + 9, 12) + 2, 5)
    + gd
    - 34840408
  );
  dayNumber = dayNumber - div(div(gy + 100100 + div(gm - 8, 6), 100) * 3, 4) + 752;
  return dayNumber;
};

const d2g = (dayNumber) => {
  let j = 4 * dayNumber + 139361631;
  j = j + div(div(4 * dayNumber + 183187720, 146097) * 3, 4) * 4 - 3908;
  const i = div(mod(j, 1461), 4) * 5 + 308;
  const gd = div(mod(i, 153), 5) + 1;
  const gm = mod(div(i, 153), 12) + 1;
  const gy = div(j, 1461) - 100100 + div(8 - gm, 6);
  return { gy, gm, gd };
};

const jalCal = (jy) => {
  let jump = 0;
  let jp = JALALI_BREAKS[0];
  let jm = 0;
  let leapJ = -14;
  const gy = jy + 621;

  for (let i = 1; i < JALALI_BREAKS.length; i += 1) {
    jm = JALALI_BREAKS[i];
    jump = jm - jp;

    if (jy < jm) {
      break;
    }

    leapJ = leapJ + div(jump, 33) * 8 + div(mod(jump, 33), 4);
    jp = jm;
  }

  let n = jy - jp;
  leapJ = leapJ + div(n, 33) * 8 + div(mod(n, 33) + 3, 4);

  if (mod(jump, 33) === 4 && jump - n === 4) {
    leapJ += 1;
  }

  const leapG = div(gy, 4) - div((div(gy, 100) + 1) * 3, 4) - 150;
  const march = 20 + leapJ - leapG;

  if (jump - n < 6) {
    n = n - jump + div(jump + 4, 33) * 33;
  }

  let leap = mod(mod(n + 1, 33) - 1, 4);
  if (leap === -1) {
    leap = 4;
  }

  return { leap, gy, march };
};

const j2d = (jy, jm, jd) => {
  const r = jalCal(jy);
  return g2d(r.gy, 3, r.march) + (jm - 1) * 31 - div(jm, 7) * (jm - 7) + jd - 1;
};

const d2j = (dayNumber) => {
  const g = d2g(dayNumber);
  let jy = g.gy - 621;
  const r = jalCal(jy);
  const firstFarvardin = g2d(g.gy, 3, r.march);
  let dayDiff = dayNumber - firstFarvardin;

  if (dayDiff >= 0) {
    if (dayDiff <= 185) {
      return {
        jy,
        jm: 1 + div(dayDiff, 31),
        jd: mod(dayDiff, 31) + 1,
      };
    }

    dayDiff -= 186;
  } else {
    jy -= 1;
    dayDiff += 179;
    if (r.leap === 1) {
      dayDiff += 1;
    }
  }

  return {
    jy,
    jm: 7 + div(dayDiff, 30),
    jd: mod(dayDiff, 30) + 1,
  };
};

const toGregorian = (jy, jm, jd) => d2g(j2d(jy, jm, jd));

const toJalali = (gy, gm, gd) => d2j(g2d(gy, gm, gd));

const APP_TIME_ZONE = "Asia/Tehran";
const APP_TIME_ZONE_OFFSET = "+03:30";

const getIntlPersianDateParts = (date, timeZone = APP_TIME_ZONE) => {
  const formatter = new Intl.DateTimeFormat("en-US-u-ca-persian-nu-latn", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });

  const parts = formatter.formatToParts(date);
  const yearPart = parts.find((part) => part.type === "year")?.value;
  const monthPart = parts.find((part) => part.type === "month")?.value;
  const dayPart = parts.find((part) => part.type === "day")?.value;

  const jy = Number(yearPart);
  const jm = Number(monthPart);
  const jd = Number(dayPart);

  if (!Number.isInteger(jy) || !Number.isInteger(jm) || !Number.isInteger(jd)) {
    return null;
  }

  return { jy, jm, jd };
};

const getIntlPersianDateTimeParts = (date, timeZone = APP_TIME_ZONE) => {
  const formatter = new Intl.DateTimeFormat("en-US-u-ca-persian-nu-latn", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

  const parts = formatter.formatToParts(date);
  const yearPart = parts.find((part) => part.type === "year")?.value;
  const monthPart = parts.find((part) => part.type === "month")?.value;
  const dayPart = parts.find((part) => part.type === "day")?.value;
  const hourPart = parts.find((part) => part.type === "hour")?.value;
  const minutePart = parts.find((part) => part.type === "minute")?.value;

  const jy = Number(yearPart);
  const jm = Number(monthPart);
  const jd = Number(dayPart);
  const hour = Number(hourPart);
  const minute = Number(minutePart);

  if (
    !Number.isInteger(jy)
    || !Number.isInteger(jm)
    || !Number.isInteger(jd)
    || !Number.isInteger(hour)
    || !Number.isInteger(minute)
  ) {
    return null;
  }

  return {
    date: { jy, jm, jd },
    time: `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`,
  };
};

const formatJalaliDate = ({ jy, jm, jd }) => (
  `${jy}/${String(jm).padStart(2, "0")}/${String(jd).padStart(2, "0")}`
);

const normalizeLocalizedDigits = (value = "") => (
  String(value || "")
    .replace(/۰/g, "0")
    .replace(/۱/g, "1")
    .replace(/۲/g, "2")
    .replace(/۳/g, "3")
    .replace(/۴/g, "4")
    .replace(/۵/g, "5")
    .replace(/۶/g, "6")
    .replace(/۷/g, "7")
    .replace(/۸/g, "8")
    .replace(/۹/g, "9")
    .replace(/٠/g, "0")
    .replace(/١/g, "1")
    .replace(/٢/g, "2")
    .replace(/٣/g, "3")
    .replace(/٤/g, "4")
    .replace(/٥/g, "5")
    .replace(/٦/g, "6")
    .replace(/٧/g, "7")
    .replace(/٨/g, "8")
    .replace(/٩/g, "9")
);

const parseJalaliDate = (jalaliDateValue) => {
  if (!jalaliDateValue || jalaliDateValue === "all") {
    return null;
  }

  const normalizedDateValue = normalizeLocalizedDigits(jalaliDateValue).replace(/-/g, "/").trim();
  const [yearValue, monthValue, dayValue] = String(normalizedDateValue).split("/");
  const jy = Number(yearValue);
  const jm = Number(monthValue);
  const jd = Number(dayValue);

  if (!Number.isInteger(jy) || !Number.isInteger(jm) || !Number.isInteger(jd)) {
    return null;
  }

  if (jm < 1 || jm > 12) {
    return null;
  }

  return { jy, jm, jd };
};

const buildIsoDateTimeFromJalaliAndTime = (jalaliDateValue, timeValue) => {
  const parsedJalali = parseJalaliDate(jalaliDateValue);
  const normalizedTime = normalizeTimeDigits(timeValue).trim();
  if (!parsedJalali || !/^\d{2}:\d{2}$/.test(normalizedTime)) {
    return "";
  }
  const [hourValue, minuteValue] = normalizedTime.split(":");
  const hour = Number(hourValue);
  const minute = Number(minuteValue);
  if (!Number.isInteger(hour) || !Number.isInteger(minute) || hour < 0 || hour > 23 || minute < 0 || minute > 59) {
    return "";
  }
  const gregorianDate = toGregorian(parsedJalali.jy, parsedJalali.jm, parsedJalali.jd);
  return [
    `${String(gregorianDate.gy).padStart(4, "0")}-${String(gregorianDate.gm).padStart(2, "0")}-${String(gregorianDate.gd).padStart(2, "0")}`,
    `T${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}:00${APP_TIME_ZONE_OFFSET}`,
  ].join("");
};

const mapIsoToJalaliDateTime = (isoValue = "") => {
  const normalized = normalizeLocalizedDigits(isoValue).trim();
  if (!normalized) {
    return { date: "all", time: "" };
  }
  const parsed = new Date(normalized);
  if (Number.isNaN(parsed.getTime())) {
    return { date: "all", time: "" };
  }
  const localizedParts = getIntlPersianDateTimeParts(parsed);
  if (localizedParts) {
    return {
      date: formatJalaliDate(localizedParts.date),
      time: localizedParts.time,
    };
  }
  const jalali = toJalali(parsed.getFullYear(), parsed.getMonth() + 1, parsed.getDate());
  return {
    date: formatJalaliDate(jalali),
    time: `${String(parsed.getHours()).padStart(2, "0")}:${String(parsed.getMinutes()).padStart(2, "0")}`,
  };
};

const normalizeTimeDigits = (timeValue = "") => (
  normalizeLocalizedDigits(timeValue)
);

const toFaTimeValue = (timeValue = "") => {
  const normalizedTime = normalizeTimeDigits(timeValue).trim();

  if (!/^\d{2}:\d{2}$/.test(normalizedTime)) {
    return "";
  }

  return normalizedTime.replace(/\d/g, (digit) => "۰۱۲۳۴۵۶۷۸۹"[Number(digit)]);
};

const getJalaliMonthLength = (jy, jm) => {
  if (jm <= 6) {
    return 31;
  }

  if (jm <= 11) {
    return 30;
  }

  return jalCal(jy).leap === 0 ? 30 : 29;
};

const getJalaliWeekdayIndex = (jy, jm, jd) => {
  const gregorianDate = toGregorian(jy, jm, jd);
  const weekDay = new Date(gregorianDate.gy, gregorianDate.gm - 1, gregorianDate.gd).getDay();
  return (weekDay + 1) % 7;
};

const getTodayJalali = () => {
  const today = new Date();
  const intlDate = getIntlPersianDateParts(today);

  if (intlDate) {
    return intlDate;
  }

  // Fallback for browsers/environments that do not support Intl Persian calendar.
  return toJalali(today.getFullYear(), today.getMonth() + 1, today.getDate());
};

const JalaliDatePicker = ({
  id,
  label,
  value,
  onChange,
  minDate = "all",
  maxDate = "all",
  allowFuture = false,
}) => {
  const wrapperRef = useRef(null);
  const popoverRef = useRef(null);
  const parsedValue = parseJalaliDate(value);
  const todayJalali = getTodayJalali();
  const todayValue = formatJalaliDate(todayJalali);
  const effectiveMaxDate = allowFuture
    ? maxDate
    : ((maxDate === "all" || maxDate > todayValue) ? todayValue : maxDate);
  const parsedEffectiveMaxDate = parseJalaliDate(effectiveMaxDate);
  const [isOpen, setIsOpen] = useState(false);
  const [viewYear, setViewYear] = useState(parsedValue?.jy || todayJalali.jy);
  const [viewMonth, setViewMonth] = useState(parsedValue?.jm || todayJalali.jm);

  useEffect(() => {
    if (parsedValue) {
      setViewYear(parsedValue.jy);
      setViewMonth(parsedValue.jm);
    }
  }, [parsedValue?.jy, parsedValue?.jm]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleClickOutside = (event) => {
      if (!wrapperRef.current?.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const animationFrameId = window.requestAnimationFrame(() => {
      popoverRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "nearest",
      });
    });

    return () => {
      window.cancelAnimationFrame(animationFrameId);
    };
  }, [isOpen]);

  const goToPreviousMonth = () => {
    if (viewMonth === 1) {
      setViewMonth(12);
      setViewYear((prevYear) => prevYear - 1);
      return;
    }

    setViewMonth((prevMonth) => prevMonth - 1);
  };

  const canGoToNextMonth = (
    !parsedEffectiveMaxDate
    || viewYear < parsedEffectiveMaxDate.jy
    || (viewYear === parsedEffectiveMaxDate.jy && viewMonth < parsedEffectiveMaxDate.jm)
  );

  const goToNextMonth = () => {
    if (!canGoToNextMonth) {
      return;
    }

    if (viewMonth === 12) {
      setViewMonth(1);
      setViewYear((prevYear) => prevYear + 1);
      return;
    }

    setViewMonth((prevMonth) => prevMonth + 1);
  };

  const daysInMonth = getJalaliMonthLength(viewYear, viewMonth);
  const firstWeekdayIndex = getJalaliWeekdayIndex(viewYear, viewMonth, 1);
  const isTodayDisabled = (
    (minDate !== "all" && todayValue < minDate)
    || (effectiveMaxDate !== "all" && todayValue > effectiveMaxDate)
  );

  return (
    <div className="dashboard-filter-control dashboard-filter-control-date" ref={wrapperRef}>
      <label htmlFor={id}>{label}</label>
      <button
        type="button"
        id={id}
        className={`dashboard-date-trigger ${value === "all" ? "is-placeholder" : ""}`}
        onClick={() => setIsOpen((prevState) => !prevState)}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
      >
        <span>{value === "all" ? "انتخاب تاریخ" : value}</span>
        <span className="dashboard-date-trigger-icon" aria-hidden="true">⌄</span>
      </button>

      {isOpen && (
        <div
          ref={popoverRef}
          className="jalali-calendar-popover"
          role="dialog"
          aria-label={`تقویم ${label}`}
        >
          <div className="jalali-calendar-header">
            <button
              type="button"
              className="jalali-nav-btn"
              onClick={goToPreviousMonth}
              aria-label="ماه قبل"
            >
              ‹
            </button>
            <strong>
              {PERSIAN_MONTH_NAMES[viewMonth - 1]} {formatFaNumber(viewYear)}
            </strong>
            <button
              type="button"
              className="jalali-nav-btn"
              onClick={goToNextMonth}
              disabled={!canGoToNextMonth}
              aria-label="ماه بعد"
            >
              ›
            </button>
          </div>

          <div className="jalali-calendar-weekdays">
            {PERSIAN_WEEKDAY_SHORT.map((dayLabel) => (
              <span key={dayLabel}>{dayLabel}</span>
            ))}
          </div>

          <div className="jalali-calendar-days">
            {Array.from({ length: firstWeekdayIndex }).map((_, index) => (
              <span key={`calendar-empty-${index}`} className="jalali-day-cell empty" aria-hidden="true" />
            ))}

            {Array.from({ length: daysInMonth }, (_, index) => {
              const dayNumber = index + 1;
              const dateValue = formatJalaliDate({ jy: viewYear, jm: viewMonth, jd: dayNumber });
              const isDisabled = (
                (minDate !== "all" && dateValue < minDate)
                || (effectiveMaxDate !== "all" && dateValue > effectiveMaxDate)
              );
              const isSelected = value === dateValue;
              const isToday = dateValue === todayValue;

              return (
                <button
                  type="button"
                  key={dateValue}
                  className={`
                    jalali-day-cell
                    ${isSelected ? "selected" : ""}
                    ${isToday ? "today" : ""}
                  `}
                  disabled={isDisabled}
                  onClick={() => {
                    onChange(dateValue);
                    setIsOpen(false);
                  }}
                >
                  {formatFaNumber(dayNumber)}
                </button>
              );
            })}
          </div>

          <div className="jalali-calendar-actions">
            <button
              type="button"
              className="jalali-calendar-today-btn"
              disabled={isTodayDisabled}
              onClick={() => {
                onChange(todayValue);
                setIsOpen(false);
              }}
            >
              امروز
            </button>
            <button
              type="button"
              className="jalali-calendar-clear-btn"
              onClick={() => {
                onChange("all");
                setIsOpen(false);
              }}
            >
              پاک کردن
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const getProjectManagerName = (project) => (
  project?.manager_name
    || project?.admin_name
    || project?.owner_name
    || project?.created_by_name
    || project?.manager
    || ""
);

const getProjectCode = (project) => (
  project?.code
    || project?.project_code
    || project?.projectId
    || project?.id
    || "--"
);

const getProjectCount = (project, keys) => {
  for (const key of keys) {
    const value = project?.[key];

    if (Array.isArray(value)) {
      return value.length;
    }

    if (typeof value === "number") {
      return value;
    }

    if (typeof value === "string" && value.trim() !== "" && !Number.isNaN(Number(value))) {
      return Number(value);
    }
  }

  return 0;
};

const getProjectSourceNames = (project, keys, fallbackKeys = []) => {
  for (const key of keys) {
    const value = project?.[key];
    if (Array.isArray(value)) {
      const names = value.map((item) => String(item || "").trim()).filter(Boolean);
      if (names.length > 0) {
        return names;
      }
    }
  }

  for (const key of fallbackKeys) {
    const value = String(project?.[key] || "").trim();
    if (value) {
      return [value];
    }
  }

  return [];
};

const getProjectStatusInfo = (project) => {
  const statusValue = project?.status ?? project?.is_active ?? project?.active;

  if (typeof statusValue === "boolean") {
    return statusValue
      ? { key: "active", label: "فعال" }
      : { key: "inactive", label: "غیرفعال" };
  }

  const normalizedStatus = String(statusValue || "").trim().toLowerCase();
  const inactiveWords = ["inactive", "disabled", "deactive", "غیرفعال", "غیر فعال", "false", "0"];
  const activeWords = ["active", "enabled", "فعال", "true", "1"];

  if (inactiveWords.some((word) => normalizedStatus.includes(word))) {
    return { key: "inactive", label: "غیرفعال" };
  }

  if (activeWords.some((word) => normalizedStatus.includes(word))) {
    return { key: "active", label: "فعال" };
  }

  return { key: "active", label: "فعال" };
};

const getProjectCreatedDate = (project) => {
  const rawDate = (
    project?.created_at
    || project?.createdAt
    || project?.creation_date
    || project?.created_date
    || project?.date_created
    || ""
  );

  if (!rawDate) {
    return "--";
  }

  const parsedDate = new Date(rawDate);
  if (Number.isNaN(parsedDate.getTime())) {
    return rawDate;
  }

  return new Intl.DateTimeFormat("fa-IR", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(parsedDate);
};

const getNewsTimestamp = (dateValue) => {
  if (!dateValue) {
    return 0;
  }

  const parsedDate = new Date(dateValue);
  if (Number.isNaN(parsedDate.getTime())) {
    return 0;
  }

  return parsedDate.getTime();
};

// A news item can legitimately have no title of its own: messenger sources have
// no headline field (the receive adapter promotes the first line of the post and
// strips it from the body), and the unified AI mode clears the title on purpose
// because the whole post is one piece of text.
//
// Fall back to the first line of the same content the publish adapters use, in
// the same priority order, so the dashboard shows the headline that actually
// went out rather than "بدون عنوان". First line, not first N characters - the
// unified mode puts a real headline there, and slicing would cut mid-sentence.
//
// Display only. news.title on the server is untouched.
const deriveNewsDisplayTitle = (newsItem) => {
  const explicit = String(newsItem?.title || newsItem?.headline || "").trim();
  if (explicit) {
    return explicit;
  }
  const body = String(
    newsItem?.ai_edited_text
    || newsItem?.system_edited_text
    || newsItem?.extracted_text
    || newsItem?.text
    || ""
  ).trim();
  if (!body) {
    return "بدون عنوان";
  }
  const firstLine = body
    .split("\n")
    .map((line) => line.trim())
    .find(Boolean) || "";
  if (!firstLine) {
    return "بدون عنوان";
  }
  return firstLine.length > 80
    ? `${firstLine.slice(0, 80).trim()}…`
    : firstLine;
};

const formatNewsDate = (dateValue) => {
  if (!dateValue) {
    return "--";
  }

  const parsedDate = new Date(dateValue);
  if (Number.isNaN(parsedDate.getTime())) {
    return String(dateValue);
  }

  return new Intl.DateTimeFormat("fa-IR", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(parsedDate);
};

const getNewsStatusInfo = (statusValue) => {
  const normalizedStatus = String(statusValue || "").trim().toLowerCase();
  const publishedWords = ["published", "publish", "done", "ok", "success", "منتشر", "ارسال"];
  const failedWords = ["failed", "error", "reject", "ناموفق", "خطا"];
  const receivedWords = ["received", "new", "pending", "دریافت", "ثبت", "queued"];

  if (publishedWords.some((word) => normalizedStatus.includes(word))) {
    return { key: "published", label: "منتشر شده" };
  }

  if (failedWords.some((word) => normalizedStatus.includes(word))) {
    return { key: "failed", label: "ناموفق" };
  }

  if (receivedWords.some((word) => normalizedStatus.includes(word))) {
    return { key: "received", label: "دریافت شده" };
  }

  return { key: "received", label: "دریافت شده" };
};

const getUserStatusInfo = (statusValue) => {
  const normalizedStatus = String(statusValue || "").trim().toLowerCase();
  const activeWords = ["active", "enabled", "فعال", "true", "1"];
  const inactiveWords = ["inactive", "disabled", "غیرفعال", "غیر فعال", "false", "0"];

  if (inactiveWords.some((word) => normalizedStatus.includes(word))) {
    return { key: "inactive", label: "غیرفعال" };
  }

  if (activeWords.some((word) => normalizedStatus.includes(word))) {
    return { key: "active", label: "فعال" };
  }

  return { key: "active", label: "فعال" };
};

const getAdStatusInfo = (statusValue) => {
  const normalizedStatus = String(statusValue || "").trim().toLowerCase();
  const newsStatusInfo = getNewsStatusInfo(normalizedStatus);
  if (newsStatusInfo.key === "published") {
    return { key: "published", label: "منتشر شده" };
  }
  if (newsStatusInfo.key === "failed") {
    return { key: "paused", label: "متوقف شده" };
  }
  if (newsStatusInfo.key === "received") {
    return { key: "draft", label: "پیش نویس" };
  }
  const publishedWords = ["published", "active", "enabled", "منتشر", "فعال", "success"];
  const pausedWords = ["paused", "inactive", "disabled", "متوقف", "غیرفعال"];
  const draftWords = ["draft", "pending", "پیش نویس", "در انتظار"];

  if (publishedWords.some((word) => normalizedStatus.includes(word))) {
    return { key: "published", label: "منتشر شده" };
  }

  if (pausedWords.some((word) => normalizedStatus.includes(word))) {
    return { key: "paused", label: "متوقف شده" };
  }

  if (draftWords.some((word) => normalizedStatus.includes(word))) {
    return { key: "draft", label: "پیش نویس" };
  }

  return { key: "draft", label: "پیش نویس" };
};

const NEWS_MEDIA_TYPE_LABELS_FA = {
  video: "ویدیو",
  image: "تصویر",
  text: "متن",
  file: "فایل",
  audio: "صوت",
  "text+video": "متن + ویدیو",
  "text+image": "متن + تصویر",
  "text+audio": "متن + صوت",
  "text+file": "متن + فایل",
};

const getNewsMediaType = (newsItem) => (
  String(
    newsItem?.media_type
    || newsItem?.mediaType
    || newsItem?.type
    || newsItem?.content_type
    || ""
  ).trim().toLowerCase()
);

const getNewsMediaBadgeLabel = (newsItem) => {
  const mediaType = getNewsMediaType(newsItem);
  return NEWS_MEDIA_TYPE_LABELS_FA[mediaType] || "متن";
};

const normalizeNewsLink = (linkValue) => {
  const normalizedLink = String(linkValue || "").trim();
  if (!normalizedLink || normalizedLink === "--") {
    return "";
  }

  if (
    normalizedLink.startsWith("http://")
    || normalizedLink.startsWith("https://")
    || normalizedLink.startsWith("blob:")
    || normalizedLink.startsWith("data:")
  ) {
    return normalizedLink;
  }

  // Backend media/file fields are often relative (e.g. /media/news/..).
  // Convert them against API base URL instead of forcing https:// prefix.
  if (normalizedLink.startsWith("/") || normalizedLink.startsWith("media/")) {
    return buildAbsoluteFileUrl(normalizedLink);
  }

  return `https://${normalizedLink}`;
};

const handleMediaPreviewError = (event, fallbackUrl = "") => {
  const imageElement = event.currentTarget;
  const normalizedFallbackUrl = String(fallbackUrl || "").trim();
  if (normalizedFallbackUrl && imageElement.src !== normalizedFallbackUrl) {
    imageElement.src = normalizedFallbackUrl;
    return;
  }

  imageElement.style.display = "none";
  const placeholder = imageElement
    .closest(".news-view-media-wrap")
    ?.querySelector(".news-view-media-placeholder");
  if (placeholder) {
    placeholder.style.display = "flex";
  }
};

const isSqliteOnlyBackupError = (error) => {
  const message = String(error?.message || error || "").toLowerCase();
  return message.includes("supported only for sqlite")
    || message.includes("supported only for sqlite3")
    || message.includes("sqlite3");
};

const sanitizeBackupFilename = (value, backupCode) => {
  const raw = String(value || "").trim();
  if (!raw) {
    return `backup_${backupCode}.dump`;
  }
  const safe = raw.replace(/[\\/:*?"<>|]+/g, "_");
  return safe || `backup_${backupCode}.dump`;
};

const createInitialNewsCreateForm = () => ({
  title: "",
  fullContent: "",
  publishMode: "scheduled",
  scheduleMode: "future_times",
  projectId: "all",
  publishSourceId: "",
  publishName: "",
  publishPlatform: "",
  sourceName: "",
  publishLink: "",
  mediaType: "متنی",
  statusKey: "received",
  publishedAt: "",
  recurringStartDate: "all",
  recurringStartTime: "",
  recurringIntervalSeconds: "60",
  recurringMaxRuns: "10",
  mediaPreviewUrl: "",
  mediaFile: null,
});

const createNewsScheduledPublishRow = () => ({
  id: createProjectFormRowId("news-scheduled"),
  date: "all",
  time: "",
});

const createNewsExtraPublishTarget = () => ({
  id: createProjectFormRowId("news-target"),
  publish_source_id: "",
  name: "",
  platform: "",
  link: "",
});

const getProjectPublishSourceIds = (project = {}) => {
  const explicitIds = Array.isArray(project?.publish_source_ids)
    ? project.publish_source_ids.map((value) => String(value || "").trim()).filter(Boolean)
    : [];
  const primaryId = String(project?.publish_source_id || "").trim();
  return Array.from(new Set([primaryId, ...explicitIds].filter(Boolean)));
};

const getProjectReceiveSourceOptions = (project = {}) => {
  const seenSourceIds = new Set();
  const options = [];

  const addOption = (sourceId, sourceLabel) => {
    const normalizedSourceId = String(sourceId || "").trim();
    if (!normalizedSourceId || seenSourceIds.has(normalizedSourceId)) {
      return;
    }

    const normalizedLabel = String(sourceLabel || "").trim() || `منبع ${normalizedSourceId}`;
    seenSourceIds.add(normalizedSourceId);
    options.push({
      value: normalizedSourceId,
      label: normalizedLabel,
    });
  };

  addOption(
    project?.receive_source_id,
    project?.source_name || project?.receive_source_name || project?.receive_source || project?.source
  );

  if (Array.isArray(project?.receive_source_names)) {
    project.receive_source_names.forEach((sourceName, index) => {
      const sourceId = Array.isArray(project?.receive_source_ids)
        ? project.receive_source_ids[index]
        : "";
      addOption(sourceId, sourceName);
    });
  }

  if (Array.isArray(project?.receive_source_ids)) {
    project.receive_source_ids.forEach((sourceId) => {
      addOption(sourceId, `منبع ${String(sourceId || "").trim()}`);
    });
  }

  if (Array.isArray(project?.extra_sources)) {
    project.extra_sources.forEach((row) => {
      addOption(
        row?.receive_source_id || row?.id,
        row?.name || row?.source_name || row?.receive_source_name || row?.title
      );
    });
  }

  return options;
};

const mapProjectOverviewToNewsRows = (overviewData, fallbackProject = null) => {
  const projectData = overviewData?.project || fallbackProject || {};
  const projectId = String(projectData?.id || fallbackProject?.id || "unknown");
  const projectName = projectData?.title || fallbackProject?.title || "بدون پروژه";
  const defaultSourceName = projectData?.source_name || fallbackProject?.source_name || "--";
  const defaultPublishLink = projectData?.publish_link || fallbackProject?.publish_link || "";
  const rowsByKey = new Map();

  const upsertRow = (newsItem = {}, fallbackStatus = "received") => {
    const itemId = (
      newsItem?.id
      || newsItem?.news_id
      || newsItem?.uuid
      || newsItem?.guid
      || ""
    );
    const rowKey = String(
      itemId
      || `${projectId}-${newsItem?.title || ""}-${newsItem?.source_pub_date || newsItem?.created_at || ""}`
    );
    const statusInfo = getNewsStatusInfo(newsItem?.status || fallbackStatus);
    const publishedAtRaw = (
      newsItem?.source_pub_date
      || newsItem?.published_at
      || newsItem?.created_at
      || newsItem?.updated_at
      || ""
    );
    const publishLink = (
      newsItem?.publish_link
      || newsItem?.url
      || newsItem?.link
      || defaultPublishLink
      || ""
    );
    const mediaUrl = buildAbsoluteFileUrl(
      newsItem?.media_url
      || newsItem?.processed_media_url
      || newsItem?.extracted_media_url
      || newsItem?.source_media_url
      || newsItem?.make_media_url
      || ""
    );
    const newsText = String(
      newsItem?.ai_edited_text
      || newsItem?.system_edited_text
      || newsItem?.extracted_text
      || newsItem?.text
      || ""
    );

    const publishDestinationMeta = buildPublishDestinationBadges({ newsItem, projectData });
    const antiDuplicateBadge = getAntiDuplicateBadgeLabel(newsItem);
    const baseNewsTypeBadges = getNewsTypeBadges(newsItem);
    const newsTypeBadges = antiDuplicateBadge
      ? [...baseNewsTypeBadges, antiDuplicateBadge]
      : baseNewsTypeBadges;
    const nextRow = {
      rowId: `news-${rowKey}`,
      projectId,
      code: String(itemId || newsItem?.code || newsItem?.news_code || "--"),
      title: deriveNewsDisplayTitle(newsItem),
      text: newsText,
      publishedAtRaw,
      publishedAt: formatNewsDate(publishedAtRaw),
      projectName,
      sourceName: newsItem?.source_name || newsItem?.source || defaultSourceName || "--",
      mediaType: getNewsMediaType(newsItem),
      mediaUrl,
      sourceMediaUrl: buildAbsoluteFileUrl(newsItem?.source_media_url || ""),
      extractedMediaUrl: buildAbsoluteFileUrl(newsItem?.extracted_media_url || ""),
      processedMediaUrl: buildAbsoluteFileUrl(newsItem?.processed_media_url || ""),
      statusKey: statusInfo.key,
      statusLabel: statusInfo.label,
      publishLink: publishLink || "--",
      editLink: publishLink || "",
      publishDestinationBadges: publishDestinationMeta.badges,
      publishFailReasons: publishDestinationMeta.failReasons,
      newsTypeBadges,
    };

    const existingRow = rowsByKey.get(rowKey);
    if (!existingRow) {
      rowsByKey.set(rowKey, nextRow);
      return;
    }

    if (existingRow.statusKey !== "published" && nextRow.statusKey === "published") {
      rowsByKey.set(rowKey, nextRow);
      return;
    }

    if (!existingRow.publishedAtRaw && nextRow.publishedAtRaw) {
      rowsByKey.set(rowKey, nextRow);
    }
  };

  (overviewData?.received_news || []).forEach((newsItem) => {
    upsertRow(newsItem, "received");
  });

  (overviewData?.published_news || []).forEach((newsItem) => {
    upsertRow(newsItem, "published");
  });

  return Array.from(rowsByKey.values());
};

const buildReceiveSourceLabel = (project) => {
  const platform = String(
    project?.source_platform_name
    || project?.source_platform
    || project?.receive_source_platform
    || ""
  ).trim();
  const name = String(
    project?.source_name
    || project?.receive_source_name
    || project?.receive_source
    || ""
  ).trim();
  if (platform && name) {
    return `${name}  |  ${platform}`;
  }
  return name || platform || "--";
};

const buildPublishDestinationBadges = ({ newsItem = {}, relatedProject = null, projectData = null } = {}) => {
  const badgeRows = [];
  const failReasonMap = [];
  const addBadgeRow = ({ label = "", link = "" } = {}) => {
    const normalized = String(label || "").replace(/\s+/g, " ").trim();
    if (!normalized) {
      return;
    }
    const normalizedLink = String(link || "").trim();
    const alreadyExists = badgeRows.some((row) => (
      String(row?.label || "").trim() === normalized
      && String(row?.link || "").trim() === normalizedLink
    ));
    if (!alreadyExists) {
      badgeRows.push({ label: normalized, link: normalizedLink });
    }
  };
  const buildLabel = (nameValue, platformValue) => {
    const name = String(nameValue || "").trim();
    const platform = String(platformValue || "").trim();
    if (name && platform && name !== platform) {
      return `${platform} | ${name}`;
    }
    return name || platform;
  };
  const buildFailReasonText = (platformValue, reasonValue) => {
    return "اختلال در سرویس میزبان";
  };
  const publishedTargets = Array.isArray(newsItem?.published_targets) ? newsItem.published_targets : [];
  publishedTargets.forEach((targetRow) => {
    if (!targetRow || typeof targetRow !== "object") {
      return;
    }
    const isFailed = Boolean(targetRow.publish_fail);
    const platformLabel = buildLabel(
      targetRow.publish_name || targetRow.name || "",
      targetRow.publish_platform || targetRow.platform || ""
    );
    const failReasonText = isFailed
      ? buildFailReasonText(platformLabel, targetRow.publish_fail_reason)
      : "";
    addBadgeRow({
      label: isFailed ? `ناموفق: ${platformLabel || "انتشار"}` : platformLabel,
      link: normalizeNewsLink(targetRow.publish_link || targetRow.link || ""),
    });
    if (isFailed) {
      failReasonMap.push({
        label: platformLabel,
        text: failReasonText,
      });
    }
  });

  return {
    badges: badgeRows,
    failReasons: failReasonMap,
  };
};

const getAntiDuplicateBadgeLabel = (newsItem = {}) => {
  const isRepeated = Boolean(newsItem?.repeated_news);
  const hasHash = String(newsItem?.news_hash || "").trim().length > 0;
  if (isRepeated) {
    return "تکراری";
  }
  if (hasHash) {
    return "عملیات ضد تکرار انجام شد";
  }
  return "";
};

  const normalizeNewsRowFromApi = (newsItem = {}, projects = []) => {
  const projectId = String(newsItem?.project_id || newsItem?.projectId || "");
  const relatedProject = projects.find((project) => String(project?.id) === projectId);
  const statusInfo = getNewsStatusInfo(newsItem?.status || "received");
  const publishedAtRaw = String(newsItem?.published_at || newsItem?.created_at || "").trim();
  const publishDestinationMeta = buildPublishDestinationBadges({ newsItem, relatedProject });
  const antiDuplicateBadge = getAntiDuplicateBadgeLabel(newsItem);
  const baseNewsTypeBadges = getNewsTypeBadges(newsItem);
  const newsTypeBadges = antiDuplicateBadge
    ? [...baseNewsTypeBadges, antiDuplicateBadge]
    : baseNewsTypeBadges;
  const scheduledPublishTiming = (
    newsItem?.scheduled_publish_timing
    && typeof newsItem.scheduled_publish_timing === "object"
  ) ? newsItem.scheduled_publish_timing : null;
  const scheduleMode = String(scheduledPublishTiming?.publish_mode || "future_times").trim().toLowerCase();
  const scheduledPublishRowsRaw = Array.isArray(scheduledPublishTiming?.times)
    ? scheduledPublishTiming.times
    : [];
  const scheduledPublishRows = scheduledPublishRowsRaw.map((isoValue) => {
    const mapped = mapIsoToJalaliDateTime(isoValue);
    return {
      id: createProjectFormRowId("news-scheduled-api"),
      date: mapped.date,
      time: mapped.time,
      label: [mapped.date, mapped.time].filter(Boolean).join(" "),
    };
  }).filter((row) => row.date !== "all" && row.time);
  const recurringStartAtMapped = mapIsoToJalaliDateTime(scheduledPublishTiming?.start_at || "");
  const scheduledPublishLabel = scheduleMode === "recurring"
    ? [
      recurringStartAtMapped.date !== "all" ? `شروع: ${recurringStartAtMapped.date} ${recurringStartAtMapped.time}` : "",
      Number(scheduledPublishTiming?.interval_seconds) > 0 ? `هر ${scheduledPublishTiming.interval_seconds} ثانیه` : "",
      Number(scheduledPublishTiming?.max_runs) > 0 ? `تا ${scheduledPublishTiming.max_runs} بار` : "",
    ].filter(Boolean).join(" | ")
    : scheduledPublishRows
      .map((row) => row.label)
      .filter(Boolean)
      .join(" ، ");
  const scheduledPublishTargetNames = Array.from(
    new Set(
      (Array.isArray(scheduledPublishTiming?.publish_targets) ? scheduledPublishTiming.publish_targets : [])
        .map((target) => String(target?.name || "").trim())
        .filter(Boolean)
    )
  );
  const scheduledPublishTargetLabel = scheduledPublishTargetNames.join(" ، ");

  return {
    id: String(newsItem?.id || ""),
    rowId: `news-${newsItem?.id || createProjectFormRowId("news")}`,
    revisionType: String(newsItem?.revision_type || newsItem?.revisionType || "").trim().toLowerCase(),
    projectId,
    receiveSourceId: String(newsItem?.receive_source_id || ""),
    code: String(newsItem?.code || newsItem?.id || "--"),
    title: deriveNewsDisplayTitle(newsItem),
    publishedAtRaw,
    publishedAt: formatNewsDate(publishedAtRaw),
    projectName: String(newsItem?.project_title || relatedProject?.title || "--"),
    sourceName: String(
      (
        String(newsItem?.source_platform || "").trim()
        && String(newsItem?.source_name || "").trim()
      )
        ? `${String(newsItem?.source_name || "").trim()}  |  ${String(newsItem?.source_platform || "").trim()}`
        : (
          newsItem?.source_name
          || buildReceiveSourceLabel(relatedProject)
          || newsItem?.publish_name
          || "--"
        )
    ),
    publishSourceId: String(newsItem?.publish_source_id || ""),
    extractedText: String(newsItem?.extracted_text || ""),
    systemEditedText: String(newsItem?.system_edited_text || ""),
    aiEditedText: String(newsItem?.ai_edited_text || ""),
    fullContent: String(
      newsItem?.ai_edited_text
      || newsItem?.system_edited_text
      || newsItem?.extracted_text
      || newsItem?.text
      || ""
    ),
    createdAtRaw: String(newsItem?.created_at || ""),
    updatedAtRaw: String(newsItem?.updated_at || ""),
    extractedMediaUrl: buildAbsoluteFileUrl(newsItem?.extracted_media_url || ""),
    processedMediaUrl: buildAbsoluteFileUrl(newsItem?.processed_media_url || ""),
    mediaUrl: buildAbsoluteFileUrl(newsItem?.media_url || newsItem?.source_media_url || ""),
    sourceMediaUrl: buildAbsoluteFileUrl(newsItem?.source_media_url || ""),
    watermarkFileUrl: buildAbsoluteFileUrl(newsItem?.watermark_news_file_url || ""),
    watermarkText: String(newsItem?.watermark_news_text || ""),
    watermarkFont: String(newsItem?.watermark_news_font || ""),
    watermarkPosition: String(newsItem?.watermark_news_position || ""),
    watermarkSize: String(newsItem?.watermark_news_font_size ?? ""),
    watermarkColor: String(newsItem?.watermark_news_color || ""),
    watermarkStatus: String(newsItem?.watermark_news_activity_status || "active"),
    watermarkApplyTo: String(newsItem?.watermark_news_add_to || ""),
    mediaType: getNewsMediaType(newsItem),
    statusKey: statusInfo.key,
    statusLabel: statusInfo.label,
    publishLink: String(newsItem?.publish_link || "--"),
    editLink: String(newsItem?.publish_link || ""),
    publishDestinationBadges: publishDestinationMeta.badges,
    publishFailReasons: publishDestinationMeta.failReasons,
    newsTypeBadges,
    scheduledPublishTiming,
    scheduleMode,
    scheduledPublishRows,
    scheduledPublishLabel,
    scheduledPublishTargetNames,
    scheduledPublishTargetLabel,
    publishedTargets: Array.isArray(newsItem?.published_targets) ? newsItem.published_targets : [],
    alreadyPublished: Boolean(newsItem?.already_published),
    alreadyPublishedDescription: String(newsItem?.already_published_description || ""),
  };
};

const mapProjectsToSourcesRows = (projects = []) => {
  const rowsByKey = new Map();

  projects.forEach((project = {}) => {
    const sourceName = project?.source_name || project?.source || "";
    const sourceLink = project?.source_link || project?.link || "";
    const rowKey = String(sourceLink || sourceName || project?.id || "");

    if (!rowKey) {
      return;
    }

    const createdAtRaw = (
      project?.created_at
      || project?.createdAt
      || project?.creation_date
      || project?.created_date
      || project?.date_created
      || ""
    );
    const sourceStatus = getProjectStatusInfo(project);
    const receivedNewsCount = getProjectCount(project, [
      "received_news_count",
      "total_received_news",
      "news_received_count",
      "received_count",
      "incoming_news_count",
      "received_news",
    ]);
    const nextRow = {
      rowId: `source-${rowKey}`,
      code: String(project?.source_code || project?.source_id || project?.id || "--"),
      sourceName: sourceName || "بدون نام",
      managerName: project?.manager_name || project?.manager || "--",
      createdAt: getProjectCreatedDate(project),
      createdAtRaw,
      platform: project?.source_platform_name || project?.platform || "--",
      sourceLink: sourceLink || "--",
      receivedNewsCount,
      statusKey: sourceStatus.key,
      statusLabel: sourceStatus.label,
    };

    const existingRow = rowsByKey.get(rowKey);
    if (!existingRow) {
      rowsByKey.set(rowKey, nextRow);
      return;
    }

    const mergedRow = {
      ...existingRow,
      receivedNewsCount: existingRow.receivedNewsCount + nextRow.receivedNewsCount,
      statusKey: existingRow.statusKey === "active" || nextRow.statusKey === "active"
        ? "active"
        : "inactive",
    };
    mergedRow.statusLabel = mergedRow.statusKey === "active" ? "فعال" : "غیرفعال";

    if (getNewsTimestamp(nextRow.createdAtRaw) > getNewsTimestamp(existingRow.createdAtRaw)) {
      mergedRow.createdAt = nextRow.createdAt;
      mergedRow.createdAtRaw = nextRow.createdAtRaw;
      mergedRow.code = nextRow.code;
      mergedRow.managerName = nextRow.managerName;
      mergedRow.platform = nextRow.platform;
      mergedRow.sourceLink = nextRow.sourceLink;
    }

    rowsByKey.set(rowKey, mergedRow);
  });

  return Array.from(rowsByKey.values()).sort((firstRow, secondRow) => (
    getNewsTimestamp(secondRow.createdAtRaw) - getNewsTimestamp(firstRow.createdAtRaw)
  ));
};

const normalizeReceiveSourceRowFromApi = (sourceItem = {}) => {
  const statusKey = sourceItem?.activity_status ? "active" : "inactive";
  const statusInfo = getProjectStatusInfo({ status: statusKey });
  const createdAtRaw = String(sourceItem?.created_at || "");
  const sourceTypeKey = String(sourceItem?.receive_source_type || "dynamic").trim().toLowerCase() === "static"
    ? "static"
    : "dynamic";
  const sourceTypeLabel = sourceTypeKey === "static" ? "استاتیک" : "داینامیک";
  const antiAdWithAi = Boolean(sourceItem?.anti_ad_with_ai);
  const antiAdWithSystem = Boolean(sourceItem?.anti_ad_with_system);
  const antiAdMode = antiAdWithAi ? "ai" : (antiAdWithSystem ? "system" : "none");
  const antiAdRules = Array.isArray(sourceItem?.anti_ad_with_system_rules)
    ? sourceItem.anti_ad_with_system_rules
      .map((item) => {
        if (typeof item === "string") {
          const ruleText = String(item || "").trim();
          if (!ruleText) {
            return null;
          }
          return {
            field_1: ruleText,
            field_2: "",
            field_3: "",
            field_4: "",
            selected_flag: "is_ad",
          };
        }
        if (!item || typeof item !== "object") {
          return null;
        }
        const normalizedRule = {
          field_1: String(item.field_1 || "").trim(),
          field_2: String(item.field_2 || "").trim(),
          field_3: String(item.field_3 || "").trim(),
          field_4: String(item.field_4 || "").trim(),
          selected_flag: String(item.selected_flag || "is_ad").trim() || "is_ad",
          match_mode: String(item.match_mode || "contains").trim() || "contains",
        };
        if (!normalizedRule.field_1 && !normalizedRule.field_2 && !normalizedRule.field_3 && !normalizedRule.field_4) {
          return null;
        }
        return normalizedRule;
      })
      .filter(Boolean)
    : [];

  return {
    rowId: `receive-source-${sourceItem?.id || createProjectFormRowId("source")}`,
    id: sourceItem?.id,
    code: String(sourceItem?.code || "--"),
    sourceName: String(sourceItem?.name || "").trim(),
    createdAtRaw,
    createdAt: formatNewsDate(createdAtRaw),
    platform: String(sourceItem?.platform || "--").trim() || "--",
    sourceLink: String(sourceItem?.link || "--").trim() || "--",
    receiveIntervalSeconds: Number(sourceItem?.receive_interval_seconds ?? 60),
    receivedNewsCount: Number(sourceItem?.news_count ?? sourceItem?.received_news_count ?? 0),
    statusKey: statusInfo.key,
    statusLabel: statusInfo.label,
    sourceTypeKey,
    sourceTypeLabel,
    rubikaAccountId: sourceItem?.rubika_account_id ? String(sourceItem.rubika_account_id) : "",
    eitaAccountId: sourceItem?.eita_account_id ? String(sourceItem.eita_account_id) : "",
    priceTemplateId: sourceItem?.price_template_id ? String(sourceItem.price_template_id) : "",
    antiAdMode,
    antiAdWithAiPrompt: String(sourceItem?.anti_ad_with_ai_prompt || "").trim(),
    antiAdWithSystemRules: antiAdRules,
    canEdit: true,
    canDelete: true,
    managerName: "--",
  };
};

const normalizePublishSourceRowFromApi = (sourceItem = {}) => {
  const activityStatusText = String(sourceItem?.activity_status || "").trim().toLowerCase();
  const statusKey = activityStatusText === "active" ? "active" : "inactive";
  const statusInfo = getProjectStatusInfo({ status: statusKey });
  const createdAtRaw = String(sourceItem?.created_at || "");

  return {
    rowId: `publish-source-${sourceItem?.id || createProjectFormRowId("publish-source")}`,
    id: sourceItem?.id,
    code: String(sourceItem?.code || "--"),
    sourceName: String(sourceItem?.name || "").trim(),
    createdAtRaw,
    createdAt: formatNewsDate(createdAtRaw),
    platform: String(sourceItem?.platform || "--").trim() || "--",
    sourceLink: String(sourceItem?.link || "--").trim() || "--",
    receivedNewsCount: Number(sourceItem?.news_count ?? sourceItem?.received_news_count ?? 0),
    statusKey: statusInfo.key,
    statusLabel: statusInfo.label,
    managerName: "--",
  };
};


const normalizeAdRowFromApi = (adItem = {}) => {
  const newsRow = normalizeNewsRowFromApi(adItem, []);
  const statusInfo = getAdStatusInfo(adItem?.status || "draft");
  const createdAtRaw = String(adItem?.created_at || "");
  const scheduledRowsRaw = Array.isArray(adItem?.scheduled_publish_rows) ? adItem.scheduled_publish_rows : [];
  const scheduledRows = scheduledRowsRaw.map((row) => {
    if (typeof row === "string") {
      const mappedFromString = mapIsoToJalaliDateTime(row);
      const mappedLabel = [mappedFromString.date, mappedFromString.time].filter(Boolean).join(" ");
      return {
        id: createProjectFormRowId("ad-scheduled-api"),
        date: mappedFromString.date,
        time: mappedFromString.time,
        label: mappedLabel,
      };
    }

    const isoValue = String(
      row?.datetime
      || row?.scheduled_at
      || row?.publish_at
      || row?.date_time
      || row?.timestamp
      || ""
    ).trim();
    if (isoValue) {
      const mappedFromIso = mapIsoToJalaliDateTime(isoValue);
      const mappedLabel = [mappedFromIso.date, mappedFromIso.time].filter(Boolean).join(" ");
      return {
        id: String(row?.id || createProjectFormRowId("ad-scheduled-api")),
        date: mappedFromIso.date,
        time: mappedFromIso.time,
        label: mappedLabel,
      };
    }

    const rawDate = String(row?.date || row?.publish_date || "").trim();
    const rawTime = String(row?.time || row?.publish_time || "").trim();
    return {
      id: String(row?.id || createProjectFormRowId("ad-scheduled-api")),
      date: rawDate,
      time: rawTime,
      label: [rawDate, rawTime].filter(Boolean).join(" "),
    };
  }).filter((row) => row.label);
  const scheduledLabelFromRows = scheduledRows
    .map((row) => row.label)
    .filter(Boolean)
    .join(" ، ");
  const scheduledLabel = (
    scheduledLabelFromRows
    || newsRow.scheduledPublishLabel
    || formatNewsDate(String(adItem?.scheduled_publish_at || adItem?.publish_at || ""))
    || "--"
  );
  const makeMediaUrl = buildAbsoluteFileUrl(adItem?.make_media_url);

  return {
    rowId: `ad-${adItem?.id || createProjectFormRowId("ad")}`,
    id: adItem?.id,
    code: String(adItem?.code || adItem?.id || "--"),
    title: String(adItem?.title || "بدون عنوان"),
    createdAt: formatNewsDate(createdAtRaw),
    createdAtRaw,
    projectName: String(adItem?.project_title || "--"),
    publishSourceId: String(adItem?.publish_source_id || ""),
    publishPlatform: String(adItem?.publish_platform || ""),
    mediaType: getNewsMediaType(adItem),
    topicName: String(adItem?.publish_name || "--"),
    managerName: "--",
    statusKey: statusInfo.key,
    statusLabel: statusInfo.label,
    publishLink: String(adItem?.publish_link || "--"),
    description: String(adItem?.text || ""),
    mediaUrl: makeMediaUrl,
    scheduledPublishRows: scheduledRows,
    scheduledPublishLabel: scheduledLabel,
    publishType: String(adItem?.publish_type || "project"),
    revisionType: newsRow.revisionType,
    projectId: newsRow.projectId,
    receiveSourceId: newsRow.receiveSourceId,
    sourceName: newsRow.sourceName,
    extractedText: newsRow.extractedText,
    systemEditedText: newsRow.systemEditedText,
    aiEditedText: newsRow.aiEditedText,
    fullContent: newsRow.fullContent,
    updatedAtRaw: newsRow.updatedAtRaw,
    extractedMediaUrl: newsRow.extractedMediaUrl,
    processedMediaUrl: newsRow.processedMediaUrl,
    sourceMediaUrl: newsRow.sourceMediaUrl,
    watermarkFileUrl: newsRow.watermarkFileUrl,
    watermarkText: newsRow.watermarkText,
    watermarkFont: newsRow.watermarkFont,
    watermarkPosition: newsRow.watermarkPosition,
    watermarkSize: newsRow.watermarkSize,
    watermarkColor: newsRow.watermarkColor,
    watermarkStatus: newsRow.watermarkStatus,
    watermarkApplyTo: newsRow.watermarkApplyTo,
    publishDestinationBadges: newsRow.publishDestinationBadges,
    publishFailReasons: newsRow.publishFailReasons,
    newsTypeBadges: newsRow.newsTypeBadges,
    scheduledPublishTargetNames: newsRow.scheduledPublishTargetNames,
    scheduledPublishTargetLabel: newsRow.scheduledPublishTargetLabel,
    publishedTargets: newsRow.publishedTargets,
    alreadyPublished: newsRow.alreadyPublished,
    alreadyPublishedDescription: newsRow.alreadyPublishedDescription,
  };
};

const getMiniChartCoordinates = (points, width = 360, height = 190, options = {}) => {
  if (!points.length) {
    return [];
  }

  const hasMinOverride = Number.isFinite(options?.minValue);
  const hasMaxOverride = Number.isFinite(options?.maxValue);
  const maxPoints = Math.max(...points);
  const minPoints = Math.min(...points);
  const max = hasMaxOverride ? Number(options.maxValue) : maxPoints;
  const min = hasMinOverride ? Number(options.minValue) : minPoints;
  const range = max - min || 1;
  const stepX = width / (points.length - 1 || 1);

  return points.map((point, index) => ({
    x: index * stepX,
    y: height - ((point - min) / range) * (height - 24) - 12,
  }));
};

const renderMiniChartPath = (points, width = 360, height = 190, options = {}) => {
  if (!points.length) {
    return "";
  }

  const coordinates = getMiniChartCoordinates(points, width, height, options);

  return coordinates
    .map((coordinate, index) => {
      const pathPrefix = index === 0 ? "M" : "L";
      return `${pathPrefix}${coordinate.x.toFixed(2)},${coordinate.y.toFixed(2)}`;
    })
    .join(" ");
};

const renderMiniChartAreaPath = (points, width = 360, height = 190, options = {}) => {
  const coordinates = getMiniChartCoordinates(points, width, height, options);

  if (!coordinates.length) {
    return "";
  }

  const linePath = coordinates
    .map((coordinate, index) => {
      const pathPrefix = index === 0 ? "M" : "L";
      return `${pathPrefix}${coordinate.x.toFixed(2)},${coordinate.y.toFixed(2)}`;
    })
    .join(" ");

  const lastCoordinate = coordinates[coordinates.length - 1];
  const firstCoordinate = coordinates[0];

  return `${linePath}L${lastCoordinate.x.toFixed(2)},${height}L${firstCoordinate.x.toFixed(2)},${height}Z`;
};

const buildFallbackTrendLabels = ({ period = "weekly", count = 0 }) => {
  const safeCount = Math.max(0, Number(count) || 0);
  if (!safeCount) {
    return [];
  }

  if (period === "daily") {
    const threeHourSlots = ["00:00", "03:00", "06:00", "09:00", "12:00", "15:00", "18:00", "21:00"];
    return Array.from({ length: safeCount }, (_, index) => threeHourSlots[index % threeHourSlots.length]);
  }

  if (period === "weekly") {
    return Array.from({ length: safeCount }, (_, index) => REPORT_WEEKDAY_LABELS[index % REPORT_WEEKDAY_LABELS.length]);
  }

  if (period === "yearly") {
    return Array.from({ length: safeCount }, (_, index) => PERSIAN_MONTH_LABELS[index % PERSIAN_MONTH_LABELS.length]);
  }

  // monthly fallback
  return Array.from({ length: safeCount }, (_, index) => `روز ${index + 1}`);
};

const normalizeDailySeriesToFullDay = (labels = [], points = []) => {
  const hourTotals = Array.from({ length: 24 }, () => 0);
  const hasExplicitPoint = Array.from({ length: 24 }, () => false);
  const hourLabels = Array.from({ length: 24 }, (_, hour) => `${String(hour).padStart(2, "0")}:00`);

  const resolveHourFromLabel = (labelValue) => {
    const raw = String(labelValue || "").trim();
    if (!raw) {
      return null;
    }

    const parsedDate = new Date(raw);
    if (!Number.isNaN(parsedDate.getTime())) {
      return parsedDate.getHours();
    }

    const match = raw.match(/(\d{1,2})(?::\d{2})?/);
    if (!match) {
      return null;
    }
    const hour = Number(match[1]);
    if (!Number.isFinite(hour) || hour < 0 || hour > 23) {
      return null;
    }
    return hour;
  };

  const safePoints = Array.isArray(points) ? points.map((point) => Number(point) || 0) : [];
  const safeLabels = Array.isArray(labels) ? labels : [];
  const maxLen = Math.max(safePoints.length, safeLabels.length);

  for (let index = 0; index < maxLen; index += 1) {
    const pointValue = Number(safePoints[index] || 0);
    const hour = resolveHourFromLabel(safeLabels[index]);

    let hourIndex = 0;
    if (hour !== null) {
      hourIndex = hour;
    } else if (maxLen > 1) {
      hourIndex = Math.floor((index / (maxLen - 1)) * 23);
    }

    hourIndex = Math.max(0, Math.min(23, hourIndex));
    hourTotals[hourIndex] += pointValue;
    hasExplicitPoint[hourIndex] = true;
  }

  const knownIndexes = hasExplicitPoint
    .map((isKnown, index) => (isKnown ? index : -1))
    .filter((index) => index >= 0);

  if (knownIndexes.length === 1) {
    const onlyIndex = knownIndexes[0];
    const onlyValue = hourTotals[onlyIndex];
    for (let hour = 0; hour < 24; hour += 1) {
      hourTotals[hour] = onlyValue;
    }
  } else if (knownIndexes.length >= 2) {
    for (let idx = 0; idx < knownIndexes.length - 1; idx += 1) {
      const startHour = knownIndexes[idx];
      const endHour = knownIndexes[idx + 1];
      const startValue = hourTotals[startHour];
      const endValue = hourTotals[endHour];
      const span = endHour - startHour;

      for (let hour = startHour; hour <= endHour; hour += 1) {
        const ratio = span === 0 ? 0 : (hour - startHour) / span;
        hourTotals[hour] = Math.round(startValue + (endValue - startValue) * ratio);
      }
    }

    const firstKnown = knownIndexes[0];
    for (let hour = 0; hour < firstKnown; hour += 1) {
      hourTotals[hour] = hourTotals[firstKnown];
    }

    const lastKnown = knownIndexes[knownIndexes.length - 1];
    for (let hour = lastKnown + 1; hour < 24; hour += 1) {
      hourTotals[hour] = hourTotals[lastKnown];
    }
  }

  return {
    labels: hourLabels,
    points: hourTotals,
  };
};

function DashboardNewsTrendChart({
  labels = [],
  points = [],
  period = "monthly",
  yAxisTitle = "تعداد خبر",
  ariaLabel = "نمودار روند خبر",
  tooltipValueLabel = "تعداد خبر",
}) {
  const hostRef = useRef(null);
  const [hoverIndex, setHoverIndex] = useState(null);
  const safeLabels = Array.isArray(labels) ? labels : [];
  const safePoints = Array.isArray(points) ? points.map((value) => Number(value) || 0) : [];
  const visibleLabels = useMemo(() => {
    if (period !== "daily") {
      return safeLabels;
    }
    const nowHour = new Date().getHours();
    return safeLabels.slice(0, Math.max(1, nowHour + 1));
  }, [period, safeLabels]);
  const visiblePoints = useMemo(() => {
    if (period !== "daily") {
      return safePoints;
    }
    const nowHour = new Date().getHours();
    return safePoints.slice(0, Math.max(1, nowHour + 1));
  }, [period, safePoints]);
  const count = Math.max(visibleLabels.length, visiblePoints.length);
  const paddedLabels = Array.from({ length: count }, (_, index) => visibleLabels[index] || "");
  const paddedPoints = Array.from({ length: count }, (_, index) => visiblePoints[index] || 0);

  const chartMetrics = useMemo(() => {
    const width = 720;
    const height = 226;
    const padding = { top: 18, right: 20, bottom: 34, left: 44 };
    const plotWidth = Math.max(1, width - padding.left - padding.right);
    const plotHeight = Math.max(1, height - padding.top - padding.bottom);
    const maxValue = Math.max(1, ...paddedPoints);
    const yMax = Math.max(2, Math.ceil(maxValue * 1.1));
    return { width, height, padding, plotWidth, plotHeight, yMax };
  }, [paddedPoints]);

  const pointCoordinates = useMemo(() => (
    paddedPoints.map((value, index) => {
      const ratioX = count <= 1 ? 0 : index / (count - 1);
      const x = chartMetrics.padding.left + (ratioX * chartMetrics.plotWidth);
      const ratioY = chartMetrics.yMax <= 0 ? 0 : value / chartMetrics.yMax;
      const y = chartMetrics.padding.top + ((1 - ratioY) * chartMetrics.plotHeight);
      return { x, y, value, index };
    })
  ), [paddedPoints, count, chartMetrics]);

  const pathD = useMemo(() => {
    if (!pointCoordinates.length) {
      return "";
    }
    return pointCoordinates
      .map((point, index) => `${index === 0 ? "M" : "L"} ${point.x} ${point.y}`)
      .join(" ");
  }, [pointCoordinates]);

  const getLabelInterval = () => {
    if (period === "daily") {
      return 3;
    }
    if (period === "monthly") {
      return 5;
    }
    if (period === "yearly") {
      return 1;
    }
    return 1;
  };

  const xLabelInterval = getLabelInterval();
  const yTicks = Array.from({ length: 5 }, (_, index) => (
    Math.round(chartMetrics.yMax - ((chartMetrics.yMax * index) / 4))
  ));

  const handleMouseMove = (event) => {
    if (!hostRef.current || count <= 0) {
      return;
    }
    const rect = hostRef.current.getBoundingClientRect();
    if (!rect.width) {
      return;
    }
    const scaleX = chartMetrics.width / rect.width;
    const localX = (event.clientX - rect.left) * scaleX;
    const clampedX = Math.max(
      chartMetrics.padding.left,
      Math.min(chartMetrics.padding.left + chartMetrics.plotWidth, localX)
    );
    const ratio = (clampedX - chartMetrics.padding.left) / chartMetrics.plotWidth;
    const index = Math.max(0, Math.min(count - 1, Math.round(ratio * (count - 1))));
    setHoverIndex(index);
  };

  return (
    <div className="reports-line-chart reports-line-chart-echarts">
      <div className="reports-axis-title reports-axis-title-y">{yAxisTitle}</div>
      <div
        className="reports-echarts-host"
        ref={hostRef}
        aria-label={ariaLabel}
        onMouseMove={handleMouseMove}
        onMouseLeave={() => setHoverIndex(null)}
      >
        <svg
          viewBox={`0 0 ${chartMetrics.width} ${chartMetrics.height}`}
          className="reports-line-svg"
          role="img"
          aria-label={ariaLabel}
          preserveAspectRatio="none"
        >
          {yTicks.map((tickValue, index) => {
            const y = chartMetrics.padding.top + ((index / 4) * chartMetrics.plotHeight);
            return (
              <g key={`published-grid-${tickValue}-${index}`}>
                <line
                  x1={chartMetrics.padding.left}
                  x2={chartMetrics.padding.left + chartMetrics.plotWidth}
                  y1={y}
                  y2={y}
                  stroke="#eef2f7"
                  strokeWidth="1"
                />
                <text x={chartMetrics.padding.left - 10} y={y + 4} fill="#94a3b8" fontSize="11" textAnchor="end">
                  {formatFaNumber(tickValue)}
                </text>
              </g>
            );
          })}
          <path d={pathD} fill="none" stroke="#22c55e" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
          {pointCoordinates.map((point) => (
            <circle key={`published-point-${point.index}`} cx={point.x} cy={point.y} r="3.5" fill="#22c55e" />
          ))}
          {hoverIndex !== null && pointCoordinates[hoverIndex] && (
            <line
              x1={pointCoordinates[hoverIndex].x}
              x2={pointCoordinates[hoverIndex].x}
              y1={chartMetrics.padding.top}
              y2={chartMetrics.padding.top + chartMetrics.plotHeight}
              stroke="#94a3b8"
              strokeDasharray="4 4"
              strokeWidth="1"
            />
          )}
          {paddedLabels.map((label, index) => {
            if (index % xLabelInterval !== 0 && index !== count - 1) {
              return null;
            }
            const ratioX = count <= 1 ? 0 : index / (count - 1);
            const x = chartMetrics.padding.left + (ratioX * chartMetrics.plotWidth);
            return (
              <text
                key={`published-x-${label}-${index}`}
                x={x}
                y={chartMetrics.height - 8}
                fill="#94a3b8"
                fontSize="11"
                textAnchor="middle"
              >
                {label}
              </text>
            );
          })}
        </svg>
        {hoverIndex !== null && pointCoordinates[hoverIndex] && (
          <>
            <div
              className="reports-daily-tooltip"
              style={{
                left: `${Math.max(8, Math.min(((pointCoordinates[hoverIndex].x / chartMetrics.width) * 100), 82))}%`,
              }}
            >
              <strong>{paddedLabels[hoverIndex] || "--:--"}</strong>
              <span>{tooltipValueLabel}: {formatFaNumber(paddedPoints[hoverIndex] || 0)}</span>
            </div>
          </>
        )}
      </div>
      <div className="reports-axis-title reports-axis-title-x">زمان</div>
    </div>
  );
}

function Dashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const sidebarRef = useRef(null);
  const contentRef = useRef(null);
  const topbarNotificationsRef = useRef(null);
  const [showProjectForm, setShowProjectForm] = useState(false);
  const [editingProjectId, setEditingProjectId] = useState(null);
  const [activeTab, setActiveTab] = useState(() => {
    if (typeof window === "undefined") {
      return "dashboard";
    }
    const params = new URLSearchParams(window.location.search || "");
    const tabFromUrl = String(params.get("tab") || "").trim();
    if (tabFromUrl) {
      return tabFromUrl;
    }
    return window.localStorage.getItem(DASHBOARD_ACTIVE_TAB_STORAGE_KEY) || "dashboard";
  });
  const [menuOpen, setMenuOpen] = useState(false);
  const [selectedProjectViewTitle, setSelectedProjectViewTitle] = useState(() => {
    if (typeof window === "undefined") {
      return "";
    }
    return window.localStorage.getItem(DASHBOARD_PROJECT_VIEW_TITLE_STORAGE_KEY) || "";
  });
  const [selectedProjectViewId, setSelectedProjectViewId] = useState(() => {
    if (typeof window === "undefined") {
      return "";
    }
    return window.localStorage.getItem(DASHBOARD_PROJECT_VIEW_ID_STORAGE_KEY) || "";
  });
  const [projectViewNewsTab, setProjectViewNewsTab] = useState(() => {
    if (typeof window === "undefined") {
      return PROJECT_VIEW_NEWS_TABS[0].key;
    }
    const storedTab = window.localStorage.getItem(DASHBOARD_PROJECT_VIEW_NEWS_TAB_STORAGE_KEY) || "";
    return PROJECT_VIEW_NEWS_TABS.some((tabItem) => tabItem.key === storedTab)
      ? storedTab
      : PROJECT_VIEW_NEWS_TABS[0].key;
  });
  const [projectViewNewsPage, setProjectViewNewsPage] = useState(1);

  // ---- state های فرم پروژه ----
  const [projects, setProjects] = useState([]);
  const [receiveSourcesOptions, setReceiveSourcesOptions] = useState([]);
  const [publishSourcesOptions, setPublishSourcesOptions] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [projectsMsg, setProjectsMsg] = useState({ type: "", text: "" });
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [deletingProject, setDeletingProject] = useState(false);
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
  const [isTopbarNotificationsOpen, setIsTopbarNotificationsOpen] = useState(false);
  const [topbarUserInfo, setTopbarUserInfo] = useState(TOPBAR_USER_INFO);
  const [topbarSystemMessages, setTopbarSystemMessages] = useState(TOPBAR_SYSTEM_MESSAGES);
  const [authLoading, setAuthLoading] = useState(true);
  const [allowedModuleKeys, setAllowedModuleKeys] = useState([]);
  const [allowedProjectLabels, setAllowedProjectLabels] = useState([]);
  const [restrictProjectsByAccess, setRestrictProjectsByAccess] = useState(false);
  const [dashboardStats, setDashboardStats] = useState({
    totalReceiveSources: 0,
    totalProjects: 0,
    totalReceivedNews: 0,
    totalPublishedNews: 0,
    criticalEventsCount: 0,
    hasCriticalEvents: false,
  });
  const [loadingStats, setLoadingStats] = useState(false);
  const [dashboardTrendCharts, setDashboardTrendCharts] = useState({
    published: { labels: REPORT_WEEKDAY_LABELS, points: REPORT_PUBLISHED_NEWS_POINTS },
    received: { labels: REPORT_WEEKDAY_LABELS, points: REPORT_RECEIVED_NEWS_POINTS },
  });
  const [loadingDashboardTrends, setLoadingDashboardTrends] = useState(false);
  const [dashboardTrendsError, setDashboardTrendsError] = useState("");
  const [systemUsage, setSystemUsage] = useState(buildFakeSystemUsage);
  const [loadingSystemUsage, setLoadingSystemUsage] = useState(false);
  const [projectSearchTerm, setProjectSearchTerm] = useState("");
  const [projectStatusFilter, setProjectStatusFilter] = useState("all");
  const [projectManagerFilter, setProjectManagerFilter] = useState("all");
  const [projectRowsPerPage, setProjectRowsPerPage] = useState(PROJECT_ROWS_PER_PAGE_OPTIONS[0]);
  const [projectPage, setProjectPage] = useState(1);
  const [selectedProjectIds, setSelectedProjectIds] = useState([]);
  const [dashboardActivityFromDate, setDashboardActivityFromDate] = useState("all");
  const [dashboardActivityToDate, setDashboardActivityToDate] = useState("all");
  const [dashboardActivityRoleFilter, setDashboardActivityRoleFilter] = useState("all");
  const [dashboardActivityModuleFilter, setDashboardActivityModuleFilter] = useState("all");
  const [dashboardActivityPage, setDashboardActivityPage] = useState(1);
  const [newsRows, setNewsRows] = useState([]);
  const [loadingNewsRows, setLoadingNewsRows] = useState(false);
  const [newsError, setNewsError] = useState("");
  const [newsMsg, setNewsMsg] = useState({ type: "", text: "" });
  const [newsSectionControls, setNewsSectionControls] = useState({
    extract: {
      searchTerm: "",
      projectFilter: "all",
      managerFilter: "all",
      sourceFilter: "all",
      mediaFilter: "all",
      statusFilter: "all",
      rowsPerPage: NEWS_ROWS_PER_PAGE_OPTIONS[0],
      page: 1,
    },
    make: {
      searchTerm: "",
      projectFilter: "all",
      managerFilter: "all",
      sourceFilter: "all",
      mediaFilter: "all",
      statusFilter: "all",
      rowsPerPage: NEWS_ROWS_PER_PAGE_OPTIONS[0],
      page: 1,
    },
  });
  const [newsSectionRows, setNewsSectionRows] = useState({
    extract: [],
    make: [],
  });
  const [newsSectionTotalCount, setNewsSectionTotalCount] = useState({
    extract: 0,
    make: 0,
  });
  const [newsReloadToken, setNewsReloadToken] = useState(0);
  const [selectedNewsIds, setSelectedNewsIds] = useState([]);
  const [isNewsDeleteModalOpen, setIsNewsDeleteModalOpen] = useState(false);
  const [newsDeleteTargetIds, setNewsDeleteTargetIds] = useState([]);
  const [deletingNews, setDeletingNews] = useState(false);
  const [selectedNewsRowId, setSelectedNewsRowId] = useState("");
  const [isNewsViewLoading, setIsNewsViewLoading] = useState(false);
  const [selectedNewsIdForView, setSelectedNewsIdForView] = useState(0);
  const [showPreviewVideoPlayer, setShowPreviewVideoPlayer] = useState(false);
  const [showExtractedVideoPlayer, setShowExtractedVideoPlayer] = useState(false);
  const [showProcessedVideoPlayer, setShowProcessedVideoPlayer] = useState(false);
  const [loadingNewsVideo, setLoadingNewsVideo] = useState(false);
  const [newsViewMessage, setNewsViewMessage] = useState({ type: "", text: "" });
  const [newsViewReturnTab, setNewsViewReturnTab] = useState("news");
  const [newsCreateMsg, setNewsCreateMsg] = useState({ type: "", text: "" });
  const [isNewsEditing, setIsNewsEditing] = useState(false);
  const [newsEditDraft, setNewsEditDraft] = useState({
    title: "",
    fullContent: "",
    mediaType: "",
    mediaPreviewUrl: "",
  });
  const [newsViewForm, setNewsViewForm] = useState({
    title: "",
    code: "",
    publishedAt: "",
    projectName: "",
    sourceName: "",
    mediaType: "",
    statusLabel: "",
    publishLink: "",
    fullContent: "",
    extractedContent: "",
    processedContent: "",
    extractedMediaUrl: "",
    processedMediaUrl: "",
    sourceMediaUrl: "",
    mediaUrl: "",
  });

  const persistNewsViewSnapshot = (snapshot) => {
    if (typeof window === "undefined") {
      return;
    }
    try {
      window.localStorage.setItem(
        DASHBOARD_NEWS_VIEW_SNAPSHOT_STORAGE_KEY,
        JSON.stringify(snapshot || {})
      );
    } catch (_error) {
      // intentionally ignored
    }
  };

  const clearNewsViewSnapshot = () => {
    if (typeof window === "undefined") {
      return;
    }
    try {
      window.localStorage.removeItem(DASHBOARD_NEWS_VIEW_SNAPSHOT_STORAGE_KEY);
    } catch (_error) {
      // intentionally ignored
    }
  };
  const [newsCreateForm, setNewsCreateForm] = useState(createInitialNewsCreateForm);
  const [showNewsCreateVideoPlayer, setShowNewsCreateVideoPlayer] = useState(false);
  const [isNewsCreateVideoPlaying, setIsNewsCreateVideoPlaying] = useState(false);
  const newsCreateMediaInputRef = useRef(null);
  const newsCreateVideoRef = useRef(null);
  const [openNewsTimePickerInputId, setOpenNewsTimePickerInputId] = useState("");
  const [pendingNewsPublishTargetScrollId, setPendingNewsPublishTargetScrollId] = useState("");
  const [pendingNewsScheduledScrollId, setPendingNewsScheduledScrollId] = useState("");
  const [newsFormMode, setNewsFormMode] = useState("create");
  const [editingNewsRowId, setEditingNewsRowId] = useState("");
  const [newsCreateExtraPublishTargets, setNewsCreateExtraPublishTargets] = useState([]);
  const [newsCreateSelectedProjectIds, setNewsCreateSelectedProjectIds] = useState([]);
  const [newsRemovedProjectSourceIds, setNewsRemovedProjectSourceIds] = useState([]);
  const [newsCreateScheduledPublishRows, setNewsCreateScheduledPublishRows] = useState([createNewsScheduledPublishRow()]);
  const [newsWatermarkSettings, setNewsWatermarkSettings] = useState(createInitialNewsMediaSettings);
  const [newsActionForm, setNewsActionForm] = useState({
    generatedTitle: "",
    generatedMediaType: "متنی",
    generatedTone: "رسمی",
    generatedPlatform: "روبیکا",
    republishProject: "",
    fileName: "",
    captionText: "",
    fontName: "",
    textPosition: "",
    textSize: "",
    textColor: "",
    addVideo: "",
    isEnabled: "enabled",
  });
  const [projectAllNewsRows, setProjectAllNewsRows] = useState([]);
  const [sourcesRows, setSourcesRows] = useState([]);
  const [sourceRubikaAccountsOptions, setSourceRubikaAccountsOptions] = useState([]);
  const [sourceEitaAccountsOptions, setSourceEitaAccountsOptions] = useState([]);
  const [showSourceCreateForm, setShowSourceCreateForm] = useState(false);
  const [sourceCreateForm, setSourceCreateForm] = useState(createInitialSourceCreateForm);
  const [editingSourceRowId, setEditingSourceRowId] = useState("");
  const [savingSource, setSavingSource] = useState(false);
  const [sourcesMsg, setSourcesMsg] = useState({ type: "", text: "" });
  const [priceTemplateRows, setPriceTemplateRows] = useState([]);
  const [priceTemplateForm, setPriceTemplateForm] = useState(createInitialPriceTemplateForm);
  const [editingPriceTemplateId, setEditingPriceTemplateId] = useState("");
  const [savingPriceTemplate, setSavingPriceTemplate] = useState(false);
  const [loadingPriceTemplates, setLoadingPriceTemplates] = useState(false);
  const [sourcesSearchTerm, setSourcesSearchTerm] = useState("");
  const [sourcesStatusFilter, setSourcesStatusFilter] = useState("all");
  const [sourcesManagerFilter, setSourcesManagerFilter] = useState("all");
  const [sourcesPlatformFilter, setSourcesPlatformFilter] = useState("all");
  const [sourcesRowsPerPage, setSourcesRowsPerPage] = useState(SOURCE_ROWS_PER_PAGE_OPTIONS[0]);
  const [sourcesPage, setSourcesPage] = useState(1);
  const [selectedSourceIds, setSelectedSourceIds] = useState([]);
  const [isSourcesDeleteModalOpen, setIsSourcesDeleteModalOpen] = useState(false);
  const [deletingSources, setDeletingSources] = useState(false);
  const [publishSourcesRows, setPublishSourcesRows] = useState([]);
  const [showPublishSourceCreateForm, setShowPublishSourceCreateForm] = useState(false);
  const [publishSourceCreateForm, setPublishSourceCreateForm] = useState(createInitialPublishSourceCreateForm);
  const [editingPublishSourceRowId, setEditingPublishSourceRowId] = useState("");
  const [savingPublishSource, setSavingPublishSource] = useState(false);
  const [publishSourcesMsg, setPublishSourcesMsg] = useState({ type: "", text: "" });
  const [publishSourcesSearchTerm, setPublishSourcesSearchTerm] = useState("");
  const [publishSourcesStatusFilter, setPublishSourcesStatusFilter] = useState("all");
  const [publishSourcesManagerFilter, setPublishSourcesManagerFilter] = useState("all");
  const [publishSourcesPlatformFilter, setPublishSourcesPlatformFilter] = useState("all");
  const [publishSourcesRowsPerPage, setPublishSourcesRowsPerPage] = useState(SOURCE_ROWS_PER_PAGE_OPTIONS[0]);
  const [publishSourcesPage, setPublishSourcesPage] = useState(1);
  const [selectedPublishSourceIds, setSelectedPublishSourceIds] = useState([]);
  const [isPublishSourcesDeleteModalOpen, setIsPublishSourcesDeleteModalOpen] = useState(false);
  const [deletingPublishSources, setDeletingPublishSources] = useState(false);
  const [adsRows, setAdsRows] = useState([]);
  const [adsTotalCount, setAdsTotalCount] = useState(0);
  const [adsMsg, setAdsMsg] = useState({ type: "", text: "" });
  const [adsCreateMsg, setAdsCreateMsg] = useState({ type: "", text: "" });
  const [adsSearchTerm, setAdsSearchTerm] = useState("");
  const [adsTopicFilter, setAdsTopicFilter] = useState("all");
  const [adsManagerFilter, setAdsManagerFilter] = useState("all");
  const [adsRowsPerPage, setAdsRowsPerPage] = useState(ADS_ROWS_PER_PAGE_OPTIONS[0]);
  const [adsPage, setAdsPage] = useState(1);
  const [selectedAdIds, setSelectedAdIds] = useState([]);
  const [isAdsDeleteModalOpen, setIsAdsDeleteModalOpen] = useState(false);
  const [deletingAds, setDeletingAds] = useState(false);
  const [selectedAdRowId, setSelectedAdRowId] = useState("");
  const [selectedAdDetails, setSelectedAdDetails] = useState(null);
  const [loadingAdDetails, setLoadingAdDetails] = useState(false);
  const [usersRows, setUsersRows] = useState(INITIAL_USERS_ROWS);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [usersActivityRows, setUsersActivityRows] = useState(USER_ACTIVITY_ROWS);
  const [loadingUsersActivity, setLoadingUsersActivity] = useState(false);
  const [submittingUserCreate, setSubmittingUserCreate] = useState(false);
  const [usersMsg, setUsersMsg] = useState({ type: "", text: "" });
  const [usersSearchTerm, setUsersSearchTerm] = useState("");
  const [usersProjectFilter, setUsersProjectFilter] = useState("all");
  const [usersManagerFilter, setUsersManagerFilter] = useState("all");
  const [usersRoleFilter, setUsersRoleFilter] = useState("all");
  const [usersStatusFilter, setUsersStatusFilter] = useState("all");
  const [usersRowsPerPage, setUsersRowsPerPage] = useState(USER_ROWS_PER_PAGE_OPTIONS[0]);
  const [usersPage, setUsersPage] = useState(1);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [isUsersDeleteModalOpen, setIsUsersDeleteModalOpen] = useState(false);
  const [deletingUsers, setDeletingUsers] = useState(false);
  const [usersActivitySearchTerm, setUsersActivitySearchTerm] = useState("");
  const [usersActivityFromDate, setUsersActivityFromDate] = useState("all");
  const [usersActivityToDate, setUsersActivityToDate] = useState("all");
  const [usersActivityRoleFilter, setUsersActivityRoleFilter] = useState("all");
  const [usersActivityModuleFilter, setUsersActivityModuleFilter] = useState("all");
  const [usersActivityRowsPerPage, setUsersActivityRowsPerPage] = useState(
    USER_ACTIVITY_ROWS_PER_PAGE_OPTIONS[0]
  );
  const [usersActivityPage, setUsersActivityPage] = useState(1);
  const [userFormMode, setUserFormMode] = useState("");
  const [editingUserId, setEditingUserId] = useState("");
  const [userCreateForm, setUserCreateForm] = useState(createInitialUserCreateForm);
  const [userCreateSelectedProject, setUserCreateSelectedProject] = useState("");
  const [showUserCreatePassword, setShowUserCreatePassword] = useState(false);
  const [showUserCreateRepeatPassword, setShowUserCreateRepeatPassword] = useState(false);
  const [reportsManagerFilter, setReportsManagerFilter] = useState("all");
  const [reportsMainProjectFilter, setReportsMainProjectFilter] = useState("all");
  const [reportsTopProjectsData, setReportsTopProjectsData] = useState([]);
  const [reportsTopSourcesData, setReportsTopSourcesData] = useState([]);
  const [loadingReportsTopCharts, setLoadingReportsTopCharts] = useState(false);
  const [reportsTopChartsError, setReportsTopChartsError] = useState("");
  const [reportsCardFilters, setReportsCardFilters] = useState(createInitialReportsCardFilters);
  const [reportsRealtimeRange, setReportsRealtimeRange] = useState(
    REPORT_REALTIME_RANGE_OPTIONS[0].value
  );
  const [reportsRealtimeLogs, setReportsRealtimeLogs] = useState([]);
  const [loadingReportsRealtimeLogs, setLoadingReportsRealtimeLogs] = useState(false);
  const [reportsRealtimeError, setReportsRealtimeError] = useState("");
  const [downloadingReportFormat, setDownloadingReportFormat] = useState("");
  const [settingsTopTab, setSettingsTopTab] = useState("settings");
  const [settingsGeneralView, setSettingsGeneralView] = useState("list");
  const [settingsBotsRows, setSettingsBotsRows] = useState(SETTINGS_BOT_ROWS);
  const [loadingSettingsBots, setLoadingSettingsBots] = useState(false);
  const [activeSettingsBotGuide, setActiveSettingsBotGuide] = useState(null);
  const [settingsAccountsRows, setSettingsAccountsRows] = useState([]);
  const [settingsEitaAccountsRows, setSettingsEitaAccountsRows] = useState([]);
  const [settingsAccountForm, setSettingsAccountForm] = useState(createInitialSettingsAccountForm);
  const [loadingSettingsAccounts, setLoadingSettingsAccounts] = useState(false);
  const [loadingSettingsEitaAccounts, setLoadingSettingsEitaAccounts] = useState(false);
  const [reactivatingRubikaAccountId, setReactivatingRubikaAccountId] = useState("");
  const [reactivatingEitaAccountId, setReactivatingEitaAccountId] = useState("");
  const [submittingRubikaAccount, setSubmittingRubikaAccount] = useState(false);
  const [settingsBackupRows, setSettingsBackupRows] = useState(INITIAL_SETTINGS_BACKUP_ROWS);
  const [settingsBackupPage, setSettingsBackupPage] = useState(1);
  const [settingsBackupSchedule, setSettingsBackupSchedule] = useState("daily");
  const [loadingSettingsBackups, setLoadingSettingsBackups] = useState(false);
  const [isBackupRestoreModalOpen, setIsBackupRestoreModalOpen] = useState(false);
  const [backupCodeToRestore, setBackupCodeToRestore] = useState("");
  const [selectedSettingsAccountIds, setSelectedSettingsAccountIds] = useState([]);
  const [isSettingsDeleteModalOpen, setIsSettingsDeleteModalOpen] = useState(false);
  const [deletingSettingsAccounts, setDeletingSettingsAccounts] = useState(false);
  const [settingsRateLimit, setSettingsRateLimit] = useState("50");
  const [settingsSystemCheckIntervalSeconds, setSettingsSystemCheckIntervalSeconds] = useState("300");
  const [settingsCleanupNewsIntervalHours, setSettingsCleanupNewsIntervalHours] = useState("24");
  const [isSystemRestartModalOpen, setIsSystemRestartModalOpen] = useState(false);
  const [isSystemRestartWithNewsCleanupModalOpen, setIsSystemRestartWithNewsCleanupModalOpen] = useState(false);
  const [restartingSystem, setRestartingSystem] = useState(false);
  const [settingsDbConfig, setSettingsDbConfig] = useState({
    host: "localhost",
    port: "5432",
    dbName: "news_agent",
  });
  const [loadingSettingsGeneralConfig, setLoadingSettingsGeneralConfig] = useState(false);
  const [securityApiRestrictionMode, setSecurityApiRestrictionMode] = useState("enabled");
  const [securityWhiteIpRows, setSecurityWhiteIpRows] = useState(SETTINGS_SECURITY_WHITE_IP_ROWS);
  const [securityLoginAttemptsRows, setSecurityLoginAttemptsRows] = useState([]);
  const [securityLoginAttemptsPage, setSecurityLoginAttemptsPage] = useState(1);
  const [securityBlockedAccountsRows, setSecurityBlockedAccountsRows] = useState([]);
  const [loadingSecurityLoginAttempts, setLoadingSecurityLoginAttempts] = useState(false);
  const [loadingSecurityBlockedAccounts, setLoadingSecurityBlockedAccounts] = useState(false);
  const [savedSecuritySnapshot, setSavedSecuritySnapshot] = useState(INITIAL_SECURITY_SETTINGS_SNAPSHOT);
  const [isSecurityModeConfirmModalOpen, setIsSecurityModeConfirmModalOpen] = useState(false);
  const [pendingSecurityModeValue, setPendingSecurityModeValue] = useState("");
  const [savingSecurityMode, setSavingSecurityMode] = useState(false);
  const [securityIpForm, setSecurityIpForm] = useState({
    userId: "",
    ip: "",
  });
  const [securityUsersOptions, setSecurityUsersOptions] = useState([]);
  const [deletingSecurityWhiteIpId, setDeletingSecurityWhiteIpId] = useState("");
  const [settingsMsg, setSettingsMsg] = useState({ type: "", text: "" });
  const [settingsDbTestMsg, setSettingsDbTestMsg] = useState({ type: "", text: "" });
  const [settingsHomepageConfig, setSettingsHomepageConfig] = useState({
    projectTitle: "",
    homeDescription: "",
    termsAndConditions: "",
    homeLogoUrl: "",
    homeLogoFile: null,
  });
  const [loadingSettingsHomepageConfig, setLoadingSettingsHomepageConfig] = useState(false);
  const [timePickerPopup, setTimePickerPopup] = useState({
    isOpen: false,
    inputId: "",
    hour: "00",
    minute: "00",
    top: 0,
    left: 0,
  });

  useEffect(() => {
    if (!isTopbarNotificationsOpen) {
      return undefined;
    }

    const handleClickOutside = (event) => {
      if (!topbarNotificationsRef.current?.contains(event.target)) {
        setIsTopbarNotificationsOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setIsTopbarNotificationsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isTopbarNotificationsOpen]);

  const [projectForm, setProjectForm] = useState(createInitialProjectForm);
  const [projectExtraPublishWindows, setProjectExtraPublishWindows] = useState([]);
  const [projectExtraPublishTargets, setProjectExtraPublishTargets] = useState([]);
  const [projectExtraSources, setProjectExtraSources] = useState([]);
  const [showMainSourceSettings, setShowMainSourceSettings] = useState(false);
  const [systemMessagesReturnTab, setSystemMessagesReturnTab] = useState("dashboard");

  const openSystemMessagesTab = () => {
    if (activeTab !== "system-messages") {
      setSystemMessagesReturnTab(activeTab || "dashboard");
    }
    setActiveTab("system-messages");
  };

  const getTitle = () => {
    switch (activeTab) {
      case "dashboard":
        return "داشبورد";
      case "projects":
        return "پروژه ها";
      case "projects-view":
        return "پروژه ها";
      case "sources":
        return "منابع";
      case "news":
        return "اخبار";
      case "news-view":
        return "اخبار";
      case "news-create":
        return "اخبار";
      case "ads":
        return "تبلیغات";
      case "ads-view":
        return "تبلیغات";
      case "ads-create":
        return newsFormMode === "edit" ? "ویرایش تبلیغ" : "تبلیغات";
      case "users":
        return "کاربران";
      case "users-create":
        return "کاربران";
      case "users-edit":
        return "کاربران";
      case "reports":
        return "گزارشات";
      case "reports-realtime":
        return "گزارشات";
      case "settings":
        return "تنظیمات";
      case "system-messages":
        return "پیام های سیستم";
      default:
        return "";
    }
  };

  const isUsersSectionActive = (
    activeTab === "users"
    || activeTab === "users-create"
    || activeTab === "users-edit"
  );
  const isProjectsSectionActive = activeTab === "projects" || activeTab === "projects-view";
  const isNewsSectionActive = (
    activeTab === "news"
    || activeTab === "news-view"
    || activeTab === "news-create"
  );
  const isAdsSectionActive = (
    activeTab === "ads"
    || activeTab === "ads-view"
    || activeTab === "ads-create"
  );
  const isReportsSectionActive = activeTab === "reports" || activeTab === "reports-realtime";
  const allowedTabKeys = useMemo(
    () => new Set(
      allowedModuleKeys
        .map((moduleKey) => MODULE_KEY_TO_TAB_KEY[moduleKey])
        .filter(Boolean)
    ),
    [allowedModuleKeys]
  );
  const filteredSidebarItems = useMemo(
    () => SIDEBAR_ITEMS.filter((item) => allowedTabKeys.has(item.key)),
    [allowedTabKeys]
  );
  const allowedProjectLabelsSet = useMemo(
    () => new Set(allowedProjectLabels.map((item) => String(item || "").trim()).filter(Boolean)),
    [allowedProjectLabels]
  );

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    window.localStorage.setItem(DASHBOARD_ACTIVE_TAB_STORAGE_KEY, activeTab || "dashboard");
  }, [activeTab]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    window.localStorage.setItem(DASHBOARD_PROJECT_VIEW_ID_STORAGE_KEY, selectedProjectViewId || "");
  }, [selectedProjectViewId]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    window.localStorage.setItem(DASHBOARD_PROJECT_VIEW_TITLE_STORAGE_KEY, selectedProjectViewTitle || "");
  }, [selectedProjectViewTitle]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    window.localStorage.setItem(DASHBOARD_PROJECT_VIEW_NEWS_TAB_STORAGE_KEY, projectViewNewsTab || PROJECT_VIEW_NEWS_TABS[0].key);
  }, [projectViewNewsTab]);

  useEffect(() => {
    const params = new URLSearchParams(location.search || "");
    const tabFromUrl = String(params.get("tab") || "").trim();
    const projectIdFromUrl = String(params.get("project_id") || "").trim();
    const projectTitleFromUrl = String(params.get("project_title") || "").trim();
    const projectNewsTabFromUrl = String(params.get("project_news_tab") || "").trim();
    const adRowIdFromUrl = String(params.get("ad_row_id") || "").trim();

    if (tabFromUrl && tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
    }
    if (projectIdFromUrl && projectIdFromUrl !== selectedProjectViewId) {
      setSelectedProjectViewId(projectIdFromUrl);
    }
    if (projectTitleFromUrl && projectTitleFromUrl !== selectedProjectViewTitle) {
      setSelectedProjectViewTitle(projectTitleFromUrl);
    }
    if (
      projectNewsTabFromUrl
      && PROJECT_VIEW_NEWS_TABS.some((item) => item.key === projectNewsTabFromUrl)
      && projectNewsTabFromUrl !== projectViewNewsTab
    ) {
      setProjectViewNewsTab(projectNewsTabFromUrl);
    }
    if (adRowIdFromUrl && adRowIdFromUrl !== selectedAdRowId) {
      setSelectedAdRowId(adRowIdFromUrl);
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search || "");
    const normalizedActiveTab = String(activeTab || "").trim() || "dashboard";
    params.set("tab", normalizedActiveTab);

    if (normalizedActiveTab === "projects-view" && selectedProjectViewId) {
      params.set("project_id", String(selectedProjectViewId));
      if (selectedProjectViewTitle) {
        params.set("project_title", String(selectedProjectViewTitle));
      } else {
        params.delete("project_title");
      }
      params.set("project_news_tab", String(projectViewNewsTab || PROJECT_VIEW_NEWS_TABS[0].key));
    } else {
      params.delete("project_id");
      params.delete("project_title");
      params.delete("project_news_tab");
    }

    if (normalizedActiveTab === "ads-view" && selectedAdRowId) {
      params.set("ad_row_id", String(selectedAdRowId));
    } else {
      params.delete("ad_row_id");
    }

    if (normalizedActiveTab !== "news-view") {
      params.delete("news_id");
      params.delete("return_tab");
      clearNewsViewSnapshot();
    }

    const nextSearch = `?${params.toString()}`;
    if (nextSearch !== location.search) {
      navigate({ pathname: location.pathname, search: nextSearch }, { replace: true });
    }
  }, [
    activeTab,
    selectedProjectViewId,
    selectedProjectViewTitle,
    projectViewNewsTab,
    selectedAdRowId,
    location.pathname,
    location.search,
    navigate,
  ]);

  useEffect(() => {
    setProjectViewNewsPage(1);
  }, [projectViewNewsTab, selectedProjectViewId]);

  useEffect(() => {
    if (
      activeTab === "dashboard"
      || activeTab === "dashboards"
      || activeTab === "projects"
      || activeTab === "projects-view"
      || activeTab === "news"
      || activeTab === "news-view"
      || activeTab === "news-create"
      || activeTab === "sources"
      || activeTab === "ads"
      || activeTab === "ads-view"
      || activeTab === "ads-create"
      || activeTab === "users"
      || activeTab === "users-create"
      || activeTab === "users-edit"
      || activeTab === "reports"
      || activeTab === "reports-realtime"
    ) {
      fetchProjects();
      fetchSourceOptions();
    }
  }, [activeTab]);

  useEffect(() => {
    if (authLoading || activeTab === "system-messages") {
      return;
    }
    const moduleAccessKey = TAB_TO_MODULE_ACCESS_KEY[activeTab];
    if (moduleAccessKey && !allowedModuleKeys.includes(moduleAccessKey)) {
      const fallbackTab = filteredSidebarItems[0]?.key || "dashboard";
      setActiveTab(fallbackTab);
    }
  }, [activeTab, authLoading, allowedModuleKeys, filteredSidebarItems]);

  useEffect(() => {
    if (activeTab !== "reports-realtime") {
      return;
    }
    fetchReportsRealtimeLogs();
  }, [activeTab, reportsRealtimeRange]);


  useEffect(() => {
    if (activeTab !== "reports") {
      return;
    }
    fetchReportsTopCharts();
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === "users" || activeTab === "users-create" || activeTab === "users-edit") {
      fetchUsers();
      fetchUsersActivityLogs();
    }
  }, [activeTab]);

  useEffect(() => {
    setSecurityLoginAttemptsPage(1);
  }, [securityLoginAttemptsRows.length]);

  useEffect(() => {
    setSettingsBackupPage(1);
  }, [settingsBackupRows.length]);

  useEffect(() => {
    if (activeTab === "dashboard") {
      fetchDashboardStats();
      fetchDashboardSystemUsage();
      fetchDashboardTrendCharts();
      fetchUsersActivityLogs();
    }
    if (activeTab === "settings") {
      fetchDashboardSystemUsage();
      fetchSettingsBots();
      fetchSettingsGeneralConfig();
      fetchSettingsBackupConfig();
      fetchSettingsBackups();
      fetchSettingsHomepageConfig();
    }
  }, [activeTab]);

  useEffect(() => {
    fetchDashboardSystemUsage();
    const systemUsageTimerId = window.setInterval(fetchDashboardSystemUsage, SYSTEM_USAGE_UPDATE_INTERVAL_MS);
    return () => {
      window.clearInterval(systemUsageTimerId);
    };
  }, []);

  useEffect(() => {
    if (activeTab !== "settings" || settingsTopTab !== "backup") {
      return;
    }
    fetchSettingsBackupConfig();
    fetchSettingsBackups();
  }, [activeTab, settingsTopTab]);

  useEffect(() => {
    if (activeTab !== "settings" || settingsTopTab !== "security") {
      return;
    }
    fetchSecurityLoginAttempts();
    fetchSecurityWhitelistIps();
    fetchSecurityBlockedAccounts();
    fetchSecurityUsersOptions();
  }, [activeTab, settingsTopTab]);

  useEffect(() => {
    if (activeTab !== "settings" || settingsTopTab !== "settings") {
      return;
    }
    fetchSettingsRubikaAccounts();
    fetchSettingsEitaAccounts();
  }, [activeTab, settingsTopTab]);

  const dashboardPublishedTrendFilter = useMemo(
    () => reportsCardFilters["published-news"] || REPORT_DEFAULT_CARD_FILTER,
    [reportsCardFilters]
  );
  const dashboardReceivedTrendFilter = useMemo(
    () => reportsCardFilters["received-news"] || REPORT_DEFAULT_CARD_FILTER,
    [reportsCardFilters]
  );

  useEffect(() => {
    if (activeTab !== "dashboard" && activeTab !== "reports") {
      return;
    }
    fetchDashboardTrendCharts();
  }, [
    activeTab,
    dashboardPublishedTrendFilter.period,
    dashboardPublishedTrendFilter.projectFilter,
    dashboardReceivedTrendFilter.period,
    dashboardReceivedTrendFilter.projectFilter,
  ]);

  useEffect(() => {
    const contentElement = contentRef.current;
    if (!contentElement) {
      return;
    }

    // Always reset scroll to top when switching to a new section/tab.
    contentElement.scrollTop = 0;
  }, [activeTab]);

  useEffect(() => {
    setSelectedProjectIds([]);
  }, [projects]);

  useEffect(() => {
    const sidebarElement = sidebarRef.current;
    const contentElement = contentRef.current;

    if (!sidebarElement || !contentElement) {
      return undefined;
    }

    let animationFrameId = null;
    let scrollVelocity = 0;

    const normalizeWheelDelta = (event) => {
      if (event.deltaMode === 1) {
        return event.deltaY * 16;
      }
      if (event.deltaMode === 2) {
        return event.deltaY * window.innerHeight;
      }
      return event.deltaY;
    };

    const animateMomentumScroll = () => {
      if (Math.abs(scrollVelocity) < 0.1) {
        scrollVelocity = 0;
        animationFrameId = null;
        return;
      }

      contentElement.scrollTop += scrollVelocity;
      scrollVelocity *= 0.85;
      animationFrameId = window.requestAnimationFrame(animateMomentumScroll);
    };

    const handleSidebarWheel = (event) => {
      event.preventDefault();
      const deltaY = normalizeWheelDelta(event);
      scrollVelocity += deltaY * 0.16;

      if (!animationFrameId) {
        animationFrameId = window.requestAnimationFrame(animateMomentumScroll);
      }
    };

    sidebarElement.addEventListener("wheel", handleSidebarWheel, { passive: false });

    return () => {
      sidebarElement.removeEventListener("wheel", handleSidebarWheel);
      if (animationFrameId) {
        window.cancelAnimationFrame(animationFrameId);
      }
    };
  }, []);

  useEffect(() => {
    setProjectPage(1);
  }, [projectSearchTerm, projectStatusFilter, projectManagerFilter, projectRowsPerPage]);

  useEffect(() => {
    setDashboardActivityPage(1);
  }, [
    dashboardActivityFromDate,
    dashboardActivityToDate,
    dashboardActivityRoleFilter,
    dashboardActivityModuleFilter,
  ]);

  useEffect(() => {
    const extractProjectFilter = String(newsSectionControls.extract?.projectFilter || "all");
    if (extractProjectFilter === "all") {
      return;
    }

    const hasSelectedProject = projects.some(
      (project) => String(project?.id) === extractProjectFilter
    );

    if (!hasSelectedProject) {
      setNewsSectionControls((prev) => ({
        ...prev,
        extract: {
          ...prev.extract,
          projectFilter: "all",
          sourceFilter: "all",
          page: 1,
        },
      }));
    }
  }, [newsSectionControls.extract?.projectFilter, projects]);

  useEffect(() => {
    const validProjectIds = new Set(
      projects
        .map((project) => project?.id)
        .filter((projectId) => projectId !== undefined && projectId !== null)
        .map((projectId) => String(projectId))
    );

    if (
      reportsMainProjectFilter !== "all"
      && !validProjectIds.has(String(reportsMainProjectFilter))
    ) {
      setReportsMainProjectFilter("all");
    }

    setReportsCardFilters((prevFilters) => {
      let changed = false;
      const nextFilters = { ...prevFilters };

      REPORT_CARD_KEYS.forEach((cardKey) => {
        const currentFilter = prevFilters[cardKey] || { ...REPORT_DEFAULT_CARD_FILTER };
        const currentProjectFilter = currentFilter.projectFilter ?? "all";

        if (
          currentProjectFilter !== "all"
          && !validProjectIds.has(String(currentProjectFilter))
        ) {
          nextFilters[cardKey] = {
            ...currentFilter,
            projectFilter: "all",
          };
          changed = true;
          return;
        }

        if (!prevFilters[cardKey]) {
          nextFilters[cardKey] = currentFilter;
          changed = true;
        }
      });

      return changed ? nextFilters : prevFilters;
    });
  }, [projects, reportsMainProjectFilter]);

  useEffect(() => {
    if (reportsManagerFilter === "all") {
      return;
    }

    const availableManagers = new Set(
      projects
        .map((project) => getProjectManagerName(project))
        .filter((managerName) => managerName)
    );

    if (!availableManagers.has(reportsManagerFilter)) {
      setReportsManagerFilter("all");
    }
  }, [reportsManagerFilter, projects]);

  useEffect(() => {
    setSelectedSettingsAccountIds((prevSelectedIds) => (
      prevSelectedIds.filter((rowId) => (
        settingsAccountsRows.some((row) => row.id === rowId)
      ))
    ));
  }, [settingsAccountsRows]);

  useEffect(() => {
    setSelectedNewsIds([]);
  }, [newsRows]);

  useEffect(() => {
    setNewsSectionControls((prev) => ({
      extract: { ...prev.extract, page: 1 },
      make: { ...prev.make, page: 1 },
    }));
  }, [newsReloadToken]);

  useEffect(() => {
    if (activeTab !== "news") {
      return undefined;
    }

    let isCancelled = false;

    const fetchNewsRows = async () => {
      setLoadingNewsRows(true);
      setNewsError("");

      try {
        const [extractResult, makeResult] = await Promise.all([
          newsApi.list({
            page: newsSectionControls.extract.page || 1,
            page_size: newsSectionControls.extract.rowsPerPage || NEWS_ROWS_PER_PAGE_OPTIONS[0],
            search: newsSectionControls.extract.searchTerm || "",
            project_id: newsSectionControls.extract.projectFilter !== "all" ? newsSectionControls.extract.projectFilter : "",
            manager_name: newsSectionControls.extract.managerFilter !== "all" ? newsSectionControls.extract.managerFilter : "",
            source_id: newsSectionControls.extract.sourceFilter !== "all" ? newsSectionControls.extract.sourceFilter : "",
            media_type: newsSectionControls.extract.mediaFilter !== "all" ? newsSectionControls.extract.mediaFilter : "",
            status: newsSectionControls.extract.statusFilter !== "all" ? newsSectionControls.extract.statusFilter : "all",
            revision_type: "extract",
          }),
          newsApi.list({
            page: newsSectionControls.make.page || 1,
            page_size: newsSectionControls.make.rowsPerPage || NEWS_ROWS_PER_PAGE_OPTIONS[0],
            search: newsSectionControls.make.searchTerm || "",
            manager_name: newsSectionControls.make.managerFilter !== "all" ? newsSectionControls.make.managerFilter : "",
            publish_source_id: newsSectionControls.make.sourceFilter !== "all" ? newsSectionControls.make.sourceFilter : "",
            media_type: newsSectionControls.make.mediaFilter !== "all" ? newsSectionControls.make.mediaFilter : "",
            status: newsSectionControls.make.statusFilter !== "all" ? newsSectionControls.make.statusFilter : "all",
            exclude_revision_type: "extract",
          }),
        ]);

        const extractItems = Array.isArray(extractResult?.items) ? extractResult.items : [];
        const makeItems = Array.isArray(makeResult?.items) ? makeResult.items : [];
        const mappedExtractRows = extractItems.map((item) => normalizeNewsRowFromApi(item, projects));
        const mappedMakeRows = makeItems.map((item) => normalizeNewsRowFromApi(item, projects));
        const mappedNewsRows = [...mappedExtractRows, ...mappedMakeRows];
        const extractTotalCount = Number(extractResult?.total_count || mappedExtractRows.length || 0);
        const makeTotalCount = Number(makeResult?.total_count || mappedMakeRows.length || 0);

        if (!isCancelled) {
          setNewsRows(mappedNewsRows);
          setNewsSectionRows({
            extract: mappedExtractRows,
            make: mappedMakeRows,
          });
          setNewsSectionTotalCount({
            extract: extractTotalCount,
            make: makeTotalCount,
          });
        }
      } catch (error) {
        if (!isCancelled) {
          setNewsRows([]);
          setNewsSectionRows({ extract: [], make: [] });
          setNewsSectionTotalCount({ extract: 0, make: 0 });
          setNewsError(error.message || "بارگذاری اخبار ناموفق بود.");
        }
      } finally {
        if (!isCancelled) {
          setLoadingNewsRows(false);
        }
      }
    };

    fetchNewsRows();

    return () => {
      isCancelled = true;
    };
  }, [
    activeTab,
    projects,
    newsSectionControls,
    newsReloadToken,
  ]);

  useEffect(() => {
    if (activeTab === "sources") {
      fetchReceiveSources();
      fetchPublishSources();
      fetchSourceRubikaAccountsOptions();
      fetchSourceEitaAccountsOptions();
      fetchPriceTemplates();
      return;
    }

    setSourcesRows(mapProjectsToSourcesRows(projects));
  }, [activeTab, projects]);

  useEffect(() => {
    if (activeTab === "projects-view" && selectedProjectViewId) {
      fetchProjectAllNewsRows(selectedProjectViewId);
    }
  }, [activeTab, selectedProjectViewId, projects]);

  useEffect(() => {
    if (activeTab !== "ads-view" || !selectedAdRowId) {
      return;
    }
    const selectedRowFromList = adsRows.find((row) => row.rowId === selectedAdRowId);
    if (selectedRowFromList?.id) {
      fetchAdDetails(selectedRowFromList.id);
    }
  }, [activeTab, selectedAdRowId, adsRows]);

  useEffect(() => {
    setSelectedSourceIds([]);
  }, [sourcesRows]);

  useEffect(() => {
    setSourcesPage(1);
  }, [sourcesSearchTerm, sourcesStatusFilter, sourcesManagerFilter, sourcesPlatformFilter, sourcesRowsPerPage]);

  useEffect(() => {
    setSelectedPublishSourceIds([]);
  }, [publishSourcesRows]);

  useEffect(() => {
    setPublishSourcesPage(1);
  }, [
    publishSourcesSearchTerm,
    publishSourcesStatusFilter,
    publishSourcesManagerFilter,
    publishSourcesPlatformFilter,
    publishSourcesRowsPerPage,
  ]);

  useEffect(() => {
    if (activeTab !== "ads") {
      return;
    }
    fetchAds();
  }, [activeTab, adsPage, adsRowsPerPage, adsSearchTerm, adsTopicFilter, adsManagerFilter, newsReloadToken]);

  useEffect(() => {
    setSelectedAdIds([]);
  }, [adsRows]);

  useEffect(() => {
    setAdsPage(1);
  }, [adsSearchTerm, adsTopicFilter, adsManagerFilter, adsRowsPerPage]);

  useEffect(() => {
    setSelectedUserIds([]);
  }, [usersRows]);

  useEffect(() => {
    setUsersPage(1);
  }, [
    usersSearchTerm,
    usersProjectFilter,
    usersManagerFilter,
    usersRoleFilter,
    usersStatusFilter,
    usersRowsPerPage,
  ]);

  useEffect(() => {
    setUsersActivityPage(1);
  }, [
    usersActivitySearchTerm,
    usersActivityFromDate,
    usersActivityToDate,
    usersActivityRoleFilter,
    usersActivityModuleFilter,
    usersActivityRowsPerPage,
  ]);

  const fetchProjects = async () => {
    setLoadingProjects(true);

    try {
      const result = await projectsApi.list();
      const items = Array.isArray(result?.items) ? result.items : [];
      const normalized = items.map(normalizeProjectRowFromApi);
      const canViewAllProjects = !restrictProjectsByAccess;
      setProjects(
        canViewAllProjects
          ? normalized
          : normalized.filter((project) => allowedProjectLabelsSet.has(String(project.title || "").trim()))
      );
    } catch (error) {
      setProjectsMsg({
        type: "error",
        text: error.message || "خطا در دریافت پروژه‌ها.",
      });
    } finally {
      setLoadingProjects(false);
    }
  };

  const fetchSourceOptions = async () => {
    try {
      const [receiveResult, publishResult] = await Promise.all([
        sourcesApi.listReceive(),
        sourcesApi.listPublish(),
      ]);
      const receiveItems = Array.isArray(receiveResult?.items) ? receiveResult.items : [];
      const publishItems = Array.isArray(publishResult?.items) ? publishResult.items : [];
      setReceiveSourcesOptions(receiveItems);
      setPublishSourcesOptions(publishItems);
    } catch (error) {
      setProjectsMsg({
        type: "error",
        text: error.message || "خطا در دریافت لیست منابع و بسترهای انتشار.",
      });
    }
  };

  const fetchUsers = async () => {
    setLoadingUsers(true);

    try {
      const result = await usersApi.list();
      const userItems = Array.isArray(result?.items) ? result.items : [];
      setUsersRows(userItems.map(normalizeUserRowFromApi));
    } catch (error) {
      setUsersMsg({
        type: "error",
        text: error.message || "خطا در دریافت لیست کاربران.",
      });
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchUsersActivityLogs = async () => {
    setLoadingUsersActivity(true);

    try {
      const result = await usersApi.activityLogs();
      const items = Array.isArray(result?.items) ? result.items : [];
      const normalizedRows = items.map((item) => ({
        id: String(item.id || item.code || createProjectFormRowId("activity")),
        role: String(item.role || "--").trim() || "--",
        activityDate: formatNewsDate(item.activity_date),
        activityDateRaw: String(item.activity_date || ""),
        userName: String(item.user_name || "--").trim() || "--",
        module: String(item.module || "--").trim() || "--",
        ip: String(item.ip || "--").trim() || "--",
        event: String(item.event || "--").trim() || "--",
      }));
      setUsersActivityRows(normalizedRows);
    } catch (error) {
      setUsersMsg({
        type: "error",
        text: error.message || "خطا در دریافت فعالیت‌های اخیر کاربران.",
      });
    } finally {
      setLoadingUsersActivity(false);
    }
  };

  const fetchReceiveSources = async () => {
    setLoadingProjects(true);
    try {
      const result = await sourcesApi.listReceive();
      const items = Array.isArray(result?.items) ? result.items : [];
      setSourcesRows(items.map(normalizeReceiveSourceRowFromApi));
    } catch (error) {
      setSourcesMsg({
        type: "error",
        text: error.message || "خطا در دریافت منابع.",
      });
    } finally {
      setLoadingProjects(false);
    }
  };

  const fetchPriceTemplates = async () => {
    setLoadingPriceTemplates(true);
    try {
      const result = await priceTemplatesApi.list();
      const items = Array.isArray(result?.items) ? result.items : [];
      setPriceTemplateRows(items.map(normalizePriceTemplateRowFromApi));
    } catch (error) {
      setPriceTemplateRows([]);
      setSourcesMsg({
        type: "error",
        text: error.message || "خطا در دریافت قالب‌های قیمت.",
      });
    } finally {
      setLoadingPriceTemplates(false);
    }
  };

  const fetchSourceRubikaAccountsOptions = async () => {
    try {
      const result = await settingsApi.listRubikaAccounts();
      const items = Array.isArray(result?.items) ? result.items : [];
      const normalizedRows = items
        .map((item) => ({
          id: String(item?.id || "").trim(),
          phone: String(item?.phone || item?.username || item?.accountReference || "").trim(),
          isActive: String(item?.status || "").trim().toLowerCase() !== "inactive",
        }))
        .filter((item) => item.id);
      setSourceRubikaAccountsOptions(normalizedRows);
    } catch (error) {
      setSourceRubikaAccountsOptions([]);
      setSourcesMsg({
        type: "error",
        text: error.message || "خطا در دریافت اکانت‌های روبیکا.",
      });
    }
  };


  const fetchSourceEitaAccountsOptions = async () => {
    try {
      const result = await settingsApi.listEitaAccounts();
      const items = Array.isArray(result?.items) ? result.items : [];
      const normalizedRows = items
        .map((item) => ({
          id: String(item?.id || "").trim(),
          phone: String(item?.phone || item?.username || item?.accountReference || "").trim(),
          isActive: String(item?.status || "").trim().toLowerCase() !== "inactive",
        }))
        .filter((item) => item.id);
      setSourceEitaAccountsOptions(normalizedRows);
    } catch (error) {
      setSourceEitaAccountsOptions([]);
      setSourcesMsg({
        type: "error",
        text: error.message || "خطا در دریافت اکانت‌های ایتا.",
      });
    }
  };

  const fetchPublishSources = async () => {
    setLoadingProjects(true);
    try {
      const result = await sourcesApi.listPublish();
      const items = Array.isArray(result?.items) ? result.items : [];
      setPublishSourcesRows(items.map(normalizePublishSourceRowFromApi));
    } catch (error) {
      setPublishSourcesMsg({
        type: "error",
        text: error.message || "خطا در دریافت منابع انتشار.",
      });
    } finally {
      setLoadingProjects(false);
    }
  };

  const fetchAds = async () => {
    setLoadingProjects(true);
    try {
      const result = await newsApi.list({
        page: adsPage,
        page_size: adsRowsPerPage,
        search: adsSearchTerm,
        manager_name: adsManagerFilter !== "all" ? adsManagerFilter : "",
        publish_source_id: adsTopicFilter !== "all" ? adsTopicFilter : "",
        my_ad: true,
      });
      const items = Array.isArray(result?.items) ? result.items : [];
      const totalCount = Number(result?.total_count || items.length || 0);
      setAdsRows(items.map(normalizeAdRowFromApi));
      setAdsTotalCount(totalCount);
    } catch (error) {
      setAdsRows([]);
      setAdsTotalCount(0);
      setAdsMsg({
        type: "error",
        text: error.message || "خطا در دریافت تبلیغات.",
      });
    } finally {
      setLoadingProjects(false);
    }
  };

  const fetchAdDetails = async (adId) => {
    if (!adId) {
      setSelectedAdDetails(null);
      return;
    }

    setLoadingAdDetails(true);
    try {
      const result = await newsApi.detail(adId);
      setSelectedAdDetails(normalizeAdRowFromApi(result?.item || {}));
    } catch (error) {
      setSelectedAdDetails(null);
      setAdsMsg({
        type: "error",
        text: error.message || "خطا در دریافت جزئیات تبلیغ.",
      });
    } finally {
      setLoadingAdDetails(false);
    }
  };

  const fetchDashboardStats = async () => {
    setLoadingStats(true);
    try {
      const statsData = await dashboardApi.stats();

      setDashboardStats({
        totalReceiveSources: statsData?.total_receive_sources || 0,
        totalProjects: statsData?.total_projects || 0,
        totalReceivedNews: statsData?.total_received_news || 0,
        totalPublishedNews: statsData?.total_published_news || 0,
        criticalEventsCount: Number(statsData?.critical_events_count || 0),
        hasCriticalEvents: Boolean(statsData?.has_critical_events),
      });
    } catch (error) {
      setDashboardStats({
        totalReceiveSources: 0,
        totalProjects: 0,
        totalReceivedNews: 0,
        totalPublishedNews: 0,
        criticalEventsCount: 0,
        hasCriticalEvents: false,
      });
      console.error("خطا در دریافت آمار داشبورد", error);
    } finally {
      setLoadingStats(false);
    }
  };

  const fetchDashboardSystemUsage = () => {
    setLoadingSystemUsage(false);
    setSystemUsage(buildFakeSystemUsage());
  };

  const fetchDashboardTrendCharts = async () => {
    // Each dashboard trend card has independent period/project filters.
    const publishedPeriod = (
      REPORT_PERIOD_TO_API[dashboardPublishedTrendFilter.period]
      || REPORT_PERIOD_TO_API[REPORT_DEFAULT_CARD_FILTER.period]
    );
    const receivedPeriod = (
      REPORT_PERIOD_TO_API[dashboardReceivedTrendFilter.period]
      || REPORT_PERIOD_TO_API[REPORT_DEFAULT_CARD_FILTER.period]
    );
    const publishedProjectId = dashboardPublishedTrendFilter.projectFilter || "all";
    const receivedProjectId = dashboardReceivedTrendFilter.projectFilter || "all";

    setLoadingDashboardTrends(true);
    setDashboardTrendsError("");
    try {
      const [publishedResponse, receivedResponse] = await Promise.all([
        dashboardApi.newsTrend({
          metric: "published",
          period: publishedPeriod,
          projectId: publishedProjectId,
        }),
        dashboardApi.newsTrend({
          metric: "received",
          period: receivedPeriod,
          projectId: receivedProjectId,
        }),
      ]);

      const normalizeTrendPayload = (payload, fallbackPoints, period) => {
        const pointsRaw = Array.isArray(payload?.points) && payload.points.length
          ? payload.points.map((point) => Number(point) || 0)
          : fallbackPoints;
        const labelsRaw = Array.isArray(payload?.labels) && payload.labels.length
          ? payload.labels.map((label) => String(label))
          : buildFallbackTrendLabels({ period, count: pointsRaw.length });

        if (period === "daily") {
          return normalizeDailySeriesToFullDay(labelsRaw, pointsRaw);
        }

        return { labels: labelsRaw, points: pointsRaw };
      };

      setDashboardTrendCharts({
        published: normalizeTrendPayload(publishedResponse, REPORT_PUBLISHED_NEWS_POINTS, publishedPeriod),
        received: normalizeTrendPayload(receivedResponse, REPORT_RECEIVED_NEWS_POINTS, receivedPeriod),
      });
    } catch (error) {
      setDashboardTrendsError(error.message || "خطا در دریافت داده نمودارهای داشبورد.");
      setDashboardTrendCharts({
        published: { labels: REPORT_WEEKDAY_LABELS, points: REPORT_PUBLISHED_NEWS_POINTS },
        received: { labels: REPORT_WEEKDAY_LABELS, points: REPORT_RECEIVED_NEWS_POINTS },
      });
    } finally {
      setLoadingDashboardTrends(false);
    }
  };

  const fetchReportsRealtimeLogs = async () => {
    setLoadingReportsRealtimeLogs(true);
    setReportsRealtimeError("");
    try {
      const result = await reportsApi.systemEvents({
        rangeMinutes: reportsRealtimeRange,
      });
      const items = Array.isArray(result?.items) ? result.items : [];
      setReportsRealtimeLogs(items);
    } catch (error) {
      setReportsRealtimeLogs([]);
      setReportsRealtimeError(error.message || "خطا در دریافت لاگ های لحظه ای سیستم.");
    } finally {
      setLoadingReportsRealtimeLogs(false);
    }
  };


  const fetchReportsTopCharts = async () => {
    const projectsPeriod = "all_time";
    const sourcesPeriod = "all_time";
    const projectsFilterProjectId = "all";
    const sourcesFilterProjectId = "all";

    setLoadingReportsTopCharts(true);
    setReportsTopChartsError("");
    try {
      const [topProjectsResult, topSourcesResult] = await Promise.all([
        reportsApi.topProjectsPublished({
          period: projectsPeriod,
          projectId: projectsFilterProjectId,
        }),
        reportsApi.topReceiveSources({
          period: sourcesPeriod,
          projectId: sourcesFilterProjectId,
        }),
      ]);

      const projectItems = Array.isArray(topProjectsResult?.items) ? topProjectsResult.items : [];
      const sourceItems = Array.isArray(topSourcesResult?.items) ? topSourcesResult.items : [];

      const normalizeProjectRows = projectItems.slice(0, 5).map((row, index) => ({
        projectId: String(row?.project_id || row?.projectId || row?.id || "").trim(),
        label: String(row?.label || `پروژه ${index + 1}`),
        value: Number(row?.value || 0),
      }));
      const normalizeSourceRows = sourceItems.slice(0, 5).map((row, index) => ({
        receiveSourceId: String(row?.receive_source_id || row?.receiveSourceId || row?.id || "").trim(),
        label: String(row?.label || `منبع ${index + 1}`),
        value: Number(row?.value || 0),
      }));

      setReportsTopProjectsData(normalizeProjectRows);
      setReportsTopSourcesData(normalizeSourceRows);
    } catch (error) {
      setReportsTopProjectsData([]);
      setReportsTopSourcesData([]);
      setReportsTopChartsError(error.message || "خطا در دریافت نمودارهای گزارشات.");
    } finally {
      setLoadingReportsTopCharts(false);
    }
  };

  const fetchSettingsBots = async () => {
    setLoadingSettingsBots(true);
    try {
      const result = await settingsApi.listRobots();
      const items = Array.isArray(result?.items) ? result.items : [];
      setSettingsBotsRows(items.map((row, index) => normalizeSettingsBotRowFromApi(row, index)));
    } catch (error) {
      setSettingsBotsRows([]);
      setSettingsMsg({
        type: "error",
        text: error.message || "خطا در دریافت ربات‌ها از سرور.",
      });
    } finally {
      setLoadingSettingsBots(false);
    }
  };

  const fetchSettingsGeneralConfig = async () => {
    setLoadingSettingsGeneralConfig(true);
    try {
      const result = await settingsApi.getGeneralConfig();
      setSettingsRateLimit(String(result?.limit_rate ?? 0));
      setSettingsSystemCheckIntervalSeconds(String(result?.system_check_interval ?? 0));
      setSettingsCleanupNewsIntervalHours(String(result?.cleanup_news_interval_hours ?? 24));
      setSettingsDbConfig({
        host: String(result?.database_host_name || ""),
        port: String(result?.database_port ?? 0),
        dbName: String(result?.database_name || ""),
      });
      const nextSecurityMode = result?.ip_limit_activity_status ? "enabled" : "disabled";
      setSecurityApiRestrictionMode(nextSecurityMode);
      setSavedSecuritySnapshot((prevSnapshot) => (
        createSecuritySettingsSnapshot({
          apiRestrictionMode: nextSecurityMode,
          whiteIpRows: prevSnapshot?.whiteIpRows || securityWhiteIpRows,
        })
      ));
    } catch (error) {
      setSettingsMsg({
        type: "error",
        text: error.message || "خطا در دریافت تنظیمات عمومی.",
      });
    } finally {
      setLoadingSettingsGeneralConfig(false);
    }
  };

  const fetchSettingsBackupConfig = async () => {
    try {
      const result = await settingsApi.getBackupConfig();
      const nextSchedule = String(result?.backup_duration || "daily");
      if (SETTINGS_BACKUP_SCHEDULE_LABELS[nextSchedule]) {
        setSettingsBackupSchedule(nextSchedule);
      }
    } catch (error) {
      setSettingsMsg({
        type: "error",
        text: error.message || "خطا در دریافت زمان‌بندی بکاپ.",
      });
    }
  };

  const fetchSettingsBackups = async () => {
    setLoadingSettingsBackups(true);
    try {
      const result = await settingsApi.listBackups();
      const items = Array.isArray(result?.items) ? result.items : [];
      setSettingsBackupRows(items.map((row, index) => normalizeSettingsBackupRowFromApi(row, index)));
    } catch (error) {
      setSettingsBackupRows([]);
      if (isSqliteOnlyBackupError(error)) {
        return;
      }
      setSettingsMsg({
        type: "error",
        text: error.message || "خطا در دریافت لیست بکاپ‌ها.",
      });
    } finally {
      setLoadingSettingsBackups(false);
    }
  };

  const fetchSecurityLoginAttempts = async () => {
    setLoadingSecurityLoginAttempts(true);
    try {
      const result = await usersApi.loginAttempts();
      const items = Array.isArray(result?.items) ? result.items : [];
      setSecurityLoginAttemptsRows(items.map((row, index) => normalizeSecurityLoginAttemptFromApi(row, index)));
    } catch (error) {
      setSecurityLoginAttemptsRows([]);
      setSettingsMsg({
        type: "error",
        text: error.message || "خطا در دریافت لیست تلاش های ورود.",
      });
    } finally {
      setLoadingSecurityLoginAttempts(false);
    }
  };

  const fetchSecurityWhitelistIps = async () => {
    try {
      const result = await settingsApi.listWhitelistIps();
      const items = Array.isArray(result?.items) ? result.items : [];
      setSecurityWhiteIpRows(items.map((row, index) => normalizeSecurityWhiteIpRowFromApi(row, index)));
    } catch (error) {
      setSecurityWhiteIpRows([]);
      setSettingsMsg({
        type: "error",
        text: error.message || "خطا در دریافت لیست آی پی های سفید.",
      });
    }
  };

  const fetchSecurityBlockedAccounts = async () => {
    setLoadingSecurityBlockedAccounts(true);
    try {
      const result = await settingsApi.listBlockedPublishSources();
      const items = Array.isArray(result?.items) ? result.items : [];
      setSecurityBlockedAccountsRows(items.map((row, index) => normalizeSecurityBlockedAccountRowFromApi(row, index)));
    } catch (error) {
      setSecurityBlockedAccountsRows([]);
      setSettingsMsg({
        type: "error",
        text: error.message || "خطا در دریافت حساب های بلاک شده.",
      });
    } finally {
      setLoadingSecurityBlockedAccounts(false);
    }
  };

  const fetchSecurityUsersOptions = async () => {
    try {
      const result = await usersApi.list();
      const items = Array.isArray(result?.items) ? result.items : [];
      const options = items.map((user) => ({
        id: String(user?.id || ""),
        label: String(
          user?.full_name
          || `${user?.first_name || ""} ${user?.last_name || ""}`.trim()
          || user?.username
          || "کاربر"
        ),
      })).filter((item) => item.id);
      setSecurityUsersOptions(options);
    } catch (error) {
      setSecurityUsersOptions([]);
    }
  };

  const fetchSettingsHomepageConfig = async () => {
    setLoadingSettingsHomepageConfig(true);
    try {
      const result = await settingsApi.getHomepageConfig();
      setSettingsHomepageConfig({
        projectTitle: String(result?.project_title || ""),
        homeDescription: String(result?.home_description || ""),
        termsAndConditions: String(result?.terms_and_conditions || ""),
        homeLogoUrl: buildAbsoluteFileUrl(result?.home_logo || ""),
        homeLogoFile: null,
      });
    } catch (error) {
      setSettingsMsg({
        type: "error",
        text: error.message || "خطا در دریافت تنظیمات صفحه اصلی.",
      });
    } finally {
      setLoadingSettingsHomepageConfig(false);
    }
  };

  const handleDownloadReportsRealtime = async (format) => {
    const normalizedFormat = String(format || "").toLowerCase();
    if (!["xlsx", "pdf"].includes(normalizedFormat)) {
      return;
    }

    setDownloadingReportFormat(normalizedFormat);
    try {
      const blob = await reportsApi.exportSystemEvents({
        rangeMinutes: reportsRealtimeRange,
        format: normalizedFormat,
      });
      const extension = normalizedFormat === "xlsx" ? "xlsx" : "pdf";
      const fileName = `system_events_${Date.now()}.${extension}`;
      const objectUrl = URL.createObjectURL(blob);
      const tempLink = document.createElement("a");
      tempLink.href = objectUrl;
      tempLink.download = fileName;
      document.body.appendChild(tempLink);
      tempLink.click();
      tempLink.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (error) {
      setReportsRealtimeError(error.message || "خطا در دانلود گزارش.");
    } finally {
      setDownloadingReportFormat("");
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProjectForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const buildProjectFormFromRow = (project) => {
    const initialForm = createInitialProjectForm();
    const logoUrl = String(project?.media_logo_file_url || "");
    const logoName = getFileNameFromUrl(logoUrl);
    const normalizedPublishSourceIds = Array.isArray(project?.publish_source_ids)
      ? project.publish_source_ids.map((value) => String(value || "")).filter(Boolean)
      : (project?.publish_source_id ? [String(project.publish_source_id)] : []);
    const primaryPublishSourceId = String(project?.publish_source_id || normalizedPublishSourceIds[0] || "");
    const primaryPublishSourceRow = publishSourcesOptions.find(
      (row) => String(row?.id || "") === primaryPublishSourceId
    );

    return {
      ...initialForm,
      ...project,
      ...readViewBoostSettings(project, primaryPublishSourceId),
      receive_source_ids: Array.isArray(project?.receive_source_ids)
        ? project.receive_source_ids.map((value) => String(value || "")).filter(Boolean)
        : (project?.receive_source_id ? [String(project.receive_source_id)] : []),
      publish_source_id: primaryPublishSourceId,
      publish_source_ids: normalizedPublishSourceIds,
      publish_platform_name: String(
        primaryPublishSourceRow?.name
        || project?.publish_platform_name
        || project?.publish_source_name
        || project?.publish_source
        || ""
      ),
      publish_platform_type: String(
        primaryPublishSourceRow?.platform
        || project?.publish_platform_type
        || ""
      ),
      publish_link: String(
        primaryPublishSourceRow?.link
        || project?.publish_link
        || ""
      ),
      media_logo_file: project?.media_logo_file || logoName,
      media_logo_file_file: null,
      media_logo_file_clear: false,
      media_logo_preview_url: logoUrl,
      media_process_mode: String(project?.media_process_mode || initialForm.media_process_mode || ""),
      media_apply_to: String(project?.media_apply_to || initialForm.media_apply_to || "image_and_video"),
      media_process_active: Boolean(
        project?.media_process_mode
        || project?.media_process_active
      ),
      media_logo_size: String(
        project?.media_logo_size ?? initialForm.media_logo_size
      ),
      media_logo_color: String(project?.media_logo_color || initialForm.media_logo_color || ""),
      media_logo_x: String(project?.media_logo_x ?? initialForm.media_logo_x ?? "0"),
      media_logo_y: String(project?.media_logo_y ?? initialForm.media_logo_y ?? "0"),
      media_text_content: String(project?.media_text_content || initialForm.media_text_content || ""),
      media_text_size: String(
        project?.media_text_size ?? initialForm.media_text_size
      ),
      media_text_color: String(project?.media_text_color || initialForm.media_text_color || ""),
      media_text_font: String(project?.media_text_font || initialForm.media_text_font || ""),
      media_text_x: String(project?.media_text_x ?? initialForm.media_text_x ?? "0"),
      media_text_y: String(project?.media_text_y ?? initialForm.media_text_y ?? "0"),
      media_opacity: String(project?.media_opacity ?? initialForm.media_opacity ?? "100"),
      status: getProjectStatusInfo(project).key,
      operation_type: String(project?.operation_type ?? initialForm.operation_type),
      fetch_interval_minutes: String(
        project?.fetch_interval_minutes ?? initialForm.fetch_interval_minutes
      ),
      publish_interval_seconds: String(
        project?.publish_interval_seconds ?? project?.publish_interval_minutes ?? initialForm.publish_interval_seconds
      ),
      outside_window_mode: String(project?.outside_window_mode || initialForm.outside_window_mode),
      similarity_threshold: String(
        project?.similarity_threshold ?? initialForm.similarity_threshold
      ),
    };
  };

  const buildProjectExtraPublishWindowsFromRow = (project) => (
    Array.isArray(project?.extra_publish_windows)
      ? project.extra_publish_windows.map((row) => ({
          ...createProjectExtraPublishWindow(),
          ...row,
          id: row?.id || createProjectFormRowId("range"),
        }))
      : []
  );

  const buildProjectExtraPublishTargetsFromRow = (project) => (
    (() => {
      const explicitRows = Array.isArray(project?.extra_publish_targets)
        ? project.extra_publish_targets
        : [];
      if (explicitRows.length > 0) {
        return explicitRows.map((row) => ({
          ...createProjectExtraPublishTarget(),
          ...row,
          ...readViewBoostSettings(project, row?.publish_source_id),
          id: row?.id || createProjectFormRowId("publish-target"),
        }));
      }

      const allIds = Array.isArray(project?.publish_source_ids)
        ? project.publish_source_ids.map((value) => String(value || "")).filter(Boolean)
        : [];
      const primaryId = String(project?.publish_source_id || "");
      const extraIds = allIds.filter((id) => id && id !== primaryId);

      return extraIds.map((id) => {
        const selectedRow = publishSourcesOptions.find((item) => String(item?.id) === id);
        return {
          ...createProjectExtraPublishTarget(),
          id: createProjectFormRowId("publish-target"),
          publish_source_id: id,
          name: String(selectedRow?.name || ""),
          platform: String(selectedRow?.platform || ""),
          link: String(selectedRow?.link || ""),
          ...readViewBoostSettings(project, id),
        };
      });
    })()
  );

  const buildProjectExtraSourcesFromRow = (project) => (
    (() => {
      const explicitRows = Array.isArray(project?.extra_sources)
        ? project.extra_sources
        : [];
      if (explicitRows.length > 0) {
        return explicitRows.map((row) => ({
          ...createProjectExtraSource(),
          ...row,
          media_logo_file: String(row?.media_logo_file || getFileNameFromUrl(row?.media_logo_file_url || "")),
          media_logo_file_file: null,
          media_logo_file_clear: false,
          media_logo_preview_url: String(row?.media_logo_file_url || ""),
          id: row?.id || createProjectFormRowId("source"),
          show_source_settings: false,
        }));
      }

      const allIds = Array.isArray(project?.receive_source_ids)
        ? project.receive_source_ids.map((value) => String(value || "")).filter(Boolean)
        : [];
      const primaryId = String(project?.receive_source_id || "");
      const extraIds = allIds.filter((id) => id && id !== primaryId);

      return extraIds.map((id) => {
        const selectedRow = receiveSourcesOptions.find((item) => String(item?.id) === id);
        return {
          ...createProjectExtraSource(),
          id: createProjectFormRowId("source"),
          receive_source_id: id,
          name: String(selectedRow?.name || ""),
          platform: String(selectedRow?.platform || ""),
          link: String(selectedRow?.link || ""),
          show_source_settings: false,
        };
      });
    })()
  );

  const getNextProjectNumericId = (projectRows) => {
    const numericIds = projectRows
      .map((project) => Number(project?.id))
      .filter((projectId) => Number.isFinite(projectId));

    return numericIds.length ? Math.max(...numericIds) + 1 : 1001;
  };

  const closeTimePickerPopup = (inputId = "") => {
    setTimePickerPopup((prev) => ({
      ...prev,
      isOpen: false,
      inputId: "",
    }));
    if (inputId && openNewsTimePickerInputId === inputId) {
      setOpenNewsTimePickerInputId("");
    }
  };

  const openCustomTimePicker = (inputId) => {
    const normalizedInputId = String(inputId || "");
    const inputElement = document.getElementById(normalizedInputId);
    if (!inputElement) {
      return;
    }

    const currentValue = normalizeTimeDigits(String(inputElement.value || "")).trim();
    const [hour = "00", minute = "00"] = currentValue.split(":");
    const rect = inputElement.getBoundingClientRect();
    const viewportWidth = typeof window !== "undefined" ? window.innerWidth : 0;
    const viewportHeight = typeof window !== "undefined" ? window.innerHeight : 0;
    const popupWidth = 280;
    const popupHeight = 170;
    const nextLeft = viewportWidth
      ? Math.max(16, Math.min(rect.left, viewportWidth - popupWidth - 16))
      : 16;
    const nextTop = viewportHeight
      ? Math.max(16, Math.min(rect.bottom + 8, viewportHeight - popupHeight - 16))
      : 16;

    setTimePickerPopup({
      isOpen: true,
      inputId: normalizedInputId,
      hour: hour.padStart(2, "0"),
      minute: minute.padStart(2, "0"),
      top: nextTop,
      left: nextLeft,
    });
  };

  const openTimeInputPicker = (inputId) => {
    const inputElement = document.getElementById(inputId);
    if (!inputElement) {
      return;
    }

    if (typeof navigator !== "undefined" && /firefox/i.test(navigator.userAgent || "")) {
      openCustomTimePicker(inputId);
      return;
    }

    inputElement.focus();
    let isPickerShown = false;
    if (typeof inputElement.showPicker === "function") {
      try {
        inputElement.showPicker();
        isPickerShown = true;
      } catch (error) {
        isPickerShown = false;
      }
    }
    if (!isPickerShown) {
      try {
        inputElement.click();
        isPickerShown = true;
      } catch (error) {
        isPickerShown = false;
      }
    }
    if (!isPickerShown) {
      openCustomTimePicker(inputId);
    }
  };

  const handleTimeFieldPointerDown = (event, inputId) => {
    const targetTag = String(event?.target?.tagName || "").toLowerCase();
    if (targetTag === "input" || targetTag === "button") {
      return;
    }
    event.preventDefault();
    openTimeInputPicker(inputId);
  };

  const toggleNewsTimeInputPicker = (inputId) => {
    const normalizedInputId = String(inputId || "");
    const inputElement = document.getElementById(normalizedInputId);
    if (!inputElement) {
      return;
    }

    if (openNewsTimePickerInputId === normalizedInputId) {
      inputElement.blur();
      closeTimePickerPopup(normalizedInputId);
      setOpenNewsTimePickerInputId("");
      return;
    }

    closeTimePickerPopup(normalizedInputId);
    openTimeInputPicker(normalizedInputId);
    setOpenNewsTimePickerInputId(normalizedInputId);
  };

  const handleEditProject = (project) => {
    if (!project) {
      setProjectsMsg({ type: "error", text: "پروژه انتخاب شده پیدا نشد." });
      return;
    }

    setEditingProjectId(String(project.id));
    const nextProjectForm = buildProjectFormFromRow(project);
    const nextExtraSources = buildProjectExtraSourcesFromRow(project).map((row) => ({
      ...row,
      show_source_settings: false,
    }));
    setProjectForm(nextProjectForm);
    setProjectExtraPublishWindows(buildProjectExtraPublishWindowsFromRow(project));
    setProjectExtraPublishTargets(buildProjectExtraPublishTargetsFromRow(project));
    setProjectExtraSources(nextExtraSources);
    setShowMainSourceSettings(false);
    setShowProjectForm(true);
    setErrorMsg("");
    setSuccessMsg("");
    setProjectsMsg({ type: "", text: "" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    setSuccessMsg("");
    const isInstantOperation = projectForm.operation_type === "instant";
    const isPriceReceiveSource = normalizeReceivePlatformValue(projectForm.source_platform_name) === "price";

    if (
      !projectForm.title ||
      !projectForm.source_name ||
      !projectForm.source_platform_name ||
      (!isPriceReceiveSource && !projectForm.source_link) ||
      !projectForm.publish_platform_name ||
      !projectForm.publish_platform_type ||
      !projectForm.publish_link ||
      !projectForm.operation_type ||
      (!isInstantOperation && !projectForm.fetch_interval_minutes) ||
      (!isInstantOperation && !projectForm.publish_interval_seconds)
    ) {
      setErrorMsg("لطفا همه فیلدها را تکمیل کنید");
      return;
    }

    const similarityThresholdValue = Number(projectForm.similarity_threshold);
    const publishIntervalValue = Number(projectForm.publish_interval_seconds);

    if (!Number.isFinite(similarityThresholdValue) || similarityThresholdValue < 0 || similarityThresholdValue > 100) {
      setErrorMsg("درصد شباهت باید عددی بین 0 تا 100 باشد.");
      return;
    }

    if (
      !isInstantOperation
      && (!Number.isFinite(publishIntervalValue) || publishIntervalValue < 1)
    ) {
      setErrorMsg("فاصله انتشار باید یک عدد صحیح بزرگ‌تر یا مساوی 1 ثانیه باشد.");
      return;
    }

    setSaving(true);

    try {
      const normalizedPublishWindows = projectExtraPublishWindows.map((row) => ({
        id: row?.id || createProjectFormRowId("range"),
        from: row?.from || "00:00",
        to: row?.to || "23:59",
      }));

      const normalizedPublishTargets = projectExtraPublishTargets.map((row) => ({
        id: row?.id || createProjectFormRowId("publish-target"),
        publish_source_id: row?.publish_source_id || "",
        name: row?.name || "",
        platform: row?.platform || "",
        link: row?.link || "",
        view_boost_active: Boolean(row?.view_boost_active),
        view_boost_count: String(row?.view_boost_count ?? ""),
        view_boost_minutes: String(row?.view_boost_minutes ?? ""),
      }));

      const normalizedSources = projectExtraSources.map((row) => ({
        ...createProjectExtraSource(),
        ...row,
        id: row?.id || createProjectFormRowId("source"),
        receive_source_id: row?.receive_source_id || "",
        allowed_media: Array.isArray(row?.allowed_media)
          ? row.allowed_media.map((item) => String(item || "").trim().toLowerCase()).filter(Boolean)
          : [],
        is_content_classify: Array.isArray(row?.allowed_media) && row.allowed_media.length > 0,
        show_source_settings: false,
      }));
      const receiveSourceIds = Array.from(
        new Set(
          [
            String(projectForm.receive_source_id || "").trim(),
            ...normalizedSources.map((row) => String(row?.receive_source_id || "").trim()),
          ].filter(Boolean)
        )
      ).map((value) => Number(value));
      const publishSourceIds = Array.from(
        new Set(
          [
            String(projectForm.publish_source_id || "").trim(),
            ...normalizedPublishTargets.map((row) => String(row?.publish_source_id || "").trim()),
          ].filter(Boolean)
        )
      ).map((value) => Number(value));

      const systemRules = Array.isArray(projectForm.system_text_rules) ? projectForm.system_text_rules : [];
      const aiRules = Array.isArray(projectForm.ai_text_rules) ? projectForm.ai_text_rules : [];
      if (systemRules.length > 0 && aiRules.length > 0) {
        setErrorMsg("برای یک منبع نمی‌توانید همزمان پردازش متنی سیستمی و هوش مصنوعی را فعال کنید.");
        setSaving(false);
        return;
      }

      // One row per destination, primary included. Only platforms with a
      // derivable post URL can be boosted, so the rest are sent as off rather
      // than carrying a setting the pipeline would ignore.
      const publishSourceSettings = [
        {
          publish_source_id: projectForm.publish_source_id,
          platform: projectForm.publish_platform_type,
          view_boost_active: projectForm.view_boost_active,
          view_boost_count: projectForm.view_boost_count,
          view_boost_minutes: projectForm.view_boost_minutes,
        },
        ...normalizedPublishTargets,
      ]
        .filter((row) => String(row.publish_source_id || "").trim())
        .map((row) => {
          const enabled = supportsViewBoost(row.platform) && Boolean(row.view_boost_active);
          return {
            publish_source_id: Number(row.publish_source_id),
            view_boost_active: enabled,
            view_boost_count: enabled ? Number(row.view_boost_count) || 0 : 0,
            view_boost_minutes: enabled ? Number(row.view_boost_minutes) || 0 : 0,
          };
        });

      const invalidBoostRow = publishSourceSettings.find((row) => (
        row.view_boost_active
        && (row.view_boost_count < VIEW_BOOST_MIN_COUNT
          || row.view_boost_count > VIEW_BOOST_MAX_COUNT
          || row.view_boost_minutes < VIEW_BOOST_MIN_MINUTES)
      ));
      if (invalidBoostRow) {
        setErrorMsg(
          `برای بوست بازدید، تعداد باید بین ${VIEW_BOOST_MIN_COUNT} و ${VIEW_BOOST_MAX_COUNT} و زمان انجام حداقل ${VIEW_BOOST_MIN_MINUTES} دقیقه باشد.`
        );
        setSaving(false);
        return;
      }

      const projectPayload = {
        ...projectForm,
        receive_source_id: projectForm.receive_source_id ? Number(projectForm.receive_source_id) : null,
        receive_source_ids: receiveSourceIds,
        publish_source_id: projectForm.publish_source_id ? Number(projectForm.publish_source_id) : null,
        publish_source_ids: publishSourceIds,
        status: projectForm.status || "active",
        operation_type: projectForm.operation_type || "scheduled",
        fetch_interval_minutes: isInstantOperation ? "1" : String(projectForm.fetch_interval_minutes),
        publish_interval_seconds: isInstantOperation ? "1" : String(Math.trunc(publishIntervalValue)),
        similarity_threshold: String(Math.trunc(similarityThresholdValue)),
        outside_window_mode: String(projectForm.outside_window_mode || "publish_only"),
        source_count: receiveSourceIds.length,
        destination_count: publishSourceIds.length,
        extra_publish_windows: normalizedPublishWindows,
        extra_publish_targets: normalizedPublishTargets,
        publish_source_settings: publishSourceSettings,
        extra_sources: normalizedSources,
        text_process_mode: String(projectForm.text_process_mode || ""),
        system_text_process_active: String(projectForm.text_process_mode || "") === "system",
        text_process_title_isolated: Boolean(projectForm.text_process_title_isolated),
        system_text_rules: Array.isArray(projectForm.system_text_rules) ? projectForm.system_text_rules : [],
        system_text_max_chars: String(projectForm.system_text_max_chars || "0"),
        system_text_remove_phrase: String(projectForm.system_text_remove_phrase || ""),
        system_text_prefix: String(projectForm.system_text_prefix || ""),
        system_text_suffix: String(projectForm.system_text_suffix || ""),
        ai_activity_status: deriveAiActivityStatus(projectForm),
        ai_text_process_active: String(projectForm.text_process_mode || "") === "ai",
        ai_text_prompt: String(projectForm.ai_text_prompt || ""),
        ai_text_rules: Array.isArray(projectForm.ai_text_rules) ? projectForm.ai_text_rules : [],
        media_process_active: Boolean(projectForm.media_process_mode),
        media_process_mode: String(projectForm.media_process_mode || ""),
        media_apply_to: String(projectForm.media_apply_to || "image_and_video"),
        media_logo_file_clear: Boolean(projectForm.media_logo_file_clear),
        media_logo_size: String(projectForm.media_logo_size || "0"),
        media_logo_color: String(projectForm.media_logo_color || ""),
        media_logo_x: String(projectForm.media_logo_x || "0"),
        media_logo_y: String(projectForm.media_logo_y || "0"),
        media_text_content: String(projectForm.media_text_content || ""),
        media_text_size: String(projectForm.media_text_size || "0"),
        media_text_color: String(projectForm.media_text_color || ""),
        media_text_font: String(projectForm.media_text_font || ""),
        media_text_x: String(projectForm.media_text_x || "0"),
        media_text_y: String(projectForm.media_text_y || "0"),
        media_opacity: String(projectForm.media_opacity || "100"),
        ai_media_process_active: false,
        ai_media_prompt: "",
        ai_media_model: "",
        allowed_media: Array.isArray(projectForm.allowed_media)
          ? projectForm.allowed_media.map((item) => String(item || "").trim().toLowerCase()).filter(Boolean)
          : [],
        is_content_classify: Array.isArray(projectForm.allowed_media) && projectForm.allowed_media.length > 0,
      };

      const hasMainLogoFile = projectForm.media_logo_file_file instanceof File;
      const hasExtraSourceLogoFile = normalizedSources.some((row) => row.media_logo_file_file instanceof File);
      const submitPayload = (hasMainLogoFile || hasExtraSourceLogoFile)
        ? (() => {
            const normalizedExtraSourcesPayload = normalizedSources.map((row) => {
              const nextRow = { ...row };
              delete nextRow.media_logo_file_file;
              return nextRow;
            });
            const payloadForMultipart = {
              ...projectPayload,
              extra_sources: normalizedExtraSourcesPayload,
            };
            const formData = new FormData();
            Object.entries(payloadForMultipart).forEach(([key, value]) => {
              if (value === undefined || value === null) {
                return;
              }
              if (key === "media_logo_file_file") {
                return;
              }
              if (Array.isArray(value)) {
                formData.append(key, JSON.stringify(value));
                return;
              }
              if (typeof value === "boolean") {
                formData.append(key, value ? "true" : "false");
                return;
              }
              formData.append(key, String(value));
            });
            if (hasMainLogoFile) {
              formData.append("media_logo_file", projectForm.media_logo_file_file);
            }
            normalizedSources.forEach((row, index) => {
              if (row.media_logo_file_file instanceof File) {
                formData.append(`extra_sources_media_logo_file_${index}`, row.media_logo_file_file);
              }
            });
            return formData;
          })()
        : projectPayload;

      if (editingProjectId) {
        const updateResult = await projectsApi.update(editingProjectId, submitPayload);
        const updatedItem = normalizeProjectRowFromApi(updateResult?.item || {});
        setProjects((prevRows) => (
          prevRows.map((row) => (String(row.id) === String(editingProjectId) ? { ...row, ...updatedItem } : row))
        ));
        fetchProjects();
        setProjectsMsg({ type: "success", text: "تغییرات پروژه ذخیره شد." });
        setSuccessMsg("تغییرات پروژه ذخیره شد");
      } else {
        const createResult = await projectsApi.create(submitPayload);
        const createdItem = normalizeProjectRowFromApi(createResult?.item || {});
        if (createdItem?.id) {
          setProjects((prevRows) => [createdItem, ...prevRows]);
          fetchProjects();
        } else {
          fetchProjects();
        }
        setProjectsMsg({ type: "success", text: "پروژه جدید با موفقیت ذخیره شد." });
        setSuccessMsg("پروژه ذخیره شد");
      }

      setProjectForm(createInitialProjectForm());
      setProjectExtraPublishWindows([]);
      setProjectExtraPublishTargets([]);
      setProjectExtraSources([]);
      setShowMainSourceSettings(false);
      setSelectedProjectIds([]);
      setEditingProjectId(null);
      setShowProjectForm(false);
    } catch (error) {
      setErrorMsg(error?.message || "ذخیره پروژه ناموفق بود");
    } finally {
      setSaving(false);
    }
  };

  const openDeleteModal = (projectOrProjects) => {
    setProjectsMsg({ type: "", text: "" });

    if (Array.isArray(projectOrProjects)) {
      const normalizedIds = projectOrProjects
        .map((project) => String(project?.id || ""))
        .filter((projectId) => projectId);
      setProjectToDelete({
        ids: normalizedIds,
        title: "",
      });
    } else {
      setProjectToDelete(projectOrProjects);
    }

    setIsDeleteModalOpen(true);
  };

  const handleOpenProjectView = (project) => {
    const projectId = String(project?.id || "");
    setSelectedProjectViewId(projectId);
    setSelectedProjectViewTitle(project?.title || "بدون عنوان");
    setProjectViewNewsTab(PROJECT_VIEW_NEWS_TABS[0].key);
    setActiveTab("projects-view");
    fetchProjectAllNewsRows(projectId);
  };

  const fetchProjectAllNewsRows = async (projectId) => {
    if (!projectId) {
      setProjectAllNewsRows([]);
      return;
    }
    try {
      const result = await newsApi.list({ page: 1, page_size: 500, project_id: projectId, status: "all" });
      const items = Array.isArray(result?.items) ? result.items : [];
      const mappedRows = items
        .map((item) => normalizeNewsRowFromApi(item, projects))
        .sort((firstRow, secondRow) => (
          getNewsTimestamp(secondRow.publishedAtRaw) - getNewsTimestamp(firstRow.publishedAtRaw)
        ));
      setProjectAllNewsRows(mappedRows);
    } catch (error) {
      setProjectAllNewsRows([]);
    }
  };

  const closeDeleteModal = () => {
    if (deletingProject) {
      return;
    }
    setIsDeleteModalOpen(false);
    setProjectToDelete(null);
  };

  const handleDeleteProject = async () => {
    const deletingIds = Array.isArray(projectToDelete?.ids) && projectToDelete.ids.length > 0
      ? projectToDelete.ids
      : (projectToDelete?.id ? [String(projectToDelete.id)] : []);

    if (deletingIds.length === 0) {
      return;
    }

    setDeletingProject(true);
    setProjectsMsg({ type: "", text: "" });

    try {
      await projectsApi.bulkDelete(deletingIds);
      await fetchProjects();
      setSelectedProjectIds((prevSelectedIds) => (
        prevSelectedIds.filter((projectId) => !deletingIds.includes(String(projectId)))
      ));
      setProjectsMsg({
        type: "success",
        text: deletingIds.length > 1
          ? `${formatFaNumber(deletingIds.length)} پروژه با موفقیت حذف شد.`
          : "پروژه با موفقیت حذف شد.",
      });
      setIsDeleteModalOpen(false);
      setProjectToDelete(null);
    } catch {
      setProjectsMsg({ type: "error", text: "حذف پروژه ناموفق بود." });
    } finally {
      setDeletingProject(false);
    }
  };

  const projectManagerOptions = Array.from(
    new Set(
      projects
        .map((project) => getProjectManagerName(project))
        .filter((managerName) => managerName)
    )
  );

  const normalizedProjectSearchTerm = projectSearchTerm.trim().toLowerCase();

  const filteredProjects = projects.filter((project) => {
    const statusInfo = getProjectStatusInfo(project);
    const managerName = getProjectManagerName(project);
    const searchableText = [
      project?.title,
      project?.source_name,
      project?.publish_platform_name,
      String(getProjectCode(project)),
    ]
      .filter((value) => value)
      .join(" ")
      .toLowerCase();

    if (normalizedProjectSearchTerm && !searchableText.includes(normalizedProjectSearchTerm)) {
      return false;
    }

    if (projectStatusFilter !== "all" && statusInfo.key !== projectStatusFilter) {
      return false;
    }

    if (projectManagerFilter !== "all" && managerName !== projectManagerFilter) {
      return false;
    }

    return true;
  });

  const rowsPerPage = Number(projectRowsPerPage) || PROJECT_ROWS_PER_PAGE_OPTIONS[0];
  const totalProjectPages = Math.max(1, Math.ceil(filteredProjects.length / rowsPerPage));
  const currentProjectPage = Math.min(projectPage, totalProjectPages);
  const pageStartIndex = (currentProjectPage - 1) * rowsPerPage;
  const paginatedProjects = filteredProjects.slice(pageStartIndex, pageStartIndex + rowsPerPage);
  const paginatedProjectIds = paginatedProjects.map((project) => String(project.id));
  const areAllVisibleProjectsSelected = (
    paginatedProjectIds.length > 0
    && paginatedProjectIds.every((projectId) => selectedProjectIds.includes(projectId))
  );
  const emptyProjectsMessage = projects.length === 0
    ? "هنوز پروژه‌ای ثبت نشده است."
    : "پروژه‌ای مطابق فیلترهای انتخابی پیدا نشد.";

  const handleProjectRowSelection = (projectId) => {
    const normalizedProjectId = String(projectId);
    setSelectedProjectIds((prevSelectedIds) => (
      prevSelectedIds.includes(normalizedProjectId)
        ? prevSelectedIds.filter((selectedId) => selectedId !== normalizedProjectId)
        : [...prevSelectedIds, normalizedProjectId]
    ));
  };

  const handleSelectAllVisibleProjects = () => {
    if (!paginatedProjectIds.length) {
      return;
    }

    setSelectedProjectIds((prevSelectedIds) => {
      const allVisibleSelected = paginatedProjectIds.every((projectId) => (
        prevSelectedIds.includes(projectId)
      ));

      if (allVisibleSelected) {
        return prevSelectedIds.filter((projectId) => !paginatedProjectIds.includes(projectId));
      }

      return Array.from(new Set([...prevSelectedIds, ...paginatedProjectIds]));
    });
  };

  const handleProjectPageChange = (direction) => {
    if (direction === "next") {
      setProjectPage((prevPage) => Math.min(totalProjectPages, prevPage + 1));
      return;
    }

    setProjectPage((prevPage) => Math.max(1, prevPage - 1));
  };

  const handleBulkDeleteProjects = () => {
    if (selectedProjectIds.length === 0) {
      setProjectsMsg({ type: "error", text: "برای حذف، حداقل یک پروژه را انتخاب کنید." });
      return;
    }

    const selectedProjects = projects.filter((project) => (
      selectedProjectIds.includes(String(project.id))
    ));

    if (!selectedProjects.length) {
      setProjectsMsg({ type: "error", text: "پروژه انتخاب شده پیدا نشد." });
      return;
    }

    openDeleteModal(selectedProjects);
  };

  const handleCopySelectedProjects = () => {
    if (selectedProjectIds.length === 0) {
      setProjectsMsg({ type: "error", text: "برای کپی، حداقل یک پروژه را انتخاب کنید." });
      return;
    }

    const selectedRows = projects.filter((project) => (
      selectedProjectIds.includes(String(project.id))
    ));

    if (selectedRows.length === 0) {
      setProjectsMsg({ type: "error", text: "پروژه‌ای برای کپی پیدا نشد." });
      return;
    }

    const duplicatedProjectsCount = selectedRows.length;

    setProjects((prevProjects) => {
      let nextNumericId = getNextProjectNumericId(prevProjects);
      const usedTitles = new Set(prevProjects.map((project) => (
        String(project?.title || "بدون عنوان")
      )));

      const duplicatedProjects = selectedRows.map((project) => {
        const nextId = String(nextNumericId);
        nextNumericId += 1;

        const baseTitle = String(project?.title || "بدون عنوان").trim() || "بدون عنوان";
        let suffixNumber = 1;
        let duplicatedTitle = `${baseTitle} (${suffixNumber})`;

        while (usedTitles.has(duplicatedTitle)) {
          suffixNumber += 1;
          duplicatedTitle = `${baseTitle} (${suffixNumber})`;
        }

        usedTitles.add(duplicatedTitle);

        return {
          ...project,
          id: nextId,
          code: nextId,
          title: duplicatedTitle,
          created_at: new Date().toISOString(),
          extra_publish_windows: (project.extra_publish_windows || []).map((row) => ({
            ...row,
            id: createProjectFormRowId("range"),
          })),
          extra_publish_targets: (project.extra_publish_targets || []).map((row) => ({
            ...row,
            id: createProjectFormRowId("publish-target"),
          })),
          extra_sources: (project.extra_sources || []).map((row) => ({
            ...row,
            id: createProjectFormRowId("source"),
          })),
        };
      });

      return [...duplicatedProjects, ...prevProjects];
    });

    setProjectsMsg({
      type: "success",
      text: `${formatFaNumber(duplicatedProjectsCount)} پروژه جدید از روی انتخاب‌ها ساخته شد.`,
    });
    setSelectedProjectIds([]);
  };

  const handleDashboardActivityFromDateChange = (nextFromDate) => {
    setDashboardActivityFromDate(nextFromDate);

    if (
      nextFromDate !== "all"
      && dashboardActivityToDate !== "all"
      && nextFromDate > dashboardActivityToDate
    ) {
      setDashboardActivityToDate(nextFromDate);
    }
  };

  const handleDashboardActivityToDateChange = (nextToDate) => {
    setDashboardActivityToDate(nextToDate);

    if (
      nextToDate !== "all"
      && dashboardActivityFromDate !== "all"
      && nextToDate < dashboardActivityFromDate
    ) {
      setDashboardActivityFromDate(nextToDate);
    }
  };

  const filteredDashboardActivityRows = usersActivityRows.filter((row) => {
    const rowDate = getDashboardActivityDate(row.activityDate);
    const rowRole = row.role;
    const rowModule = row.module;

    if (dashboardActivityRoleFilter !== "all" && rowRole !== dashboardActivityRoleFilter) {
      return false;
    }

    if (dashboardActivityModuleFilter !== "all" && rowModule !== dashboardActivityModuleFilter) {
      return false;
    }

    if (dashboardActivityFromDate !== "all" && rowDate < dashboardActivityFromDate) {
      return false;
    }

    if (dashboardActivityToDate !== "all" && rowDate > dashboardActivityToDate) {
      return false;
    }

    return true;
  });

  const totalDashboardActivityPages = Math.max(
    1,
    Math.ceil(filteredDashboardActivityRows.length / DASHBOARD_ACTIVITY_ROWS_PER_PAGE)
  );
  const currentDashboardActivityPage = Math.min(dashboardActivityPage, totalDashboardActivityPages);
  const dashboardActivityPageStart = (
    (currentDashboardActivityPage - 1) * DASHBOARD_ACTIVITY_ROWS_PER_PAGE
  );
  const paginatedDashboardActivityRows = filteredDashboardActivityRows.slice(
    dashboardActivityPageStart,
    dashboardActivityPageStart + DASHBOARD_ACTIVITY_ROWS_PER_PAGE
  );

  const handleDashboardActivityPageChange = (direction) => {
    if (direction === "next") {
      setDashboardActivityPage((prevPage) => Math.min(totalDashboardActivityPages, prevPage + 1));
      return;
    }

    setDashboardActivityPage((prevPage) => Math.max(1, prevPage - 1));
  };

  const scrollBySelectHeight = (event, optionsCount) => {
    const dropdownHeight = Math.max(1, Number(optionsCount) || 1) * 40;
    const triggerBottom = event.currentTarget.getBoundingClientRect().bottom;
    const spaceBelow = window.innerHeight - triggerBottom;

    if (spaceBelow < dropdownHeight) {
      const requiredScroll = (dropdownHeight - spaceBelow) + 16;
      window.scrollBy({
        top: requiredScroll,
        behavior: "auto",
      });
    }
  };

  const newsProjectOptions = projects
    .filter((project) => project?.id !== undefined && project?.id !== null)
    .map((project) => ({
      value: String(project.id),
      label: project?.title || `پروژه ${project.id}`,
    }));
  const newsProjectManagersMap = new Map(
    projects.map((project) => [String(project?.id), getProjectManagerName(project) || "--"])
  );
  const newsManagerOptions = Array.from(
    new Set(
      Array.from(newsProjectManagersMap.values())
        .filter((managerName) => managerName && managerName !== "--")
    )
  );

  const newsMediaOptions = Object.entries(NEWS_MEDIA_TYPE_LABELS_FA).map(([value, label]) => ({
    value,
    label,
  }));
  const paginatedNewsRows = newsRows;
  const paginatedNewsIds = paginatedNewsRows.map((row) => row.rowId);
  const areAllVisibleNewsSelected = (
    paginatedNewsIds.length > 0
    && paginatedNewsIds.every((rowId) => selectedNewsIds.includes(rowId))
  );
  const emptyNewsMessage = projects.length === 0
    ? "برای نمایش اخبار، ابتدا یک پروژه ایجاد کنید."
    : "خبری مطابق فیلترهای انتخابی پیدا نشد.";

  const handleNewsRowSelection = (rowId) => {
    setSelectedNewsIds((prevSelectedNewsIds) => (
      prevSelectedNewsIds.includes(rowId)
        ? prevSelectedNewsIds.filter((selectedRowId) => selectedRowId !== rowId)
        : [...prevSelectedNewsIds, rowId]
    ));
  };

  const handleSelectAllVisibleNews = (rowIds = paginatedNewsIds) => {
    if (!rowIds.length) {
      return;
    }

    setSelectedNewsIds((prevSelectedNewsIds) => {
      const areAllSelected = rowIds.every((rowId) => prevSelectedNewsIds.includes(rowId));

      if (areAllSelected) {
        return prevSelectedNewsIds.filter((rowId) => !rowIds.includes(rowId));
      }

      return Array.from(new Set([...prevSelectedNewsIds, ...rowIds]));
    });
  };

  const handleCreateNews = () => {
    setNewsCreateMsg({ type: "", text: "" });
    setNewsCreateForm((prevForm) => {
      revokeObjectPreviewUrl(prevForm.mediaPreviewUrl);
      return createInitialNewsCreateForm();
    });
    setNewsCreateExtraPublishTargets([]);
    setNewsCreateSelectedProjectIds([]);
    setNewsRemovedProjectSourceIds([]);
    setNewsCreateScheduledPublishRows([createNewsScheduledPublishRow()]);
    setNewsWatermarkSettings(createInitialNewsMediaSettings());
    setNewsFormMode("create");
    setEditingNewsRowId("");
    setActiveTab("news-create");
  };

  const handleCloseNewsCreate = () => {
    setNewsCreateMsg({ type: "", text: "" });
    setNewsCreateForm((prevForm) => {
      revokeObjectPreviewUrl(prevForm.mediaPreviewUrl);
      return createInitialNewsCreateForm();
    });
    setNewsCreateExtraPublishTargets([]);
    setNewsCreateSelectedProjectIds([]);
    setNewsRemovedProjectSourceIds([]);
    setNewsCreateScheduledPublishRows([createNewsScheduledPublishRow()]);
    setNewsWatermarkSettings(createInitialNewsMediaSettings());
    setNewsFormMode("create");
    setEditingNewsRowId("");
    setActiveTab("news");
  };

  const handleDeleteSelectedNews = async () => {
    if (!selectedNewsIds.length) {
      setNewsMsg({
        type: "error",
        text: "برای حذف، حداقل یک خبر را انتخاب کنید.",
      });
      return;
    }

    const selectedRows = newsRows.filter((row) => selectedNewsIds.includes(row.rowId));
    const newsIds = selectedRows
      .map((row) => Number(row.id))
      .filter((rowId) => Number.isFinite(rowId) && rowId > 0);

    if (!newsIds.length) {
      setNewsMsg({
        type: "error",
        text: "آیتم‌های انتخاب‌شده هنوز در بک‌اند ثبت نشده‌اند.",
      });
      return;
    }

    try {
      await newsApi.bulkDelete(newsIds);
      setNewsRows((prevNewsRows) => (
        prevNewsRows.filter((row) => !selectedNewsIds.includes(row.rowId))
      ));
      setNewsMsg({
        type: "success",
        text: `${formatFaNumber(newsIds.length)} خبر حذف شد.`,
      });
      setSelectedNewsIds([]);
      setNewsReloadToken((prevToken) => prevToken + 1);
    } catch (error) {
      setNewsMsg({
        type: "error",
        text: error.message || "حذف اخبار ناموفق بود.",
      });
    }
  };

  const openNewsDeleteModal = (rowIds = selectedNewsIds) => {
    if (!rowIds.length) {
      setNewsMsg({
        type: "error",
        text: "برای حذف، حداقل یک خبر را انتخاب کنید.",
      });
      return;
    }

    setNewsMsg({ type: "", text: "" });
    setNewsDeleteTargetIds(rowIds);
    setIsNewsDeleteModalOpen(true);
  };

  const closeNewsDeleteModal = () => {
    if (deletingNews) {
      return;
    }

    setNewsDeleteTargetIds([]);
    setIsNewsDeleteModalOpen(false);
  };

  const confirmDeleteSelectedNews = async () => {
    setDeletingNews(true);
    const previousSelected = selectedNewsIds;
    const targetIds = newsDeleteTargetIds.length ? newsDeleteTargetIds : previousSelected;
    setSelectedNewsIds(targetIds);
    await handleDeleteSelectedNews();
    setSelectedNewsIds(previousSelected.filter((rowId) => !targetIds.includes(rowId)));
    setNewsDeleteTargetIds([]);
    setIsNewsDeleteModalOpen(false);
    setDeletingNews(false);
  };

  const handleOpenNewsEditor = (row) => {
    const normalizedLink = normalizeNewsLink(row?.editLink);

    if (!normalizedLink) {
      setNewsMsg({
        type: "error",
        text: "برای این خبر لینک معتبری ثبت نشده است.",
      });
      return;
    }

    window.open(normalizedLink, "_blank", "noopener,noreferrer");
  };

  const getNewsViewDefaultContent = (row) => (
    `خلاصه خبر: ${row?.title || "بدون عنوان"}\n\nاین خبر از منبع ${row?.sourceName || "--"} دریافت شده و در پروژه ${row?.projectName || "--"} قرار گرفته است.\nلطفاً بعد از بازبینی، عملیات انتشار یا ویرایش را انجام دهید.`
  );

  const revokeObjectPreviewUrl = (urlValue = "") => {
    const normalizedUrl = String(urlValue || "").trim();

    if (normalizedUrl.startsWith("blob:")) {
      URL.revokeObjectURL(normalizedUrl);
    }
  };

  const handleEditNewsRow = async (row, options = {}) => {
    const destinationTab = String(options?.destinationTab || "").trim() || "news-create";
    const setSectionMessage = destinationTab === "ads-create" ? setAdsMsg : setNewsMsg;
    const setFormMessage = destinationTab === "ads-create" ? setAdsCreateMsg : setNewsCreateMsg;
    if (!row?.rowId) {
      return;
    }
    if (row?.revisionType === "extract") {
      return;
    }

    let resolvedRow = row;
    const rowNewsId = Number(row?.id);
    if (Number.isFinite(rowNewsId) && rowNewsId > 0) {
      try {
        const result = await newsApi.detail(rowNewsId);
        resolvedRow = normalizeNewsRowFromApi(result?.item || {}, projects);
      } catch (error) {
        setSectionMessage({
          type: "error",
          text: error.message || "دریافت جزئیات خبر برای ویرایش ناموفق بود.",
        });
      }
    }

    const normalizedSourceMediaUrl = normalizeNewsLink(resolvedRow.sourceMediaUrl);
    const normalizedPublishLink = normalizeNewsLink(resolvedRow.mediaUrl || normalizedSourceMediaUrl || resolvedRow.publishLink);
    const normalizedExtractedMediaUrl = normalizeNewsLink(resolvedRow.extractedMediaUrl || normalizedSourceMediaUrl);
    const normalizedProcessedMediaUrl = normalizeNewsLink(resolvedRow.processedMediaUrl || normalizedSourceMediaUrl);
    const defaultContent = (
      String(resolvedRow.fullContent || "").trim()
      || getNewsViewDefaultContent(resolvedRow)
    );
    const extractedContent = String(resolvedRow.extractedText || "").trim();
    const processedContent = String(resolvedRow.aiEditedText || resolvedRow.systemEditedText || "").trim();
    const scheduleTiming = resolvedRow?.scheduledPublishTiming || null;
    const scheduleMode = String(scheduleTiming?.publish_mode || resolvedRow?.scheduleMode || "future_times").trim().toLowerCase();
    const scheduledRows = Array.isArray(resolvedRow?.scheduledPublishRows) && resolvedRow.scheduledPublishRows.length
      ? resolvedRow.scheduledPublishRows.map((row) => ({
        id: createProjectFormRowId("news-scheduled-edit"),
        date: String(row?.date || "all"),
        time: normalizeTimeDigits(String(row?.time || "")),
      }))
      : [createNewsScheduledPublishRow()];
    const recurringStart = mapIsoToJalaliDateTime(scheduleTiming?.start_at || "");

    setFormMessage({ type: "", text: "" });
    setNewsFormMode("edit");
    setEditingNewsRowId(resolvedRow.rowId);
    setNewsCreateForm((prevForm) => {
      revokeObjectPreviewUrl(prevForm.mediaPreviewUrl);
      return {
        ...createInitialNewsCreateForm(),
        title: resolvedRow.title || "",
        fullContent: defaultContent,
        publishMode: "scheduled",
        scheduleMode: scheduleMode === "recurring" ? "recurring" : "future_times",
        projectId: resolvedRow.projectId || "all",
        publishSourceId: resolvedRow.publishSourceId || "",
        publishName: resolvedRow.sourceName === "--" ? "" : (resolvedRow.sourceName || ""),
        publishPlatform: "",
        sourceName: resolvedRow.sourceName === "--" ? "" : (resolvedRow.sourceName || ""),
        publishLink: resolvedRow.publishLink === "--" ? "" : (resolvedRow.publishLink || ""),
        publishedAt: resolvedRow.publishedAt || "",
        mediaType: resolvedRow.mediaType || "متنی",
        statusKey: resolvedRow.statusKey || "received",
        mediaPreviewUrl: normalizedPublishLink || "",
        recurringStartDate: recurringStart.date,
        recurringStartTime: recurringStart.time,
        recurringIntervalSeconds: String(scheduleTiming?.interval_seconds || "60"),
        recurringMaxRuns: String(scheduleTiming?.max_runs || "10"),
      };
    });
    const publishTargetIds = Array.isArray(scheduleTiming?.publish_target_ids)
      ? scheduleTiming.publish_target_ids.map((value) => String(value || "")).filter(Boolean)
      : [];
    const allPublishRows = publishTargetIds.map((targetId) => {
      const matchedSource = publishSourcesRows.find((sourceRow) => String(sourceRow?.id || "") === String(targetId));
      return {
        id: createProjectFormRowId("news-target-edit"),
        publish_source_id: String(targetId || ""),
        name: String(matchedSource?.name || ""),
        platform: String(matchedSource?.platform || ""),
        link: String(matchedSource?.link || ""),
      };
    }).filter((row) => row.publish_source_id);
    const [primaryTarget, ...extraTargets] = allPublishRows;
    if (primaryTarget) {
      setNewsCreateForm((prevForm) => ({
        ...prevForm,
        publishSourceId: primaryTarget.publish_source_id,
        publishName: primaryTarget.name,
        publishPlatform: primaryTarget.platform,
        publishLink: primaryTarget.link,
      }));
    }
    setNewsCreateExtraPublishTargets(extraTargets);
    setNewsCreateSelectedProjectIds(
      resolvedRow.projectId && resolvedRow.projectId !== "all"
        ? [String(resolvedRow.projectId)]
        : []
    );
    setNewsRemovedProjectSourceIds([]);
    setNewsCreateScheduledPublishRows(scheduledRows);
    setNewsWatermarkSettings({
      ...createInitialNewsMediaSettings(),
      watermark_file: resolvedRow.watermarkFileUrl || "",
      watermark_file_file: null,
      watermark_text: resolvedRow.watermarkText || "",
      watermark_font: resolvedRow.watermarkFont || createInitialNewsMediaSettings().watermark_font,
      watermark_position: resolvedRow.watermarkPosition || createInitialNewsMediaSettings().watermark_position,
      watermark_size: resolvedRow.watermarkSize || createInitialNewsMediaSettings().watermark_size,
      watermark_color: resolvedRow.watermarkColor || createInitialNewsMediaSettings().watermark_color,
      watermark_status: resolvedRow.watermarkStatus === "deactivate" ? "disabled" : "enabled",
      watermark_apply_to: resolvedRow.watermarkApplyTo || createInitialNewsMediaSettings().watermark_apply_to,
      media_process_mode: resolvedRow.watermarkFileUrl ? "logo" : "text",
    });
    setSelectedNewsRowId(resolvedRow.rowId);
    setNewsViewForm((prevForm) => ({
      ...prevForm,
      title: resolvedRow.title || "",
      code: resolvedRow.code || "",
      publishedAt: resolvedRow.publishedAt || "",
      projectName: resolvedRow.projectName || "",
      sourceName: resolvedRow.sourceName || "",
      mediaType: resolvedRow.mediaType || "",
      statusLabel: resolvedRow.statusLabel || "",
      publishLink: resolvedRow.publishLink || "",
      fullContent: defaultContent,
      extractedContent,
      processedContent,
      extractedMediaUrl: normalizedExtractedMediaUrl || "",
      processedMediaUrl: normalizedProcessedMediaUrl || "",
      sourceMediaUrl: normalizedSourceMediaUrl || "",
      mediaUrl: normalizedPublishLink || "",
    }));
    setNewsViewMessage({ type: "", text: "" });
    setActiveTab(destinationTab);
  };

  const handleOpenNewsView = async (row) => {
    const rowNewsId = Number(row?.id);
    const resolvedRowId = String(row?.rowId || "").trim() || (Number.isFinite(rowNewsId) && rowNewsId > 0 ? `news-${rowNewsId}` : "");
    if (!resolvedRowId) {
      return;
    }

    let resolvedRow = row;
    if (Number.isFinite(rowNewsId) && rowNewsId > 0) {
      try {
        const result = await newsApi.detail(rowNewsId);
        resolvedRow = normalizeNewsRowFromApi(result?.item || {}, projects);
      } catch (error) {
        setNewsMsg({
          type: "error",
          text: error.message || "دریافت جزئیات خبر ناموفق بود.",
        });
      }
    }

    const normalizedSourceMediaUrl = normalizeNewsLink(resolvedRow.sourceMediaUrl);
    const normalizedPublishLink = normalizeNewsLink(resolvedRow.mediaUrl || normalizedSourceMediaUrl || resolvedRow.publishLink);
    const normalizedExtractedMediaUrl = normalizeNewsLink(resolvedRow.extractedMediaUrl || normalizedSourceMediaUrl);
    const normalizedProcessedMediaUrl = normalizeNewsLink(resolvedRow.processedMediaUrl || normalizedSourceMediaUrl);
    const defaultContent = (
      String(resolvedRow.fullContent || "").trim()
      || getNewsViewDefaultContent(resolvedRow)
    );
    const extractedContent = String(resolvedRow.extractedText || "").trim();
    const processedContent = String(resolvedRow.aiEditedText || resolvedRow.systemEditedText || "").trim();
    const resolvedNewsId = Number(resolvedRow?.id || rowNewsId || 0);
    const activeTabValue = String(activeTab || "").trim();
    const normalizedOriginTab = (
      activeTabValue && activeTabValue !== "news-view"
        ? activeTabValue
        : (String(newsViewReturnTab || "").trim() || "news")
    );

    setSelectedNewsRowId(resolvedRow.rowId || resolvedRowId);
    setSelectedNewsIdForView(Number.isFinite(resolvedNewsId) && resolvedNewsId > 0 ? resolvedNewsId : 0);
    setNewsViewReturnTab(normalizedOriginTab);
    setShowPreviewVideoPlayer(false);
    setShowExtractedVideoPlayer(false);
    setShowProcessedVideoPlayer(false);
    setIsNewsEditing(false);
    setNewsViewForm({
      title: resolvedRow.title || "",
      code: resolvedRow.code || "",
      publishedAt: resolvedRow.publishedAt || "",
      projectName: resolvedRow.projectName || "",
      sourceName: resolvedRow.sourceName || "",
      mediaType: resolvedRow.mediaType || "",
      statusLabel: resolvedRow.statusLabel || "",
      publishLink: resolvedRow.publishLink || "",
      fullContent: defaultContent,
      extractedContent,
      processedContent,
      extractedMediaUrl: normalizedExtractedMediaUrl || "",
      processedMediaUrl: normalizedProcessedMediaUrl || "",
      sourceMediaUrl: normalizedSourceMediaUrl || "",
      mediaUrl: normalizedPublishLink || "",
    });
    setNewsEditDraft((prevDraft) => {
      if (prevDraft.mediaPreviewUrl !== normalizedPublishLink) {
        revokeObjectPreviewUrl(prevDraft.mediaPreviewUrl);
      }

      return {
        title: resolvedRow.title || "",
        fullContent: defaultContent,
        mediaType: resolvedRow.mediaType || "متنی",
        mediaPreviewUrl: normalizedPublishLink || "",
      };
    });
    setNewsViewMessage({ type: "", text: "" });
    setActiveTab("news-view");
    const nextParams = new URLSearchParams(location.search);
    nextParams.set("tab", "news-view");
    if (Number.isFinite(rowNewsId) && rowNewsId > 0) {
      nextParams.set("news_id", String(rowNewsId));
    } else {
      nextParams.delete("news_id");
    }
    nextParams.set("return_tab", normalizedOriginTab);
    persistNewsViewSnapshot({
      newsId: Number.isFinite(resolvedNewsId) && resolvedNewsId > 0
        ? resolvedNewsId
        : (Number.isFinite(rowNewsId) && rowNewsId > 0 ? rowNewsId : 0),
      rowId: resolvedRow.rowId || resolvedRowId || "",
      returnTab: normalizedOriginTab,
      form: {
        title: resolvedRow.title || "",
        code: resolvedRow.code || "",
        publishedAt: resolvedRow.publishedAt || "",
        projectName: resolvedRow.projectName || "",
        sourceName: resolvedRow.sourceName || "",
        mediaType: resolvedRow.mediaType || "",
        statusLabel: resolvedRow.statusLabel || "",
        publishLink: resolvedRow.publishLink || "",
        fullContent: defaultContent,
        extractedContent,
        processedContent,
        extractedMediaUrl: normalizedExtractedMediaUrl || "",
        processedMediaUrl: normalizedProcessedMediaUrl || "",
        sourceMediaUrl: normalizedSourceMediaUrl || "",
        mediaUrl: normalizedPublishLink || "",
      },
    });
    navigate({ pathname: location.pathname, search: `?${nextParams.toString()}` }, { replace: true });
  };

  useEffect(() => {
    if (activeTab !== "news-view") {
      return;
    }
    const params = new URLSearchParams(location.search || "");
    const returnTabFromParams = String(params.get("return_tab") || "").trim();
    if (returnTabFromParams && returnTabFromParams !== "news-view") {
      setNewsViewReturnTab(returnTabFromParams);
    }
    const requestedNewsId = Number(params.get("news_id") || 0);
    if (!Number.isFinite(requestedNewsId) || requestedNewsId <= 0) {
      return;
    }
    if (String(newsViewForm.code || "").trim()) {
      return;
    }

    let isMounted = true;
    const bootstrapNewsViewAfterRefresh = async () => {
      setIsNewsViewLoading(true);
      try {
        const result = await newsApi.detail(requestedNewsId);
        if (!isMounted) {
          return;
        }
        const resolvedRow = normalizeNewsRowFromApi(result?.item || {}, projects);
        const normalizedSourceMediaUrl = normalizeNewsLink(resolvedRow.sourceMediaUrl);
        const normalizedPublishLink = normalizeNewsLink(resolvedRow.mediaUrl || normalizedSourceMediaUrl || resolvedRow.publishLink);
        const normalizedExtractedMediaUrl = normalizeNewsLink(resolvedRow.extractedMediaUrl || normalizedSourceMediaUrl);
        const normalizedProcessedMediaUrl = normalizeNewsLink(resolvedRow.processedMediaUrl || normalizedSourceMediaUrl);
        const defaultContent = (
          String(resolvedRow.fullContent || "").trim()
          || getNewsViewDefaultContent(resolvedRow)
        );
        const extractedContent = String(resolvedRow.extractedText || "").trim();
        const processedContent = String(resolvedRow.aiEditedText || resolvedRow.systemEditedText || "").trim();

        setSelectedNewsRowId(resolvedRow.rowId || `news-${requestedNewsId}`);
        setSelectedNewsIdForView(requestedNewsId);
        setShowPreviewVideoPlayer(false);
        setShowExtractedVideoPlayer(false);
        setShowProcessedVideoPlayer(false);
        setIsNewsEditing(false);
        setNewsViewForm({
          title: resolvedRow.title || "",
          code: resolvedRow.code || "",
          publishedAt: resolvedRow.publishedAt || "",
          projectName: resolvedRow.projectName || "",
          sourceName: resolvedRow.sourceName || "",
          mediaType: resolvedRow.mediaType || "",
          statusLabel: resolvedRow.statusLabel || "",
          publishLink: resolvedRow.publishLink || "",
          fullContent: defaultContent,
          extractedContent,
          processedContent,
          extractedMediaUrl: normalizedExtractedMediaUrl || "",
          processedMediaUrl: normalizedProcessedMediaUrl || "",
          sourceMediaUrl: normalizedSourceMediaUrl || "",
          mediaUrl: normalizedPublishLink || "",
        });
      } catch (_error) {
        if (!isMounted) {
          return;
        }
        setNewsViewMessage({
          type: "error",
          text: "بارگذاری خبر انتخاب‌شده پس از رفرش ناموفق بود.",
        });
      } finally {
        if (isMounted) {
          setIsNewsViewLoading(false);
        }
      }
    };

    bootstrapNewsViewAfterRefresh();
    return () => {
      isMounted = false;
    };
  }, [activeTab, location.search, newsViewForm.code, projects]);

  useEffect(() => {
    if (activeTab !== "news-view") {
      return;
    }
    if (String(newsViewForm.code || "").trim()) {
      return;
    }
    if (typeof window === "undefined") {
      return;
    }
    const params = new URLSearchParams(location.search || "");
    const requestedNewsId = Number(params.get("news_id") || 0);
    try {
      const rawSnapshot = window.localStorage.getItem(DASHBOARD_NEWS_VIEW_SNAPSHOT_STORAGE_KEY);
      if (!rawSnapshot) {
        return;
      }
      const parsedSnapshot = JSON.parse(rawSnapshot);
      const snapshotNewsId = Number(parsedSnapshot?.newsId || 0);
      const snapshotForm = parsedSnapshot?.form && typeof parsedSnapshot.form === "object"
        ? parsedSnapshot.form
        : null;
      if (!snapshotForm) {
        return;
      }
      if (requestedNewsId > 0 && snapshotNewsId > 0 && snapshotNewsId !== requestedNewsId) {
        return;
      }

      setSelectedNewsRowId(String(parsedSnapshot?.rowId || "").trim() || (snapshotNewsId > 0 ? `news-${snapshotNewsId}` : ""));
      setSelectedNewsIdForView(snapshotNewsId > 0 ? snapshotNewsId : requestedNewsId);
      setNewsViewReturnTab(
        String(parsedSnapshot?.returnTab || "").trim()
        || String(params.get("return_tab") || "").trim()
        || "news"
      );
      setNewsViewForm((prevForm) => ({
        ...prevForm,
        title: snapshotForm.title || "",
        code: snapshotForm.code || "",
        publishedAt: snapshotForm.publishedAt || "",
        projectName: snapshotForm.projectName || "",
        sourceName: snapshotForm.sourceName || "",
        mediaType: snapshotForm.mediaType || "",
        statusLabel: snapshotForm.statusLabel || "",
        publishLink: snapshotForm.publishLink || "",
        fullContent: snapshotForm.fullContent || "",
        extractedContent: snapshotForm.extractedContent || "",
        processedContent: snapshotForm.processedContent || "",
        extractedMediaUrl: snapshotForm.extractedMediaUrl || "",
        processedMediaUrl: snapshotForm.processedMediaUrl || "",
        sourceMediaUrl: snapshotForm.sourceMediaUrl || "",
        mediaUrl: snapshotForm.mediaUrl || "",
      }));
    } catch (_error) {
      // intentionally ignored
    }
  }, [activeTab, location.search, newsViewForm.code]);

  const ensureNewsVideoUrlsLoaded = async () => {
    const params = new URLSearchParams(location.search || "");
    const requestedNewsId = Number(selectedNewsIdForView || params.get("news_id") || 0);
    if (!Number.isFinite(requestedNewsId) || requestedNewsId <= 0) {
      setNewsViewMessage({
        type: "error",
        text: "شناسه خبر برای پخش ویدیو معتبر نیست.",
      });
      return false;
    }
    setLoadingNewsVideo(true);
    try {
      const result = await newsApi.detail(requestedNewsId);
      const resolvedRow = normalizeNewsRowFromApi(result?.item || {}, projects);
      const normalizedSourceMediaUrl = normalizeNewsLink(resolvedRow.sourceMediaUrl);
      const normalizedPublishLink = normalizeNewsLink(resolvedRow.mediaUrl || normalizedSourceMediaUrl || resolvedRow.publishLink);
      const normalizedExtractedMediaUrl = normalizeNewsLink(resolvedRow.extractedMediaUrl || normalizedSourceMediaUrl);
      const normalizedProcessedMediaUrl = normalizeNewsLink(resolvedRow.processedMediaUrl || normalizedSourceMediaUrl);
      const defaultContent = (
        String(resolvedRow.fullContent || "").trim()
        || getNewsViewDefaultContent(resolvedRow)
      );
      const extractedContent = String(resolvedRow.extractedText || "").trim();
      const processedContent = String(resolvedRow.aiEditedText || resolvedRow.systemEditedText || "").trim();

      setNewsViewForm((prevForm) => ({
        ...prevForm,
        title: resolvedRow.title || prevForm.title || "",
        code: resolvedRow.code || prevForm.code || "",
        publishedAt: resolvedRow.publishedAt || prevForm.publishedAt || "",
        projectName: resolvedRow.projectName || prevForm.projectName || "",
        sourceName: resolvedRow.sourceName || prevForm.sourceName || "",
        mediaType: resolvedRow.mediaType || prevForm.mediaType || "",
        statusLabel: resolvedRow.statusLabel || prevForm.statusLabel || "",
        publishLink: resolvedRow.publishLink || prevForm.publishLink || "",
        fullContent: defaultContent || prevForm.fullContent || "",
        extractedContent: extractedContent || prevForm.extractedContent || "",
        processedContent: processedContent || prevForm.processedContent || "",
        extractedMediaUrl: normalizedExtractedMediaUrl || prevForm.extractedMediaUrl || "",
        processedMediaUrl: normalizedProcessedMediaUrl || prevForm.processedMediaUrl || "",
        sourceMediaUrl: normalizedSourceMediaUrl || prevForm.sourceMediaUrl || "",
        mediaUrl: normalizedPublishLink || prevForm.mediaUrl || "",
      }));
      setSelectedNewsIdForView(requestedNewsId);
      return true;
    } catch (_error) {
      setNewsViewMessage({
        type: "error",
        text: "دریافت ویدیوی خبر ناموفق بود.",
      });
      return false;
    } finally {
      setLoadingNewsVideo(false);
    }
  };

  const handleExtractNewsContent = () => {
    const extracted = String(newsViewForm.fullContent || "").trim();

    setNewsViewForm((prevForm) => ({
      ...prevForm,
      extractedContent: extracted || "متن خبر برای استخراج موجود نیست.",
    }));
    setNewsViewMessage({
      type: "success",
      text: "محتوای خام خبر استخراج شد.",
    });
  };

  const handleStartNewsEdit = () => {
    setNewsViewMessage({ type: "", text: "" });
    setIsNewsEditing(true);
    setNewsEditDraft((prevDraft) => {
      if (prevDraft.mediaPreviewUrl !== newsViewForm.mediaUrl) {
        revokeObjectPreviewUrl(prevDraft.mediaPreviewUrl);
      }

      return {
        title: newsViewForm.title || "",
        fullContent: newsViewForm.fullContent || "",
        mediaType: newsViewForm.mediaType || "متنی",
        mediaPreviewUrl: newsViewForm.mediaUrl || "",
      };
    });
  };

  const handleCancelNewsEdit = () => {
    setNewsEditDraft((prevDraft) => {
      if (prevDraft.mediaPreviewUrl !== newsViewForm.mediaUrl) {
        revokeObjectPreviewUrl(prevDraft.mediaPreviewUrl);
      }

      return {
        title: newsViewForm.title || "",
        fullContent: newsViewForm.fullContent || "",
        mediaType: newsViewForm.mediaType || "متنی",
        mediaPreviewUrl: newsViewForm.mediaUrl || "",
      };
    });
    setIsNewsEditing(false);
    setNewsViewMessage({ type: "", text: "" });
  };

  const handleNewsMediaUpload = (event) => {
    const selectedFile = event.target.files?.[0];

    if (!selectedFile) {
      return;
    }

    const nextPreviewUrl = URL.createObjectURL(selectedFile);
    const nextMediaType = selectedFile.type.startsWith("video/") ? "ویدئو" : "عکس";

    setNewsEditDraft((prevDraft) => {
      if (prevDraft.mediaPreviewUrl !== newsViewForm.mediaUrl) {
        revokeObjectPreviewUrl(prevDraft.mediaPreviewUrl);
      }

      return {
        ...prevDraft,
        mediaType: nextMediaType,
        mediaPreviewUrl: nextPreviewUrl,
      };
    });
  };

  const handleSaveNewsEdit = async () => {
    const updatedTitle = String(newsEditDraft.title || "").trim();
    const updatedContent = String(newsEditDraft.fullContent || "").trim();
    const updatedMediaType = newsEditDraft.mediaType || newsViewForm.mediaType || "متنی";
    const updatedMediaUrl = newsEditDraft.mediaPreviewUrl || newsViewForm.mediaUrl || "";

    if (!updatedTitle || !updatedContent) {
      setNewsViewMessage({
        type: "error",
        text: "عنوان و متن خبر نمی‌تواند خالی باشد.",
      });
      return;
    }

    const selectedRow = newsRows.find((row) => row.rowId === selectedNewsRowId);
    const selectedRowNewsId = Number(selectedRow?.id);

    if (!Number.isFinite(selectedRowNewsId) || selectedRowNewsId <= 0) {
      setNewsViewMessage({
        type: "error",
        text: "شناسه خبر برای ویرایش معتبر نیست.",
      });
      return;
    }

    const updatePayload = {
      title: updatedTitle,
      fullContent: updatedContent,
      publishMode: "scheduled",
      projectId: null,
      publish_source_id: selectedRow?.publishSourceId ? Number(selectedRow.publishSourceId) : null,
      publishName: selectedRow?.sourceName === "--" ? "" : String(selectedRow?.sourceName || ""),
      publishPlatform: "",
      publishLink: selectedRow?.publishLink === "--" ? "" : String(selectedRow?.publishLink || ""),
      mediaType: updatedMediaType,
      watermark_news_activity_status: (
        String(newsWatermarkSettings.watermark_status || "").toLowerCase() === "disabled"
          ? "deactivate"
          : "active"
      ),
      watermark_news_file: newsWatermarkSettings.watermark_file_file || null,
      watermark_news_text: String(newsWatermarkSettings.watermark_text || ""),
      watermark_news_font_size: String(newsWatermarkSettings.watermark_size || "0"),
      watermark_news_color: String(newsWatermarkSettings.watermark_color || ""),
      watermark_news_add_to: (
        String(newsWatermarkSettings.watermark_apply_to || "").includes("video")
          ? "video"
          : "image"
      ),
      watermark_news_position: String(newsWatermarkSettings.watermark_position || ""),
      watermark_news_font: String(newsWatermarkSettings.watermark_font || ""),
      scheduled_publish_timing: selectedRow?.scheduledPublishTiming || undefined,
    };

    try {
      const result = await newsApi.update(selectedRowNewsId, updatePayload);
      const normalizedRow = normalizeNewsRowFromApi(result?.item || {}, projects);
      setNewsRows((prevRows) => (
        prevRows.map((row) => (
          row.rowId === selectedNewsRowId ? normalizedRow : row
        ))
      ));
    } catch (error) {
      setNewsViewMessage({
        type: "error",
        text: error.message || "ذخیره ویرایش خبر ناموفق بود.",
      });
      return;
    }

    setNewsViewForm((prevForm) => ({
      ...prevForm,
      title: updatedTitle,
      fullContent: updatedContent,
      mediaType: updatedMediaType,
      mediaUrl: updatedMediaUrl,
    }));
    setNewsActionForm((prevForm) => ({
      ...prevForm,
      generatedTitle: updatedTitle,
      captionText: updatedTitle,
      generatedMediaType: updatedMediaType,
    }));
    setIsNewsEditing(false);
    setNewsViewMessage({
      type: "success",
      text: "ویرایش خبر با موفقیت ذخیره شد.",
    });
    setNewsReloadToken((prevToken) => prevToken + 1);
  };

  const handleApplyNewsGeneratedOps = () => {
    const updatedTitle = String(newsActionForm.captionText || newsActionForm.generatedTitle || "").trim();
    const updatedMediaType = newsActionForm.generatedMediaType || "متنی";

    if (!updatedTitle) {
      setNewsViewMessage({
        type: "error",
        text: "عنوان جدید خبر را وارد کنید.",
      });
      return;
    }

    if (selectedNewsRowId) {
      setNewsRows((prevRows) => (
        prevRows.map((row) => (
          row.rowId === selectedNewsRowId
            ? {
              ...row,
              title: updatedTitle,
              mediaType: updatedMediaType,
            }
            : row
        ))
      ));
    }

    setNewsViewForm((prevForm) => ({
      ...prevForm,
      title: updatedTitle,
      mediaType: updatedMediaType,
    }));
    setNewsViewMessage({
      type: "success",
      text: "تغییرات عملیات جدید روی خبر اعمال شد.",
    });
  };

  const handleCreateNewsFieldChange = (field, value) => {
    setNewsCreateForm((prevForm) => ({
      ...prevForm,
      [field]: value,
    }));
  };

  const handleNewsWatermarkSettingsChange = (field, value) => {
    setNewsWatermarkSettings((prevValues) => ({
      ...prevValues,
      [field]: value,
    }));
  };

  const newsCreateProjectOptions = useMemo(() => (
    projects.reduce((options, project) => {
      const projectId = project?.id;
      if (projectId === undefined || projectId === null) {
        return options;
      }
      const optionValue = String(projectId);
      if (options.some((option) => option.value === optionValue)) {
        return options;
      }
      options.push({
        value: optionValue,
        label: String(
          project?.title
          || project?.name
          || project?.project_name
          || `پروژه ${optionValue}`
        ),
      });
      return options;
    }, [])
  ), [projects]);

  const newsProjectSelectionOptions = useMemo(() => (
    [
      { id: "all", name: "انتخاب پروژه برای افزودن" },
      { id: NEWS_PROJECT_ALL_ID, name: "همه" },
      ...newsCreateProjectOptions.map((project) => ({
        id: String(project.value || ""),
        name: String(project.label || ""),
      })),
    ]
  ), [newsCreateProjectOptions]);

  const newsAvailablePublishSources = useMemo(() => (
    [NEWS_PUBLISH_SOURCE_ALL_OPTION, ...publishSourcesOptions]
  ), [publishSourcesOptions]);

  const newsSelectedProjectGroups = useMemo(() => (
    newsCreateSelectedProjectIds
      .map((projectId) => {
        const normalizedProjectId = String(projectId || "").trim();
        if (!normalizedProjectId) {
          return null;
        }

        const selectedProject = projects.find((project) => String(project?.id || "") === normalizedProjectId);
        if (!selectedProject) {
          return null;
        }

        const projectName = String(
          selectedProject?.title
          || selectedProject?.name
          || selectedProject?.project_name
          || `پروژه ${normalizedProjectId}`
        );
        const allowedSourceIds = new Set(getProjectPublishSourceIds(selectedProject));
        const sources = publishSourcesOptions
          .filter((row) => allowedSourceIds.has(String(row?.id || "")))
          .map((row) => ({
            sourceId: String(row?.id || ""),
            name: String(row?.name || `بستر ${row?.id || ""}`),
          }))
          .filter((row) => (
            row.sourceId
            && !newsRemovedProjectSourceIds.includes(`${normalizedProjectId}:${row.sourceId}`)
          ));

        return {
          itemType: "project-group",
          rowId: `project-group-${normalizedProjectId}`,
          projectId: normalizedProjectId,
          projectName,
          sources,
        };
      })
      .filter(Boolean)
  ), [newsCreateSelectedProjectIds, newsRemovedProjectSourceIds, projects, publishSourcesOptions]);

  const newsManualSelectedPublishTargets = useMemo(() => {
    const selectedSourceIds = [];
    if (newsCreateForm.publishSourceId) {
      selectedSourceIds.push(String(newsCreateForm.publishSourceId));
    }
    selectedSourceIds.push(
      ...newsCreateExtraPublishTargets
        .map((row) => String(row?.publish_source_id || "").trim())
        .filter(Boolean)
    );

    const uniqueIds = Array.from(new Set(selectedSourceIds));
    return uniqueIds.map((sourceId) => {
      if (sourceId === NEWS_PUBLISH_SOURCE_ALL_ID) {
        return {
          itemType: "source",
          sourceId,
          rowId: NEWS_PUBLISH_SOURCE_ALL_ID,
          name: NEWS_PUBLISH_SOURCE_ALL_OPTION.name,
        };
      }
      const matchedRow = publishSourcesOptions.find((row) => String(row?.id || "") === sourceId);
      return {
        itemType: "source",
        sourceId,
        rowId: sourceId,
        name: String(matchedRow?.name || `بستر ${sourceId}`),
      };
    });
  }, [newsCreateForm.publishSourceId, newsCreateExtraPublishTargets, publishSourcesOptions]);


  const newsSelectedProjectSourceIds = useMemo(() => (
    new Set(
      newsSelectedProjectGroups.flatMap((groupItem) => (
        groupItem.sources
          .map((sourceItem) => String(sourceItem?.sourceId || "").trim())
          .filter(Boolean)
      ))
    )
  ), [newsSelectedProjectGroups]);

  const handleNewsProjectSelection = (nextProjectId) => {
    const normalizedProjectId = String(nextProjectId || "all").trim() || "all";
    setNewsCreateForm((prevForm) => ({
      ...prevForm,
      projectId: normalizedProjectId,
    }));

    if (normalizedProjectId === "all") {
      return;
    }

    if (normalizedProjectId === NEWS_PROJECT_ALL_ID) {
      const allProjectIds = newsCreateProjectOptions.map((project) => String(project.value || "")).filter(Boolean);
      setNewsCreateSelectedProjectIds(allProjectIds);
      setNewsRemovedProjectSourceIds([]);
      return;
    }

    setNewsCreateSelectedProjectIds((prevIds) => {
      if (prevIds.includes(normalizedProjectId)) {
        return prevIds;
      }
      return [...prevIds, normalizedProjectId];
    });

    setNewsRemovedProjectSourceIds((prevIds) => (
      prevIds.filter((item) => !item.startsWith(`${normalizedProjectId}:`))
    ));
  };

  const handleSelectNewsPublishTarget = (selectedId, selectedRow) => {
    const normalizedSelectedId = String(selectedId || "").trim();
    if (!normalizedSelectedId) {
      return;
    }

    if (newsSelectedProjectSourceIds.has(normalizedSelectedId)) {
      return;
    }

    if (normalizedSelectedId === NEWS_PUBLISH_SOURCE_ALL_ID) {
      setNewsCreateForm((prevForm) => ({
        ...prevForm,
        publishSourceId: NEWS_PUBLISH_SOURCE_ALL_ID,
        publishName: NEWS_PUBLISH_SOURCE_ALL_OPTION.name,
        publishPlatform: NEWS_PUBLISH_SOURCE_ALL_OPTION.platform,
        publishLink: "",
      }));
      setNewsCreateExtraPublishTargets([]);
      return;
    }

    const nextRow = {
      id: createProjectFormRowId("news-target"),
      publish_source_id: normalizedSelectedId,
      name: String(selectedRow?.name || ""),
      platform: String(selectedRow?.platform || ""),
      link: String(selectedRow?.link || ""),
    };

    setNewsCreateForm((prevForm) => {
      const currentPrimaryId = String(prevForm.publishSourceId || "").trim();
      if (!currentPrimaryId || currentPrimaryId === NEWS_PUBLISH_SOURCE_ALL_ID) {
        return {
          ...prevForm,
          publishSourceId: normalizedSelectedId,
          publishName: nextRow.name,
          publishPlatform: nextRow.platform,
          publishLink: nextRow.link,
        };
      }
      return prevForm;
    });

    setNewsCreateExtraPublishTargets((prevRows) => {
      const normalizedPrimary = String(newsCreateForm.publishSourceId || "").trim();
      const alreadyExists = prevRows.some((row) => String(row?.publish_source_id || "") === normalizedSelectedId)
        || normalizedPrimary === normalizedSelectedId;
      if (alreadyExists || !normalizedPrimary || normalizedPrimary === NEWS_PUBLISH_SOURCE_ALL_ID) {
        return prevRows;
      }
      return [...prevRows, nextRow];
    });
  };

  const handleRemoveNewsPublishTarget = (targetItem) => {
    if (targetItem?.itemType === "project-group") {
      const normalizedProjectId = String(targetItem?.projectId || "").trim();
      if (!normalizedProjectId) {
        return;
      }
      setNewsCreateSelectedProjectIds((prevIds) => (
        prevIds.filter((projectId) => String(projectId || "") !== normalizedProjectId)
      ));
      setNewsRemovedProjectSourceIds((prevIds) => (
        prevIds.filter((item) => !item.startsWith(`${normalizedProjectId}:`))
      ));
      return;
    }

    if (targetItem?.itemType === "project-source") {
      const normalizedProjectId = String(targetItem?.projectId || "").trim();
      const normalizedSourceId = String(targetItem?.sourceId || "").trim();
      if (!normalizedProjectId || !normalizedSourceId) {
        return;
      }
      setNewsRemovedProjectSourceIds((prevIds) => {
        const key = `${normalizedProjectId}:${normalizedSourceId}`;
        if (prevIds.includes(key)) {
          return prevIds;
        }
        return [...prevIds, key];
      });
      return;
    }

    const normalizedSourceId = String(targetItem?.sourceId || targetItem || "").trim();
    if (!normalizedSourceId) {
      return;
    }

    if (String(newsCreateForm.publishSourceId || "") === normalizedSourceId) {
      handleRemoveNewsPrimaryPublishTarget();
      return;
    }

    setNewsCreateExtraPublishTargets((prevRows) => (
      prevRows.filter((row) => String(row?.publish_source_id || "") !== normalizedSourceId)
    ));
  };

  const handleAddNewsExtraPublishTarget = () => {
    const nextRow = createNewsExtraPublishTarget();
    setNewsCreateExtraPublishTargets((prevRows) => [...prevRows, nextRow]);
    setPendingNewsPublishTargetScrollId(`${nextRow.id}-publish-target-row`);
  };

  const handleRemoveNewsExtraPublishTarget = (rowId) => {
    setNewsCreateExtraPublishTargets((prevRows) => prevRows.filter((row) => row.id !== rowId));
  };

  const handleRemoveNewsPrimaryPublishTarget = () => {
    setNewsCreateExtraPublishTargets((prevRows) => {
      if (!prevRows.length) {
        setNewsCreateForm((prevForm) => ({
          ...prevForm,
          publishSourceId: "",
          publishName: "",
          publishPlatform: "",
          publishLink: "",
        }));
        return prevRows;
      }

      const [nextPrimaryRow, ...remainingRows] = prevRows;
      setNewsCreateForm((prevForm) => ({
        ...prevForm,
        publishSourceId: String(nextPrimaryRow?.publish_source_id || ""),
        publishName: String(nextPrimaryRow?.name || ""),
        publishPlatform: String(nextPrimaryRow?.platform || ""),
        publishLink: String(nextPrimaryRow?.link || ""),
      }));
      return remainingRows;
    });
  };

  const handleAddNewsScheduledPublishRow = () => {
    const nextRow = createNewsScheduledPublishRow();
    setNewsCreateScheduledPublishRows((prevRows) => [...prevRows, nextRow]);
    setPendingNewsScheduledScrollId(`${nextRow.id}-scheduled-row`);
  };

  useEffect(() => {
    const availableSourceIds = new Set(
      [
        NEWS_PUBLISH_SOURCE_ALL_ID,
        ...publishSourcesOptions.map((row) => String(row?.id || "")).filter(Boolean),
      ]
    );
    const currentPrimaryId = String(newsCreateForm.publishSourceId || "").trim();

    if (currentPrimaryId && !availableSourceIds.has(currentPrimaryId)) {
      setNewsCreateForm((prevForm) => ({
        ...prevForm,
        publishSourceId: "",
        publishName: "",
        publishPlatform: "",
        publishLink: "",
      }));
    }

    setNewsCreateExtraPublishTargets((prevRows) => (
      prevRows.filter((row) => availableSourceIds.has(String(row?.publish_source_id || "")))
    ));
  }, [publishSourcesOptions, newsCreateForm.publishSourceId]);


  useEffect(() => {
    if (!newsSelectedProjectSourceIds.size) {
      return;
    }

    const primarySourceId = String(newsCreateForm.publishSourceId || "").trim();
    const filteredExtraRows = newsCreateExtraPublishTargets.filter((row) => (
      !newsSelectedProjectSourceIds.has(String(row?.publish_source_id || "").trim())
    ));
    const hasExtraChanges = filteredExtraRows.length !== newsCreateExtraPublishTargets.length;
    const shouldReplacePrimary = (
      Boolean(primarySourceId)
      && primarySourceId !== NEWS_PUBLISH_SOURCE_ALL_ID
      && newsSelectedProjectSourceIds.has(primarySourceId)
    );

    if (!hasExtraChanges && !shouldReplacePrimary) {
      return;
    }

    if (shouldReplacePrimary) {
      if (filteredExtraRows.length) {
        const [nextPrimaryRow, ...remainingRows] = filteredExtraRows;
        setNewsCreateForm((prevForm) => ({
          ...prevForm,
          publishSourceId: String(nextPrimaryRow?.publish_source_id || ""),
          publishName: String(nextPrimaryRow?.name || ""),
          publishPlatform: String(nextPrimaryRow?.platform || ""),
          publishLink: String(nextPrimaryRow?.link || ""),
        }));
        setNewsCreateExtraPublishTargets(remainingRows);
      } else {
        setNewsCreateForm((prevForm) => ({
          ...prevForm,
          publishSourceId: "",
          publishName: "",
          publishPlatform: "",
          publishLink: "",
        }));
        setNewsCreateExtraPublishTargets([]);
      }
      return;
    }

    if (hasExtraChanges) {
      setNewsCreateExtraPublishTargets(filteredExtraRows);
    }
  }, [newsSelectedProjectSourceIds, newsCreateForm.publishSourceId, newsCreateExtraPublishTargets]);

  useEffect(() => {
    if (!pendingNewsPublishTargetScrollId) {
      return;
    }
    const targetElement = document.getElementById(pendingNewsPublishTargetScrollId);
    const contentElement = contentRef.current;
    if (!targetElement || !contentElement) {
      return;
    }
    const targetTop = targetElement.offsetTop;
    contentElement.scrollTo({
      top: Math.max(0, targetTop - 110),
      behavior: "smooth",
    });
    setPendingNewsPublishTargetScrollId("");
  }, [newsCreateExtraPublishTargets, pendingNewsPublishTargetScrollId]);

  useEffect(() => {
    if (!pendingNewsScheduledScrollId) {
      return;
    }
    const targetElement = document.getElementById(pendingNewsScheduledScrollId);
    const contentElement = contentRef.current;
    if (!targetElement || !contentElement) {
      return;
    }
    const targetTop = targetElement.offsetTop;
    contentElement.scrollTo({
      top: Math.max(0, targetTop - 110),
      behavior: "smooth",
    });
    setPendingNewsScheduledScrollId("");
  }, [newsCreateScheduledPublishRows, pendingNewsScheduledScrollId]);

  const handleNewsScheduledPublishRowChange = (rowId, field, value) => {
    setNewsCreateScheduledPublishRows((prevRows) => (
      prevRows.map((row) => (row.id === rowId ? { ...row, [field]: value } : row))
    ));
  };

  const handleRemoveNewsScheduledPublishRow = (rowId) => {
    setNewsCreateScheduledPublishRows((prevRows) => {
      const remainingRows = prevRows.filter((row) => row.id !== rowId);
      return remainingRows.length ? remainingRows : [createNewsScheduledPublishRow()];
    });
  };

  const handleCreateNewsMediaUpload = (event) => {
    const selectedFile = event.target.files?.[0];

    if (!selectedFile) {
      return;
    }

    const nextPreviewUrl = URL.createObjectURL(selectedFile);
    const nextMediaType = selectedFile.type.startsWith("video/") ? "ویدئو" : "عکس";
    setShowNewsCreateVideoPlayer(false);
    setIsNewsCreateVideoPlaying(false);

    setNewsCreateForm((prevForm) => {
      revokeObjectPreviewUrl(prevForm.mediaPreviewUrl);

      return {
        ...prevForm,
        mediaType: nextMediaType,
        mediaPreviewUrl: nextPreviewUrl,
        mediaFile: selectedFile,
      };
    });
  };

  const handleOpenNewsCreateMediaPicker = () => {
    const mediaInputElement = newsCreateMediaInputRef.current
      || document.getElementById("create-news-media-upload")
      || document.getElementById("create-ad-media-upload");
    mediaInputElement?.click();
  };

  const handleToggleNewsCreateVideoPlayback = () => {
    if (!showNewsCreateVideoPlayer) {
      setShowNewsCreateVideoPlayer(true);
      setTimeout(() => {
        const videoElement = newsCreateVideoRef.current;
        if (!videoElement) {
          return;
        }
        const playPromise = videoElement.play();
        if (playPromise && typeof playPromise.catch === "function") {
          playPromise.catch(() => {});
        }
      }, 0);
      return;
    }

    const videoElement = newsCreateVideoRef.current;
    if (!videoElement) {
      return;
    }

    if (videoElement.paused || videoElement.ended) {
      const playPromise = videoElement.play();
      if (playPromise && typeof playPromise.catch === "function") {
        playPromise.catch(() => {});
      }
      return;
    }

    videoElement.pause();
  };

  const handleSaveCreatedNews = async () => {
    const isAdsCreateTab = activeTab === "ads-create";
    const isEditMode = newsFormMode === "edit" && Boolean(editingNewsRowId);
    const title = String(newsCreateForm.title || "").trim();
    const fullContent = String(newsCreateForm.fullContent || "").trim();

    if (!title || !fullContent) {
      setNewsCreateMsg({
        type: "error",
        text: "عنوان و متن خبر برای ایجاد خبر الزامی است.",
      });
      return;
    }

    const scheduleMode = String(newsCreateForm.scheduleMode || "future_times").trim().toLowerCase();
    const normalizedScheduleMode = scheduleMode === "recurring" ? "recurring" : "future_times";
    const normalizedScheduleRows = newsCreateScheduledPublishRows
      .map((row) => ({
        date: String(row?.date || "").trim(),
        time: normalizeTimeDigits(row?.time).trim(),
      }))
      .filter((row) => row.date || row.time);
    const scheduleTimesIso = normalizedScheduleRows
      .map((row) => buildIsoDateTimeFromJalaliAndTime(row.date, row.time))
      .filter(Boolean);
    const recurringStartAtIso = buildIsoDateTimeFromJalaliAndTime(
      newsCreateForm.recurringStartDate,
      newsCreateForm.recurringStartTime
    );
    const recurringIntervalSeconds = Number(newsCreateForm.recurringIntervalSeconds);
    const recurringMaxRuns = Number(newsCreateForm.recurringMaxRuns);
    const nowTimestamp = Date.now();
    const isFutureIsoDateTime = (isoValue) => {
      const parsed = new Date(isoValue);
      return !Number.isNaN(parsed.getTime()) && parsed.getTime() > nowTimestamp;
    };

    if (normalizedScheduleMode === "future_times") {
      if (!scheduleTimesIso.length || scheduleTimesIso.length !== normalizedScheduleRows.length) {
        setNewsCreateMsg({
          type: "error",
          text: "برای حالت یک/چند زمان، تاریخ و ساعت معتبر برای همه زمان‌ها وارد کنید.",
        });
        return;
      }
      if (!scheduleTimesIso.every(isFutureIsoDateTime)) {
        setNewsCreateMsg({
          type: "error",
          text: "زمان انتشار باید در آینده باشد.",
        });
        return;
      }
    } else if (!recurringStartAtIso || !Number.isFinite(recurringIntervalSeconds) || recurringIntervalSeconds <= 0 || !Number.isFinite(recurringMaxRuns) || recurringMaxRuns <= 0) {
      setNewsCreateMsg({
        type: "error",
        text: "برای حالت دوره‌ای، شروع، اینتروال و تعداد اجرا را معتبر وارد کنید.",
      });
      return;
    }
    if (normalizedScheduleMode === "recurring" && !isFutureIsoDateTime(recurringStartAtIso)) {
      setNewsCreateMsg({
        type: "error",
        text: "زمان شروع باید در آینده باشد.",
      });
      return;
    }
    const expandedPrimaryTargetIds = (
      String(newsCreateForm.publishSourceId || "") === NEWS_PUBLISH_SOURCE_ALL_ID
        ? publishSourcesOptions
            .map((row) => Number(row?.id))
            .filter((value) => Number.isFinite(value) && value > 0)
        : [Number(newsCreateForm.publishSourceId)]
    );
    const manualPublishTargetIds = [
      ...expandedPrimaryTargetIds,
      ...newsCreateExtraPublishTargets.map((row) => Number(row?.publish_source_id)),
    ].filter((value) => Number.isFinite(value) && value > 0);
    const projectPublishTargetIds = newsSelectedProjectGroups.flatMap((group) => (
      group.sources
        .map((sourceItem) => Number(sourceItem?.sourceId))
        .filter((value) => Number.isFinite(value) && value > 0)
    ));
    const selectedPublishTargetIds = Array.from(new Set([
      ...manualPublishTargetIds,
      ...projectPublishTargetIds,
    ]));
    const selectedProjectIds = newsCreateSelectedProjectIds
      .map((projectId) => Number(projectId))
      .filter((value) => Number.isFinite(value) && value > 0);
    const editingRow = newsRows.find((row) => row.rowId === editingNewsRowId);

    const payload = {
      title,
      fullContent,
      publishMode: "scheduled",
      projectId: (
        selectedProjectIds.length
          ? selectedProjectIds[0]
          : (
            newsCreateForm.projectId && newsCreateForm.projectId !== "all"
              ? Number(newsCreateForm.projectId)
              : null
          )
      ),
      publish_source_id: (
        String(newsCreateForm.publishSourceId || "") === NEWS_PUBLISH_SOURCE_ALL_ID
          ? (selectedPublishTargetIds[0] || null)
          : (newsCreateForm.publishSourceId ? Number(newsCreateForm.publishSourceId) : null)
      ),
      publishName: String(newsCreateForm.publishName || "").trim(),
      publishPlatform: String(newsCreateForm.publishPlatform || "").trim(),
      publishLink: String(newsCreateForm.publishLink || "").trim(),
      mediaType: newsCreateForm.mediaType || "متنی",
      make_media_file: newsCreateForm.mediaFile || null,
      watermark_news_activity_status: (
        String(newsWatermarkSettings.watermark_status || "").toLowerCase() === "disabled"
          ? "deactivate"
          : "active"
      ),
      watermark_news_file: newsWatermarkSettings.watermark_file_file || null,
      watermark_news_text: String(newsWatermarkSettings.watermark_text || "").trim(),
      watermark_news_font_size: String(newsWatermarkSettings.watermark_size || "0"),
      watermark_news_color: String(newsWatermarkSettings.watermark_color || "").trim(),
      watermark_news_add_to: (
        String(newsWatermarkSettings.watermark_apply_to || "").includes("video")
          ? "video"
          : "image"
      ),
      watermark_news_position: String(newsWatermarkSettings.watermark_position || "").trim(),
      watermark_news_font: String(newsWatermarkSettings.watermark_font || "").trim(),
      scheduled_publish_timing: (
        normalizedScheduleMode === "future_times"
          ? {
            publish_mode: "future_times",
            times: scheduleTimesIso,
            publish_target_ids: selectedPublishTargetIds,
          }
          : {
            publish_mode: "recurring",
            start_at: recurringStartAtIso,
            interval_seconds: recurringIntervalSeconds,
            max_runs: recurringMaxRuns,
            publish_target_ids: selectedPublishTargetIds,
          }
      ),
      my_ad: isAdsCreateTab,
    };

    try {
      const result = (isEditMode && editingRow?.id)
        ? await newsApi.update(editingRow.id, payload)
        : await newsApi.create(payload);
      const normalizedRow = normalizeNewsRowFromApi(result?.item || {}, projects);

      if (isEditMode && editingRow?.id) {
        setNewsRows((prevRows) => prevRows.map((row) => (
          row.rowId === editingNewsRowId ? normalizedRow : row
        )));
        (isAdsCreateTab ? setAdsMsg : setNewsMsg)({
          type: "success",
          text: isAdsCreateTab
            ? `تغییرات تبلیغ «${title}» با موفقیت ذخیره شد.`
            : `تغییرات خبر «${title}» با موفقیت ذخیره شد.`,
        });
      } else {
        setNewsRows((prevRows) => [normalizedRow, ...prevRows]);
        (isAdsCreateTab ? setAdsMsg : setNewsMsg)({
          type: "success",
          text: isAdsCreateTab
            ? `تبلیغ «${title}» با موفقیت ایجاد شد.`
            : `خبر «${title}» با موفقیت ایجاد شد.`,
        });
      }
    } catch (error) {
      (isAdsCreateTab ? setAdsCreateMsg : setNewsCreateMsg)({
        type: "error",
        text: error.message || (isAdsCreateTab ? "ذخیره تبلیغ ناموفق بود." : "ذخیره خبر ناموفق بود."),
      });
      return;
    }

    setNewsCreateForm((prevForm) => {
      revokeObjectPreviewUrl(prevForm.mediaPreviewUrl);
      return createInitialNewsCreateForm();
    });
    setNewsCreateExtraPublishTargets([]);
    setNewsCreateSelectedProjectIds([]);
    setNewsRemovedProjectSourceIds([]);
    setNewsCreateScheduledPublishRows([createNewsScheduledPublishRow()]);
    setNewsWatermarkSettings(createInitialNewsMediaSettings());
    (isAdsCreateTab ? setAdsCreateMsg : setNewsCreateMsg)({ type: "", text: "" });
    setNewsFormMode("create");
    setEditingNewsRowId("");
    setNewsReloadToken((prevToken) => prevToken + 1);
    setActiveTab(isAdsCreateTab ? "ads" : "news");
  };

  const renderNewsCreateTab = () => {
    const isNewsEditMode = newsFormMode === "edit";
    const previewUrl = normalizeNewsLink(newsCreateForm.mediaPreviewUrl);
    const isVideoMedia = String(newsCreateForm.mediaType || "").includes("ویدئو");
    const minPublishDate = formatJalaliDate(getTodayJalali());

    return (
      <div className="ads-create-layout">
        <div className="projects-create-head ads-create-head news-create-head">
          <div>
            <h3>{isNewsEditMode ? "ویرایش خبر" : "ایجاد خبر"}</h3>
          </div>
          <button
            type="button"
            className="projects-create-back-btn"
            onClick={handleCloseNewsCreate}
          >
            بازگشت به لیست اخبار
          </button>
        </div>

        <form className="ads-create-form" onSubmit={(event) => {
          event.preventDefault();
          handleSaveCreatedNews();
        }}>
          <div className="ads-create-media-card news-create-media-card">
            <div className="ads-create-media-label">
              {previewUrl ? (
                isVideoMedia ? (
                  showNewsCreateVideoPlayer ? (
                    <video
                      ref={newsCreateVideoRef}
                      src={previewUrl}
                      className="news-create-video-thumbnail"
                      controls
                      playsInline
                      preload="metadata"
                      onPlay={() => setIsNewsCreateVideoPlaying(true)}
                      onPause={() => setIsNewsCreateVideoPlaying(false)}
                      onEnded={() => setIsNewsCreateVideoPlaying(false)}
                    />
                  ) : (
                    <div className="news-create-video-thumbnail-wrap" aria-hidden="true">
                      <video src={previewUrl} className="news-create-video-thumbnail news-create-video-thumbnail-static" muted playsInline preload="metadata" />
                      <span className="news-create-video-thumbnail-play">▶</span>
                    </div>
                  )
                ) : (
                  <img src={previewUrl} alt="پیش‌نمایش خبر" />
                )
              ) : (
                <span>پیش‌نمایش مدیا</span>
              )}
            </div>
            <input
              ref={newsCreateMediaInputRef}
              id="create-news-media-upload"
              type="file"
              accept="image/*,video/*"
              onChange={handleCreateNewsMediaUpload}
            />
            <div className="news-create-media-actions">
              <button
                type="button"
                className="news-create-upload-media-btn"
                onClick={handleOpenNewsCreateMediaPicker}
              >
                افزودن مدیا
              </button>
              {previewUrl && isVideoMedia && (
                <button
                  type="button"
                  className="news-create-play-video-btn"
                  onClick={handleToggleNewsCreateVideoPlayback}
                >
                  {showNewsCreateVideoPlayer && isNewsCreateVideoPlaying ? "توقف ویدیو" : "پخش ویدیو"}
                </button>
              )}
            </div>
          </div>

          <div className="ads-create-fields-grid">
            <div className="project-create-field">
              <label htmlFor="create-news-title">عنوان خبر</label>
              <input
                id="create-news-title"
                value={newsCreateForm.title}
                onChange={(event) => handleCreateNewsFieldChange("title", event.target.value)}
                placeholder="عنوان خبر"
              />
            </div>

            <div className="project-create-field ads-create-full-row">
              <label htmlFor="create-news-content">افزودن متن خبر</label>
              <textarea
                id="create-news-content"
                value={newsCreateForm.fullContent}
                onChange={(event) => handleCreateNewsFieldChange("fullContent", event.target.value)}
                placeholder="متن خبر"
                rows={4}
              />
            </div>

          </div>

          <div className="project-create-card">
                <div className="project-wireframe-section-head">
                  <h4>بستر انتشار</h4>
                </div>

                <div className="project-create-grid project-create-grid-2">
                  <div className="project-create-field">
                    <label htmlFor="create-news-project">پروژه</label>
                    <SearchableSourceSelect
                      id="create-news-project"
                      listId="create-news-project-list"
                      value={newsCreateForm.projectId || "all"}
                      options={newsProjectSelectionOptions}
                      labelKey="name"
                      placeholder="انتخاب پروژه برای افزودن"
                      onSelect={(selectedId) => handleNewsProjectSelection(selectedId)}
                    />
                  </div>

                  <div className="project-create-field">
                    <label htmlFor="create-news-publish-name">نام بستر انتشار</label>
                    <SearchableSourceSelect
                      id="create-news-publish-name"
                      listId="create-news-publish-name-list"
                      value=""
                      options={publishSourcesOptions}
                      labelKey="name"
                      placeholder="انتخاب بستر انتشار"
                      onSelect={handleSelectNewsPublishTarget}
                    />
                  </div>

                  <div className="project-create-field ads-create-full-row">
                    <label>موارد انتخاب شده</label>
                    <div className="news-publish-chip-list">
                      {(newsSelectedProjectGroups.length > 0 || newsManualSelectedPublishTargets.length > 0) ? (
                        <>
                          {newsSelectedProjectGroups.map((groupItem) => (
                            <div key={groupItem.rowId} className="news-publish-chip-group">
                              <div className="project-source-chip project-source-chip--project">
                                <span>{groupItem.projectName}</span>
                                <button
                                  type="button"
                                  className="project-source-chip-remove"
                                  onClick={() => handleRemoveNewsPublishTarget(groupItem)}
                                  aria-label={`حذف ${groupItem.projectName}`}
                                >
                                  ×
                                </button>
                              </div>
                              {groupItem.sources.length > 0 ? (
                                groupItem.sources.map((sourceItem) => (
                                  <div
                                    key={`${groupItem.rowId}-source-${sourceItem.sourceId}`}
                                    className="project-source-chip project-source-chip--project-source"
                                  >
                                    <span>{sourceItem.name}</span>
                                    <button
                                      type="button"
                                      className="project-source-chip-remove"
                                      onClick={() => handleRemoveNewsPublishTarget({
                                        itemType: "project-source",
                                        projectId: groupItem.projectId,
                                        sourceId: sourceItem.sourceId,
                                        name: sourceItem.name,
                                      })}
                                      aria-label={`حذف ${sourceItem.name}`}
                                    >
                                      ×
                                    </button>
                                  </div>
                                ))
                              ) : (
                                <div className="project-source-chip project-source-chip--project-source">
                                  <span>بدون بستر انتشار</span>
                                </div>
                              )}
                            </div>
                          ))}

                          {newsManualSelectedPublishTargets.length > 0 && (
                            <div className="news-publish-chip-group">
                              <div className="project-source-chip project-source-chip--manual-label">
                                <span>انتخاب دستی</span>
                              </div>
                              {newsManualSelectedPublishTargets.map((item) => (
                                <div key={`news-publish-chip-${item.rowId}`} className="project-source-chip">
                                  <span>{item.name}</span>
                                  <button
                                    type="button"
                                    className="project-source-chip-remove"
                                    onClick={() => handleRemoveNewsPublishTarget(item)}
                                    aria-label={`حذف ${item.name}`}
                                  >
                                    ×
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="project-process-mode-badge">هیچ بستر انتشاری انتخاب نشده است</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

          <div id="news-scheduled-publish-card" className="project-create-card">
              <div className="project-wireframe-section-head">
                <h4>زمان بندی انتشار</h4>
                {newsCreateForm.scheduleMode === "future_times" && (
                  <button
                    type="button"
                    className="project-wireframe-link-btn"
                    onClick={handleAddNewsScheduledPublishRow}
                  >
                    + افزودن زمان انتشار
                  </button>
                )}
              </div>
              <div className="project-wireframe-row-grid project-wireframe-extra-row">
                <div className="project-create-field">
                  <label htmlFor="create-news-schedule-mode">مدل انتشار</label>
                  <select
                    id="create-news-schedule-mode"
                    value={newsCreateForm.scheduleMode}
                    onChange={(event) => handleCreateNewsFieldChange("scheduleMode", event.target.value)}
                  >
                    <option value="future_times">یک/چند زمان در آینده</option>
                    <option value="recurring">دوره‌ای</option>
                  </select>
                </div>
              </div>

              {newsCreateForm.scheduleMode === "future_times" && newsCreateScheduledPublishRows.map((row) => (
                <div
                  key={row.id}
                  id={`${row.id}-scheduled-row`}
                  className="project-wireframe-row-grid project-wireframe-extra-row project-wireframe-schedule-grid"
                >
                  <JalaliDatePicker
                    id={`${row.id}-date`}
                    label="تاریخ انتشار"
                    value={row.date || "all"}
                    minDate={minPublishDate}
                    allowFuture
                    onChange={(nextValue) => handleNewsScheduledPublishRowChange(row.id, "date", nextValue)}
                  />
                  <div
                    className="project-create-field project-time-field"
                    onPointerDown={(event) => handleTimeFieldPointerDown(event, `${row.id}-time`)}
                  >
                    <label htmlFor={`${row.id}-time`}>ساعت انتشار</label>
                    <div className="project-time-field-control">
                      <input
                        id={`${row.id}-time`}
                        type="time"
                        value={normalizeTimeDigits(row.time)}
                        onChange={(event) => handleNewsScheduledPublishRowChange(row.id, "time", event.target.value)}
                        onBlur={() => {
                          if (openNewsTimePickerInputId === `${row.id}-time`) {
                            setOpenNewsTimePickerInputId("");
                          }
                        }}
                      />
                      <button
                        type="button"
                        className="project-time-picker-btn"
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => toggleNewsTimeInputPicker(`${row.id}-time`)}
                      >
                        {openNewsTimePickerInputId === `${row.id}-time` ? "بستن ساعت" : "انتخاب ساعت"}
                      </button>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="project-wireframe-remove-btn"
                    onClick={() => handleRemoveNewsScheduledPublishRow(row.id)}
                    aria-label="حذف زمان انتشار"
                  >
                    حذف
                  </button>
                </div>
              ))}

              {newsCreateForm.scheduleMode === "recurring" && (
                <>
                  <div className="project-wireframe-row-grid project-wireframe-extra-row project-wireframe-schedule-grid">
                    <JalaliDatePicker
                      id="create-news-recurring-start-date"
                      label="تاریخ شروع"
                      value={newsCreateForm.recurringStartDate || "all"}
                      minDate={minPublishDate}
                      allowFuture
                      onChange={(nextValue) => handleCreateNewsFieldChange("recurringStartDate", nextValue)}
                    />
                    <div
                      className="project-create-field project-time-field"
                      onPointerDown={(event) => handleTimeFieldPointerDown(event, "create-news-recurring-start-time")}
                    >
                      <label htmlFor="create-news-recurring-start-time">ساعت شروع</label>
                      <div className="project-time-field-control">
                        <input
                          id="create-news-recurring-start-time"
                          type="time"
                          value={normalizeTimeDigits(newsCreateForm.recurringStartTime)}
                          onChange={(event) => handleCreateNewsFieldChange("recurringStartTime", event.target.value)}
                          onBlur={() => {
                            if (openNewsTimePickerInputId === "create-news-recurring-start-time") {
                              setOpenNewsTimePickerInputId("");
                            }
                          }}
                        />
                        <button
                          type="button"
                          className="project-time-picker-btn"
                          onMouseDown={(event) => event.preventDefault()}
                          onClick={() => toggleNewsTimeInputPicker("create-news-recurring-start-time")}
                        >
                          {openNewsTimePickerInputId === "create-news-recurring-start-time" ? "بستن ساعت" : "انتخاب ساعت"}
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="project-wireframe-row-grid project-wireframe-extra-row">
                    <div className="project-create-field">
                      <label htmlFor="create-news-recurring-interval">اینتروال (ثانیه)</label>
                      <input
                        id="create-news-recurring-interval"
                        type="number"
                        min="1"
                        value={newsCreateForm.recurringIntervalSeconds}
                        onChange={(event) => handleCreateNewsFieldChange("recurringIntervalSeconds", event.target.value)}
                      />
                    </div>
                    <div className="project-create-field">
                      <label htmlFor="create-news-recurring-max-runs">حداکثر دفعات اجرا</label>
                      <input
                        id="create-news-recurring-max-runs"
                        type="number"
                        min="1"
                        value={newsCreateForm.recurringMaxRuns}
                        onChange={(event) => handleCreateNewsFieldChange("recurringMaxRuns", event.target.value)}
                      />
                    </div>
                  </div>
                </>
              )}
            </div>

          {newsCreateMsg.text && (
            <div className={`project-list-message ${newsCreateMsg.type}`}>{newsCreateMsg.text}</div>
          )}

          <div className="project-create-actions ads-create-actions">
            <button
              type="button"
              className="project-create-cancel-btn"
              onClick={handleCloseNewsCreate}
            >
              انصراف
            </button>
            <button type="submit" className="save-btn project-create-save-btn">
              {isNewsEditMode ? "ذخیره تغییرات" : "ایجاد خبر"}
            </button>
          </div>
          <div id="news-create-actions-empty-card-spacer" className="project-create-empty-card ads-create-actions-empty-card-spacer" aria-hidden="true" />
        </form>
      </div>
    );
  };

  const renderNewsViewTab = () => {
    const backToOriginTab = () => {
      const nextParams = new URLSearchParams(location.search);
      const returnTab = (
        String(nextParams.get("return_tab") || "").trim()
        || String(newsViewReturnTab || "").trim()
        || "news"
      );
      nextParams.delete("news_id");
      nextParams.delete("return_tab");
      nextParams.set("tab", returnTab);
      navigate({ pathname: location.pathname, search: `?${nextParams.toString()}` }, { replace: true });
      setActiveTab(returnTab);
    };
    const selectedRow = newsRows.find((row) => row.rowId === selectedNewsRowId);
    const hasSelectedNews = Boolean(selectedRow || newsViewForm.title);
    const baseNewsMediaUrl = normalizeNewsLink(
      newsViewForm.processedMediaUrl
      || newsViewForm.mediaUrl
      || newsViewForm.sourceMediaUrl
      || newsViewForm.publishLink
    );
    const extractedMediaUrl = normalizeNewsLink(newsViewForm.extractedMediaUrl || newsViewForm.sourceMediaUrl);
    const processedMediaUrl = normalizeNewsLink(newsViewForm.processedMediaUrl || newsViewForm.sourceMediaUrl);
    const draftMediaUrl = normalizeNewsLink(newsEditDraft.mediaPreviewUrl);
    const newsMediaUrl = isNewsEditing
      ? (draftMediaUrl || baseNewsMediaUrl)
      : baseNewsMediaUrl;
    const activeMediaType = isNewsEditing
      ? (newsEditDraft.mediaType || newsViewForm.mediaType)
      : newsViewForm.mediaType;
    const normalizedActiveMediaType = String(activeMediaType || "").trim().toLowerCase();
    const isVideoMedia = (
      normalizedActiveMediaType.includes("ویدئو")
      || normalizedActiveMediaType.includes("ویدیو")
      || normalizedActiveMediaType.includes("video")
    );
    const handleOpenNewsVideoPreview = async (target) => {
      const loaded = await ensureNewsVideoUrlsLoaded();
      if (!loaded) {
        return;
      }
      if (target === "preview") {
        setShowPreviewVideoPlayer(true);
      } else if (target === "extracted") {
        setShowExtractedVideoPlayer(true);
      } else if (target === "processed") {
        setShowProcessedVideoPlayer(true);
      }
      setNewsViewMessage({ type: "", text: "" });
    };

    if (isNewsViewLoading) {
      return (
        <div className="news-view-layout">
          <div className="projects-create-head news-view-topbar">
            <div>
              <h3>مشاهده خبر</h3>
            </div>
            <button
              type="button"
              className="projects-create-back-btn"
              onClick={backToOriginTab}
            >
              بازگشت
            </button>
          </div>
          <div className="project-list-message">در حال بارگذاری خبر...</div>
        </div>
      );
    }

    if (!hasSelectedNews) {
      return (
        <div className="news-view-layout">
          <div className="projects-create-head news-view-topbar">
            <div>
              <h3>مشاهده خبر</h3>
            </div>
            <button
              type="button"
              className="projects-create-back-btn"
              onClick={backToOriginTab}
            >
              بازگشت
            </button>
          </div>
          <div className="project-list-message error">
            خبر انتخاب‌شده پیدا نشد. از لیست اخبار یک خبر را انتخاب کنید.
          </div>
        </div>
      );
    }

    return (
      <div className="news-view-layout">
        <div className="projects-create-head news-view-topbar">
          <div>
            <h3>مشاهده خبر</h3>
          </div>
          <button
            type="button"
            className="projects-create-back-btn"
            onClick={backToOriginTab}
          >
            بازگشت
          </button>
        </div>

        <div className="news-view-card news-view-main-card">
          <div className="news-preview-head">
            <h4>پیشنمایش خبر</h4>
          </div>
          <article className="news-preview-card">
            <div className="news-view-media-wrap">
              <span className="news-media-type-badge">{getNewsMediaBadgeLabel(newsViewForm)}</span>
              {isVideoMedia ? (
                showPreviewVideoPlayer && newsMediaUrl ? (
                  <video controls src={newsMediaUrl} />
                ) : (
                  <button
                    type="button"
                    className="news-video-thumbnail-placeholder"
                    onClick={() => handleOpenNewsVideoPreview("preview")}
                    disabled={loadingNewsVideo}
                    aria-label="پخش پیش‌نمایش ویدیوی خبر"
                  >
                    <span className="news-video-thumbnail-play">▶</span>
                  </button>
                )
              ) : newsMediaUrl ? (
                (
                  <>
                    <img
                      src={newsMediaUrl}
                      alt={newsViewForm.title || "مدیای خبر"}
                      onError={(event) => handleMediaPreviewError(event, newsViewForm.sourceMediaUrl)}
                    />
                    <div className="news-view-media-placeholder hidden">مدیای ثبت شده برای این خبر موجود نیست</div>
                  </>
                )
              ) : (
                <div className="news-view-media-placeholder">مدیای ثبت شده برای این خبر موجود نیست</div>
              )}
            </div>
            <div className="news-preview-card-body">
              <h5>{newsViewForm.title || "بدون عنوان"}</h5>
              <p>{newsViewForm.fullContent || "متن خبر ثبت نشده است."}</p>
              <span className="news-preview-card-date">{newsViewForm.publishedAt || "--"}</span>
            </div>
          </article>
        </div>
        <div id="news-view-empty-card-spacer" className="project-create-empty-card news-view-empty-card-spacer" aria-hidden="true" />

        <div className="news-view-card">
          <h4>اطلاعات خبر</h4>
          <div className="project-create-grid project-create-grid-3">
            <div className="project-create-field">
              <label htmlFor="news-view-code">کد خبر</label>
              <input id="news-view-code" value={newsViewForm.code || "--"} readOnly />
            </div>
            <div className="project-create-field">
              <label htmlFor="news-view-received-at">زمان دریافت</label>
              <input id="news-view-received-at" value={newsViewForm.publishedAt || "--"} readOnly />
            </div>
            <div className="project-create-field">
              <label htmlFor="news-view-status">وضعیت خبر</label>
              <input id="news-view-status" value={newsViewForm.statusLabel || "--"} readOnly />
            </div>
            <div className="project-create-field">
              <label htmlFor="news-view-process-status">وضعیت پردازش</label>
              <input id="news-view-process-status" value="پردازش شده" readOnly />
            </div>
            <div className="project-create-field">
              <label htmlFor="news-view-published-at">زمان انتشار</label>
              <input id="news-view-published-at" value={newsViewForm.publishedAt || "--"} readOnly />
            </div>
            <div className="project-create-field">
              <label htmlFor="news-view-media-type">نوع مدیا</label>
              <input id="news-view-media-type" value={newsViewForm.mediaType || "--"} readOnly />
            </div>
            <div className="project-create-field">
              <label htmlFor="news-view-processed-at">زمان پردازش</label>
              <input id="news-view-processed-at" value={newsViewForm.publishedAt || "--"} readOnly />
            </div>
            <div className="project-create-field">
              <label htmlFor="news-view-type">نوع خبر</label>
              <input id="news-view-type" value="استخراج شده" readOnly />
            </div>
            <div className="project-create-field">
              <label htmlFor="news-view-project">پروژه</label>
              <input id="news-view-project" value={newsViewForm.projectName || "--"} readOnly />
            </div>
            <div className="project-create-field">
              <label htmlFor="news-view-link">بستر های انتشار</label>
              <input id="news-view-link" value={newsViewForm.publishLink || "--"} readOnly />
            </div>
            <div className="project-create-field">
              <label htmlFor="news-view-source">منابع</label>
              <input id="news-view-source" value={newsViewForm.sourceName || "--"} readOnly />
            </div>
          </div>
        </div>
        <div id="news-view-empty-card-spacer-2" className="project-create-empty-card news-view-empty-card-spacer" aria-hidden="true" />

        <div className="news-view-text-grid">
          <div className="news-view-card">
            <h4>متن استخراج شده خبر</h4>
            <textarea
              value={newsViewForm.extractedContent || "متن استخراج‌شده برای این خبر موجود نیست."}
              readOnly
              rows={6}
            />
          </div>
          <div className="news-view-card">
            <h4>متن پردازش شده خبر</h4>
            <textarea
              value={newsViewForm.processedContent || "متن پردازش‌شده برای این خبر موجود نیست."}
              readOnly
              rows={6}
            />
          </div>
        </div>
        <div id="news-view-empty-card-spacer-3" className="project-create-empty-card news-view-empty-card-spacer" aria-hidden="true" />

        <div className="news-view-media-grid">
          <div className="news-view-card">
            <h4>مدیای استخراج شده خبر</h4>
            <div className="news-view-media-wrap">
              {isVideoMedia ? (
                showExtractedVideoPlayer && extractedMediaUrl ? (
                  <video controls src={extractedMediaUrl} />
                ) : (
                  <button
                    type="button"
                    className="news-video-thumbnail-placeholder"
                    onClick={() => handleOpenNewsVideoPreview("extracted")}
                    disabled={loadingNewsVideo}
                    aria-label="پخش ویدیوی استخراج‌شده خبر"
                  >
                    <span className="news-video-thumbnail-play">▶</span>
                  </button>
                )
              ) : extractedMediaUrl ? (
                (
                  <>
                    <img
                      src={extractedMediaUrl}
                      alt="مدیای استخراج شده خبر"
                      onError={(event) => handleMediaPreviewError(event, newsViewForm.sourceMediaUrl)}
                    />
                    <div className="news-view-media-placeholder hidden">مدیای استخراج‌شده برای این خبر موجود نیست</div>
                  </>
                )
              ) : (
                <div className="news-view-media-placeholder">مدیای استخراج‌شده برای این خبر موجود نیست</div>
              )}
            </div>
          </div>
          <div className="news-view-card">
            <h4>مدیای پردازش شده خبر</h4>
            <div className="news-view-media-wrap">
              {isVideoMedia ? (
                showProcessedVideoPlayer && processedMediaUrl ? (
                  <video controls src={processedMediaUrl} />
                ) : (
                  <button
                    type="button"
                    className="news-video-thumbnail-placeholder"
                    onClick={() => handleOpenNewsVideoPreview("processed")}
                    disabled={loadingNewsVideo}
                    aria-label="پخش ویدیوی پردازش‌شده خبر"
                  >
                    <span className="news-video-thumbnail-play">▶</span>
                  </button>
                )
              ) : processedMediaUrl ? (
                (
                  <>
                    <img
                      src={processedMediaUrl}
                      alt="مدیای پردازش شده خبر"
                      onError={(event) => handleMediaPreviewError(event, newsViewForm.sourceMediaUrl)}
                    />
                    <div className="news-view-media-placeholder hidden">مدیای پردازش‌شده برای این خبر موجود نیست</div>
                  </>
                )
              ) : (
                <div className="news-view-media-placeholder">مدیای پردازش‌شده برای این خبر موجود نیست</div>
              )}
            </div>
          </div>
        </div>
        <div id="news-view-empty-card-spacer-4" className="project-create-empty-card news-view-empty-card-spacer" aria-hidden="true" />

        {newsViewMessage.text && (
          <div className={`project-list-message ${newsViewMessage.type}`}>{newsViewMessage.text}</div>
        )}
      </div>
    );
  };

  const sourcesPlatformOptions = Array.from(
    new Set(
      sourcesRows
        .map((row) => row.platform)
        .filter((platformValue) => platformValue && platformValue !== "--")
    )
  );
  const sourcesManagerOptions = Array.from(
    new Set(
      sourcesRows
        .map((row) => row.managerName)
        .filter((managerName) => managerName && managerName !== "--")
    )
  );
  const sourceCreatePlatformTypeOptions = Array.from(
    new Set(
      [
        "rss",
        "telegram",
        "rubika",
        "bale",
        "eita",
        "eita with video",
        "price",
      ]
        .map((platformValue) => String(platformValue || "").trim())
        .filter(Boolean)
    )
  );

  const publishSourcesPlatformOptions = Array.from(
    new Set(
      publishSourcesRows
        .map((row) => row.platform)
        .filter((platformValue) => platformValue && platformValue !== "--")
    )
  );
  const publishSourcesManagerOptions = Array.from(
    new Set(
      publishSourcesRows
        .map((row) => row.managerName)
        .filter((managerName) => managerName && managerName !== "--")
    )
  );
  const publishSourcePlatformTypeOptions = Array.from(
    new Set(
      [
        ...FIXED_PUBLISH_PLATFORM_NAME_OPTIONS.map((option) => option.value),
      ]
        .map((platformValue) => String(platformValue || "").trim())
        .filter(Boolean)
    )
  );

  useEffect(() => {
    if (sourcesManagerFilter === "all") {
      return;
    }

    if (!sourcesManagerOptions.includes(sourcesManagerFilter)) {
      setSourcesManagerFilter("all");
    }
  }, [sourcesManagerFilter, sourcesManagerOptions]);

  useEffect(() => {
    if (sourcesPlatformFilter === "all") {
      return;
    }

    if (!sourcesPlatformOptions.includes(sourcesPlatformFilter)) {
      setSourcesPlatformFilter("all");
    }
  }, [sourcesPlatformFilter, sourcesPlatformOptions]);

  useEffect(() => {
    if (publishSourcesManagerFilter === "all") {
      return;
    }

    if (!publishSourcesManagerOptions.includes(publishSourcesManagerFilter)) {
      setPublishSourcesManagerFilter("all");
    }
  }, [publishSourcesManagerFilter, publishSourcesManagerOptions]);

  useEffect(() => {
    if (publishSourcesPlatformFilter === "all") {
      return;
    }

    if (!publishSourcesPlatformOptions.includes(publishSourcesPlatformFilter)) {
      setPublishSourcesPlatformFilter("all");
    }
  }, [publishSourcesPlatformFilter, publishSourcesPlatformOptions]);

  const normalizedSourcesSearchTerm = sourcesSearchTerm.trim().toLowerCase();

  const filteredSourcesRows = sourcesRows.filter((row) => {
    if (sourcesStatusFilter !== "all" && row.statusKey !== sourcesStatusFilter) {
      return false;
    }

    if (sourcesManagerFilter !== "all" && row.managerName !== sourcesManagerFilter) {
      return false;
    }

    if (sourcesPlatformFilter !== "all" && row.platform !== sourcesPlatformFilter) {
      return false;
    }

    if (!normalizedSourcesSearchTerm) {
      return true;
    }

    const searchableText = [
      row.code,
      row.sourceName,
      row.platform,
      row.sourceLink,
    ]
      .filter((value) => value)
      .join(" ")
      .toLowerCase();

    return searchableText.includes(normalizedSourcesSearchTerm);
  });

  const normalizedPublishSourcesSearchTerm = publishSourcesSearchTerm.trim().toLowerCase();

  const filteredPublishSourcesRows = publishSourcesRows.filter((row) => {
    if (publishSourcesStatusFilter !== "all" && row.statusKey !== publishSourcesStatusFilter) {
      return false;
    }

    if (publishSourcesManagerFilter !== "all" && row.managerName !== publishSourcesManagerFilter) {
      return false;
    }

    if (publishSourcesPlatformFilter !== "all" && row.platform !== publishSourcesPlatformFilter) {
      return false;
    }

    if (!normalizedPublishSourcesSearchTerm) {
      return true;
    }

    const searchableText = [
      row.code,
      row.sourceName,
      row.platform,
      row.sourceLink,
    ]
      .filter((value) => value)
      .join(" ")
      .toLowerCase();

    return searchableText.includes(normalizedPublishSourcesSearchTerm);
  });

  const sourcesRowsLimit = Number(sourcesRowsPerPage) || SOURCE_ROWS_PER_PAGE_OPTIONS[0];
  const totalSourcesPages = Math.max(1, Math.ceil(filteredSourcesRows.length / sourcesRowsLimit));
  const currentSourcesPage = Math.min(sourcesPage, totalSourcesPages);
  const sourcesStartIndex = (currentSourcesPage - 1) * sourcesRowsLimit;
  const paginatedSourcesRows = filteredSourcesRows.slice(
    sourcesStartIndex,
    sourcesStartIndex + sourcesRowsLimit
  );
  const paginatedSourceIds = paginatedSourcesRows.map((row) => row.rowId);
  const paginatedDeletableSourceIds = paginatedSourcesRows
    .filter((row) => row.canDelete)
    .map((row) => row.rowId);
  const areAllVisibleSourcesSelected = (
    paginatedDeletableSourceIds.length > 0
    && paginatedDeletableSourceIds.every((rowId) => selectedSourceIds.includes(rowId))
  );

  const publishSourcesRowsLimit = Number(publishSourcesRowsPerPage) || SOURCE_ROWS_PER_PAGE_OPTIONS[0];
  const totalPublishSourcesPages = Math.max(1, Math.ceil(filteredPublishSourcesRows.length / publishSourcesRowsLimit));
  const currentPublishSourcesPage = Math.min(publishSourcesPage, totalPublishSourcesPages);
  const publishSourcesStartIndex = (currentPublishSourcesPage - 1) * publishSourcesRowsLimit;
  const paginatedPublishSourcesRows = filteredPublishSourcesRows.slice(
    publishSourcesStartIndex,
    publishSourcesStartIndex + publishSourcesRowsLimit
  );
  const paginatedPublishSourceIds = paginatedPublishSourcesRows.map((row) => row.rowId);
  const areAllVisiblePublishSourcesSelected = (
    paginatedPublishSourceIds.length > 0
    && paginatedPublishSourceIds.every((rowId) => selectedPublishSourceIds.includes(rowId))
  );

  const handleSourceRowSelection = (rowId) => {
    const selectedRow = sourcesRows.find((row) => row.rowId === rowId);
    if (!selectedRow || !selectedRow.canDelete) {
      return;
    }
    setSelectedSourceIds((prevSelectedSourceIds) => (
      prevSelectedSourceIds.includes(rowId)
        ? prevSelectedSourceIds.filter((selectedRowId) => selectedRowId !== rowId)
        : [...prevSelectedSourceIds, rowId]
    ));
  };

  const handleSelectAllVisibleSources = () => {
    if (!paginatedDeletableSourceIds.length) {
      return;
    }

    setSelectedSourceIds((prevSelectedSourceIds) => {
      const areAllSelected = paginatedDeletableSourceIds.every((rowId) => (
        prevSelectedSourceIds.includes(rowId)
      ));

      if (areAllSelected) {
        return prevSelectedSourceIds.filter((rowId) => !paginatedDeletableSourceIds.includes(rowId));
      }

      return Array.from(new Set([...prevSelectedSourceIds, ...paginatedDeletableSourceIds]));
    });
  };

  const handleSourcesPageChange = (direction) => {
    if (direction === "next") {
      setSourcesPage((prevPage) => Math.min(totalSourcesPages, prevPage + 1));
      return;
    }

    setSourcesPage((prevPage) => Math.max(1, prevPage - 1));
  };

  const handlePublishSourceRowSelection = (rowId) => {
    setSelectedPublishSourceIds((prevSelectedSourceIds) => (
      prevSelectedSourceIds.includes(rowId)
        ? prevSelectedSourceIds.filter((selectedRowId) => selectedRowId !== rowId)
        : [...prevSelectedSourceIds, rowId]
    ));
  };

  const handleSelectAllVisiblePublishSources = () => {
    if (!paginatedPublishSourceIds.length) {
      return;
    }

    setSelectedPublishSourceIds((prevSelectedSourceIds) => {
      const areAllSelected = paginatedPublishSourceIds.every((rowId) => (
        prevSelectedSourceIds.includes(rowId)
      ));

      if (areAllSelected) {
        return prevSelectedSourceIds.filter((rowId) => !paginatedPublishSourceIds.includes(rowId));
      }

      return Array.from(new Set([...prevSelectedSourceIds, ...paginatedPublishSourceIds]));
    });
  };

  const handlePublishSourcesPageChange = (direction) => {
    if (direction === "next") {
      setPublishSourcesPage((prevPage) => Math.min(totalPublishSourcesPages, prevPage + 1));
      return;
    }

    setPublishSourcesPage((prevPage) => Math.max(1, prevPage - 1));
  };

  const handleSourceCreateFieldChange = (field, value) => {
    setSourceCreateForm((prevForm) => {
      const nextForm = {
        ...prevForm,
        [field]: value,
      };
      if (field === "source_platform") {
        const normalizedPlatform = normalizeReceivePlatformValue(value);
        if (normalizedPlatform === "price") {
          nextForm.source_link = "";
          nextForm.receive_interval_seconds = "3000";
          nextForm.rubika_account_id = "";
          nextForm.eita_account_id = "";
          nextForm.anti_ad_mode = "none";
          nextForm.anti_ad_with_ai_prompt = "";
          nextForm.anti_ad_with_system_rules = [];
        } else if (normalizeReceivePlatformValue(prevForm.source_platform) === "price") {
          nextForm.price_template_id = "";
          nextForm.receive_interval_seconds = "60";
        }
      }
      return nextForm;
    });
  };

  const handleAntiAdSystemRuleChange = (index, field, value) => {
    setSourceCreateForm((prevForm) => {
      const currentRules = Array.isArray(prevForm.anti_ad_with_system_rules)
        ? [...prevForm.anti_ad_with_system_rules]
        : [];
      while (currentRules.length <= index) {
        currentRules.push({
          field_1: "",
          field_2: "",
          field_3: "",
          field_4: "",
          selected_flag: "is_ad",
        });
      }
      const currentRule = currentRules[index] && typeof currentRules[index] === "object"
        ? currentRules[index]
        : {
          field_1: "",
          field_2: "",
          field_3: "",
          field_4: "",
          selected_flag: "is_ad",
        };
      currentRules[index] = {
        ...currentRule,
        [field]: value,
      };
      return {
        ...prevForm,
        anti_ad_with_system_rules: currentRules,
      };
    });
  };

  const handleAddAntiAdSystemRule = () => {
    setSourceCreateForm((prevForm) => ({
      ...prevForm,
      anti_ad_with_system_rules: [
        ...(Array.isArray(prevForm.anti_ad_with_system_rules) ? prevForm.anti_ad_with_system_rules : []),
        {
          field_1: "",
          field_2: "",
          field_3: "",
          field_4: "",
          selected_flag: "is_ad",
        },
      ],
    }));
  };

  const handleRemoveAntiAdSystemRule = (index) => {
    setSourceCreateForm((prevForm) => {
      const currentRules = Array.isArray(prevForm.anti_ad_with_system_rules)
        ? [...prevForm.anti_ad_with_system_rules]
        : [];
      const nextRules = currentRules.filter((_, itemIndex) => itemIndex !== index);
      return {
        ...prevForm,
        anti_ad_with_system_rules: nextRules.length ? nextRules : [
          {
            field_1: "",
            field_2: "",
            field_3: "",
            field_4: "",
            selected_flag: "is_ad",
          },
        ],
      };
    });
  };

  const handlePriceTemplateFieldChange = (field, value) => {
    setPriceTemplateForm((prevForm) => ({
      ...prevForm,
      [field]: value,
    }));
  };

  const handleEditPriceTemplate = (templateRow) => {
    if (!templateRow) {
      return;
    }
    setEditingPriceTemplateId(String(templateRow.id || ""));
    setPriceTemplateForm({
      title: templateRow.title || "",
      news_title_template: templateRow.newsTitleTemplate || DEFAULT_PRICE_NEWS_TITLE_TEMPLATE,
      text_template: templateRow.textTemplate || DEFAULT_PRICE_TEXT_TEMPLATE,
      is_active: Boolean(templateRow.isActive),
      receive_source_ids: Array.isArray(templateRow.receiveSourceIds) ? templateRow.receiveSourceIds : [],
    });
  };

  const handleResetPriceTemplateForm = () => {
    setEditingPriceTemplateId("");
    setPriceTemplateForm(createInitialPriceTemplateForm());
  };

  const handleSavePriceTemplate = async () => {
    const title = String(priceTemplateForm.title || "").trim();
    const newsTitleTemplate = String(priceTemplateForm.news_title_template || "").trim();
    const textTemplate = String(priceTemplateForm.text_template || "").trim();
    if (!title || !newsTitleTemplate || !textTemplate) {
      setSourcesMsg({ type: "error", text: "عنوان قالب، قالب عنوان خبر و متن قالب قیمت الزامی است." });
      return;
    }
    const payload = {
      title,
      news_title_template: newsTitleTemplate,
      text_template: textTemplate,
      is_active: Boolean(priceTemplateForm.is_active),
      receive_source_ids: Array.isArray(priceTemplateForm.receive_source_ids)
        ? priceTemplateForm.receive_source_ids
          .map((value) => Number(value))
          .filter((value) => Number.isInteger(value) && value > 0)
        : [],
    };
    setSavingPriceTemplate(true);
    try {
      if (editingPriceTemplateId) {
        await priceTemplatesApi.update(editingPriceTemplateId, payload);
      } else {
        await priceTemplatesApi.create(payload);
      }
      await fetchPriceTemplates();
      handleResetPriceTemplateForm();
      setSourcesMsg({ type: "success", text: "قالب قیمت با موفقیت ذخیره شد." });
    } catch (error) {
      setSourcesMsg({ type: "error", text: error.message || "خطا در ذخیره قالب قیمت." });
    } finally {
      setSavingPriceTemplate(false);
    }
  };

  const handleDeletePriceTemplate = async (templateId) => {
    if (!templateId || savingPriceTemplate) {
      return;
    }
    setSavingPriceTemplate(true);
    try {
      await priceTemplatesApi.delete(templateId);
      await fetchPriceTemplates();
      if (String(editingPriceTemplateId) === String(templateId)) {
        handleResetPriceTemplateForm();
      }
      setSourcesMsg({ type: "success", text: "قالب قیمت حذف شد." });
    } catch (error) {
      setSourcesMsg({ type: "error", text: error.message || "خطا در حذف قالب قیمت." });
    } finally {
      setSavingPriceTemplate(false);
    }
  };

  const openSourceCreatePanel = () => {
    setShowSourceCreateForm(true);
    setEditingSourceRowId("");
    setSourceCreateForm(createInitialSourceCreateForm());
    setSourcesMsg({ type: "", text: "" });
  };

  const closeSourceCreatePanel = () => {
    if (savingSource) {
      return;
    }

    setShowSourceCreateForm(false);
    setEditingSourceRowId("");
    setSourceCreateForm(createInitialSourceCreateForm());
  };

  const handleSourceCreateSubmit = async (event) => {
    event.preventDefault();

    const isEditMode = Boolean(editingSourceRowId);
    const sourceName = String(sourceCreateForm.source_name || "").trim();
    const sourcePlatform = (
      normalizeReceivePlatformValue(sourceCreateForm.source_platform)
      || "rss"
    );
    const isPricePlatform = sourcePlatform === "price";
    const sourceLink = isPricePlatform ? "" : String(sourceCreateForm.source_link || "").trim();
    if (!sourceName || (!isPricePlatform && !sourceLink)) {
      setSourcesMsg({
        type: "error",
        text: "عنوان منبع و لینک یا شناسه الزامی است.",
      });
      return;
    }

    const selectedRubikaAccountId = String(sourceCreateForm.rubika_account_id || "").trim();
    const selectedEitaAccountId = String(sourceCreateForm.eita_account_id || "").trim();
    if (sourcePlatform === "eita with video" && !selectedEitaAccountId) {
      setSourcesMsg({ type: "error", text: "برای دریافت ایتا با ویدئو، انتخاب اکانت ایتا الزامی است." });
      return;
    }

    setSavingSource(true);
    const antiAdMode = String(sourceCreateForm.anti_ad_mode || "none").trim().toLowerCase();
    const antiAdRules = (
      Array.isArray(sourceCreateForm.anti_ad_with_system_rules)
        ? sourceCreateForm.anti_ad_with_system_rules
        : []
    ).map((rule) => {
      if (!rule || typeof rule !== "object") {
        return null;
      }
      const normalizedRule = {
        field_1: String(rule.field_1 || "").trim(),
        field_2: String(rule.field_2 || "").trim(),
        field_3: String(rule.field_3 || "").trim(),
        field_4: String(rule.field_4 || "").trim(),
        selected_flag: String(rule.selected_flag || "is_ad").trim() || "is_ad",
        match_mode: String(rule.match_mode || "contains").trim() || "contains",
      };
      if (!normalizedRule.field_1 && !normalizedRule.field_2 && !normalizedRule.field_3 && !normalizedRule.field_4) {
        return null;
      }
      return normalizedRule;
    }).filter(Boolean);

    try {
      const receiveIntervalSeconds = Number.parseInt(String(sourceCreateForm.receive_interval_seconds || "").trim(), 10);
      if (!Number.isFinite(receiveIntervalSeconds) || receiveIntervalSeconds <= 0) {
        throw new Error("مقدار زمان بررسی منبع دریافت باید یک عدد صحیح بزرگتر از صفر باشد.");
      }
      const payload = {
        name: sourceName,
        platform: sourcePlatform,
        link: sourceLink,
        price_template_id: isPricePlatform
          ? (
            String(sourceCreateForm.price_template_id || "").trim()
              ? Number(sourceCreateForm.price_template_id)
              : (isEditMode ? undefined : null)
          )
          : null,
        receive_interval_seconds: receiveIntervalSeconds,
        receive_source_type: sourcePlatform === "telegram" || sourcePlatform === "rubika" || sourcePlatform === "bale" || sourcePlatform === "eita" || sourcePlatform === "eita with video" || sourcePlatform === "price" ? "dynamic" : "static",
        rubika_account_id: sourcePlatform === "rubika" && selectedRubikaAccountId ? Number(selectedRubikaAccountId) : null,
        eita_account_id: sourcePlatform === "eita with video" && selectedEitaAccountId ? Number(selectedEitaAccountId) : null,
        anti_ad_with_ai: !isPricePlatform && antiAdMode === "ai",
        anti_ad_with_system: !isPricePlatform && antiAdMode === "system",
        anti_ad_with_system_rules: !isPricePlatform && antiAdMode === "system" ? antiAdRules : [],
        anti_ad_with_ai_prompt: !isPricePlatform && antiAdMode === "ai"
          ? String(sourceCreateForm.anti_ad_with_ai_prompt || "").trim()
          : "",
      };

      if (isEditMode) {
        const editingRow = sourcesRows.find((row) => row.rowId === editingSourceRowId);
        if (!editingRow?.id) {
          throw new Error("شناسه منبع برای ویرایش یافت نشد.");
        }
        await sourcesApi.updateReceive(editingRow.id, payload);
      } else {
        await sourcesApi.createReceive(payload);
      }

      await fetchReceiveSources();
      await fetchPriceTemplates();

      setShowSourceCreateForm(false);
      setEditingSourceRowId("");
      setSourceCreateForm(createInitialSourceCreateForm());
      setSourcesMsg({
        type: "success",
        text: isEditMode
          ? `تغییرات منبع ${sourceName} با موفقیت ذخیره شد.`
          : `منبع ${sourceName} با موفقیت ذخیره شد.`,
      });
    } catch (error) {
      setSourcesMsg({
        type: "error",
        text: error.message || "ذخیره منبع ناموفق بود.",
      });
    } finally {
      setSavingSource(false);
    }
  };

  const handleCreateSource = () => {
    openSourceCreatePanel();
  };

  const handlePublishSourceCreateFieldChange = (field, value) => {
    setPublishSourceCreateForm((prevForm) => ({
      ...prevForm,
      [field]: value,
    }));
  };

  const openPublishSourceCreatePanel = () => {
    setShowPublishSourceCreateForm(true);
    setEditingPublishSourceRowId("");
    setPublishSourceCreateForm(createInitialPublishSourceCreateForm());
    setPublishSourcesMsg({ type: "", text: "" });
  };

  const closePublishSourceCreatePanel = () => {
    if (savingPublishSource) {
      return;
    }

    setShowPublishSourceCreateForm(false);
    setEditingPublishSourceRowId("");
    setPublishSourceCreateForm(createInitialPublishSourceCreateForm());
  };

  const handlePublishSourceCreateSubmit = async (event) => {
    event.preventDefault();

    const isEditMode = Boolean(editingPublishSourceRowId);
    const sourceName = String(publishSourceCreateForm.source_name || "").trim();
    const sourceLink = normalizePublishSourceLinkByPlatform({
      platform: publishSourceCreateForm.source_platform,
      link: publishSourceCreateForm.source_link,
    });

    if (!sourceName || !sourceLink) {
      setPublishSourcesMsg({
        type: "error",
        text: "عنوان منبع و لینک یا شناسه الزامی است.",
      });
      return;
    }

    if (!sourceLink.startsWith("@")) {
      setPublishSourcesMsg({
        type: "error",
        text: "لینک یا شناسه منبع انتشار باید با @ شروع شود.",
      });
      return;
    }

    setSavingPublishSource(true);

    const sourcePlatform = normalizePublishPlatformValue(
      String(publishSourceCreateForm.source_platform || "").trim()
      || String(publishSourceCreateForm.source_platform_name || "").trim()
      || FIXED_PUBLISH_PLATFORM_NAME_OPTIONS[0]?.value
      || "rubika"
    );

    try {
      const payload = {
        name: sourceName,
        platform: sourcePlatform,
        link: sourceLink,
      };

      if (isEditMode) {
        const editingRow = publishSourcesRows.find((row) => row.rowId === editingPublishSourceRowId);
        if (!editingRow?.id) {
          throw new Error("شناسه منبع انتشار برای ویرایش یافت نشد.");
        }
        await sourcesApi.updatePublish(editingRow.id, payload);
      } else {
        await sourcesApi.createPublish(payload);
      }

      await fetchPublishSources();

      setShowPublishSourceCreateForm(false);
      setEditingPublishSourceRowId("");
      setPublishSourceCreateForm(createInitialPublishSourceCreateForm());
      setPublishSourcesMsg({
        type: "success",
        text: isEditMode
          ? `تغییرات منبع انتشار ${sourceName} با موفقیت ذخیره شد.`
          : `منبع انتشار ${sourceName} با موفقیت ذخیره شد.`,
      });
    } catch (error) {
      setPublishSourcesMsg({
        type: "error",
        text: error.message || "ذخیره منبع انتشار ناموفق بود.",
      });
    } finally {
      setSavingPublishSource(false);
    }
  };

  const handleDeleteSelectedPublishSources = async () => {
    if (!selectedPublishSourceIds.length) {
      setPublishSourcesMsg({
        type: "error",
        text: "برای حذف، حداقل یک منبع انتشار را انتخاب کنید.",
      });
      return;
    }

    const sourceIds = publishSourcesRows
      .filter((row) => selectedPublishSourceIds.includes(row.rowId))
      .map((row) => Number(row.id))
      .filter((idValue) => Number.isFinite(idValue) && idValue > 0);

    if (!sourceIds.length) {
      setPublishSourcesMsg({
        type: "error",
        text: "شناسه منابع انتشار برای حذف معتبر نیست.",
      });
      return;
    }

    try {
      await sourcesApi.bulkDeletePublish(sourceIds);
      await fetchPublishSources();
      setPublishSourcesMsg({
        type: "success",
        text: `${formatFaNumber(sourceIds.length)} منبع انتشار از لیست حذف شد.`,
      });
      setSelectedPublishSourceIds([]);
    } catch (error) {
      setPublishSourcesMsg({
        type: "error",
        text: error.message || "حذف منابع انتشار ناموفق بود.",
      });
    }
  };

  const openPublishSourcesDeleteModal = () => {
    if (!selectedPublishSourceIds.length) {
      setPublishSourcesMsg({
        type: "error",
        text: "برای حذف، حداقل یک منبع انتشار را انتخاب کنید.",
      });
      return;
    }

    setPublishSourcesMsg({ type: "", text: "" });
    setIsPublishSourcesDeleteModalOpen(true);
  };

  const closePublishSourcesDeleteModal = () => {
    if (deletingPublishSources) {
      return;
    }

    setIsPublishSourcesDeleteModalOpen(false);
  };

  const confirmDeleteSelectedPublishSources = async () => {
    setDeletingPublishSources(true);
    await handleDeleteSelectedPublishSources();
    setIsPublishSourcesDeleteModalOpen(false);
    setDeletingPublishSources(false);
  };

  const handleEditPublishSource = (rowId) => {
    const sourceRow = publishSourcesRows.find((row) => row.rowId === rowId);

    if (!sourceRow) {
      setPublishSourcesMsg({
        type: "error",
        text: "منبع انتشار انتخاب‌شده پیدا نشد.",
      });
      return;
    }

    setEditingPublishSourceRowId(sourceRow.rowId);
    const initialPlatform = normalizePublishPlatformValue(sourceRow.platform)
      || FIXED_PUBLISH_PLATFORM_NAME_OPTIONS[0]?.value
      || "rubika";
    setPublishSourceCreateForm({
      source_name: sourceRow.sourceName || "",
      source_platform_name: initialPlatform,
      source_platform: initialPlatform,
      source_link: sourceRow.sourceLink === "--" ? "" : (sourceRow.sourceLink || ""),
      received_news_count: String(sourceRow.receivedNewsCount ?? 0),
    });
    setShowPublishSourceCreateForm(true);
    setPublishSourcesMsg({ type: "", text: "" });
  };

  const handleDeleteSelectedSources = async () => {
    if (!selectedSourceIds.length) {
      setSourcesMsg({
        type: "error",
        text: "برای حذف، حداقل یک منبع را انتخاب کنید.",
      });
      return;
    }

    const sourceIds = sourcesRows
      .filter((row) => selectedSourceIds.includes(row.rowId) && row.canDelete)
      .map((row) => Number(row.id))
      .filter((idValue) => Number.isFinite(idValue) && idValue > 0);

    if (!sourceIds.length) {
      setSourcesMsg({
        type: "error",
        text: "شناسه منابع برای حذف معتبر نیست.",
      });
      return;
    }

    try {
      await sourcesApi.bulkDeleteReceive(sourceIds);
      await fetchReceiveSources();
      setSourcesMsg({
        type: "success",
        text: `${formatFaNumber(sourceIds.length)} منبع از لیست حذف شد.`,
      });
      setSelectedSourceIds([]);
    } catch (error) {
      setSourcesMsg({
        type: "error",
        text: error.message || "حذف منابع ناموفق بود.",
      });
    }
  };

  const openSourcesDeleteModal = () => {
    if (!selectedSourceIds.length) {
      setSourcesMsg({
        type: "error",
        text: "برای حذف، حداقل یک منبع را انتخاب کنید.",
      });
      return;
    }

    setSourcesMsg({ type: "", text: "" });
    setIsSourcesDeleteModalOpen(true);
  };

  const closeSourcesDeleteModal = () => {
    if (deletingSources) {
      return;
    }

    setIsSourcesDeleteModalOpen(false);
  };

  const confirmDeleteSelectedSources = async () => {
    setDeletingSources(true);
    await handleDeleteSelectedSources();
    setIsSourcesDeleteModalOpen(false);
    setDeletingSources(false);
  };

  const handleEditSource = (rowId) => {
    const sourceRow = sourcesRows.find((row) => row.rowId === rowId);

    if (!sourceRow) {
      setSourcesMsg({
        type: "error",
        text: "منبع انتخاب‌شده پیدا نشد.",
      });
      return;
    }
    if (!sourceRow.canEdit) {
      setSourcesMsg({
        type: "error",
        text: "منبع استاتیک قابل ویرایش نیست.",
      });
      return;
    }

    setEditingSourceRowId(sourceRow.rowId);
    setSourceCreateForm({
      source_name: sourceRow.sourceName || "",
      source_platform: normalizeReceivePlatformValue(sourceRow.platform) || "rss",
      source_link: normalizeReceivePlatformValue(sourceRow.platform) === "price" ? "" : (sourceRow.sourceLink === "--" ? "" : (sourceRow.sourceLink || "")),
      price_template_id: normalizeReceivePlatformValue(sourceRow.platform) === "price" ? String(sourceRow.priceTemplateId || "") : "",
      receive_interval_seconds: String(sourceRow.receiveIntervalSeconds || (normalizeReceivePlatformValue(sourceRow.platform) === "price" ? 3000 : 60)),
      rubika_account_id: normalizeReceivePlatformValue(sourceRow.platform) === "price" ? "" : String(sourceRow.rubikaAccountId || ""),
      eita_account_id: normalizeReceivePlatformValue(sourceRow.platform) === "price" ? "" : String(sourceRow.eitaAccountId || ""),
      anti_ad_mode: normalizeReceivePlatformValue(sourceRow.platform) === "price" ? "none" : String(sourceRow.antiAdMode || "none"),
      anti_ad_with_ai_prompt: normalizeReceivePlatformValue(sourceRow.platform) === "price" ? "" : String(sourceRow.antiAdWithAiPrompt || ""),
      anti_ad_with_system_rules: (
        Array.isArray(sourceRow.antiAdWithSystemRules) && sourceRow.antiAdWithSystemRules.length
          ? sourceRow.antiAdWithSystemRules
          : [{
            field_1: "",
            field_2: "",
            field_3: "",
            field_4: "",
            selected_flag: "is_ad",
          }]
      ),
    });
    setShowSourceCreateForm(true);
    setSourcesMsg({ type: "", text: "" });
  };

  const adsTopicOptions = publishSourcesOptions
    .map((row) => ({
      value: String(row?.id || "").trim(),
      label: String(row?.name || "").trim(),
    }))
    .filter((row) => row.value && row.label);

  const adsManagerOptions = Array.from(
    new Set(
      adsRows
        .map((row) => row.managerName)
        .filter((managerName) => managerName && managerName !== "--")
    )
  );

  useEffect(() => {
    if (adsTopicFilter === "all") {
      return;
    }

    if (!adsTopicOptions.some((option) => option.value === adsTopicFilter)) {
      setAdsTopicFilter("all");
    }
  }, [adsTopicFilter, adsTopicOptions]);

  useEffect(() => {
    if (adsManagerFilter === "all") {
      return;
    }

    if (!adsManagerOptions.includes(adsManagerFilter)) {
      setAdsManagerFilter("all");
    }
  }, [adsManagerFilter, adsManagerOptions]);

  const adsRowsLimit = Number(adsRowsPerPage) || ADS_ROWS_PER_PAGE_OPTIONS[0];
  const totalAdsPages = Math.max(1, Math.ceil(adsTotalCount / adsRowsLimit));
  const currentAdsPage = Math.min(adsPage, totalAdsPages);
  const paginatedAdsRows = adsRows;
  const paginatedAdIds = paginatedAdsRows.map((row) => row.rowId);
  const areAllVisibleAdsSelected = (
    paginatedAdIds.length > 0
    && paginatedAdIds.every((rowId) => selectedAdIds.includes(rowId))
  );

  const handleAdRowSelection = (rowId) => {
    setSelectedAdIds((prevSelectedAdIds) => (
      prevSelectedAdIds.includes(rowId)
        ? prevSelectedAdIds.filter((selectedRowId) => selectedRowId !== rowId)
        : [...prevSelectedAdIds, rowId]
    ));
  };

  const handleSelectAllVisibleAds = () => {
    if (!paginatedAdIds.length) {
      return;
    }

    setSelectedAdIds((prevSelectedAdIds) => {
      const areAllSelected = paginatedAdIds.every((rowId) => prevSelectedAdIds.includes(rowId));

      if (areAllSelected) {
        return prevSelectedAdIds.filter((rowId) => !paginatedAdIds.includes(rowId));
      }

      return Array.from(new Set([...prevSelectedAdIds, ...paginatedAdIds]));
    });
  };

  const handleAdsPageChange = (direction) => {
    if (direction === "next") {
      setAdsPage((prevPage) => Math.min(totalAdsPages, prevPage + 1));
      return;
    }

    setAdsPage((prevPage) => Math.max(1, prevPage - 1));
  };

  const handleCreateAd = () => {
    setAdsCreateMsg({ type: "", text: "" });
    setNewsCreateForm((prevForm) => {
      revokeObjectPreviewUrl(prevForm.mediaPreviewUrl);
      return createInitialNewsCreateForm();
    });
    setNewsWatermarkSettings(createInitialNewsMediaSettings());
    setNewsCreateExtraPublishTargets([]);
    setNewsCreateSelectedProjectIds([]);
    setNewsRemovedProjectSourceIds([]);
    setNewsCreateScheduledPublishRows([createNewsScheduledPublishRow()]);
    setNewsFormMode("create");
    setEditingNewsRowId("");
    setActiveTab("ads-create");
  };

  const handleCloseAdsCreate = () => {
    setAdsCreateMsg({ type: "", text: "" });
    setNewsCreateForm((prevForm) => {
      revokeObjectPreviewUrl(prevForm.mediaPreviewUrl);
      return createInitialNewsCreateForm();
    });
    setNewsWatermarkSettings(createInitialNewsMediaSettings());
    setNewsCreateExtraPublishTargets([]);
    setNewsCreateSelectedProjectIds([]);
    setNewsRemovedProjectSourceIds([]);
    setNewsCreateScheduledPublishRows([createNewsScheduledPublishRow()]);
    setNewsFormMode("create");
    setEditingNewsRowId("");
    setActiveTab("ads");
  };

  const handleDeleteSelectedAds = async () => {
    if (!selectedAdIds.length) {
      setAdsMsg({
        type: "error",
        text: "برای حذف، حداقل یک تبلیغ را انتخاب کنید.",
      });
      return;
    }

    const selectedRows = adsRows.filter((row) => selectedAdIds.includes(row.rowId));
    const adIds = selectedRows
      .map((row) => Number(row.id))
      .filter((rowId) => Number.isFinite(rowId) && rowId > 0);

    if (!adIds.length) {
      setAdsMsg({
        type: "error",
        text: "آیتم‌های انتخاب‌شده هنوز در بک‌اند ثبت نشده‌اند.",
      });
      return;
    }

    try {
      await newsApi.bulkDelete(adIds);
      setAdsRows((prevAdsRows) => (
        prevAdsRows.filter((row) => !selectedAdIds.includes(row.rowId))
      ));
      setAdsMsg({
        type: "success",
        text: `${formatFaNumber(adIds.length)} تبلیغ حذف شد.`,
      });
      setSelectedAdIds([]);
    } catch (error) {
      setAdsMsg({
        type: "error",
        text: error.message || "حذف تبلیغات ناموفق بود.",
      });
    }
  };

  const openAdsDeleteModal = () => {
    if (!selectedAdIds.length) {
      setAdsMsg({
        type: "error",
        text: "برای حذف، حداقل یک تبلیغ را انتخاب کنید.",
      });
      return;
    }

    setAdsMsg({ type: "", text: "" });
    setIsAdsDeleteModalOpen(true);
  };

  const closeAdsDeleteModal = () => {
    if (deletingAds) {
      return;
    }

    setIsAdsDeleteModalOpen(false);
  };

  const confirmDeleteSelectedAds = async () => {
    setDeletingAds(true);
    await handleDeleteSelectedAds();
    setIsAdsDeleteModalOpen(false);
    setDeletingAds(false);
  };

  const handleEditAd = (adRow) => {
    handleEditNewsRow(adRow, { destinationTab: "ads-create" });
  };

  const handleOpenAdView = async (adRow) => {
    if (!adRow?.rowId) {
      return;
    }

    setSelectedAdRowId(adRow.rowId);
    setSelectedAdDetails(adRow);
    setActiveTab("ads-view");
    if (adRow?.id) {
      await fetchAdDetails(adRow.id);
    }
  };

  const usersRoleOptions = Array.from(
    new Set(
      usersRows
        .map((user) => user.role)
        .filter(Boolean)
    )
  );
  const usersManagerOptions = Array.from(
    new Set(
      usersRows
        .map((user) => user.managerName)
        .filter((managerName) => managerName && managerName !== "--")
    )
  );

  const usersProjectOptions = Array.from(
    new Set(
      usersRows
        .flatMap((user) => (
          Array.isArray(user.projects) && user.projects.length
            ? user.projects
            : [user.projectTitle]
        ))
        .filter(Boolean)
    )
  );
  const createUserProjectOptions = Array.from(
    new Set(
      [
        ...projects.map((project) => String(project?.title || "").trim()),
        ...usersProjectOptions,
      ]
        .filter(Boolean)
        .filter((title) => title !== "all")
    )
  );
  const userFormRoleOptions = USER_CREATE_ROLE_OPTIONS;
  const userFormStatusOptions = USER_STATUS_FILTER_OPTIONS.filter(
    (option) => option.value !== "all"
  );

  const getNextUserCreateProject = (selectedProjects = []) => (
    createUserProjectOptions.find((projectTitle) => !selectedProjects.includes(projectTitle))
    || createUserProjectOptions[0]
    || ""
  );

  useEffect(() => {
    if (usersRoleFilter === "all") {
      return;
    }

    if (!usersRoleOptions.includes(usersRoleFilter)) {
      setUsersRoleFilter("all");
    }
  }, [usersRoleFilter, usersRoleOptions]);

  useEffect(() => {
    if (usersProjectFilter === "all") {
      return;
    }

    if (!usersProjectOptions.includes(usersProjectFilter)) {
      setUsersProjectFilter("all");
    }
  }, [usersProjectFilter, usersProjectOptions]);

  useEffect(() => {
    if (usersManagerFilter === "all") {
      return;
    }

    if (!usersManagerOptions.includes(usersManagerFilter)) {
      setUsersManagerFilter("all");
    }
  }, [usersManagerFilter, usersManagerOptions]);

  useEffect(() => {
    if (!createUserProjectOptions.length) {
      if (userCreateSelectedProject) {
        setUserCreateSelectedProject("");
      }
      return;
    }

    if (createUserProjectOptions.includes(userCreateSelectedProject)) {
      return;
    }

    const nextProject = createUserProjectOptions.find(
      (projectTitle) => !userCreateForm.projects.includes(projectTitle)
    ) || createUserProjectOptions[0] || "";

    setUserCreateSelectedProject(nextProject);
  }, [createUserProjectOptions, userCreateSelectedProject, userCreateForm.projects]);

  const normalizedUsersSearchTerm = usersSearchTerm.trim().toLowerCase();

  const filteredUsersRows = usersRows.filter((user) => {
    const userProjects = Array.isArray(user.projects) && user.projects.length
      ? user.projects
      : [user.projectTitle];

    if (usersProjectFilter !== "all" && !userProjects.includes(usersProjectFilter)) {
      return false;
    }

    if (usersManagerFilter !== "all" && user.managerName !== usersManagerFilter) {
      return false;
    }

    if (usersRoleFilter !== "all" && user.role !== usersRoleFilter) {
      return false;
    }

    if (usersStatusFilter !== "all" && getUserStatusInfo(user.status).key !== usersStatusFilter) {
      return false;
    }

    if (!normalizedUsersSearchTerm) {
      return true;
    }

    const searchableText = [
      user.code,
      user.name,
      user.managerName,
      user.phone,
      user.role,
      userProjects.join(" "),
      Array.isArray(user.systemSections) ? user.systemSections.join(" ") : "",
    ]
      .filter((value) => value)
      .join(" ")
      .toLowerCase();

    return searchableText.includes(normalizedUsersSearchTerm);
  });

  const usersRowsLimit = Number(usersRowsPerPage) || USER_ROWS_PER_PAGE_OPTIONS[0];
  const totalUsersPages = Math.max(1, Math.ceil(filteredUsersRows.length / usersRowsLimit));
  const currentUsersPage = Math.min(usersPage, totalUsersPages);
  const usersStartIndex = (currentUsersPage - 1) * usersRowsLimit;
  const paginatedUsersRows = filteredUsersRows.slice(usersStartIndex, usersStartIndex + usersRowsLimit);
  const paginatedUserIds = paginatedUsersRows.map((user) => user.id);
  const areAllVisibleUsersSelected = (
    paginatedUserIds.length > 0
    && paginatedUserIds.every((userId) => selectedUserIds.includes(userId))
  );

  const handleUserRowSelection = (userId) => {
    setSelectedUserIds((prevSelectedUserIds) => (
      prevSelectedUserIds.includes(userId)
        ? prevSelectedUserIds.filter((selectedUserId) => selectedUserId !== userId)
        : [...prevSelectedUserIds, userId]
    ));
  };

  const handleSelectAllVisibleUsers = () => {
    if (!paginatedUserIds.length) {
      return;
    }

    setSelectedUserIds((prevSelectedUserIds) => {
      const areAllSelected = paginatedUserIds.every((userId) => (
        prevSelectedUserIds.includes(userId)
      ));

      if (areAllSelected) {
        return prevSelectedUserIds.filter((userId) => !paginatedUserIds.includes(userId));
      }

      return Array.from(new Set([...prevSelectedUserIds, ...paginatedUserIds]));
    });
  };

  const handleUsersPageChange = (direction) => {
    if (direction === "next") {
      setUsersPage((prevPage) => Math.min(totalUsersPages, prevPage + 1));
      return;
    }

    setUsersPage((prevPage) => Math.max(1, prevPage - 1));
  };

  const resetUserCreateForm = () => {
    revokeObjectPreviewUrl(userCreateForm.profileImageUrl);
    const nextInitialForm = createInitialUserCreateForm();
    setUserCreateForm(nextInitialForm);
    setShowUserCreatePassword(false);
    setShowUserCreateRepeatPassword(false);
    setUserCreateSelectedProject(getNextUserCreateProject(nextInitialForm.projects));
  };

  const handleCreateUser = () => {
    resetUserCreateForm();
    setUserFormMode("create");
    setEditingUserId("");
    setUsersMsg({ type: "", text: "" });
    setActiveTab("users-create");
  };

  const handleCloseUserFormSection = () => {
    setUserFormMode("");
    setEditingUserId("");
    resetUserCreateForm();
    setUsersMsg({ type: "", text: "" });
    setActiveTab("users");
  };

  const handleUserCreateFieldChange = (fieldName, value) => {
    setUserCreateForm((prevForm) => ({
      ...prevForm,
      [fieldName]: value,
    }));
  };

  const handleUserProfileImageUpload = (event) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) {
      return;
    }

    const nextImageUrl = URL.createObjectURL(selectedFile);
    setUserCreateForm((prevForm) => {
      revokeObjectPreviewUrl(prevForm.profileImageUrl);
      return {
        ...prevForm,
        profileImageUrl: nextImageUrl,
        profileImageName: selectedFile.name,
        profileImageFile: selectedFile,
        profileImageCleared: false,
      };
    });
  };

  const handleClearUserProfileImage = () => {
    setUserCreateForm((prevForm) => {
      revokeObjectPreviewUrl(prevForm.profileImageUrl);
      return {
        ...prevForm,
        profileImageUrl: "",
        profileImageName: "",
        profileImageFile: null,
        profileImageCleared: true,
      };
    });

    const fileInput = document.getElementById("user-create-profile-image");
    if (fileInput) {
      fileInput.value = "";
    }
  };

  const handleUserCreateSectionToggle = (sectionLabel) => {
    setUserCreateForm((prevForm) => {
      const hasSection = prevForm.systemSections.includes(sectionLabel);

      return {
        ...prevForm,
        systemSections: hasSection
          ? prevForm.systemSections.filter((section) => section !== sectionLabel)
          : [...prevForm.systemSections, sectionLabel],
      };
    });
  };

  const handleAddUserCreateProject = () => {
    if (!userCreateSelectedProject) {
      setUsersMsg({ type: "error", text: "ابتدا یک پروژه را برای دسترسی انتخاب کنید." });
      return;
    }

    if (userCreateForm.projects.includes(userCreateSelectedProject)) {
      setUsersMsg({ type: "error", text: "این پروژه قبلا به دسترسی کاربر اضافه شده است." });
      return;
    }

    setUserCreateForm((prevForm) => {
      return {
        ...prevForm,
        projects: [...prevForm.projects, userCreateSelectedProject],
      };
    });
    setUserCreateSelectedProject(
      getNextUserCreateProject([...userCreateForm.projects, userCreateSelectedProject])
    );

    setUsersMsg({ type: "", text: "" });
  };

  const handleRemoveUserCreateProject = (projectTitle) => {
    setUserCreateForm((prevForm) => ({
      ...prevForm,
      projects: prevForm.projects.filter((project) => project !== projectTitle),
    }));
  };

  const handleSubmitCreateUser = async (event) => {
    event.preventDefault();

    if (!userCreateForm.firstName.trim() || !userCreateForm.lastName.trim() || !userCreateForm.username.trim()) {
      setUsersMsg({ type: "error", text: "نام، نام خانوادگی و نام کاربری الزامی است." });
      return;
    }

    if (userFormMode !== "edit" && !userCreateForm.password.trim()) {
      setUsersMsg({ type: "error", text: "رمز عبور برای ایجاد کاربر الزامی است." });
      return;
    }

    if (userCreateForm.password && userCreateForm.password !== userCreateForm.repeatPassword) {
      setUsersMsg({ type: "error", text: "رمز عبور و تکرار آن یکسان نیست." });
      return;
    }

    const isEditMode = userFormMode === "edit";
    const firstName = userCreateForm.firstName.trim();
    const lastName = userCreateForm.lastName.trim();
    const fullName = `${firstName} ${lastName}`.trim();
    const username = userCreateForm.username.trim();
    const email = userCreateForm.email.trim();
    const phone = userCreateForm.phone.trim() || "--";
    const selectedProjects = Array.from(
      new Set(userCreateForm.projects.map((projectTitle) => String(projectTitle || "").trim()).filter(Boolean))
    );
    const selectedSections = Array.from(
      new Set(userCreateForm.systemSections.map((section) => String(section || "").trim()).filter(Boolean))
    );
    const normalizedStatusKey = getUserStatusInfo(userCreateForm.status).key;
    const normalizedRole = String(userCreateForm.role || USER_CREATE_ROLE_OPTIONS[0]).trim()
      || USER_CREATE_ROLE_OPTIONS[0];

    if (!selectedProjects.length && !selectedSections.length) {
      setUsersMsg({ type: "error", text: "حداقل یک پروژه یا یک بخش سامانه را برای کاربر انتخاب کنید." });
      return;
    }

    setSubmittingUserCreate(true);
    try {
      const managerName = userCreateForm.managerName.trim();
      const selectedManager = usersRows.find(
        (user) => String(user.name || "").trim() === managerName
      );
      const apiRole = USER_ROLE_TO_API[normalizedRole] || "project_manager";
      const apiModules = selectedSections
        .map((sectionLabel) => USER_MODULE_TO_API[sectionLabel])
        .filter(Boolean);

      const payload = {
        first_name: firstName,
        last_name: lastName,
        username,
        role: apiRole,
        phone: phone === "--" ? "" : phone,
        email,
        activity_status: normalizedStatusKey === "active",
        manager_id: selectedManager ? Number(selectedManager.id) : null,
        projects: selectedProjects,
        modules: apiModules,
        profile_image_file: userCreateForm.profileImageFile,
        profile_image_clear: userCreateForm.profileImageCleared,
      };

      if (isEditMode) {
        if (!editingUserId) {
          setUsersMsg({ type: "error", text: "کاربر انتخاب‌شده برای ویرایش پیدا نشد." });
          return;
        }

        const password = userCreateForm.password.trim();
        await usersApi.update(Number(editingUserId), {
          ...payload,
          password,
        });
      } else {
        await usersApi.create({
          ...payload,
          password: userCreateForm.password.trim(),
        });
      }

      await fetchUsers();
      setUsersMsg({
        type: "success",
        text: isEditMode
          ? `تغییرات کاربر ${fullName} با موفقیت ذخیره شد.`
          : `کاربر ${fullName} با موفقیت ایجاد شد.`,
      });
      setUserFormMode("");
      setEditingUserId("");
      resetUserCreateForm();
      setActiveTab("users");
    } catch (error) {
      setUsersMsg({
        type: "error",
        text: error.message || "ایجاد کاربر با خطا مواجه شد.",
      });
    } finally {
      setSubmittingUserCreate(false);
    }
  };

  const handleDeleteSelectedUsers = async () => {
    if (!selectedUserIds.length) {
      setUsersMsg({
        type: "error",
        text: "برای حذف، حداقل یک کاربر را انتخاب کنید.",
      });
      return;
    }

    const idsToDelete = selectedUserIds
      .map((idValue) => Number(idValue))
      .filter((idValue) => Number.isFinite(idValue) && idValue > 0);

    if (!idsToDelete.length) {
      setUsersMsg({
        type: "error",
        text: "شناسه کاربران انتخابی معتبر نیست.",
      });
      return;
    }

    try {
      const result = await usersApi.bulkDelete(idsToDelete);
      await fetchUsers();
      setUsersMsg({
        type: "success",
        text: `${formatFaNumber(result?.deleted_count || idsToDelete.length)} کاربر از لیست حذف شد.`,
      });
      setSelectedUserIds([]);
    } catch (error) {
      setUsersMsg({
        type: "error",
        text: error.message || "حذف کاربران با خطا مواجه شد.",
      });
    }
  };

  const openUsersDeleteModal = () => {
    if (!selectedUserIds.length) {
      setUsersMsg({
        type: "error",
        text: "برای حذف، حداقل یک کاربر را انتخاب کنید.",
      });
      return;
    }

    setUsersMsg({ type: "", text: "" });
    setIsUsersDeleteModalOpen(true);
  };

  const closeUsersDeleteModal = () => {
    if (deletingUsers) {
      return;
    }

    setIsUsersDeleteModalOpen(false);
  };

  const confirmDeleteSelectedUsers = async () => {
    setDeletingUsers(true);
    await handleDeleteSelectedUsers();
    setIsUsersDeleteModalOpen(false);
    setDeletingUsers(false);
  };

  const handleEditUser = (userRow) => {
    if (!userRow?.id) {
      setUsersMsg({
        type: "error",
        text: "کاربر انتخاب‌شده برای ویرایش پیدا نشد.",
      });
      return;
    }

    const nextForm = buildUserCreateFormFromRow(userRow);
    setUserCreateForm(nextForm);
    setShowUserCreatePassword(false);
    setShowUserCreateRepeatPassword(false);
    setUserCreateSelectedProject(getNextUserCreateProject(nextForm.projects));
    setEditingUserId(String(userRow.id));
    setUserFormMode("edit");
    setUsersMsg({ type: "", text: "" });
    setActiveTab("users-edit");
  };

  const usersActivityRoleOptions = Array.from(
    new Set(
      usersActivityRows
        .map((row) => row.role)
        .filter(Boolean)
    )
  );

  const usersActivityModuleOptions = Array.from(
    new Set(
      usersActivityRows
        .map((row) => row.module)
        .filter(Boolean)
    )
  );

  useEffect(() => {
    if (usersActivityRoleFilter === "all") {
      return;
    }

    if (!usersActivityRoleOptions.includes(usersActivityRoleFilter)) {
      setUsersActivityRoleFilter("all");
    }
  }, [usersActivityRoleFilter, usersActivityRoleOptions]);

  useEffect(() => {
    if (usersActivityModuleFilter === "all") {
      return;
    }

    if (!usersActivityModuleOptions.includes(usersActivityModuleFilter)) {
      setUsersActivityModuleFilter("all");
    }
  }, [usersActivityModuleFilter, usersActivityModuleOptions]);

  const normalizedUsersActivitySearchTerm = usersActivitySearchTerm.trim().toLowerCase();

  const filteredUsersActivityRows = usersActivityRows.filter((row) => {
    const rowDate = getDashboardActivityDate(row.activityDate);

    if (usersActivityRoleFilter !== "all" && row.role !== usersActivityRoleFilter) {
      return false;
    }

    if (usersActivityModuleFilter !== "all" && row.module !== usersActivityModuleFilter) {
      return false;
    }

    if (usersActivityFromDate !== "all" && rowDate < usersActivityFromDate) {
      return false;
    }

    if (usersActivityToDate !== "all" && rowDate > usersActivityToDate) {
      return false;
    }

    if (!normalizedUsersActivitySearchTerm) {
      return true;
    }

    const searchableText = [
      row.role,
      row.userName,
      row.module,
      row.ip,
      row.event,
    ]
      .filter((value) => value)
      .join(" ")
      .toLowerCase();

    return searchableText.includes(normalizedUsersActivitySearchTerm);
  });

  const usersActivityLimit = Number(usersActivityRowsPerPage) || USER_ACTIVITY_ROWS_PER_PAGE_OPTIONS[0];
  const totalUsersActivityPages = Math.max(
    1,
    Math.ceil(filteredUsersActivityRows.length / usersActivityLimit)
  );
  const currentUsersActivityPage = Math.min(usersActivityPage, totalUsersActivityPages);
  const usersActivityStartIndex = (currentUsersActivityPage - 1) * usersActivityLimit;
  const paginatedUsersActivityRows = filteredUsersActivityRows.slice(
    usersActivityStartIndex,
    usersActivityStartIndex + usersActivityLimit
  );

  const handleUsersActivityFromDateChange = (nextFromDate) => {
    setUsersActivityFromDate(nextFromDate);

    if (
      nextFromDate !== "all"
      && usersActivityToDate !== "all"
      && nextFromDate > usersActivityToDate
    ) {
      setUsersActivityToDate(nextFromDate);
    }
  };

  const handleUsersActivityToDateChange = (nextToDate) => {
    setUsersActivityToDate(nextToDate);

    if (
      nextToDate !== "all"
      && usersActivityFromDate !== "all"
      && nextToDate < usersActivityFromDate
    ) {
      setUsersActivityFromDate(nextToDate);
    }
  };

  const handleUsersActivityPageChange = (direction) => {
    if (direction === "next") {
      setUsersActivityPage((prevPage) => Math.min(totalUsersActivityPages, prevPage + 1));
      return;
    }

    setUsersActivityPage((prevPage) => Math.max(1, prevPage - 1));
  };

  // Bootstrap dashboard auth: if access token is missing/invalid we redirect to login.
  useEffect(() => {
    let isMounted = true;

    const bootstrapAuth = async () => {
      if (!authStorage.getAccessToken()) {
        navigate("/login");
        return;
      }

      try {
        const context = await authApi.meWithAutoRefresh();
        const topbarPayload = context?.topbar || {};
        const dashboardPayload = context?.dashboard || {};
        const systemMessagesPayload = Array.isArray(context?.system_messages)
          ? context.system_messages
          : [];
        const moduleItems = Array.isArray(dashboardPayload?.modules) ? dashboardPayload.modules : [];
        const projectItems = Array.isArray(dashboardPayload?.projects) ? dashboardPayload.projects : [];
        const isOwner = String(context?.user?.role || "").trim() === "owner";
        const normalizedAllowedModules = moduleItems
          .map((item) => String(item?.module || "").trim().toLowerCase())
          .filter((item) => item && MODULE_KEY_TO_TAB_KEY[item]);
        const normalizedAllowedProjects = projectItems
          .map((item) => String(item?.project_label || "").trim())
          .filter(Boolean);

        setTopbarUserInfo({
          role: String(topbarPayload.title || "").trim() || TOPBAR_USER_INFO.role,
          name: String(topbarPayload.name || "").trim() || TOPBAR_USER_INFO.name,
          profileImage: buildAbsoluteFileUrl(topbarPayload.profile_image),
        });
        setAllowedModuleKeys(
          normalizedAllowedModules.length
            ? normalizedAllowedModules
            : (isOwner ? Object.keys(TAB_KEY_TO_MODULE_KEY) : ["dashboards"])
        );
        setAllowedProjectLabels(normalizedAllowedProjects);
        setRestrictProjectsByAccess(!isOwner);
        setTopbarSystemMessages(
          systemMessagesPayload
            .filter((item) => item?.is_system_message === true)
            .map((item) => ({
              id: item?.id,
              message: String(item?.message || "").trim(),
              level: String(item?.level || "").trim(),
              module: String(item?.module || "").trim(),
              createdAt: String(item?.created_at || "").trim(),
            }))
            .filter((item) => item.message)
        );
      } catch (error) {
        const statusCode = Number(error?.status || 0);
        if (statusCode === 401 || statusCode === 403) {
          authStorage.clear();
          navigate("/login");
          return;
        }
        if (isMounted) {
          setAuthLoading(false);
          setProjectsMsg({
            type: "error",
            text: "ارتباط با سرور موقتاً قطع شد. لطفاً دوباره تلاش کنید.",
          });
        }
        return;
      }

      if (isMounted) {
        setAuthLoading(false);
      }
    };

    bootstrapAuth();

    return () => {
      isMounted = false;
    };
  }, [navigate]);

  const openProjectCreatePanel = () => {
    setEditingProjectId(null);
    setProjectForm(createInitialProjectForm());
    setProjectExtraPublishWindows([]);
    setProjectExtraPublishTargets([]);
    setProjectExtraSources([]);
    setShowProjectForm(true);
    setShowMainSourceSettings(false);
    setErrorMsg("");
    setSuccessMsg("");
    setProjectsMsg({ type: "", text: "" });
  };

  const openLogoutModal = () => {
    setIsLogoutModalOpen(true);
  };

  const closeLogoutModal = () => {
    setIsLogoutModalOpen(false);
  };

  const handleConfirmLogout = async () => {
    try {
      await authApi.logout();
    } catch (error) {
      // If backend logout fails we still clear local credentials for safety.
      authStorage.clear();
    }
    navigate("/login");
  };

  const closeProjectCreatePanel = () => {
    if (saving) {
      return;
    }

    setShowProjectForm(false);
    setEditingProjectId(null);
    setShowMainSourceSettings(false);
    setErrorMsg("");
    setSuccessMsg("");
  };

  const handleAddProjectExtraPublishWindow = () => {
    setProjectExtraPublishWindows((prevRows) => [...prevRows, createProjectExtraPublishWindow()]);
  };

  const handleProjectExtraPublishWindowChange = (rowId, field, value) => {
    setProjectExtraPublishWindows((prevRows) => (
      prevRows.map((row) => (row.id === rowId ? { ...row, [field]: value } : row))
    ));
  };

  const handleRemoveProjectExtraPublishWindow = (rowId) => {
    setProjectExtraPublishWindows((prevRows) => prevRows.filter((row) => row.id !== rowId));
  };

  const handleAddProjectExtraPublishTarget = () => {
    setProjectExtraPublishTargets((prevRows) => [...prevRows, createProjectExtraPublishTarget()]);
  };

  const handleProjectExtraPublishTargetChange = (rowId, field, value) => {
    setProjectExtraPublishTargets((prevRows) => (
      prevRows.map((row) => (row.id === rowId ? { ...row, [field]: value } : row))
    ));
  };

  const handleRemoveProjectExtraPublishTarget = (rowId) => {
    setProjectExtraPublishTargets((prevRows) => prevRows.filter((row) => row.id !== rowId));
  };

  const handleRemovePrimaryPublishTarget = () => {
    setProjectExtraPublishTargets((prevRows) => {
      if (!prevRows.length) {
        setProjectForm((prev) => ({
          ...prev,
          publish_source_id: "",
          publish_source_ids: [],
          publish_platform_name: "",
          publish_platform_type: "",
          publish_link: "",
        }));
        return prevRows;
      }

      const [nextPrimary, ...restRows] = prevRows;
      setProjectForm((prev) => ({
        ...prev,
        publish_source_id: String(nextPrimary?.publish_source_id || ""),
        publish_source_ids: nextPrimary?.publish_source_id ? [String(nextPrimary.publish_source_id)] : [],
        publish_platform_name: String(nextPrimary?.name || ""),
        publish_platform_type: String(nextPrimary?.platform || ""),
        publish_link: String(nextPrimary?.link || ""),
      }));
      return restRows;
    });
  };

  const handleAddProjectExtraSource = () => {
    const nextSourceRow = createProjectExtraSource();
    setProjectExtraSources((prevRows) => [...prevRows, nextSourceRow]);
  };

  const handleProjectExtraSourceChange = (rowId, field, value) => {
    setProjectExtraSources((prevRows) => (
      prevRows.map((row) => {
        if (row.id !== rowId) {
          return row;
        }

        const nextRow = { ...row, [field]: value };
        return {
          ...nextRow,
          ai_activity_status: deriveAiActivityStatus(nextRow),
        };
      })
    ));
  };

  const handleRemoveProjectExtraSource = (rowId) => {
    setProjectExtraSources((prevRows) => prevRows.filter((row) => row.id !== rowId));
  };

  const handleRemovePrimaryReceiveSource = () => {
    setProjectExtraSources((prevRows) => {
      if (!prevRows.length) {
        const initialSourceSettings = createInitialSourceSettings();
        setProjectForm((prev) => ({
          ...prev,
          receive_source_id: "",
          receive_source_ids: [],
          source_name: "",
          source_platform_name: "",
          source_link: "",
          ...initialSourceSettings,
          ai_activity_status: deriveAiActivityStatus(initialSourceSettings),
        }));
        setShowMainSourceSettings(false);
        return prevRows;
      }

      const [nextPrimary, ...restRows] = prevRows;
      const nextSourceSettings = {
        ...createInitialSourceSettings(),
        ...nextPrimary,
      };
      setProjectForm((prev) => ({
        ...prev,
        receive_source_id: String(nextPrimary?.receive_source_id || ""),
        receive_source_ids: nextPrimary?.receive_source_id ? [String(nextPrimary.receive_source_id)] : [],
        source_name: String(nextPrimary?.name || ""),
        source_platform_name: String(nextPrimary?.platform || ""),
        source_link: String(nextPrimary?.link || ""),
        ...nextSourceSettings,
        ai_activity_status: deriveAiActivityStatus(nextSourceSettings),
      }));
      setShowMainSourceSettings(false);
      return restRows;
    });
  };

  const scrollToElementById = (elementId) => {
    window.requestAnimationFrame(() => {
      document.getElementById(elementId)?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  };

  const handleToggleMainSourceSettings = () => {
    setShowMainSourceSettings((prev) => {
      return !prev;
    });
  };

  const handleToggleProjectExtraSourceSettings = (rowId) => {
    setProjectExtraSources((prevRows) => (
      prevRows.map((row) => {
        if (row.id !== rowId) {
          return row;
        }

        const nextState = !row.show_source_settings;
        return { ...row, show_source_settings: nextState };
      })
    ));
  };

  const getWatermarkPresetValue = (watermarkColor) => {
    const normalizedColor = String(watermarkColor || "").toLowerCase();
    const matchedPreset = PROJECT_WATERMARK_COLOR_PRESETS.find(
      (preset) => preset.value.toLowerCase() === normalizedColor
    );

    return matchedPreset ? matchedPreset.value : "custom";
  };

  const addTextProcessRule = (sourceValues, handleFieldChange) => {
    if (sourceValues.text_process_mode === "ai") {
      const next = [...(Array.isArray(sourceValues.ai_text_rules) ? sourceValues.ai_text_rules : [])];
      next.push({ prompt: "", target: "text" });
      handleFieldChange("ai_text_rules", next);
      return;
    }

    if (sourceValues.text_process_mode === "system") {
      const next = [...(Array.isArray(sourceValues.system_text_rules) ? sourceValues.system_text_rules : [])];
      next.push({ max_chars: "", remove_phrase: "", replace_remove_phrase: "", replace_add_phrase: "", prefix: "", suffix: "", target: "text" });
      handleFieldChange("system_text_rules", next);
    }
  };

  const renderSourceSettingsFields = (sourceValues, handleFieldChange, idPrefix) => (
    <>
      <div className="project-source-settings-card">
        <div className="project-text-process-head">
          <h5>تنظیمات پردازش متن</h5>
          {sourceValues.text_process_mode ? (
            <button
              type="button"
              className="dashboard-btn dashboard-btn-ghost"
              onClick={() => addTextProcessRule(sourceValues, handleFieldChange)}
            >
              {sourceValues.text_process_mode === "ai" ? "افزودن پردازش AI" : "افزودن پردازش سیستمی"}
            </button>
          ) : null}
        </div>
        <div className="project-create-grid project-create-grid-2">
          <div className="project-create-field">
            <label htmlFor={`${idPrefix}-text-process-mode`}>انتخاب پردازش متن</label>
            <select
              id={`${idPrefix}-text-process-mode`}
              value={sourceValues.text_process_mode}
              onChange={(event) => {
                const nextMode = event.target.value;
                handleFieldChange("text_process_mode", nextMode);
                handleFieldChange("system_text_process_active", nextMode === "system");
                handleFieldChange("ai_text_process_active", nextMode === "ai");
                if (nextMode === "system") {
                  handleFieldChange("ai_text_rules", []);
                  if (!Array.isArray(sourceValues.system_text_rules) || sourceValues.system_text_rules.length === 0) {
                    handleFieldChange("system_text_rules", [{ max_chars: "", remove_phrase: "", replace_remove_phrase: "", replace_add_phrase: "", prefix: "", suffix: "", target: "text" }]);
                  }
                } else if (nextMode === "ai") {
                  handleFieldChange("system_text_rules", []);
                  if (!Array.isArray(sourceValues.ai_text_rules) || sourceValues.ai_text_rules.length === 0) {
                    handleFieldChange("ai_text_rules", [{ prompt: "", target: "text" }]);
                  }
                } else {
                  handleFieldChange("system_text_rules", []);
                  handleFieldChange("ai_text_rules", []);
                }
              }}
            >
              <option value="">یک مورد را انتخاب کنید</option>
              <option value="ai">پردازش با هوش مصنوعی</option>
              <option value="system">پردازش سیستمی</option>
            </select>
          </div>
          <div className="project-create-field project-process-mode-hint project-media-process-mode-hint">
            <label>وضعیت</label>
            <div className="project-process-mode-badge">
              {sourceValues.text_process_mode === "ai"
                ? "پردازش AI فعال است"
                : sourceValues.text_process_mode === "system"
                  ? "پردازش سیستمی فعال است"
                  : "هنوز موردی انتخاب نشده است"}
            </div>
          </div>
          {sourceValues.text_process_mode === "ai" ? (
            <div className="ads-create-full-row project-process-rules">
              {(Array.isArray(sourceValues.ai_text_rules) ? sourceValues.ai_text_rules : []).map((rule, index) => (
                <div key={`${idPrefix}-ai-rule-${index}`} className="project-process-rule-card">
                  <div className="project-process-rule-grid project-create-grid project-create-grid-2">
                    <div className="project-create-field">
                      <label>پرامپت</label>
                      <input
                        value={String(rule?.prompt || "")}
                        onChange={(event) => {
                          const next = [...(sourceValues.ai_text_rules || [])];
                          next[index] = { ...(next[index] || {}), prompt: event.target.value };
                          handleFieldChange("ai_text_rules", next);
                        }}
                      />
                    </div>
                    <div className="project-create-field">
                      <label>نوع اعمال</label>
                      <select
                        value={String(rule?.target || "text")}
                        onChange={(event) => {
                          const next = [...(sourceValues.ai_text_rules || [])];
                          next[index] = { ...(next[index] || {}), target: event.target.value };
                          handleFieldChange("ai_text_rules", next);
                        }}
                      >
                        {AI_TEXT_PROCESS_TARGET_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="project-create-field project-process-remove-field">
                      <label>&nbsp;</label>
                      <button
                        type="button"
                        className="dashboard-btn dashboard-btn-danger dashboard-btn-danger-compact"
                        onClick={() => {
                          const next = [...(sourceValues.ai_text_rules || [])];
                          next.splice(index, 1);
                          handleFieldChange("ai_text_rules", next);
                        }}
                      >
                        حذف
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : sourceValues.text_process_mode === "system" ? (
            <div className="ads-create-full-row project-process-rules">
              {(Array.isArray(sourceValues.system_text_rules) ? sourceValues.system_text_rules : []).map((rule, index) => (
                <div key={`${idPrefix}-sys-rule-${index}`} className="project-process-rule-card">
                  <div className="project-process-rule-grid project-create-grid project-create-grid-2">
                    <div className="project-create-field">
                      <label>حداکثر کاراکتر</label>
                      <input
                        value={String(rule?.max_chars || "")}
                        onChange={(event) => {
                          const next = [...(sourceValues.system_text_rules || [])];
                          next[index] = { ...(next[index] || {}), max_chars: event.target.value };
                          handleFieldChange("system_text_rules", next);
                        }}
                      />
                    </div>
                    <div className="project-create-field">
                      <label>حذف عبارت</label>
                      <input
                        value={String(rule?.remove_phrase || "")}
                        onChange={(event) => {
                          const next = [...(sourceValues.system_text_rules || [])];
                          next[index] = { ...(next[index] || {}), remove_phrase: event.target.value };
                          handleFieldChange("system_text_rules", next);
                        }}
                      />
                    </div>
                    <div className="project-create-field">
                      <label>عبارت برای حذف و جایگزینی</label>
                      <input
                        value={String(rule?.replace_remove_phrase || "")}
                        onChange={(event) => {
                          const next = [...(sourceValues.system_text_rules || [])];
                          next[index] = { ...(next[index] || {}), replace_remove_phrase: event.target.value };
                          handleFieldChange("system_text_rules", next);
                        }}
                      />
                    </div>
                    <div className="project-create-field">
                      <label>عبارت جایگزین</label>
                      <input
                        value={String(rule?.replace_add_phrase || "")}
                        onChange={(event) => {
                          const next = [...(sourceValues.system_text_rules || [])];
                          next[index] = { ...(next[index] || {}), replace_add_phrase: event.target.value };
                          handleFieldChange("system_text_rules", next);
                        }}
                      />
                    </div>
                    <div className="project-create-field">
                      <label>پیشوند</label>
                      <input
                        value={String(rule?.prefix || "")}
                        onChange={(event) => {
                          const next = [...(sourceValues.system_text_rules || [])];
                          next[index] = { ...(next[index] || {}), prefix: event.target.value };
                          handleFieldChange("system_text_rules", next);
                        }}
                      />
                    </div>
                    <div className="project-create-field">
                      <label>پسوند</label>
                      <input
                        value={String(rule?.suffix || "")}
                        onChange={(event) => {
                          const next = [...(sourceValues.system_text_rules || [])];
                          next[index] = { ...(next[index] || {}), suffix: event.target.value };
                          handleFieldChange("system_text_rules", next);
                        }}
                      />
                    </div>
                    <div className="project-create-field">
                      <label>نوع اعمال</label>
                      <select
                        value={String(rule?.target || "text")}
                        onChange={(event) => {
                          const next = [...(sourceValues.system_text_rules || [])];
                          next[index] = { ...(next[index] || {}), target: event.target.value };
                          handleFieldChange("system_text_rules", next);
                        }}
                      >
                        {TEXT_PROCESS_TARGET_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="project-create-field project-process-remove-field">
                      <label>&nbsp;</label>
                      <button
                        type="button"
                        className="dashboard-btn dashboard-btn-danger dashboard-btn-danger-compact"
                        onClick={() => {
                          const next = [...(sourceValues.system_text_rules || [])];
                          next.splice(index, 1);
                          handleFieldChange("system_text_rules", next);
                        }}
                      >
                        حذف
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      </div>

      <div className="project-source-settings-card">
        <h5>پردازش مدیا</h5>
        <div className="project-create-grid project-create-grid-4">
          <div className="project-create-field">
            <label htmlFor={`${idPrefix}-media-process-mode`}>انتخاب پردازش مدیا</label>
            <select
              id={`${idPrefix}-media-process-mode`}
              value={sourceValues.media_process_mode}
              onChange={(event) => {
                const nextMode = event.target.value;
                handleFieldChange("media_process_mode", nextMode);
                handleFieldChange("media_process_active", Boolean(nextMode));
                handleFieldChange("ai_media_process_active", false);
                if (nextMode === "logo") {
                  handleFieldChange("media_text_content", "");
                  handleFieldChange("media_text_size", "");
                  handleFieldChange("media_text_color", "");
                  handleFieldChange("media_text_font", PROJECT_WATERMARK_FONT_OPTIONS[0].value);
                  handleFieldChange("media_text_x", "0");
                  handleFieldChange("media_text_y", "0");
                  handleFieldChange("media_opacity", String(sourceValues.media_opacity || "100"));
                  return;
                }
                if (nextMode === "text") {
                  handleFieldChange("media_logo_file", "");
                  handleFieldChange("media_logo_file_file", null);
                  handleFieldChange("media_logo_file_clear", true);
                  handleFieldChange("media_logo_preview_url", "");
                  handleFieldChange("media_logo_size", "");
                  handleFieldChange("media_logo_color", "");
                  handleFieldChange("media_logo_x", "0");
                  handleFieldChange("media_logo_y", "0");
                  handleFieldChange("media_opacity", String(sourceValues.media_opacity || "100"));
                  return;
                }
                handleFieldChange("media_logo_file", "");
                handleFieldChange("media_logo_file_file", null);
                handleFieldChange("media_logo_file_clear", true);
                handleFieldChange("media_logo_preview_url", "");
                handleFieldChange("media_logo_size", "");
                handleFieldChange("media_logo_color", "");
                handleFieldChange("media_logo_x", "0");
                handleFieldChange("media_logo_y", "0");
                handleFieldChange("media_text_content", "");
                handleFieldChange("media_text_size", "");
                handleFieldChange("media_text_color", "");
                handleFieldChange("media_text_font", PROJECT_WATERMARK_FONT_OPTIONS[0].value);
                handleFieldChange("media_text_x", "0");
                handleFieldChange("media_text_y", "0");
                handleFieldChange("media_opacity", "100");
              }}
            >
              <option value="">یک مورد را انتخاب کنید</option>
              <option value="logo">افزودن لوگو</option>
              <option value="text">افزودن متن</option>
            </select>
          </div>
          {sourceValues.media_process_mode === "" ? null : (
            <div className="project-create-field">
              <label htmlFor={`${idPrefix}-media-apply-to`}>محل اعمال</label>
              <select
                id={`${idPrefix}-media-apply-to`}
                value={String(sourceValues.media_apply_to || "image_and_video")}
                onChange={(event) => handleFieldChange("media_apply_to", event.target.value)}
              >
                {PROJECT_MEDIA_APPLY_TO_OPTIONS.map((option) => (
                  <option key={`${idPrefix}-media-apply-${option.value}`} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="project-create-field project-process-mode-hint project-media-process-mode-hint">
            <label>وضعیت</label>
            <div className="project-process-mode-badge">
              {sourceValues.media_process_mode === "logo"
                  ? "پردازش لوگو فعال است"
                  : sourceValues.media_process_mode === "text"
                    ? "پردازش متن روی مدیا فعال است"
                    : "هنوز موردی انتخاب نشده است"}
            </div>
          </div>
          {sourceValues.media_process_mode === "" ? null : (
            <div className="project-create-field" style={{ gridColumn: "1 / -1" }}>
              <p style={{ margin: 0, fontSize: "0.85rem", color: "#64748b", lineHeight: 1.9 }}>
                خبرهایی که نوع مدیایشان خارج از محل اعمال انتخاب‌شده باشد، بدون
                لوگو و متن و به‌صورت عادی دریافت و منتشر می‌شوند.
              </p>
            </div>
          )}
          {sourceValues.media_process_mode === "" ? null : sourceValues.media_process_mode === "logo" ? (
          
            <>
              <div className="project-create-field project-media-logo-file-field">
                <label htmlFor={`${idPrefix}-media-logo-file`}>بارگذاری فایل لوگو</label>
                <input
                  id={`${idPrefix}-media-logo-file`}
                  type="file"
                  accept="image/*"
                  onChange={(event) => {
                    const selectedFile = event.target.files?.[0] || null;
                    if (String(sourceValues.media_logo_preview_url || "").startsWith("blob:")) {
                      URL.revokeObjectURL(sourceValues.media_logo_preview_url);
                    }
                    handleFieldChange("media_logo_file", selectedFile?.name || "");
                    handleFieldChange("media_logo_file_file", selectedFile);
                    handleFieldChange("media_logo_file_clear", false);
                    const previewUrl = selectedFile ? URL.createObjectURL(selectedFile) : "";
                    handleFieldChange("media_logo_preview_url", previewUrl);
                  }}
                />
                {String(sourceValues.media_logo_preview_url || "").trim() && !sourceValues.media_logo_file_clear && (
                  <div className="project-logo-preview-row">
                    <img
                      src={normalizeNewsLink(sourceValues.media_logo_preview_url)}
                      alt="پیش‌نمایش لوگو"
                      className="project-logo-preview-image"
                    />
                  </div>
                )}
                {(sourceValues.media_logo_file || sourceValues.media_logo_file_url) && !sourceValues.media_logo_file_clear && (
                  <div className="project-file-selected-row">
                    <span className="project-file-selected-name">
                      {String(sourceValues.media_logo_file || getFileNameFromUrl(sourceValues.media_logo_file_url || ""))}
                    </span>
                    <button
                      type="button"
                      className="project-file-clear-btn"
                      onClick={() => {
                        if (String(sourceValues.media_logo_preview_url || "").startsWith("blob:")) {
                          URL.revokeObjectURL(sourceValues.media_logo_preview_url);
                        }
                        handleFieldChange("media_logo_file", "");
                        handleFieldChange("media_logo_file_file", null);
                        handleFieldChange("media_logo_file_clear", true);
                        handleFieldChange("media_logo_preview_url", "");
                        const fileInput = document.getElementById(`${idPrefix}-media-logo-file`);
                        if (fileInput) {
                          fileInput.value = "";
                        }
                      }}
                    >
                      حذف فایل
                    </button>
                  </div>
                )}
              </div>
              <div className="project-create-field">
                <label htmlFor={`${idPrefix}-media-logo-size`}>سایز لوگو (درصد)</label>
                <input
                  id={`${idPrefix}-media-logo-size`}
                  value={sourceValues.media_logo_size}
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  maxLength={2}
                  onKeyDown={(event) => {
                    if (event.ctrlKey || event.metaKey) {
                      return;
                    }
                    const allowedControlKeys = [
                      "Backspace",
                      "Delete",
                      "Tab",
                      "ArrowLeft",
                      "ArrowRight",
                      "Home",
                      "End",
                    ];
                    if (allowedControlKeys.includes(event.key)) {
                      return;
                    }
                    if (!/^\d$/.test(event.key)) {
                      event.preventDefault();
                      return;
                    }
                    const inputElement = event.currentTarget;
                    const hasSelection = inputElement.selectionStart !== inputElement.selectionEnd;
                    if (inputElement.value.length >= 2 && !hasSelection) {
                      event.preventDefault();
                    }
                  }}
                  onChange={(event) => {
                    const digitsOnly = String(event.target.value || "").replace(/\D/g, "").slice(0, 2);
                    if (!digitsOnly) {
                      handleFieldChange("media_logo_size", "");
                      return;
                    }
                    const normalized = digitsOnly.replace(/^0+/, "");
                    if (!normalized) {
                      handleFieldChange("media_logo_size", "");
                      return;
                    }
                    const numericValue = Math.max(1, Math.min(99, Number(normalized)));
                    handleFieldChange("media_logo_size", String(numericValue));
                  }}
                />
              </div>
              <div className="project-create-field">
                <label htmlFor={`${idPrefix}-media-logo-color`}>رنگ لوگو (کد رنگ)</label>
                <input
                  id={`${idPrefix}-media-logo-color`}
                  type="text"
                  inputMode="text"
                  autoComplete="off"
                  dir="ltr"
                  style={{ textAlign: "left", direction: "ltr", unicodeBidi: "plaintext" }}
                  value={`#${String(sourceValues.media_logo_color || "").replace(/^#/, "")}`}
                  onKeyDown={(event) => {
                    if (event.ctrlKey || event.metaKey) {
                      return;
                    }
                    const allowedControlKeys = [
                      "Backspace",
                      "Delete",
                      "Tab",
                      "ArrowLeft",
                      "ArrowRight",
                      "Home",
                      "End",
                    ];
                    if (allowedControlKeys.includes(event.key)) {
                      return;
                    }
                    if (!/^[0-9a-fA-F]$/.test(event.key)) {
                      event.preventDefault();
                      return;
                    }
                    const inputElement = event.currentTarget;
                    const codePart = String(inputElement.value || "").replace(/^#/, "");
                    const normalizedCode = codePart.replace(/[^0-9a-fA-F]/g, "");
                    const hasSelection = inputElement.selectionStart !== inputElement.selectionEnd;
                    if (normalizedCode.length >= 6 && !hasSelection) {
                      event.preventDefault();
                    }
                  }}
                  onChange={(event) => {
                    const valueText = String(event.target.value || "");
                    const hexOnly = valueText.replace(/^#/, "").replace(/[^0-9a-fA-F]/g, "").slice(0, 6);
                    handleFieldChange("media_logo_color", hexOnly ? `#${hexOnly}` : "");
                  }}
                />
              </div>
              <div className="project-create-field">
                <label htmlFor={`${idPrefix}-media-opacity`}>شفافیت (%)</label>
                <input
                  id={`${idPrefix}-media-opacity`}
                  value={String(sourceValues.media_opacity ?? "100")}
                  type="number"
                  min="0"
                  max="100"
                  step="1"
                  onChange={(event) => {
                    const raw = String(event.target.value ?? "").trim();
                    if (raw === "") {
                      handleFieldChange("media_opacity", "");
                      return;
                    }
                    const value = Math.max(0, Math.min(100, Number(raw)));
                    handleFieldChange("media_opacity", String(Number.isNaN(value) ? 100 : Math.trunc(value)));
                  }}
                />
              </div>
              <div className="project-create-field">
                <label htmlFor={`${idPrefix}-media-logo-x`}>محل قرارگیری عرضی (x)</label>
                <input
                  id={`${idPrefix}-media-logo-x`}
                  value={String(sourceValues.media_logo_x ?? "0")}
                  type="number"
                  min="0"
                  max="100"
                  step="1"
                  onChange={(event) => {
                    const raw = String(event.target.value ?? "").trim();
                    if (raw === "") {
                      handleFieldChange("media_logo_x", "");
                      return;
                    }
                    const value = Math.max(0, Math.min(100, Number(raw)));
                    handleFieldChange("media_logo_x", String(Number.isNaN(value) ? 0 : Math.trunc(value)));
                  }}
                />
              </div>
              <div className="project-create-field">
                <label htmlFor={`${idPrefix}-media-logo-y`}>محل قرارگیری طولی (y)</label>
                <input
                  id={`${idPrefix}-media-logo-y`}
                  value={String(sourceValues.media_logo_y ?? "0")}
                  type="number"
                  min="0"
                  max="100"
                  step="1"
                  onChange={(event) => {
                    const raw = String(event.target.value ?? "").trim();
                    if (raw === "") {
                      handleFieldChange("media_logo_y", "");
                      return;
                    }
                    const value = Math.max(0, Math.min(100, Number(raw)));
                    handleFieldChange("media_logo_y", String(Number.isNaN(value) ? 0 : Math.trunc(value)));
                  }}
                />
              </div>
            </>
          ) : (
            <>
              <div className="project-create-field">
                <label htmlFor={`${idPrefix}-media-text-content`}>متن</label>
                <input
                  id={`${idPrefix}-media-text-content`}
                  value={sourceValues.media_text_content}
                  onChange={(event) => handleFieldChange("media_text_content", event.target.value)}
                />
              </div>
              <div className="project-create-field">
                <label htmlFor={`${idPrefix}-media-text-size`}>سایز متن (درصد)</label>
                <input
                  id={`${idPrefix}-media-text-size`}
                  value={sourceValues.media_text_size}
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  maxLength={2}
                  onKeyDown={(event) => {
                    const allowedControlKeys = [
                      "Backspace",
                      "Delete",
                      "Tab",
                      "ArrowLeft",
                      "ArrowRight",
                      "Home",
                      "End",
                    ];
                    if (allowedControlKeys.includes(event.key)) {
                      return;
                    }
                    if (!/^\d$/.test(event.key)) {
                      event.preventDefault();
                      return;
                    }
                    const inputElement = event.currentTarget;
                    const hasSelection = inputElement.selectionStart !== inputElement.selectionEnd;
                    if (inputElement.value.length >= 2 && !hasSelection) {
                      event.preventDefault();
                    }
                  }}
                  onChange={(event) => {
                    const digitsOnly = String(event.target.value || "").replace(/\D/g, "").slice(0, 2);
                    if (!digitsOnly) {
                      handleFieldChange("media_text_size", "");
                      return;
                    }
                    const normalized = digitsOnly.replace(/^0+/, "");
                    if (!normalized) {
                      handleFieldChange("media_text_size", "");
                      return;
                    }
                    const numericValue = Math.max(1, Math.min(99, Number(normalized)));
                    handleFieldChange("media_text_size", String(numericValue));
                  }}
                />
              </div>
              <div className="project-create-field">
                <label htmlFor={`${idPrefix}-media-text-color`}>رنگ متن (کد رنگ)</label>
                <input
                  id={`${idPrefix}-media-text-color`}
                  type="text"
                  inputMode="text"
                  autoComplete="off"
                  dir="ltr"
                  style={{ textAlign: "left", direction: "ltr", unicodeBidi: "plaintext" }}
                  value={`#${String(sourceValues.media_text_color || "").replace(/^#/, "")}`}
                  onKeyDown={(event) => {
                    if (event.ctrlKey || event.metaKey) {
                      return;
                    }
                    const allowedControlKeys = [
                      "Backspace",
                      "Delete",
                      "Tab",
                      "ArrowLeft",
                      "ArrowRight",
                      "Home",
                      "End",
                    ];
                    if (allowedControlKeys.includes(event.key)) {
                      return;
                    }
                    if (!/^[0-9a-fA-F]$/.test(event.key)) {
                      event.preventDefault();
                      return;
                    }
                    const inputElement = event.currentTarget;
                    const codePart = String(inputElement.value || "").replace(/^#/, "");
                    const normalizedCode = codePart.replace(/[^0-9a-fA-F]/g, "");
                    const hasSelection = inputElement.selectionStart !== inputElement.selectionEnd;
                    if (normalizedCode.length >= 6 && !hasSelection) {
                      event.preventDefault();
                    }
                  }}
                  onChange={(event) => {
                    const valueText = String(event.target.value || "");
                    const hexOnly = valueText.replace(/^#/, "").replace(/[^0-9a-fA-F]/g, "").slice(0, 6);
                    handleFieldChange("media_text_color", hexOnly ? `#${hexOnly}` : "");
                  }}
                />
              </div>
              <div className="project-create-field">
                <label htmlFor={`${idPrefix}-media-text-font`}>فونت متن</label>
                <select
                  id={`${idPrefix}-media-text-font`}
                  value={sourceValues.media_text_font}
                  onChange={(event) => handleFieldChange("media_text_font", event.target.value)}
                >
                  {PROJECT_WATERMARK_FONT_OPTIONS.map((option) => (
                    <option key={`${idPrefix}-media-text-font-${option.value}`} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="project-create-field">
                <label htmlFor={`${idPrefix}-media-opacity`}>شفافیت (%)</label>
                <input
                  id={`${idPrefix}-media-opacity`}
                  value={String(sourceValues.media_opacity ?? "100")}
                  type="number"
                  min="0"
                  max="100"
                  step="1"
                  onChange={(event) => {
                    const raw = String(event.target.value ?? "").trim();
                    if (raw === "") {
                      handleFieldChange("media_opacity", "");
                      return;
                    }
                    const value = Math.max(0, Math.min(100, Number(raw)));
                    handleFieldChange("media_opacity", String(Number.isNaN(value) ? 100 : Math.trunc(value)));
                  }}
                />
              </div>
              <div className="project-create-field">
                <label htmlFor={`${idPrefix}-media-text-x`}>محل قرارگیری عرضی (x)</label>
                <input
                  id={`${idPrefix}-media-text-x`}
                  value={String(sourceValues.media_text_x ?? "0")}
                  type="number"
                  min="0"
                  max="100"
                  step="1"
                  onChange={(event) => {
                    const raw = String(event.target.value ?? "").trim();
                    if (raw === "") {
                      handleFieldChange("media_text_x", "");
                      return;
                    }
                    const value = Math.max(0, Math.min(100, Number(raw)));
                    handleFieldChange("media_text_x", String(Number.isNaN(value) ? 0 : Math.trunc(value)));
                  }}
                />
              </div>
              <div className="project-create-field">
                <label htmlFor={`${idPrefix}-media-text-y`}>محل قرارگیری طولی (y)</label>
                <input
                  id={`${idPrefix}-media-text-y`}
                  value={String(sourceValues.media_text_y ?? "0")}
                  type="number"
                  min="0"
                  max="100"
                  step="1"
                  onChange={(event) => {
                    const raw = String(event.target.value ?? "").trim();
                    if (raw === "") {
                      handleFieldChange("media_text_y", "");
                      return;
                    }
                    const value = Math.max(0, Math.min(100, Number(raw)));
                    handleFieldChange("media_text_y", String(Number.isNaN(value) ? 0 : Math.trunc(value)));
                  }}
                />
              </div>
            </>
          )}
        </div>
      </div>

      <div className="project-source-settings-card">
        <h5>فیلتر محتوا</h5>
        <div className="project-create-grid project-create-grid-2">
          <div className="project-create-field">
            <label htmlFor={`${idPrefix}-content-classify-media`}>نوع محتوای مجاز</label>
            <div className="project-source-settings-inline-actions">
              <select id={`${idPrefix}-content-classify-media`} defaultValue="">
                <option value="">یک مورد را انتخاب کنید</option>
                {CONTENT_CLASSIFY_MEDIA_OPTIONS.map((option) => (
                  <option key={`${idPrefix}-content-classify-${option.value}`} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="project-wireframe-link-btn"
                onClick={() => {
                  const selectElement = document.getElementById(`${idPrefix}-content-classify-media`);
                  const nextValue = String(selectElement?.value || "").trim().toLowerCase();
                  if (!nextValue) {
                    return;
                  }
                  const current = Array.isArray(sourceValues.allowed_media) ? sourceValues.allowed_media : [];
                  if (current.includes(nextValue)) {
                    selectElement.value = "";
                    return;
                  }
                  const nextList = [...current, nextValue];
                  handleFieldChange("allowed_media", nextList);
                  handleFieldChange("is_content_classify", nextList.length > 0);
                  selectElement.value = "";
                }}
              >
                + افزودن
              </button>
            </div>
          </div>
          <div className="project-create-field project-process-mode-hint">
            <label>وضعیت</label>
            <div className="project-process-mode-badge">
              {Array.isArray(sourceValues.allowed_media) && sourceValues.allowed_media.length > 0
                ? "فیلتر محتوا فعال است"
                : "هنوز موردی انتخاب نشده است"}
            </div>
          </div>
          <div className="project-create-field ads-create-full-row">
            <label>موارد انتخاب شده</label>
            <div className="project-rule-list-wrap">
              {Array.isArray(sourceValues.allowed_media) && sourceValues.allowed_media.length > 0 ? (
                sourceValues.allowed_media.map((item) => {
                  const option = CONTENT_CLASSIFY_MEDIA_OPTIONS.find((row) => row.value === item);
                  return (
                    <div key={`${idPrefix}-allowed-media-${item}`} className="project-source-chip">
                      <span>{option?.label || item}</span>
                      <button
                        type="button"
                        className="project-source-chip-remove"
                        onClick={() => {
                          const current = Array.isArray(sourceValues.allowed_media) ? sourceValues.allowed_media : [];
                          const nextList = current.filter((row) => row !== item);
                          handleFieldChange("allowed_media", nextList);
                          handleFieldChange("is_content_classify", nextList.length > 0);
                        }}
                        aria-label={`حذف ${option?.label || item}`}
                      >
                        ×
                      </button>
                    </div>
                  );
                })
              ) : (
                <div className="project-process-mode-badge">هیچ موردی انتخاب نشده است</div>
              )}
            </div>
          </div>
          {Array.isArray(sourceValues.allowed_media) && sourceValues.allowed_media.length > 0 && (
            <div className="project-create-field ads-create-full-row">
              <div className="project-source-settings-inline-actions">
                <button
                  type="button"
                  className="project-wireframe-remove-btn project-wireframe-remove-btn-inline"
                  onClick={() => {
                    handleFieldChange("allowed_media", []);
                    handleFieldChange("is_content_classify", false);
                  }}
                >
                  حذف همه
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );

  const handleMainSourceSettingsChange = (field, value) => {
    setProjectForm((prev) => {
      const nextForm = { ...prev, [field]: value };
      return {
        ...nextForm,
        ai_activity_status: deriveAiActivityStatus(nextForm),
      };
    });
  };

  const renderProjectsTab = () => {
    if (showProjectForm) {
      return (
        <div className="projects-create-workspace">
          <div className="projects-create-head">
            <div>
              <h3>{editingProjectId ? "ویرایش پروژه" : "ایجاد پروژه جدید"}</h3>
            </div>
            <button
              type="button"
              className="projects-create-back-btn"
              onClick={closeProjectCreatePanel}
              disabled={saving}
            >
              بازگشت به پروژه ها
            </button>
          </div>

          <form className="project-create-form project-wireframe-form" onSubmit={handleSubmit}>
            <div className="project-create-card">
              <div className="project-create-grid project-create-grid-3">
                <div className="project-create-field">
                  <label htmlFor="project-title">عنوان پروژه</label>
                  <input
                    id="project-title"
                    name="title"
                    value={projectForm.title}
                    onChange={handleChange}
                    placeholder="عنوان پروژه را وارد کنید"
                  />
                </div>
                <div className="project-create-field">
                  <label htmlFor="project-status">وضعیت پروژه</label>
                  <select id="project-status" name="status" value={projectForm.status} onChange={handleChange}>
                    <option value="active">فعال</option>
                    <option value="inactive">غیرفعال</option>
                  </select>
                  {projectForm.status === "inactive" && (
                    <small className="project-field-hint">
                      با غیرفعال‌سازی پروژه، تسک‌های فعال متوقف می‌شوند و تسک جدیدی ساخته نخواهد شد.
                    </small>
                  )}
                </div>
                <div className="project-create-field">
                  <label htmlFor="project-reactivation-publish-mode">رفتار انتشار پس از فعال‌سازی مجدد</label>
                  <select
                    id="project-reactivation-publish-mode"
                    name="reactivation_publish_mode"
                    value={projectForm.reactivation_publish_mode}
                    onChange={handleChange}
                  >
                    <option value="resume_from_stop">ادامه انتشار از زمان توقف</option>
                    <option value="new_only">شروع انتشار از خبرهای جدید</option>
                  </select>
                </div>
                <div className="project-create-field">
                  <label htmlFor="project-outside-window-mode">رفتار خارج از بازه انتشار</label>
                  <select
                    id="project-outside-window-mode"
                    name="outside_window_mode"
                    value={projectForm.outside_window_mode}
                    onChange={handleChange}
                  >
                    {PROJECT_OUTSIDE_WINDOW_MODE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <small className="project-field-hint">
                    این تنظیم مشخص می‌کند که خارج از بازه، فقط انتشار متوقف شود یا دریافت هم همراه آن متوقف شود.
                  </small>
                </div>
                <div className="project-create-field">
                  <label htmlFor="project-anti-duplicate">ضد تکرار</label>
                  <select
                    id="project-anti-duplicate"
                    name="anti_duplicate_policy"
                    value={projectForm.anti_duplicate_policy}
                    onChange={handleChange}
                  >
                    <option value="enabled">فعال</option>
                    <option value="disabled">غیرفعال</option>
                  </select>
                </div>
                <div className="project-create-field">
                  <label htmlFor="project-similarity-threshold">شباهت (%)</label>
                  <input
                    id="project-similarity-threshold"
                    name="similarity_threshold"
                    type="number"
                    min="0"
                    max="100"
                    step="1"
                    value={projectForm.similarity_threshold}
                    onChange={handleChange}
                    placeholder="مثال: 85"
                  />
                </div>
                <div className="project-create-field">
                  <label htmlFor="project-publish-interval">فاصله انتشار بین پست ها (ثانیه)</label>
                  <input
                    id="project-publish-interval"
                    name="publish_interval_seconds"
                    type="number"
                    min="1"
                    step="1"
                    value={projectForm.publish_interval_seconds}
                    onChange={handleChange}
                    placeholder="مثال: 60"
                  />
                </div>
              </div>
            </div>

            <div className="project-create-card">
              <div className="project-wireframe-section-head">
                <h4>بازه انتشار</h4>
                <button type="button" className="project-wireframe-link-btn" onClick={handleAddProjectExtraPublishWindow}>
                  + افزودن بازه انتشار
                </button>
              </div>

              <div className="project-wireframe-row-grid">
                <div
                  className="project-create-field project-time-field"
                  onPointerDown={(event) => handleTimeFieldPointerDown(event, "project-publish-window-from")}
                >
                  <label htmlFor="project-publish-window-from">از ساعت</label>
                  <div className="project-time-field-control">
                    <input
                      id="project-publish-window-from"
                      type="time"
                      name="publish_window_from"
                      value={projectForm.publish_window_from}
                      onChange={handleChange}
                      onKeyDown={(event) => {
                        if (event.key !== "Tab" && event.key !== "Escape") {
                          event.preventDefault();
                        }
                      }}
                    />
                    <button
                      type="button"
                      className="project-time-picker-btn"
                      onClick={() => openTimeInputPicker("project-publish-window-from")}
                    >
                      انتخاب ساعت
                    </button>
                  </div>
                </div>
                <div
                  className="project-create-field project-time-field"
                  onPointerDown={(event) => handleTimeFieldPointerDown(event, "project-publish-window-to")}
                >
                  <label htmlFor="project-publish-window-to">تا ساعت</label>
                  <div className="project-time-field-control">
                    <input
                      id="project-publish-window-to"
                      type="time"
                      name="publish_window_to"
                      value={projectForm.publish_window_to}
                      onChange={handleChange}
                      onKeyDown={(event) => {
                        if (event.key !== "Tab" && event.key !== "Escape") {
                          event.preventDefault();
                        }
                      }}
                    />
                    <button
                      type="button"
                      className="project-time-picker-btn"
                      onClick={() => openTimeInputPicker("project-publish-window-to")}
                    >
                      انتخاب ساعت
                    </button>
                  </div>
                </div>
              </div>

              {projectExtraPublishWindows.map((row) => (
                <div key={row.id} className="project-wireframe-row-grid project-wireframe-extra-row">
                  <div
                    className="project-create-field project-time-field"
                    onPointerDown={(event) => handleTimeFieldPointerDown(event, `${row.id}-from`)}
                  >
                    <label htmlFor={`${row.id}-from`}>از ساعت</label>
                    <div className="project-time-field-control">
                      <input
                        id={`${row.id}-from`}
                        type="time"
                        value={row.from}
                        onChange={(event) => handleProjectExtraPublishWindowChange(row.id, "from", event.target.value)}
                        onKeyDown={(event) => {
                          if (event.key !== "Tab" && event.key !== "Escape") {
                            event.preventDefault();
                          }
                        }}
                      />
                      <button
                        type="button"
                        className="project-time-picker-btn"
                        onClick={() => openTimeInputPicker(`${row.id}-from`)}
                      >
                        انتخاب ساعت
                      </button>
                    </div>
                  </div>
                  <div
                    className="project-create-field project-time-field"
                    onPointerDown={(event) => handleTimeFieldPointerDown(event, `${row.id}-to`)}
                  >
                    <label htmlFor={`${row.id}-to`}>تا ساعت</label>
                    <div className="project-time-field-control">
                      <input
                        id={`${row.id}-to`}
                        type="time"
                        value={row.to}
                        onChange={(event) => handleProjectExtraPublishWindowChange(row.id, "to", event.target.value)}
                        onKeyDown={(event) => {
                          if (event.key !== "Tab" && event.key !== "Escape") {
                            event.preventDefault();
                          }
                        }}
                      />
                      <button
                        type="button"
                        className="project-time-picker-btn"
                        onClick={() => openTimeInputPicker(`${row.id}-to`)}
                      >
                        انتخاب ساعت
                      </button>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="project-wireframe-remove-btn"
                    onClick={() => handleRemoveProjectExtraPublishWindow(row.id)}
                    aria-label="حذف بازه انتشار"
                  >
                    حذف
                  </button>
                </div>
              ))}
            </div>

            <div className="project-create-card">
              <div className="project-wireframe-section-head">
                <h4>بستر انتشار</h4>
                <button type="button" className="project-wireframe-link-btn" onClick={handleAddProjectExtraPublishTarget}>
                  + افزودن بستر انتشار
                </button>
              </div>

              <div className="project-wireframe-row-grid-3 project-wireframe-extra-row">
                <div className="project-create-field">
                  <label htmlFor="project-publish-name">نام بستر انتشار</label>
                  <SearchableSourceSelect
                    id="project-publish-name"
                    listId="project-publish-name-list"
                    value={projectForm.publish_source_id}
                    options={publishSourcesOptions}
                    labelKey="name"
                    placeholder="انتخاب بستر انتشار"
                    onSelect={(selectedId, selectedRow) => {
                      setProjectForm((prev) => ({
                        ...prev,
                        publish_source_id: selectedId,
                        publish_source_ids: selectedId ? [selectedId] : [],
                        publish_platform_name: String(selectedRow?.name || ""),
                        publish_platform_type: String(selectedRow?.platform || prev.publish_platform_type || ""),
                        publish_link: String(selectedRow?.link || prev.publish_link || ""),
                      }));
                    }}
                  />
                </div>
                {projectForm.publish_source_id && (
                  <div className="project-create-field">
                    <label htmlFor="project-publish-platform">پلتفرم</label>
                    <input
                      id="project-publish-platform"
                      name="publish_platform_type"
                      value={projectForm.publish_platform_type}
                      readOnly
                      aria-readonly="true"
                    />
                  </div>
                )}
                {projectForm.publish_source_id && (
                  <div className="project-create-field">
                    <label htmlFor="project-publish-link">لینک با شناسه</label>
                    <input
                      id="project-publish-link"
                      className="source-link-input-ltr"
                      name="publish_link"
                      value={projectForm.publish_link}
                      readOnly
                      aria-readonly="true"
                    />
                  </div>
                )}
                <button
                  type="button"
                  className="project-wireframe-remove-btn"
                  onClick={handleRemovePrimaryPublishTarget}
                  aria-label="حذف بستر انتشار اصلی"
                >
                  حذف
                </button>
              </div>
              {projectForm.publish_source_id && (
                <ViewBoostCard
                  idPrefix="project-publish-primary"
                  platform={projectForm.publish_platform_type}
                  values={projectForm}
                  onChange={(field, value) => setProjectForm((prev) => ({ ...prev, [field]: value }))}
                />
              )}
              {projectExtraPublishTargets.map((row) => (
                <React.Fragment key={row.id}>
                <div className="project-wireframe-row-grid-3 project-wireframe-extra-row">
                  <div className="project-create-field">
                    <label htmlFor={`${row.id}-name`}>نام بستر انتشار</label>
                    <SearchableSourceSelect
                      id={`${row.id}-name`}
                      listId={`${row.id}-publish-source-list`}
                      value={row.publish_source_id || ""}
                      options={publishSourcesOptions}
                      labelKey="name"
                      placeholder="انتخاب بستر انتشار"
                      onSelect={(selectedId, selectedRow) => {
                        setProjectExtraPublishTargets((prevRows) => (
                          prevRows.map((item) => (
                            item.id === row.id
                              ? {
                                  ...item,
                                  publish_source_id: selectedId,
                                  name: String(selectedRow?.name || ""),
                                  platform: String(selectedRow?.platform || ""),
                                  link: String(selectedRow?.link || ""),
                                }
                              : item
                          ))
                        ));
                      }}
                    />
                  </div>
                  {row.publish_source_id && (
                    <div className="project-create-field">
                      <label htmlFor={`${row.id}-platform`}>پلتفرم</label>
                      <input
                        id={`${row.id}-platform`}
                        value={row.platform}
                        readOnly
                        aria-readonly="true"
                      />
                    </div>
                  )}
                  {row.publish_source_id && (
                    <div className="project-create-field">
                      <label htmlFor={`${row.id}-link`}>لینک با شناسه</label>
                      <input
                        id={`${row.id}-link`}
                        className="source-link-input-ltr"
                        value={row.link}
                        readOnly
                        aria-readonly="true"
                      />
                    </div>
                  )}
                  <button
                    type="button"
                    className="project-wireframe-remove-btn"
                    onClick={() => handleRemoveProjectExtraPublishTarget(row.id)}
                    aria-label="حذف بستر انتشار"
                  >
                    حذف
                  </button>
                </div>
                {row.publish_source_id && (
                  <ViewBoostCard
                    idPrefix={row.id}
                    platform={row.platform}
                    values={row}
                    onChange={(field, value) => setProjectExtraPublishTargets((prevRows) => (
                      prevRows.map((item) => (item.id === row.id ? { ...item, [field]: value } : item))
                    ))}
                  />
                )}
                </React.Fragment>
              ))}
            </div>

            <div className="project-create-card">
              <div className="project-wireframe-section-head">
                <h4>منابع خبر</h4>
                <div className="project-wireframe-head-actions">
                  <button type="button" className="project-wireframe-link-btn" onClick={handleAddProjectExtraSource}>
                    + افزودن منبع
                  </button>
                </div>
              </div>

              <div className="project-wireframe-row-grid-3 project-wireframe-extra-row">
                <div className="project-create-field">
                  <label htmlFor="project-source-name">نام منبع خبر</label>
                  <SearchableSourceSelect
                    id="project-source-name"
                    listId="project-source-name-list"
                    value={projectForm.receive_source_id}
                    options={receiveSourcesOptions}
                    labelKey="name"
                    placeholder="انتخاب منبع دریافت"
                    onSelect={(selectedId, selectedRow) => {
                      const sourceSettings = createInitialSourceSettings();
                      setProjectForm((prev) => ({
                        ...prev,
                        receive_source_id: selectedId,
                        receive_source_ids: selectedId ? [selectedId] : [],
                        source_name: String(selectedRow?.name || ""),
                        source_platform_name: String(selectedRow?.platform || prev.source_platform_name || ""),
                        source_link: String(selectedRow?.link || prev.source_link || ""),
                        ...sourceSettings,
                        ai_activity_status: deriveAiActivityStatus(sourceSettings),
                      }));
                      setShowMainSourceSettings(false);
                    }}
                  />
                </div>
                {projectForm.receive_source_id && (
                  <div className="project-create-field">
                    <label htmlFor="project-source-platform">پلتفرم</label>
                    <input
                      id="project-source-platform"
                      name="source_platform_name"
                      value={projectForm.source_platform_name}
                      readOnly
                      aria-readonly="true"
                    />
                  </div>
                )}
                {projectForm.receive_source_id && (
                  <div className="project-create-field">
                    <label htmlFor="project-source-link">لینک با شناسه</label>
                    <input
                      id="project-source-link"
                      className="source-link-input-ltr"
                      name="source_link"
                      value={projectForm.source_link}
                      readOnly
                      aria-readonly="true"
                    />
                  </div>
                )}
                <button
                  type="button"
                  className="project-wireframe-remove-btn"
                  onClick={handleRemovePrimaryReceiveSource}
                  aria-label="حذف منبع خبر اصلی"
                >
                  حذف
                </button>
              </div>
              <button
                type="button"
                className="project-wireframe-link-btn project-main-source-settings-btn"
                onClick={handleToggleMainSourceSettings}
              >
                {showMainSourceSettings ? "پنهان کردن تنظیمات منبع" : "+ تنظیمات منبع"}
              </button>

              {showMainSourceSettings && (
                <div
                  id="main-source-settings-block"
                  className="project-source-settings-block project-source-settings-main"
                >
                  {renderSourceSettingsFields(projectForm, handleMainSourceSettingsChange, "main-source")}
                </div>
              )}

              {projectExtraSources.map((row) => (
                <div
                  key={row.id}
                  id={`${row.id}-source-row`}
                  className="project-wireframe-extra-row project-extra-source-row"
                >
                  <div className="project-wireframe-row-grid-3">
                    <div className="project-create-field">
                      <label htmlFor={`${row.id}-name`}>نام منبع خبر</label>
                      <SearchableSourceSelect
                        id={`${row.id}-name`}
                        listId={`${row.id}-receive-source-list`}
                        value={row.receive_source_id || ""}
                        options={receiveSourcesOptions}
                        labelKey="name"
                        placeholder="انتخاب منبع دریافت"
                      onSelect={(selectedId, selectedRow) => {
                          const sourceSettings = createInitialSourceSettings();
                          setProjectExtraSources((prevRows) => (
                            prevRows.map((item) => (
                              item.id === row.id
                                ? {
                                    ...item,
                                    receive_source_id: selectedId,
                                    name: String(selectedRow?.name || ""),
                                    platform: String(selectedRow?.platform || ""),
                                    link: String(selectedRow?.link || ""),
                                    ...sourceSettings,
                                    ai_activity_status: deriveAiActivityStatus(sourceSettings),
                                    show_source_settings: false,
                                  }
                                : item
                            ))
                          ));
                        }}
                      />
                    </div>
                    {row.receive_source_id && (
                      <div className="project-create-field">
                        <label htmlFor={`${row.id}-platform`}>پلتفرم</label>
                        <input
                          id={`${row.id}-platform`}
                          value={row.platform}
                          readOnly
                          aria-readonly="true"
                        />
                      </div>
                    )}
                    {row.receive_source_id && (
                      <div className="project-create-field">
                        <label htmlFor={`${row.id}-link`}>لینک با شناسه</label>
                        <input
                          id={`${row.id}-link`}
                          className="source-link-input-ltr"
                          value={row.link}
                          readOnly
                          aria-readonly="true"
                        />
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    className="project-wireframe-remove-btn"
                    onClick={() => handleRemoveProjectExtraSource(row.id)}
                    aria-label="حذف منبع خبر"
                  >
                    حذف
                  </button>
                  <button
                    type="button"
                    className="project-wireframe-link-btn project-source-settings-toggle-btn project-main-source-settings-btn"
                    onClick={() => handleToggleProjectExtraSourceSettings(row.id)}
                  >
                    {row.show_source_settings ? "پنهان کردن تنظیمات منبع" : "+ تنظیمات منبع"}
                  </button>

                  {row.show_source_settings && (
                    <div
                      id={`${row.id}-source-settings`}
                      className="project-source-settings-block"
                    >
                      {renderSourceSettingsFields(
                        row,
                        (field, value) => handleProjectExtraSourceChange(row.id, field, value),
                        row.id
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {errorMsg && <div className="form-error">{errorMsg}</div>}
            {successMsg && <div className="form-success">{successMsg}</div>}

            <div className="project-create-actions">
              <button
                type="button"
                className="project-create-cancel-btn"
                onClick={closeProjectCreatePanel}
                disabled={saving}
              >
                انصراف
              </button>
              <button className="save-btn project-create-save-btn" type="submit" disabled={saving}>
                {saving ? "در حال ذخیره..." : editingProjectId ? "ذخیره تغییرات" : "ذخیره پروژه"}
              </button>
            </div>

            <div className="project-create-empty-card" aria-hidden="true" />
          </form>
        </div>
      );
    }

    return (
      <div className="projects-container">
        <div className="projects-toolbar">
          <div className="projects-bulk-actions">
            <button
              type="button"
              className="projects-bulk-btn"
              onClick={handleBulkDeleteProjects}
              disabled={selectedProjectIds.length === 0}
            >
              حذف
            </button>
            <button
              type="button"
              className="projects-bulk-btn"
              onClick={handleCopySelectedProjects}
              disabled={selectedProjectIds.length === 0}
            >
              کپی
            </button>
          </div>

          <div className="projects-search-box">
            <input
              type="search"
              placeholder="جستجو"
              value={projectSearchTerm}
              onChange={(event) => setProjectSearchTerm(event.target.value)}
            />
            <span className="projects-search-icon" aria-hidden="true">⌕</span>
          </div>

          <button
            type="button"
            className="projects-create-btn"
            onClick={openProjectCreatePanel}
          >
            ایجاد پروژه جدید
          </button>
        </div>

        <div className="projects-filters-row">
          <select
            value={projectManagerFilter}
            onChange={(event) => setProjectManagerFilter(event.target.value)}
          >
            <option value="all">یک مدیر ارشد انتخاب کنید</option>
            {projectManagerOptions.map((managerName) => (
              <option key={managerName} value={managerName}>
                {managerName}
              </option>
            ))}
          </select>

          <select
            value={projectRowsPerPage}
            onChange={(event) => setProjectRowsPerPage(Number(event.target.value))}
          >
            {PROJECT_ROWS_PER_PAGE_OPTIONS.map((option) => (
              <option key={option} value={option}>
                ردیف {formatFaNumber(option)} تایی
              </option>
            ))}
          </select>

          <select
            value={projectStatusFilter}
            onChange={(event) => setProjectStatusFilter(event.target.value)}
          >
            <option value="all">همه وضعیت‌ها</option>
            <option value="active">فعال</option>
            <option value="inactive">غیرفعال</option>
          </select>
        </div>

        {loadingProjects && (
          <div className="dev-box">در حال دریافت پروژه‌ها...</div>
        )}

        {projectsMsg.text && (
          <div className={`project-list-message ${projectsMsg.type}`}>{projectsMsg.text}</div>
        )}

        {!loadingProjects && filteredProjects.length === 0 && (
          <div className="dev-box small">{emptyProjectsMessage}</div>
        )}

        {!loadingProjects && filteredProjects.length > 0 && (
          <>
            <div className="projects-table-card">
              <div className="projects-table-shell dashboard-activity-table-wrap">
                <table className="projects-activity-table dashboard-activity-table">
                  <thead>
                    <tr>
                      <th className="projects-check-cell">
                        <input
                          type="checkbox"
                          checked={areAllVisibleProjectsSelected}
                          onChange={handleSelectAllVisibleProjects}
                          aria-label="انتخاب همه پروژه های صفحه"
                        />
                      </th>
                      <th>کد</th>
                      <th>عنوان پروژه</th>
                      <th>تاریخ ایجاد</th>
                      <th>منابع دریافت</th>
                      <th>منابع انتشار</th>
                      <th>تعداد منابع</th>
                      <th>تعداد مقاصد</th>
                      <th>اخبار منتشر</th>
                      <th>وضعیت</th>
                      <th>مشاهده</th>
                      <th>ویرایش</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedProjects.map((project) => {
                      const projectStatus = getProjectStatusInfo(project);
                      const sourceCount = getProjectCount(project, [
                        "source_count",
                        "sources_count",
                        "total_sources",
                        "news_sources_count",
                        "sources",
                      ]);
                      const destinationCount = getProjectCount(project, [
                        "destination_count",
                        "destinations_count",
                        "target_count",
                        "targets_count",
                        "publish_targets_count",
                        "publish_links",
                      ]);
                      const publishedNewsCount = getProjectCount(project, [
                        "published_news_count",
                        "total_published_news",
                        "published_count",
                        "news_published_count",
                        "published_news",
                      ]);
                      const receiveSourceNames = getProjectSourceNames(
                        project,
                        ["receive_source_names"],
                        ["receive_source_name", "receive_source", "source_name"]
                      );
                      const publishSourceNames = getProjectSourceNames(
                        project,
                        ["publish_source_names"],
                        ["publish_source_name", "publish_source", "publish_platform_name"]
                      );
                      const isChecked = selectedProjectIds.includes(String(project.id));

                      return (
                        <tr key={project.id}>
                          <td className="projects-check-cell">
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => handleProjectRowSelection(project.id)}
                              aria-label={`انتخاب پروژه ${project.title || project.id}`}
                            />
                          </td>
                          <td>{getProjectCode(project)}</td>
                          <td>
                            <button
                              type="button"
                              className="project-title-text"
                              onClick={() => handleOpenProjectView(project)}
                              aria-label={`مشاهده پروژه ${project.title || project.id}`}
                              style={{ background: "none", border: "none", padding: 0, cursor: "pointer" }}
                            >
                              {project.title || "بدون عنوان"}
                            </button>
                          </td>
                          <td>{getProjectCreatedDate(project)}</td>
                          <td>
                            <span className="project-source-list-text">
                              {receiveSourceNames.length > 0 ? receiveSourceNames.join(" ، ") : "--"}
                            </span>
                          </td>
                          <td>
                            <span className="project-source-list-text">
                              {publishSourceNames.length > 0 ? publishSourceNames.join(" ، ") : "--"}
                            </span>
                          </td>
                          <td>{formatFaNumber(sourceCount)}</td>
                          <td>{formatFaNumber(destinationCount)}</td>
                          <td>{formatFaNumber(publishedNewsCount)}</td>
                          <td>
                            <span className={`project-status-tag ${projectStatus.key}`}>
                              {projectStatus.label}
                            </span>
                          </td>
                          <td className="projects-edit-cell">
                            <button
                              type="button"
                              className="project-edit-btn"
                              onClick={() => handleOpenProjectView(project)}
                              aria-label={`مشاهده پروژه ${project.title || project.id}`}
                            >
                              👁
                            </button>
                          </td>
                          <td className="projects-edit-cell">
                            <button
                              type="button"
                              className="project-edit-btn"
                              onClick={() => handleEditProject(project)}
                              aria-label={`ویرایش پروژه ${project.title || project.id}`}
                            >
                              ✎
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="projects-table-pagination dashboard-activity-pagination">
                <button
                  type="button"
                  onClick={() => handleProjectPageChange("prev")}
                  disabled={currentProjectPage <= 1}
                  aria-label="صفحه قبل"
                >
                  ‹
                </button>
                <span className="projects-pagination-current dashboard-activity-page-current">
                  {formatFaNumber(currentProjectPage)}
                </span>
                <span className="projects-pagination-total dashboard-activity-page-size">
                  {formatFaNumber(totalProjectPages)} صفحه
                </span>
                <button
                  type="button"
                  onClick={() => handleProjectPageChange("next")}
                  disabled={currentProjectPage >= totalProjectPages}
                  aria-label="صفحه بعد"
                >
                  ›
                </button>
              </div>
            </div>
            <div id="projects-empty-card-spacer" className="project-create-empty-card projects-empty-card-spacer" aria-hidden="true" />
          </>
        )}

        {isDeleteModalOpen && (
          <div className="confirm-modal-overlay" onClick={closeDeleteModal}>
            <div
              className="confirm-modal"
              role="dialog"
              aria-modal="true"
              aria-label="حذف پروژه"
              onClick={(event) => event.stopPropagation()}
            >
              <h4>
                {Array.isArray(projectToDelete?.ids) && projectToDelete.ids.length > 1
                  ? "حذف پروژه‌ها"
                  : "حذف پروژه"}
              </h4>
              <p>
                {Array.isArray(projectToDelete?.ids) && projectToDelete.ids.length > 1
                  ? `آیا از حذف ${formatFaNumber(projectToDelete.ids.length)} پروژه انتخاب‌شده مطمئن هستید؟`
                  : <>آیا از حذف پروژه <strong>{projectToDelete?.title || "انتخاب‌شده"}</strong> مطمئن هستید؟</>}
              </p>
              <div className="confirm-modal-actions">
                <button
                  type="button"
                  className="confirm-cancel-btn"
                  onClick={closeDeleteModal}
                  disabled={deletingProject}
                >
                  انصراف
                </button>
                <button
                  type="button"
                  className="confirm-delete-btn"
                  onClick={handleDeleteProject}
                  disabled={deletingProject}
                >
                  {deletingProject ? "در حال حذف..." : "بله، حذف شود"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const selectedProjectView = useMemo(() => (
    projects.find((project) => String(project?.id) === String(selectedProjectViewId)) || null
  ), [projects, selectedProjectViewId]);

  const projectViewRows = useMemo(() => {
    if (!selectedProjectViewId) {
      return [];
    }

    const overviewData = MOCK_NEWS_OVERVIEW_BY_PROJECT_ID[String(selectedProjectViewId)] || {
      received_news: [],
      published_news: [],
    };
    const mappedRows = mapProjectOverviewToNewsRows(overviewData, selectedProjectView);

    return mappedRows.sort((firstRow, secondRow) => (
      getNewsTimestamp(secondRow.publishedAtRaw) - getNewsTimestamp(firstRow.publishedAtRaw)
    ));
  }, [selectedProjectView, selectedProjectViewId]);

  const baseProjectViewRows = projectAllNewsRows;

  const filteredProjectViewRows = baseProjectViewRows.filter((row) => {
    if (projectViewNewsTab === "published") {
      return row.statusKey === "published";
    }

    return true;
  });

  const totalProjectViewNewsPages = Math.max(
    1,
    Math.ceil(filteredProjectViewRows.length / PROJECT_VIEW_NEWS_ROWS_PER_PAGE)
  );
  const safeProjectViewNewsPage = Math.min(projectViewNewsPage, totalProjectViewNewsPages);
  const paginatedProjectViewRows = filteredProjectViewRows.slice(
    (safeProjectViewNewsPage - 1) * PROJECT_VIEW_NEWS_ROWS_PER_PAGE,
    safeProjectViewNewsPage * PROJECT_VIEW_NEWS_ROWS_PER_PAGE
  );

  const renderProjectViewTab = () => (
    <div className="project-view-layout">
      <div className="project-view-head">
        <div className="project-view-head-meta">
          <h3>مشاهده پروژه</h3>
        </div>
        <button
          type="button"
          className="projects-create-back-btn"
          onClick={() => setActiveTab("projects")}
        >
          بازگشت به لیست پروژه‌ها
        </button>
      </div>

      <div className="project-view-tabs-shell">
        {PROJECT_VIEW_NEWS_TABS.map((tabItem) => (
          <button
            key={tabItem.key}
            type="button"
            className={`project-view-tab-btn ${projectViewNewsTab === tabItem.key ? "active" : ""}`}
            onClick={() => setProjectViewNewsTab(tabItem.key)}
          >
            {tabItem.label}
          </button>
        ))}
      </div>

      {filteredProjectViewRows.length === 0 ? (
        <div className="dev-box small">خبری برای نمایش در این تب وجود ندارد.</div>
      ) : (
        <>
        <div className="project-view-news-grid">
          {paginatedProjectViewRows.map((row) => {
            const isAllNewsTab = projectViewNewsTab === "all";
            const normalizedMediaType = String(row.mediaType || "").trim().toLowerCase();
            const isTextVideoCard = (
              normalizedMediaType === "text+video"
              || normalizedMediaType === "متن + ویدیو"
              || normalizedMediaType === "متن+ویدیو"
            );
            const isTextOnlyCard = (
              normalizedMediaType === "text"
              || normalizedMediaType === "متنی"
            );
            return (
            <article
              key={row.rowId}
              className="news-preview-card"
              role={isAllNewsTab ? "button" : undefined}
              tabIndex={isAllNewsTab ? 0 : undefined}
              onClick={isAllNewsTab ? () => handleOpenNewsView(row) : undefined}
              onKeyDown={
                isAllNewsTab
                  ? (event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      handleOpenNewsView(row);
                    }
                  }
                  : undefined
              }
              aria-label={isAllNewsTab ? `مشاهده خبر ${row.title || ""}` : undefined}
            >
              <div className="news-view-media-wrap">
                <span className="news-media-type-badge">{getNewsMediaBadgeLabel(row)}</span>
                {isTextVideoCard ? (
                  <button
                    type="button"
                    className="news-video-thumbnail-placeholder"
                    onClick={(event) => {
                      event.stopPropagation();
                      handleOpenNewsView(row);
                    }}
                    aria-label={`مشاهده خبر ${row.title || ""}`}
                  >
                    <span className="news-video-thumbnail-play">▶</span>
                  </button>
                ) : isTextOnlyCard ? (
                  <div className="news-media-thumbnail-placeholder" aria-hidden="true" />
                ) : row.mediaUrl ? (
                  <>
                    <img
                      src={row.mediaUrl}
                      alt={row.title || "media"}
                      onError={(event) => handleMediaPreviewError(event, row.sourceMediaUrl)}
                    />
                    <div className="news-view-media-placeholder hidden">مدیای ثبت شده برای این خبر موجود نیست</div>
                  </>
                ) : (
                  <div className="news-view-media-placeholder">مدیای ثبت شده برای این خبر موجود نیست</div>
                )}
              </div>
              <div className="news-preview-card-body">
                {projectViewNewsTab !== "published" && (
                  <span className="project-view-source-badge">{row.sourceName || "بدون منبع"}</span>
                )}
                {projectViewNewsTab !== "published" && row.alreadyPublished && (
                  <span
                    className="project-view-already-published-badge"
                    title={row.alreadyPublishedDescription || "این خبر قبلاً منتشر شده و به همین دلیل دوباره منتشر نشد"}
                  >
                    قبلاً منتشر شده
                  </span>
                )}
                {projectViewNewsTab !== "published"
                  && Array.isArray(row.newsTypeBadges)
                  && row.newsTypeBadges.length > 0 && (
                  <div className="project-view-news-type-badges">
                    {row.newsTypeBadges.map((badgeLabel, badgeIndex) => (
                      <span key={`${row.rowId}-news-type-${badgeIndex}`} className="project-view-news-type-badge">
                        {badgeLabel}
                      </span>
                    ))}
                  </div>
                )}
                {projectViewNewsTab === "published"
                  && Array.isArray(row.publishDestinationBadges)
                  && row.publishDestinationBadges.length > 0 && (
                  <div className="project-view-publish-badges">
                    {row.publishDestinationBadges.map((badgeItem, badgeIndex) => {
                      const badgeLabel = typeof badgeItem === "string"
                        ? badgeItem
                        : String(badgeItem?.label || "").trim();
                      const badgeKey = `${row.rowId}-publish-badge-${badgeIndex}-${badgeLabel}`;
                      return (
                        <span
                          key={badgeKey}
                          className={`project-view-publish-badge ${String(badgeLabel || "").startsWith("ناموفق:") ? "failed" : "success"}`}
                        >
                          {badgeLabel}
                        </span>
                      );
                    })}
                  </div>
                )}
                <h5>
                  {String(row.title || "بدون عنوان").length > 65
                    ? `${String(row.title || "بدون عنوان").slice(0, 65).trim()}...`
                    : String(row.title || "بدون عنوان")}
                </h5>
                <div className="news-preview-card-footer">
                  {projectViewNewsTab === "published"
                    && Array.isArray(row.publishFailReasons)
                    && row.publishFailReasons.length > 0 && (
                    <div className="project-view-publish-fail-reasons">
                      {row.publishFailReasons.map((failItem, failIndex) => (
                        <div key={`${row.rowId}-fail-${failIndex}`} className="project-view-publish-fail-reason">
                          {failItem.text}
                        </div>
                      ))}
                    </div>
                  )}
                  <span className="news-preview-card-date">{row.publishedAt || "--"}</span>
                </div>
              </div>
            </article>
          );
          })}
        </div>
        {totalProjectViewNewsPages > 1 ? (
          <div className="project-view-pagination">
            <button
              type="button"
              onClick={() => setProjectViewNewsPage((prev) => Math.max(1, prev - 1))}
              disabled={safeProjectViewNewsPage <= 1}
            >
              قبلی
            </button>
            <span>
              صفحه {formatFaNumber(safeProjectViewNewsPage)} از {formatFaNumber(totalProjectViewNewsPages)}
            </span>
            <button
              type="button"
              onClick={() => setProjectViewNewsPage((prev) => Math.min(totalProjectViewNewsPages, prev + 1))}
              disabled={safeProjectViewNewsPage >= totalProjectViewNewsPages}
            >
              بعدی
            </button>
          </div>
        ) : (
          <div
            id={`project-view-news-empty-card-spacer-${projectViewNewsTab}`}
            className="project-create-empty-card ads-empty-card-spacer"
            aria-hidden="true"
          />
        )}
        </>
      )}
    </div>
  );

  const renderSourcesTab = () => {
    if (showSourceCreateForm) {
      const isEditMode = Boolean(editingSourceRowId);
      const isPricePlatform = normalizeReceivePlatformValue(sourceCreateForm.source_platform) === "price";

      return (
        <div className="sources-create-workspace">
          <div className="projects-create-head">
            <div>
              <h3>{isEditMode ? "ویرایش منبع" : "ایجاد منبع دریافت جدید"}</h3>
            </div>
            <button
              type="button"
              className="projects-create-back-btn"
              onClick={closeSourceCreatePanel}
              disabled={savingSource}
            >
              بازگشت به منابع
            </button>
          </div>

          <form className="project-create-form" onSubmit={handleSourceCreateSubmit}>
            <div className="project-create-card source-create-card">
              <div className="source-create-grid">
                <div className="project-create-field">
                  <label htmlFor="source-create-name">عنوان منبع</label>
                  <input
                    id="source-create-name"
                    value={sourceCreateForm.source_name}
                    onChange={(event) => handleSourceCreateFieldChange("source_name", event.target.value)}
                    placeholder=""
                  />
                </div>

                <div className="project-create-field">
                  <label htmlFor="source-create-platform">پلتفرم</label>
                  <select
                    id="source-create-platform"
                    value={sourceCreateForm.source_platform}
                    onChange={(event) => handleSourceCreateFieldChange("source_platform", event.target.value)}
                  >
                    {sourceCreatePlatformTypeOptions.map((platformValue) => (
                      <option key={`source-create-platform-${platformValue}`} value={platformValue}>
                        {getReceivePlatformLabel(platformValue)}
                      </option>
                    ))}
                  </select>
                </div>

                {!isPricePlatform && (
                  <div className="project-create-field">
                    <label htmlFor="source-create-link">لینک یا شناسه</label>
                    <input
                      id="source-create-link"
                      className="source-link-input-ltr"
                      value={sourceCreateForm.source_link}
                      onChange={(event) => handleSourceCreateFieldChange("source_link", event.target.value)}
                      placeholder={getReceiveSourceLinkPlaceholder(sourceCreateForm.source_platform)}
                    />
                  </div>
                )}
                <div className="project-create-field">
                  <label htmlFor="source-create-receive-interval-seconds">زمان بررسی منبع دریافت (ثانیه)</label>
                  <input
                    id="source-create-receive-interval-seconds"
                    type="number"
                    min="1"
                    step="1"
                    value={sourceCreateForm.receive_interval_seconds}
                    onChange={(event) => handleSourceCreateFieldChange("receive_interval_seconds", event.target.value)}
                    placeholder={isPricePlatform ? "3000" : "60"}
                  />
                </div>
                {isPricePlatform && (
                  <div className="project-create-field">
                    <label htmlFor="source-create-price-template">قالب قیمت فعال برای این منبع</label>
                    <select
                      id="source-create-price-template"
                      value={String(sourceCreateForm.price_template_id || "")}
                      onChange={(event) => handleSourceCreateFieldChange("price_template_id", event.target.value)}
                    >
                      <option value="">انتخاب نشده</option>
                      {priceTemplateRows.map((templateRow) => (
                        <option key={`source-create-price-template-${templateRow.id}`} value={String(templateRow.id || "")}>
                          {templateRow.title || `قالب ${templateRow.id}`}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                {!isPricePlatform && (
                  <div className="project-create-field">
                    <label htmlFor="source-create-anti-ad-mode">ضد تبلیغ</label>
                    <select
                      id="source-create-anti-ad-mode"
                      value={sourceCreateForm.anti_ad_mode}
                      onChange={(event) => handleSourceCreateFieldChange("anti_ad_mode", event.target.value)}
                    >
                      <option value="none">یک مورد را انتخاب کنید</option>
                      <option value="ai">ضد تبلیغ با هوش مصنوعی</option>
                      <option value="system">ضد تبلیغ سیستمی</option>
                    </select>
                  </div>
                )}
                {normalizeReceivePlatformValue(sourceCreateForm.source_platform) === "rubika" && (
                  <div className="project-create-field">
                    <label htmlFor="source-create-rubika-account">اکانت روبیکا</label>
                    <select
                      id="source-create-rubika-account"
                      value={sourceCreateForm.rubika_account_id}
                      onChange={(event) => handleSourceCreateFieldChange("rubika_account_id", event.target.value)}
                    >
                      <option value="">انتخاب نشده</option>
                      {sourceRubikaAccountsOptions.map((accountItem) => (
                        <option key={`source-create-rubika-account-${accountItem.id}`} value={accountItem.id}>
                          {accountItem.phone || `اکانت ${accountItem.id}`}{accountItem.isActive ? "" : " (غیرفعال)"}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                {normalizeReceivePlatformValue(sourceCreateForm.source_platform) === "eita with video" && (
                  <div className="project-create-field">
                    <label htmlFor="source-create-eita-account">اکانت ایتا</label>
                    <select
                      id="source-create-eita-account"
                      value={sourceCreateForm.eita_account_id}
                      onChange={(event) => handleSourceCreateFieldChange("eita_account_id", event.target.value)}
                    >
                      <option value="">انتخاب نشده</option>
                      {sourceEitaAccountsOptions.map((accountItem) => (
                        <option key={`source-create-eita-account-${accountItem.id}`} value={accountItem.id}>
                          {accountItem.phone || `اکانت ${accountItem.id}`}{accountItem.isActive ? "" : " (غیرفعال)"}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                {!isPricePlatform && String(sourceCreateForm.anti_ad_mode || "").trim().toLowerCase() === "system" && (
                  <div className="project-create-field" style={{ gridColumn: "1 / -1" }}>
                    <label>قوانین</label>
                    {(Array.isArray(sourceCreateForm.anti_ad_with_system_rules)
                      ? sourceCreateForm.anti_ad_with_system_rules
                      : [{
                        field_1: "",
                        field_2: "",
                        field_3: "",
                        field_4: "",
                        selected_flag: "is_ad",
                      }]
                    ).map((ruleValue, ruleIndex) => (
                      <div
                        key={`anti-ad-rule-row-${ruleIndex}`}
                        style={{ display: "flex", gap: "8px", marginTop: ruleIndex === 0 ? "0" : "8px", alignItems: "center", flexWrap: "wrap" }}
                      >
                        <input
                          style={{ flex: "1 1 180px", minWidth: "140px" }}
                          value={String(ruleValue?.field_1 || "")}
                          onChange={(event) => handleAntiAdSystemRuleChange(ruleIndex, "field_1", event.target.value)}
                          maxLength={1000}
                          placeholder={`عبارت قانون ${formatFaNumber(ruleIndex + 1)}`}
                        />
                        <select
                          style={{ flexShrink: 0 }}
                          value={String(ruleValue?.match_mode || "contains")}
                          onChange={(event) => handleAntiAdSystemRuleChange(ruleIndex, "match_mode", event.target.value)}
                        >
                          {ANTI_AD_RULE_MATCH_MODE_OPTIONS.map((option) => (
                            <option key={`anti-ad-mode-${ruleIndex}-${option.value}`} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                        <select
                          style={{ flexShrink: 0 }}
                          value={String(ruleValue?.selected_flag || "is_ad")}
                          onChange={(event) => handleAntiAdSystemRuleChange(ruleIndex, "selected_flag", event.target.value)}
                        >
                          {ANTI_AD_RULE_FLAG_OPTIONS.map((option) => (
                            <option key={`anti-ad-flag-${ruleIndex}-${option.value}`} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          className="project-create-cancel-btn"
                          onClick={() => handleRemoveAntiAdSystemRule(ruleIndex)}
                        >
                          حذف
                        </button>
                      </div>
                    ))}
                    <p style={{ margin: "10px 0 0", fontSize: "0.85rem", color: "#64748b", lineHeight: 1.9 }}>
                      حالت «اگر شامل نباشد» یک فیلتر معکوس است: هر خبری که
                      هیچ‌کدام از عبارت‌های آن قانون را نداشته باشد، برچسب
                      انتخاب‌شده را می‌گیرد. قانونی که عبارتش خالی باشد نادیده
                      گرفته می‌شود.
                    </p>
                    <div style={{ marginTop: "8px" }}>
                      <button
                        type="button"
                        className="save-btn"
                        onClick={handleAddAntiAdSystemRule}
                      >
                        افزودن قانون
                      </button>
                    </div>
                  </div>
                )}
                {!isPricePlatform && String(sourceCreateForm.anti_ad_mode || "").trim().toLowerCase() === "ai" && (
                  <div className="project-create-field" style={{ gridColumn: "1 / -1" }}>
                    <label htmlFor="source-create-anti-ad-ai-prompt">پرامپت ضد تبلیغ با هوش مصنوعی</label>
                    <textarea
                      id="source-create-anti-ad-ai-prompt"
                      value={String(sourceCreateForm.anti_ad_with_ai_prompt || "")}
                      onChange={(event) => handleSourceCreateFieldChange("anti_ad_with_ai_prompt", event.target.value)}
                      rows={5}
                      placeholder="پرامپت موردنظر برای تشخیص و حذف تبلیغات را وارد کنید."
                    />
                  </div>
                )}

              </div>
            </div>
            <div id="source-create-card-empty-card-spacer-1" className="project-create-empty-card" aria-hidden="true" />

            <div className="project-create-actions">
              <button
                type="button"
                className="project-create-cancel-btn"
                onClick={closeSourceCreatePanel}
                disabled={savingSource}
              >
                انصراف
              </button>
              <button
                type="submit"
                className="save-btn project-create-save-btn"
                disabled={savingSource}
              >
                {savingSource
                  ? "در حال ذخیره..."
                  : isEditMode
                    ? "ذخیره تغییرات"
                    : "ذخیره منبع"}
              </button>
            </div>
            <div id="sources-create-actions-empty-card-spacer" className="project-create-empty-card" aria-hidden="true" />

            {isPricePlatform && (
              <>
              <div className="project-create-card source-create-card">
                <div className="settings-section-head">
                  <h4>قالب‌های دریافت قیمت</h4>
                  <button
                    type="button"
                    className="project-create-cancel-btn"
                    onClick={handleResetPriceTemplateForm}
                    disabled={savingPriceTemplate}
                  >
                    قالب جدید
                  </button>
                </div>
                <div className="source-create-grid">
                  <div className="project-create-field">
                    <label htmlFor="price-template-title">عنوان قالب</label>
                    <input
                      id="price-template-title"
                      value={priceTemplateForm.title}
                      onChange={(event) => handlePriceTemplateFieldChange("title", event.target.value)}
                      placeholder="قالب قیمت بازار"
                    />
                  </div>
                  <div className="project-create-field">
                    <label htmlFor="price-template-news-title">قالب عنوان خبر</label>
                    <input
                      id="price-template-news-title"
                      value={priceTemplateForm.news_title_template}
                      onChange={(event) => handlePriceTemplateFieldChange("news_title_template", event.target.value)}
                      placeholder="نرخ‌های لحظه‌ای بازار - {date} ساعت {time}"
                    />
                  </div>
                  <div className="project-create-field" style={{ gridColumn: "1 / -1" }}>
                    <label htmlFor="price-template-text">متن قالب خبر</label>
                    <textarea
                      id="price-template-text"
                      value={priceTemplateForm.text_template}
                      onChange={(event) => handlePriceTemplateFieldChange("text_template", event.target.value)}
                      rows={10}
                      placeholder="از متغیرهایی مثل {USD} و {USD_change} استفاده کنید."
                    />
                  </div>
                  <label className="project-create-checkbox-field">
                    <input
                      type="checkbox"
                      checked={Boolean(priceTemplateForm.is_active)}
                      onChange={(event) => handlePriceTemplateFieldChange("is_active", event.target.checked)}
                    />
                    <span>این قالب فعال باشد</span>
                  </label>
                </div>
                <div className="project-create-actions">
                  <button
                    type="button"
                    className="save-btn project-create-save-btn"
                    onClick={handleSavePriceTemplate}
                    disabled={savingPriceTemplate}
                  >
                    {savingPriceTemplate
                      ? "در حال ذخیره قالب..."
                      : editingPriceTemplateId
                        ? "ذخیره تغییرات قالب"
                        : "ذخیره قالب"}
                  </button>
                </div>
                <div id="price-template-create-actions-empty-card-spacer" className="project-create-empty-card" aria-hidden="true" />
                <div className="settings-table-wrap" style={{ marginTop: "16px" }}>
                  <table className="settings-table">
                    <thead>
                      <tr>
                        <th>عنوان قالب</th>
                        <th>منبع/منابع متصل</th>
                        <th>قالب عنوان خبر</th>
                        <th>وضعیت</th>
                        <th>آخرین ویرایش</th>
                        <th>عملیات</th>
                      </tr>
                    </thead>
                    <tbody>
                      {priceTemplateRows.map((templateRow) => (
                        <tr key={`price-template-${templateRow.id}`}>
                          <td>{templateRow.title || "--"}</td>
                          <td>
                            {Array.isArray(templateRow.receiveSourceNames) && templateRow.receiveSourceNames.length > 0
                              ? templateRow.receiveSourceNames.join("، ")
                              : (templateRow.receiveSourceName || "--")}
                          </td>
                          <td>{templateRow.newsTitleTemplate || "--"}</td>
                          <td>
                            <span className={`settings-status-chip ${templateRow.isActive ? "active" : "inactive"}`}>
                              {templateRow.isActive ? "فعال" : "غیرفعال"}
                            </span>
                          </td>
                          <td>{templateRow.updatedAt || "--"}</td>
                          <td>
                            <button
                              type="button"
                              className="settings-action-btn"
                              onClick={() => handleEditPriceTemplate(templateRow)}
                              disabled={savingPriceTemplate}
                              aria-label="ویرایش قالب قیمت"
                            >
                              ✎
                            </button>
                            <button
                              type="button"
                              className="settings-action-btn"
                              onClick={() => handleDeletePriceTemplate(templateRow.id)}
                              disabled={savingPriceTemplate}
                              aria-label="حذف قالب قیمت"
                            >
                              ×
                            </button>
                          </td>
                        </tr>
                      ))}
                      {!loadingPriceTemplates && priceTemplateRows.length === 0 && (
                        <tr>
                          <td colSpan={6}>قالب قیمتی برای کاربر شما ثبت نشده است.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                {loadingPriceTemplates && <div className="settings-msg">در حال دریافت قالب‌های قیمت...</div>}
              </div>
              <div id="source-create-card-empty-card-spacer-2" className="project-create-empty-card" aria-hidden="true" />
              </>
            )}

            {sourcesMsg.text && (
              <div className={`project-list-message ${sourcesMsg.type}`}>{sourcesMsg.text}</div>
            )}

          </form>
        </div>
      );
    }

    if (showPublishSourceCreateForm) {
      const isEditMode = Boolean(editingPublishSourceRowId);

      return (
        <div className="sources-create-workspace">
          <div className="projects-create-head">
            <div>
              <h3>{isEditMode ? "ویرایش منبع انتشار" : "ایجاد منبع انتشار جدید"}</h3>
            </div>
            <button
              type="button"
              className="projects-create-back-btn"
              onClick={closePublishSourceCreatePanel}
              disabled={savingPublishSource}
            >
              بازگشت به منابع
            </button>
          </div>

          <form className="project-create-form" onSubmit={handlePublishSourceCreateSubmit}>
            <div className="project-create-card source-create-card">
              <div className="source-create-grid">
                <div className="project-create-field">
                  <label htmlFor="publish-source-create-name">عنوان منبع</label>
                  <input
                    id="publish-source-create-name"
                    value={publishSourceCreateForm.source_name}
                    onChange={(event) => handlePublishSourceCreateFieldChange("source_name", event.target.value)}
                    placeholder=""
                  />
                </div>

                <div className="project-create-field">
                  <label htmlFor="publish-source-create-platform">پلتفرم</label>
                  <select
                    id="publish-source-create-platform"
                    value={publishSourceCreateForm.source_platform}
                    onChange={(event) => handlePublishSourceCreateFieldChange("source_platform", event.target.value)}
                  >
                    {publishSourcePlatformTypeOptions.map((platformValue) => (
                      <option key={`publish-source-create-platform-${platformValue}`} value={platformValue}>
                        {getPublishPlatformLabel(platformValue)}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="project-create-field">
                  <label htmlFor="publish-source-create-link">لینک یا شناسه</label>
                  <input
                    id="publish-source-create-link"
                    className="source-link-input-ltr"
                    value={publishSourceCreateForm.source_link}
                    onChange={(event) => handlePublishSourceCreateFieldChange("source_link", event.target.value)}
                    placeholder={getPublishSourceLinkPlaceholder(publishSourceCreateForm.source_platform)}
                  />
                </div>
              </div>
            </div>
            <div id="source-create-card-empty-card-spacer-3" className="project-create-empty-card" aria-hidden="true" />

            {publishSourcesMsg.text && (
              <div className={`project-list-message ${publishSourcesMsg.type}`}>{publishSourcesMsg.text}</div>
            )}

            <div className="project-create-actions">
              <button
                type="button"
                className="project-create-cancel-btn"
                onClick={closePublishSourceCreatePanel}
                disabled={savingPublishSource}
              >
                انصراف
              </button>
              <button
                type="submit"
                className="save-btn project-create-save-btn"
                disabled={savingPublishSource}
              >
                {savingPublishSource
                  ? "در حال ذخیره..."
                  : isEditMode
                    ? "ذخیره تغییرات"
                    : "ذخیره منبع انتشار"}
              </button>
            </div>
            <div id="publish-sources-create-actions-empty-card-spacer" className="project-create-empty-card" aria-hidden="true" />
          </form>
        </div>
      );
    }

    const emptySourcesMessage = sourcesRows.length === 0
      ? "هنوز منبعی ثبت نشده است."
      : "منبعی مطابق فیلترهای انتخابی پیدا نشد.";
    const emptyPublishSourcesMessage = publishSourcesRows.length === 0
      ? "هنوز منبع انتشار ثبت نشده است."
      : "منبع انتشاری مطابق فیلترهای انتخابی پیدا نشد.";

    return (
      <div className="sources-tab-layout">
        <section className="sources-panel-card receive-sources-panel-card">
          <div className="sources-panel-card-head">
            <h3>منابع دریافت</h3>
          </div>

          <div className="sources-toolbar">
            <button
              type="button"
              className="projects-create-btn sources-create-btn"
              onClick={handleCreateSource}
            >
              ایجاد منبع جدید
            </button>

            <div className="sources-toolbar-main">
              <div className="projects-search-box sources-search-box">
                <input
                  type="search"
                  placeholder="جستجو"
                  value={sourcesSearchTerm}
                  onChange={(event) => setSourcesSearchTerm(event.target.value)}
                />
                <span className="projects-search-icon" aria-hidden="true">⌕</span>
              </div>
            </div>

            <button
              type="button"
              className="projects-bulk-btn sources-delete-btn"
              onClick={openSourcesDeleteModal}
              disabled={selectedSourceIds.length === 0}
            >
              حذف
            </button>
          </div>

          <div className="sources-filters-row">
            <select
              value={sourcesManagerFilter}
              onChange={(event) => setSourcesManagerFilter(event.target.value)}
            >
              <option value="all">یک مدیر ارشد انتخاب کنید</option>
              {sourcesManagerOptions.map((managerName) => (
                <option key={managerName} value={managerName}>
                  {managerName}
                </option>
              ))}
            </select>

            <select
              value={sourcesRowsPerPage}
              onChange={(event) => setSourcesRowsPerPage(Number(event.target.value))}
            >
              {SOURCE_ROWS_PER_PAGE_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  ردیف {formatFaNumber(option)} تایی
                </option>
              ))}
            </select>

            <select
              value={sourcesStatusFilter}
              onChange={(event) => setSourcesStatusFilter(event.target.value)}
            >
              {SOURCE_STATUS_FILTER_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>

            <select
              value={sourcesPlatformFilter}
              onChange={(event) => setSourcesPlatformFilter(event.target.value)}
            >
              <option value="all">یک پلتفرم انتخاب کنید</option>
              {sourcesPlatformOptions.map((platformValue) => (
                <option key={platformValue} value={platformValue}>
                  {getReceivePlatformLabel(platformValue)}
                </option>
              ))}
            </select>
          </div>

          {sourcesMsg.text && (
            <div className={`project-list-message ${sourcesMsg.type}`}>{sourcesMsg.text}</div>
          )}

          {loadingProjects && (
            <div className="dev-box small">در حال دریافت منابع...</div>
          )}

          {!loadingProjects && (
            <div className="sources-table-card">
            <div className="projects-table-shell dashboard-activity-table-wrap">
              <table className="sources-table dashboard-activity-table">
                <thead>
                  <tr>
                    <th className="projects-check-cell">
                      <input
                        type="checkbox"
                        checked={areAllVisibleSourcesSelected}
                        onChange={handleSelectAllVisibleSources}
                        aria-label="انتخاب همه منابع صفحه"
                      />
                    </th>
                    <th>کد</th>
                    <th>نام منبع</th>
                    <th>تاریخ ایجاد</th>
                    <th>پلتفرم</th>
                    <th>نوع منبع</th>
                    <th>لینک یا شناسه</th>
                    <th>وضعیت</th>
                    <th>ویرایش</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedSourcesRows.length === 0 ? (
                    <tr>
                      <td className="dashboard-activity-empty-row sources-empty-row" colSpan={9}>
                        {emptySourcesMessage}
                      </td>
                    </tr>
                  ) : (
                    paginatedSourcesRows.map((row) => {
                      const normalizedSourceLink = normalizeNewsLink(row.sourceLink);
                      const isChecked = selectedSourceIds.includes(row.rowId);
                      const isReadOnlyStaticSource = !row.canEdit;

                      return (
                        <tr key={row.rowId}>
                          <td className="projects-check-cell">
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => handleSourceRowSelection(row.rowId)}
                              disabled={!row.canDelete}
                              aria-label={`انتخاب منبع ${row.sourceName}`}
                            />
                          </td>
                          <td>{row.code}</td>
                          <td className="sources-name-cell">{row.sourceName}</td>
                          <td>{row.createdAt}</td>
                          <td>{getReceivePlatformLabel(row.platform)}</td>
                          <td>
                            <span className={`source-type-badge ${row.sourceTypeKey}`}>
                              {row.sourceTypeLabel}
                            </span>
                          </td>
                          <td className="sources-link-cell publish-sources-link-cell">
                            {normalizedSourceLink ? (
                              <a href={normalizedSourceLink} target="_blank" rel="noreferrer">
                                {row.sourceLink}
                              </a>
                            ) : (
                              <span className="news-tab-link-placeholder">--</span>
                            )}
                          </td>
                          <td>
                            <span className={`project-status-tag ${row.statusKey}`}>
                              {row.statusLabel}
                            </span>
                          </td>
                          <td className="projects-edit-cell">
                            <button
                              type="button"
                              className="project-edit-btn"
                              onClick={() => handleEditSource(row.rowId)}
                              disabled={isReadOnlyStaticSource}
                              aria-label={`ویرایش منبع ${row.sourceName}`}
                            >
                              ✎
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {filteredSourcesRows.length > 0 && (
              <div className="projects-table-pagination dashboard-activity-pagination">
                <button
                  type="button"
                  onClick={() => handleSourcesPageChange("prev")}
                  disabled={currentSourcesPage <= 1}
                  aria-label="صفحه قبل"
                >
                  ‹
                </button>
                <span className="projects-pagination-current dashboard-activity-page-current">
                  {formatFaNumber(currentSourcesPage)}
                </span>
                <span className="projects-pagination-total dashboard-activity-page-size">
                  {formatFaNumber(totalSourcesPages)} صفحه
                </span>
                <button
                  type="button"
                  onClick={() => handleSourcesPageChange("next")}
                  disabled={currentSourcesPage >= totalSourcesPages}
                  aria-label="صفحه بعد"
                >
                  ›
                </button>
              </div>
            )}

            {isSourcesDeleteModalOpen && (
              <div className="confirm-modal-overlay" onClick={closeSourcesDeleteModal}>
                <div
                  className="confirm-modal"
                  role="dialog"
                  aria-modal="true"
                  aria-label="حذف منبع"
                  onClick={(event) => event.stopPropagation()}
                >
                  <h4>حذف منبع</h4>
                  <p>
                    آیا از حذف{" "}
                    <strong>{formatFaNumber(selectedSourceIds.length)}</strong>{" "}
                    منبع انتخاب‌شده مطمئن هستید؟
                  </p>
                  <div className="confirm-modal-actions">
                    <button
                      type="button"
                      className="confirm-cancel-btn"
                      onClick={closeSourcesDeleteModal}
                      disabled={deletingSources}
                    >
                      انصراف
                    </button>
                    <button
                      type="button"
                      className="confirm-delete-btn"
                      onClick={confirmDeleteSelectedSources}
                      disabled={deletingSources}
                    >
                      {deletingSources ? "در حال حذف..." : "بله، حذف شود"}
                    </button>
                  </div>
                </div>
              </div>
            )}
            </div>
          )}
        </section>

        <section className="sources-panel-card publish-sources-panel-card">
          <div className="sources-panel-card-head">
            <h3>منابع انتشار</h3>
          </div>

          <div className="sources-toolbar">
          <button
            type="button"
            className="projects-create-btn sources-create-btn"
            onClick={openPublishSourceCreatePanel}
          >
            ایجاد منبع انتشار جدید
          </button>

          <div className="sources-toolbar-main">
            <div className="projects-search-box sources-search-box">
              <input
                type="search"
                placeholder="جستجو در منابع انتشار"
                value={publishSourcesSearchTerm}
                onChange={(event) => setPublishSourcesSearchTerm(event.target.value)}
              />
              <span className="projects-search-icon" aria-hidden="true">⌕</span>
            </div>
          </div>

          <button
            type="button"
            className="projects-bulk-btn sources-delete-btn"
            onClick={openPublishSourcesDeleteModal}
            disabled={selectedPublishSourceIds.length === 0}
          >
            حذف
          </button>
          </div>

          <div className="sources-filters-row">
          <select
            value={publishSourcesManagerFilter}
            onChange={(event) => setPublishSourcesManagerFilter(event.target.value)}
          >
            <option value="all">یک مدیر ارشد انتخاب کنید</option>
            {publishSourcesManagerOptions.map((managerName) => (
              <option key={`publish-manager-${managerName}`} value={managerName}>
                {managerName}
              </option>
            ))}
          </select>

          <select
            value={publishSourcesRowsPerPage}
            onChange={(event) => setPublishSourcesRowsPerPage(Number(event.target.value))}
          >
            {SOURCE_ROWS_PER_PAGE_OPTIONS.map((option) => (
              <option key={`publish-page-size-${option}`} value={option}>
                ردیف {formatFaNumber(option)} تایی
              </option>
            ))}
          </select>

          <select
            value={publishSourcesStatusFilter}
            onChange={(event) => setPublishSourcesStatusFilter(event.target.value)}
          >
            {SOURCE_STATUS_FILTER_OPTIONS.map((option) => (
              <option key={`publish-status-${option.value}`} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          <select
            value={publishSourcesPlatformFilter}
            onChange={(event) => setPublishSourcesPlatformFilter(event.target.value)}
          >
            <option value="all">یک پلتفرم انتخاب کنید</option>
            {publishSourcesPlatformOptions.map((platformValue) => (
              <option key={`publish-platform-${platformValue}`} value={platformValue}>
                {platformValue}
              </option>
            ))}
          </select>
          </div>

          {publishSourcesMsg.text && (
            <div className={`project-list-message ${publishSourcesMsg.type}`}>{publishSourcesMsg.text}</div>
          )}

          {loadingProjects && (
            <div className="dev-box small">در حال دریافت منابع انتشار...</div>
          )}

          {!loadingProjects && (
            <div className="sources-table-card">
            <div className="projects-table-shell dashboard-activity-table-wrap">
              <table className="sources-table dashboard-activity-table">
                <thead>
                  <tr>
                    <th className="projects-check-cell">
                      <input
                        type="checkbox"
                        checked={areAllVisiblePublishSourcesSelected}
                        onChange={handleSelectAllVisiblePublishSources}
                        aria-label="انتخاب همه منابع انتشار صفحه"
                      />
                    </th>
                    <th>کد</th>
                    <th>نام منبع انتشار</th>
                    <th>تاریخ ایجاد</th>
                    <th>پلتفرم</th>
                    <th>لینک یا شناسه</th>
                    <th>وضعیت</th>
                    <th>ویرایش</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedPublishSourcesRows.length === 0 ? (
                    <tr>
                      <td className="dashboard-activity-empty-row sources-empty-row" colSpan={8}>
                        {emptyPublishSourcesMessage}
                      </td>
                    </tr>
                  ) : (
                    paginatedPublishSourcesRows.map((row) => {
                      const normalizedSourceLink = normalizeNewsLink(row.sourceLink);
                      const isChecked = selectedPublishSourceIds.includes(row.rowId);

                      return (
                        <tr key={row.rowId}>
                          <td className="projects-check-cell">
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => handlePublishSourceRowSelection(row.rowId)}
                              aria-label={`انتخاب منبع انتشار ${row.sourceName}`}
                            />
                          </td>
                          <td>{row.code}</td>
                          <td className="sources-name-cell">{row.sourceName}</td>
                          <td>{row.createdAt}</td>
                          <td>{row.platform}</td>
                          <td className="sources-link-cell">
                            {normalizedSourceLink ? (
                              <a href={normalizedSourceLink} target="_blank" rel="noreferrer">
                                {row.sourceLink}
                              </a>
                            ) : (
                              <span className="news-tab-link-placeholder">--</span>
                            )}
                          </td>
                          <td>
                            <span className={`project-status-tag ${row.statusKey}`}>
                              {row.statusLabel}
                            </span>
                          </td>
                          <td className="projects-edit-cell">
                            <button
                              type="button"
                              className="project-edit-btn"
                              onClick={() => handleEditPublishSource(row.rowId)}
                              aria-label={`ویرایش منبع انتشار ${row.sourceName}`}
                            >
                              ✎
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {filteredPublishSourcesRows.length > 0 && (
              <div className="projects-table-pagination dashboard-activity-pagination">
                <button
                  type="button"
                  onClick={() => handlePublishSourcesPageChange("prev")}
                  disabled={currentPublishSourcesPage <= 1}
                  aria-label="صفحه قبل"
                >
                  ‹
                </button>
                <span className="projects-pagination-current dashboard-activity-page-current">
                  {formatFaNumber(currentPublishSourcesPage)}
                </span>
                <span className="projects-pagination-total dashboard-activity-page-size">
                  {formatFaNumber(totalPublishSourcesPages)} صفحه
                </span>
                <button
                  type="button"
                  onClick={() => handlePublishSourcesPageChange("next")}
                  disabled={currentPublishSourcesPage >= totalPublishSourcesPages}
                  aria-label="صفحه بعد"
                >
                  ›
                </button>
              </div>
            )}

            {isPublishSourcesDeleteModalOpen && (
              <div className="confirm-modal-overlay" onClick={closePublishSourcesDeleteModal}>
                <div
                  className="confirm-modal"
                  role="dialog"
                  aria-modal="true"
                  aria-label="حذف منبع انتشار"
                  onClick={(event) => event.stopPropagation()}
                >
                  <h4>حذف منبع انتشار</h4>
                  <p>
                    آیا از حذف{" "}
                    <strong>{formatFaNumber(selectedPublishSourceIds.length)}</strong>{" "}
                    منبع انتشار انتخاب‌شده مطمئن هستید؟
                  </p>
                  <div className="confirm-modal-actions">
                    <button
                      type="button"
                      className="confirm-cancel-btn"
                      onClick={closePublishSourcesDeleteModal}
                      disabled={deletingPublishSources}
                    >
                      انصراف
                    </button>
                    <button
                      type="button"
                      className="confirm-delete-btn"
                      onClick={confirmDeleteSelectedPublishSources}
                      disabled={deletingPublishSources}
                    >
                      {deletingPublishSources ? "در حال حذف..." : "بله، حذف شود"}
                    </button>
                  </div>
                </div>
              </div>
            )}
            </div>
          )}
        </section>

        <div
          id="sources-publish-empty-card-spacer"
          className="project-create-empty-card sources-publish-empty-card-spacer"
          aria-hidden="true"
        />
      </div>
    );
  };

  const renderAdsTab = () => {
    const emptyAdsMessage = adsRows.length === 0
      ? "هنوز تبلیغی ثبت نشده است."
      : "تبلیغی مطابق فیلترهای انتخابی پیدا نشد.";

    return (
      <div className="ads-tab-layout">
        <div className="ads-toolbar">
          <button
            type="button"
            className="projects-create-btn ads-create-btn"
            onClick={handleCreateAd}
          >
            ایجاد تبلیغ
          </button>

          <div className="ads-toolbar-main">
            <div className="projects-search-box ads-search-box">
              <input
                type="search"
                placeholder="جستجو"
                value={adsSearchTerm}
                onChange={(event) => setAdsSearchTerm(event.target.value)}
              />
              <span className="projects-search-icon" aria-hidden="true">⌕</span>
            </div>
          </div>

          <button
            type="button"
            className="projects-bulk-btn ads-delete-btn"
            onClick={openAdsDeleteModal}
            disabled={selectedAdIds.length === 0}
          >
            حذف
          </button>
        </div>

        <div className="ads-filters-row">
          <select
            value={adsManagerFilter}
            onChange={(event) => setAdsManagerFilter(event.target.value)}
          >
            <option value="all">یک مدیر ارشد انتخاب کنید</option>
            {adsManagerOptions.map((managerName) => (
              <option key={managerName} value={managerName}>
                {managerName}
              </option>
            ))}
          </select>

          <select
            value={adsRowsPerPage}
            onChange={(event) => setAdsRowsPerPage(Number(event.target.value))}
          >
            {ADS_ROWS_PER_PAGE_OPTIONS.map((option) => (
              <option key={option} value={option}>
                ردیف {formatFaNumber(option)} تایی
              </option>
            ))}
          </select>

          <select
            value={adsTopicFilter}
            onChange={(event) => setAdsTopicFilter(event.target.value)}
          >
            <option value="all">یک بستر انتشار انتخاب کنید</option>
            {adsTopicOptions.map((publishTarget) => (
              <option key={publishTarget.value} value={publishTarget.value}>
                {publishTarget.label}
              </option>
            ))}
          </select>

        </div>

        {adsMsg.text && (
          <div className={`project-list-message ${adsMsg.type}`}>{adsMsg.text}</div>
        )}

        {loadingProjects && (
          <div className="dev-box small">در حال دریافت تبلیغات...</div>
        )}

        {!loadingProjects && (
          <>
            <div className="ads-table-card">
              <div className="projects-table-shell dashboard-activity-table-wrap">
                <table className="ads-table dashboard-activity-table">
                  <thead>
                    <tr>
                      <th className="projects-check-cell">
                        <input
                          type="checkbox"
                          checked={areAllVisibleAdsSelected}
                          onChange={handleSelectAllVisibleAds}
                          aria-label="انتخاب همه تبلیغ های صفحه"
                        />
                      </th>
                      <th>کد</th>
                      <th>عنوان تبلیغ</th>
                      <th>زمان‌های انتشار</th>
                      <th>مشاهده</th>
                      <th>ویرایش</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedAdsRows.length === 0 ? (
                      <tr>
                        <td className="dashboard-activity-empty-row ads-empty-row" colSpan={6}>
                          {emptyAdsMessage}
                        </td>
                      </tr>
                    ) : (
                      paginatedAdsRows.map((row) => {
                        const isChecked = selectedAdIds.includes(row.rowId);

                        return (
                          <tr key={row.rowId}>
                            <td className="projects-check-cell">
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={() => handleAdRowSelection(row.rowId)}
                                aria-label={`انتخاب تبلیغ ${row.title}`}
                              />
                            </td>
                            <td>{row.code}</td>
                            <td className="ads-title-cell">{row.title}</td>
                            <td>{row.scheduledPublishLabel || "--"}</td>
                            <td className="projects-edit-cell ads-view-cell">
                              <button
                                type="button"
                                className="project-edit-btn ads-view-open-btn"
                                onClick={() => handleOpenAdView(row)}
                                aria-label={`مشاهده تبلیغ ${row.title}`}
                                title={`مشاهده تبلیغ ${row.title}`}
                              >
                                👁
                              </button>
                            </td>
                            <td className="projects-edit-cell">
                              <button
                                type="button"
                                className="project-edit-btn"
                                onClick={() => handleEditAd(row)}
                                aria-label={`ویرایش تبلیغ ${row.title}`}
                              >
                                ✎
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {adsTotalCount > 0 && (
                <div className="ads-table-pagination dashboard-activity-pagination">
                  <button
                    type="button"
                    onClick={() => handleAdsPageChange("prev")}
                    disabled={currentAdsPage <= 1}
                    aria-label="صفحه قبل"
                  >
                    ‹
                  </button>
                  <span className="projects-pagination-current dashboard-activity-page-current">
                    {formatFaNumber(currentAdsPage)}
                  </span>
                  <span className="projects-pagination-total dashboard-activity-page-size">
                    {formatFaNumber(totalAdsPages)} صفحه
                  </span>
                  <button
                    type="button"
                    onClick={() => handleAdsPageChange("next")}
                    disabled={currentAdsPage >= totalAdsPages}
                    aria-label="صفحه بعد"
                  >
                    ›
                  </button>
                </div>
              )}
            </div>

            <div id="ads-empty-card-spacer" className="project-create-empty-card ads-empty-card-spacer" aria-hidden="true" />

            {isAdsDeleteModalOpen && (
              <div className="confirm-modal-overlay" onClick={closeAdsDeleteModal}>
                <div
                  className="confirm-modal"
                  role="dialog"
                  aria-modal="true"
                  aria-label="حذف تبلیغ"
                  onClick={(event) => event.stopPropagation()}
                >
                  <h4>حذف تبلیغ</h4>
                  <p>
                    آیا از حذف{" "}
                    <strong>{formatFaNumber(selectedAdIds.length)}</strong>{" "}
                    تبلیغ انتخاب‌شده مطمئن هستید؟
                  </p>
                  <div className="confirm-modal-actions">
                    <button
                      type="button"
                      className="confirm-cancel-btn"
                      onClick={closeAdsDeleteModal}
                      disabled={deletingAds}
                    >
                      انصراف
                    </button>
                    <button
                      type="button"
                      className="confirm-delete-btn"
                      onClick={confirmDeleteSelectedAds}
                      disabled={deletingAds}
                    >
                      {deletingAds ? "در حال حذف..." : "بله، حذف شود"}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  const renderAdsViewTab = () => {
    const selectedRowFromList = adsRows.find((row) => row.rowId === selectedAdRowId);
    const selectedRow = selectedAdDetails?.rowId === selectedAdRowId ? selectedAdDetails : selectedRowFromList;
    const hasSelectedAd = Boolean(selectedRow);
    const previewUrl = normalizeNewsLink(selectedRow?.mediaUrl || "");
    const isVideoMedia = selectedRow?.mediaType === "ویدئو";
    const publishTimeValue = selectedRow?.scheduledPublishLabel || selectedRow?.createdAt || "--";

    if (!hasSelectedAd) {
      return (
        <div className="ads-view-layout">
          <div className="projects-create-head ads-view-topbar">
            <div>
              <h3>تبلیغات</h3>
            </div>
            <button
              type="button"
              className="projects-create-back-btn"
              onClick={() => setActiveTab("ads")}
            >
              بازگشت به لیست تبلیغات
            </button>
          </div>
          <div className="project-list-message error">
            تبلیغ انتخاب‌شده پیدا نشد. از لیست تبلیغات یک مورد را انتخاب کنید.
          </div>
        </div>
      );
    }

    return (
      <div className="ads-view-layout">
        <div className="projects-create-head ads-view-topbar">
          <div>
            <h3>تبلیغات</h3>
          </div>
          <button
            type="button"
            className="projects-create-back-btn"
            onClick={() => setActiveTab("ads")}
          >
            بازگشت به لیست تبلیغات
          </button>
        </div>

        <div className="news-view-card news-view-main-card ads-view-main-card">
          <div className="news-preview-head">
            <h4>پیشنمایش تبلیغ</h4>
          </div>
          <article className="news-preview-card">
            <div className="news-view-media-wrap">
              <span className="news-media-type-badge">{getNewsMediaBadgeLabel(selectedRow)}</span>
              {previewUrl ? (
                isVideoMedia
                  ? <video src={previewUrl} controls />
                  : <img src={previewUrl} alt={`پیش نمایش ${selectedRow.title || "تبلیغ"}`} />
              ) : (
                <div className="news-view-media-placeholder">مدیای تبلیغ</div>
              )}
            </div>
            <div className="news-preview-card-body news-preview-card-body-demo">
              <h5>{selectedRow.title || "--"}</h5>
              <p>{selectedRow.description || "متن تبلیغ ثبت نشده است."}</p>
              <span className="news-preview-card-date">{selectedRow.createdAt || "--"}</span>
            </div>
          </article>
        </div>
        <div id="ads-view-empty-card-spacer" className="project-create-empty-card news-view-empty-card-spacer" aria-hidden="true" />

        <div className="news-view-card">
          <h4>اطلاعات تبلیغ</h4>
          <div className="project-create-grid project-create-grid-3">
            <div className="project-create-field">
              <label htmlFor="ads-view-code">کد تبلیغ</label>
              <input id="ads-view-code" value={selectedRow.code || "--"} readOnly />
            </div>
            <div className="project-create-field">
              <label htmlFor="ads-view-created-at">زمان ایجاد</label>
              <input id="ads-view-created-at" value={selectedRow.createdAt || "--"} readOnly />
            </div>
            <div className="project-create-field">
              <label htmlFor="ads-view-status">وضعیت تبلیغ</label>
              <input id="ads-view-status" value={selectedRow.statusLabel || "--"} readOnly />
            </div>
            <div className="project-create-field">
              <label htmlFor="ads-view-published-at">زمان انتشار</label>
              <input id="ads-view-published-at" value={publishTimeValue} readOnly />
            </div>
            <div className="project-create-field">
              <label htmlFor="ads-view-media-type">نوع مدیا</label>
              <input id="ads-view-media-type" value={selectedRow.mediaType || "--"} readOnly />
            </div>
            <div className="project-create-field">
              <label htmlFor="ads-view-project">پروژه</label>
              <input id="ads-view-project" value={selectedRow.projectName || "--"} readOnly />
            </div>
            <div className="project-create-field">
              <label htmlFor="ads-view-link">بستر های انتشار</label>
              <input id="ads-view-link" value={selectedRow.publishLink || "--"} readOnly />
            </div>
            <div className="project-create-field">
              <label htmlFor="ads-view-topic">موضوع</label>
              <input id="ads-view-topic" value={selectedRow.topicName || "--"} readOnly />
            </div>
            <div className="project-create-field ads-create-full-row">
              <label htmlFor="ads-view-description">متن تبلیغ</label>
              <textarea id="ads-view-description" value={selectedRow.description || "--"} readOnly />
            </div>
          </div>
        </div>
        <div id="ads-view-empty-card-spacer-2" className="project-create-empty-card news-view-empty-card-spacer" aria-hidden="true" />
        {loadingAdDetails && (
          <div className="project-list-message">در حال دریافت اطلاعات کامل تبلیغ...</div>
        )}

      </div>
    );
  };

  const renderAdsCreateTab = () => {
    const isNewsEditMode = newsFormMode === "edit";
    const previewUrl = normalizeNewsLink(newsCreateForm.mediaPreviewUrl);
    const isVideoMedia = String(newsCreateForm.mediaType || "").includes("ویدئو");
    const minPublishDate = formatJalaliDate(getTodayJalali());

    return (
      <div className="ads-create-layout">
        <div className="projects-create-head ads-create-head news-create-head">
          <div>
            <h3>{isNewsEditMode ? "ویرایش تبلیغ" : "ایجاد تبلیغ"}</h3>
          </div>
          <button
            type="button"
            className="projects-create-back-btn"
            onClick={handleCloseAdsCreate}
          >
            بازگشت به لیست تبلیغات
          </button>
        </div>

        <form className="ads-create-form" onSubmit={(event) => {
          event.preventDefault();
          handleSaveCreatedNews();
        }}>
          <div className="ads-create-media-card news-create-media-card">
            <div className="ads-create-media-label">
              {previewUrl ? (
                isVideoMedia ? (
                  showNewsCreateVideoPlayer ? (
                    <video
                      ref={newsCreateVideoRef}
                      src={previewUrl}
                      className="news-create-video-thumbnail"
                      controls
                      playsInline
                      preload="metadata"
                      onPlay={() => setIsNewsCreateVideoPlaying(true)}
                      onPause={() => setIsNewsCreateVideoPlaying(false)}
                      onEnded={() => setIsNewsCreateVideoPlaying(false)}
                    />
                  ) : (
                    <div className="news-create-video-thumbnail-wrap" aria-hidden="true">
                      <video src={previewUrl} className="news-create-video-thumbnail news-create-video-thumbnail-static" muted playsInline preload="metadata" />
                      <span className="news-create-video-thumbnail-play">▶</span>
                    </div>
                  )
                ) : (
                  <img src={previewUrl} alt="پیش‌نمایش تبلیغ" />
                )
              ) : (
                <span>پیش‌نمایش مدیا</span>
              )}
            </div>
            <input
              ref={newsCreateMediaInputRef}
              id="create-ad-media-upload"
              type="file"
              accept="image/*,video/*"
              onChange={handleCreateNewsMediaUpload}
            />
            <div className="news-create-media-actions">
              <button
                type="button"
                className="news-create-upload-media-btn"
                onClick={handleOpenNewsCreateMediaPicker}
              >
                افزودن مدیا
              </button>
              {previewUrl && isVideoMedia && (
                <button
                  type="button"
                  className="news-create-play-video-btn"
                  onClick={handleToggleNewsCreateVideoPlayback}
                >
                  {showNewsCreateVideoPlayer && isNewsCreateVideoPlaying ? "توقف ویدیو" : "پخش ویدیو"}
                </button>
              )}
            </div>
          </div>

          <div className="ads-create-fields-grid">
            <div className="project-create-field">
              <label htmlFor="create-ad-title">عنوان تبلیغ</label>
              <input
                id="create-ad-title"
                value={newsCreateForm.title}
                onChange={(event) => handleCreateNewsFieldChange("title", event.target.value)}
                placeholder="عنوان تبلیغ"
              />
            </div>

            <div className="project-create-field ads-create-full-row">
              <label htmlFor="create-ad-content">افزودن متن تبلیغ</label>
              <textarea
                id="create-ad-content"
                value={newsCreateForm.fullContent}
                onChange={(event) => handleCreateNewsFieldChange("fullContent", event.target.value)}
                placeholder="متن تبلیغ"
                rows={4}
              />
            </div>
          </div>

          <div className="project-create-card">
            <div className="project-wireframe-section-head">
              <h4>بستر انتشار</h4>
            </div>

            <div className="project-create-grid project-create-grid-2">
              <div className="project-create-field">
                <label htmlFor="create-ad-project">پروژه</label>
                <SearchableSourceSelect
                  id="create-ad-project"
                  listId="create-ad-project-list"
                  value={newsCreateForm.projectId || "all"}
                  options={newsProjectSelectionOptions}
                  labelKey="name"
                  placeholder="انتخاب پروژه برای افزودن"
                  onSelect={(selectedId) => handleNewsProjectSelection(selectedId)}
                />
              </div>

              <div className="project-create-field">
                <label htmlFor="create-ad-publish-name">نام بستر انتشار</label>
                <SearchableSourceSelect
                  id="create-ad-publish-name"
                  listId="create-ad-publish-name-list"
                  value=""
                  options={publishSourcesOptions}
                  labelKey="name"
                  placeholder="انتخاب بستر انتشار"
                  onSelect={handleSelectNewsPublishTarget}
                />
              </div>

              <div className="project-create-field ads-create-full-row">
                <label>موارد انتخاب شده</label>
                <div className="news-publish-chip-list">
                  {(newsSelectedProjectGroups.length > 0 || newsManualSelectedPublishTargets.length > 0) ? (
                    <>
                      {newsSelectedProjectGroups.map((groupItem) => (
                        <div key={groupItem.rowId} className="news-publish-chip-group">
                          <div className="project-source-chip project-source-chip--project">
                            <span>{groupItem.projectName}</span>
                            <button
                              type="button"
                              className="project-source-chip-remove"
                              onClick={() => handleRemoveNewsPublishTarget(groupItem)}
                              aria-label={`حذف ${groupItem.projectName}`}
                            >
                              ×
                            </button>
                          </div>
                          {groupItem.sources.length > 0 ? (
                            groupItem.sources.map((sourceItem) => (
                              <div
                                key={`${groupItem.rowId}-source-${sourceItem.sourceId}`}
                                className="project-source-chip project-source-chip--project-source"
                              >
                                <span>{sourceItem.name}</span>
                                <button
                                  type="button"
                                  className="project-source-chip-remove"
                                  onClick={() => handleRemoveNewsPublishTarget({
                                    itemType: "project-source",
                                    projectId: groupItem.projectId,
                                    sourceId: sourceItem.sourceId,
                                    name: sourceItem.name,
                                  })}
                                  aria-label={`حذف ${sourceItem.name}`}
                                >
                                  ×
                                </button>
                              </div>
                            ))
                          ) : (
                            <div className="project-source-chip project-source-chip--project-source">
                              <span>بدون بستر انتشار</span>
                            </div>
                          )}
                        </div>
                      ))}

                      {newsManualSelectedPublishTargets.length > 0 && (
                        <div className="news-publish-chip-group">
                          <div className="project-source-chip project-source-chip--manual-label">
                            <span>انتخاب دستی</span>
                          </div>
                          {newsManualSelectedPublishTargets.map((item) => (
                            <div key={`news-publish-chip-${item.rowId}`} className="project-source-chip">
                              <span>{item.name}</span>
                              <button
                                type="button"
                                className="project-source-chip-remove"
                                onClick={() => handleRemoveNewsPublishTarget(item)}
                                aria-label={`حذف ${item.name}`}
                              >
                                ×
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="project-process-mode-badge">هیچ بستر انتشاری انتخاب نشده است</div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div id="ad-scheduled-publish-card" className="project-create-card">
            <div className="project-wireframe-section-head">
              <h4>زمان بندی انتشار</h4>
              {newsCreateForm.scheduleMode === "future_times" && (
                <button
                  type="button"
                  className="project-wireframe-link-btn"
                  onClick={handleAddNewsScheduledPublishRow}
                >
                  + افزودن زمان انتشار
                </button>
              )}
            </div>
            <div className="project-wireframe-row-grid project-wireframe-extra-row">
              <div className="project-create-field">
                <label htmlFor="create-ad-schedule-mode">مدل انتشار</label>
                <select
                  id="create-ad-schedule-mode"
                  value={newsCreateForm.scheduleMode}
                  onChange={(event) => handleCreateNewsFieldChange("scheduleMode", event.target.value)}
                >
                  <option value="future_times">یک/چند زمان در آینده</option>
                  <option value="recurring">دوره‌ای</option>
                </select>
              </div>
            </div>

            {newsCreateForm.scheduleMode === "future_times" && newsCreateScheduledPublishRows.map((row) => (
              <div
                key={row.id}
                id={`${row.id}-scheduled-row`}
                className="project-wireframe-row-grid project-wireframe-extra-row project-wireframe-schedule-grid"
              >
                <JalaliDatePicker
                  id={`${row.id}-ad-date`}
                  label="تاریخ انتشار"
                  value={row.date || "all"}
                  minDate={minPublishDate}
                  allowFuture
                  onChange={(nextValue) => handleNewsScheduledPublishRowChange(row.id, "date", nextValue)}
                />
                <div
                  className="project-create-field project-time-field"
                  onPointerDown={(event) => handleTimeFieldPointerDown(event, `${row.id}-ad-time`)}
                >
                  <label htmlFor={`${row.id}-ad-time`}>ساعت انتشار</label>
                  <div className="project-time-field-control">
                    <input
                      id={`${row.id}-ad-time`}
                      type="time"
                      value={normalizeTimeDigits(row.time)}
                      onChange={(event) => handleNewsScheduledPublishRowChange(row.id, "time", event.target.value)}
                      onBlur={() => {
                        if (openNewsTimePickerInputId === `${row.id}-ad-time`) {
                          setOpenNewsTimePickerInputId("");
                        }
                      }}
                    />
                    <button
                      type="button"
                      className="project-time-picker-btn"
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => toggleNewsTimeInputPicker(`${row.id}-ad-time`)}
                    >
                      {openNewsTimePickerInputId === `${row.id}-ad-time` ? "بستن ساعت" : "انتخاب ساعت"}
                    </button>
                  </div>
                </div>
                <button
                  type="button"
                  className="project-wireframe-remove-btn"
                  onClick={() => handleRemoveNewsScheduledPublishRow(row.id)}
                  aria-label="حذف زمان انتشار"
                >
                  حذف
                </button>
              </div>
            ))}

            {newsCreateForm.scheduleMode === "recurring" && (
              <>
                <div className="project-wireframe-row-grid project-wireframe-extra-row project-wireframe-schedule-grid">
                  <JalaliDatePicker
                    id="create-ad-recurring-start-date"
                    label="تاریخ شروع"
                    value={newsCreateForm.recurringStartDate || "all"}
                    minDate={minPublishDate}
                    allowFuture
                    onChange={(nextValue) => handleCreateNewsFieldChange("recurringStartDate", nextValue)}
                  />
                  <div
                    className="project-create-field project-time-field"
                    onPointerDown={(event) => handleTimeFieldPointerDown(event, "create-ad-recurring-start-time")}
                  >
                    <label htmlFor="create-ad-recurring-start-time">ساعت شروع</label>
                    <div className="project-time-field-control">
                      <input
                        id="create-ad-recurring-start-time"
                        type="time"
                        value={normalizeTimeDigits(newsCreateForm.recurringStartTime)}
                        onChange={(event) => handleCreateNewsFieldChange("recurringStartTime", event.target.value)}
                        onBlur={() => {
                          if (openNewsTimePickerInputId === "create-ad-recurring-start-time") {
                            setOpenNewsTimePickerInputId("");
                          }
                        }}
                      />
                      <button
                        type="button"
                        className="project-time-picker-btn"
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => toggleNewsTimeInputPicker("create-ad-recurring-start-time")}
                      >
                        {openNewsTimePickerInputId === "create-ad-recurring-start-time" ? "بستن ساعت" : "انتخاب ساعت"}
                      </button>
                    </div>
                  </div>
                </div>
                <div className="project-wireframe-row-grid project-wireframe-extra-row">
                  <div className="project-create-field">
                    <label htmlFor="create-ad-recurring-interval">اینتروال (ثانیه)</label>
                    <input
                      id="create-ad-recurring-interval"
                      type="number"
                      min="1"
                      value={newsCreateForm.recurringIntervalSeconds}
                      onChange={(event) => handleCreateNewsFieldChange("recurringIntervalSeconds", event.target.value)}
                    />
                  </div>
                  <div className="project-create-field">
                    <label htmlFor="create-ad-recurring-max-runs">حداکثر دفعات اجرا</label>
                    <input
                      id="create-ad-recurring-max-runs"
                      type="number"
                      min="1"
                      value={newsCreateForm.recurringMaxRuns}
                      onChange={(event) => handleCreateNewsFieldChange("recurringMaxRuns", event.target.value)}
                    />
                  </div>
                </div>
              </>
            )}
          </div>

          {adsCreateMsg.text && (
            <div className={`project-list-message ${adsCreateMsg.type}`}>{adsCreateMsg.text}</div>
          )}

          <div className="project-create-actions ads-create-actions">
            <button
              type="button"
              className="project-create-cancel-btn"
              onClick={handleCloseAdsCreate}
            >
              انصراف
            </button>
            <button type="submit" className="save-btn project-create-save-btn">
              {isNewsEditMode ? "ذخیره تغییرات" : "ایجاد تبلیغ"}
            </button>
          </div>
          <div id="ads-create-actions-empty-card-spacer" className="project-create-empty-card ads-create-actions-empty-card-spacer" aria-hidden="true" />
        </form>
      </div>
    );
  };

  const renderNewsTab = () => {
    const renderNewsSection = (targetRevisionType, sectionTitle) => {
      const normalizedRevisionType = String(targetRevisionType || "").trim().toLowerCase();
      const isExtractSection = normalizedRevisionType === "extract";
      const sectionControls = newsSectionControls[normalizedRevisionType] || newsSectionControls.make;
      const sectionRows = Array.isArray(newsSectionRows[normalizedRevisionType])
        ? newsSectionRows[normalizedRevisionType]
        : [];
      const sectionRowsLimit = Number(sectionControls.rowsPerPage) || NEWS_ROWS_PER_PAGE_OPTIONS[0];
      const sectionSourceOptions = isExtractSection
        ? Array.from(
            new Map(
              projects
                .filter((project) => (
                  sectionControls.projectFilter === "all"
                  || String(project?.id || "") === String(sectionControls.projectFilter)
                ))
                .flatMap((project) => getProjectReceiveSourceOptions(project))
                .filter((item) => item.value && item.label && item.label !== "--")
                .map((item) => [item.value, item])
            ).values()
          )
        : publishSourcesOptions.map((row) => ({
            value: String(row?.id || ""),
            label: String(row?.name || "").trim() || `بستر ${row?.id || ""}`,
          }));
      const sectionStatusOptions = isExtractSection
        ? NEWS_STATUS_FILTER_OPTIONS
        : NEWS_STATUS_FILTER_OPTIONS.filter((option) => option.value !== "received");
      const sectionTotalCount = Number(newsSectionTotalCount[normalizedRevisionType] || sectionRows.length || 0);
      const sectionTotalPages = Math.max(1, Math.ceil(sectionTotalCount / sectionRowsLimit));
      const sectionCurrentPage = Math.min(sectionControls.page || 1, sectionTotalPages);
      const sectionPaginatedRows = sectionRows;
      const sectionRowIds = sectionPaginatedRows.map((row) => row.rowId);
      const areAllSectionRowsSelected = (
        sectionRowIds.length > 0
        && sectionRowIds.every((rowId) => selectedNewsIds.includes(rowId))
      );
      const sectionEmptyMessage = normalizedRevisionType === "extract"
        ? "خبر استخراج‌شده‌ای مطابق فیلترهای انتخابی پیدا نشد."
        : "خبر دستی‌ای مطابق فیلترهای انتخابی پیدا نشد.";

      return (
        <>
          <div className="news-tab-section-block">
            <div className="news-tab-section-head">
              <h3>{sectionTitle}</h3>
            </div>
            <div className={`news-tab-toolbar ${normalizedRevisionType === "extract" ? "news-tab-toolbar-no-create" : ""}`}>
              {normalizedRevisionType !== "extract" && (
                <button
                  type="button"
                  className="projects-create-btn news-create-btn"
                  onClick={handleCreateNews}
                >
                  ایجاد خبر
                </button>
              )}

              <div className="news-tab-toolbar-main">
                <div className="projects-search-box news-tab-search-box">
                  <input
                    type="search"
                    placeholder="جستجو"
                    value={sectionControls.searchTerm}
                    onChange={(event) => setNewsSectionControls((prev) => ({
                      ...prev,
                      [normalizedRevisionType]: {
                        ...prev[normalizedRevisionType],
                        searchTerm: event.target.value,
                        page: 1,
                      },
                    }))}
                  />
                  <span className="projects-search-icon" aria-hidden="true">⌕</span>
                </div>
              </div>

              <button
                type="button"
                className="projects-bulk-btn news-delete-btn"
                onClick={() => openNewsDeleteModal(sectionRowIds.filter((rowId) => selectedNewsIds.includes(rowId)))}
                disabled={sectionRowIds.filter((rowId) => selectedNewsIds.includes(rowId)).length === 0}
              >
                حذف
              </button>
            </div>

            <div className="news-tab-filters">
              <select
                value={sectionControls.managerFilter}
                onChange={(event) => setNewsSectionControls((prev) => ({
                  ...prev,
                  [normalizedRevisionType]: {
                    ...prev[normalizedRevisionType],
                    managerFilter: event.target.value,
                    page: 1,
                  },
                }))}
              >
                <option value="all">یک مدیر ارشد انتخاب کنید</option>
                {newsManagerOptions.map((managerName) => (
                  <option key={managerName} value={managerName}>
                    {managerName}
                  </option>
                ))}
              </select>

              <select
                value={sectionControls.rowsPerPage}
                onChange={(event) => setNewsSectionControls((prev) => ({
                  ...prev,
                  [normalizedRevisionType]: {
                    ...prev[normalizedRevisionType],
                    rowsPerPage: Number(event.target.value),
                    page: 1,
                  },
                }))}
              >
                {NEWS_ROWS_PER_PAGE_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    ردیف {formatFaNumber(option)} تایی
                  </option>
                ))}
              </select>

              {isExtractSection && (
                <select
                  value={sectionControls.projectFilter}
                  onChange={(event) => setNewsSectionControls((prev) => ({
                    ...prev,
                    [normalizedRevisionType]: {
                      ...prev[normalizedRevisionType],
                      projectFilter: event.target.value,
                      sourceFilter: "all",
                      page: 1,
                    },
                  }))}
                >
                  <option value="all">همه پروژه‌ها</option>
                  {newsProjectOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              )}

              <select
                value={sectionControls.sourceFilter}
                onChange={(event) => setNewsSectionControls((prev) => ({
                  ...prev,
                  [normalizedRevisionType]: {
                    ...prev[normalizedRevisionType],
                    sourceFilter: event.target.value,
                    page: 1,
                  },
                }))}
              >
                <option value="all">{isExtractSection ? "یک منبع انتخاب کنید" : "یک بستر انتشار انتخاب کنید"}</option>
                {sectionSourceOptions.map((sourceOption) => (
                  <option key={sourceOption.value} value={sourceOption.value}>
                    {sourceOption.label}
                  </option>
                ))}
              </select>

              <select
                value={sectionControls.mediaFilter}
                onChange={(event) => setNewsSectionControls((prev) => ({
                  ...prev,
                  [normalizedRevisionType]: {
                    ...prev[normalizedRevisionType],
                    mediaFilter: event.target.value,
                    page: 1,
                  },
                }))}
              >
                <option value="all">یک نوع مدیا انتخاب کنید</option>
                {newsMediaOptions.map((mediaType) => (
                  <option key={mediaType.value} value={mediaType.value}>
                    {mediaType.label}
                  </option>
                ))}
              </select>

              <select
                value={sectionControls.statusFilter}
                onChange={(event) => setNewsSectionControls((prev) => ({
                  ...prev,
                  [normalizedRevisionType]: {
                    ...prev[normalizedRevisionType],
                    statusFilter: event.target.value,
                    page: 1,
                  },
                }))}
              >
                {sectionStatusOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {!loadingProjects && !loadingNewsRows && !newsError && (
              <div className="news-tab-table-card">
                <div className="projects-table-shell dashboard-activity-table-wrap">
                  <table className="news-tab-table dashboard-activity-table">
                    <thead>
                      <tr>
                        <th className="projects-check-cell">
                          <input
                            type="checkbox"
                            checked={areAllSectionRowsSelected}
                            onChange={() => handleSelectAllVisibleNews(sectionRowIds)}
                            aria-label="انتخاب همه خبرهای صفحه"
                          />
                        </th>
                        <th>کد</th>
                        <th>تیتر خبر</th>
                        {isExtractSection ? (
                          <>
                            <th>تاریخ خبر</th>
                            <th>پروژه</th>
                            <th>منبع</th>
                            <th>نوع مدیا</th>
                          </>
                        ) : (
                          <>
                            <th>زمان‌های انتشار</th>
                          </>
                        )}
                        <th>مشاهده</th>
                        <th>ویرایش</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sectionPaginatedRows.length === 0 ? (
                        <tr>
                          <td className="dashboard-activity-empty-row news-tab-empty-row" colSpan={isExtractSection ? 9 : 6}>
                            {projects.length === 0 ? emptyNewsMessage : sectionEmptyMessage}
                          </td>
                        </tr>
                      ) : (
                        sectionPaginatedRows.map((row) => {
                          const isChecked = selectedNewsIds.includes(row.rowId);
                          const canEditRow = row?.revisionType !== "extract";

                          return (
                            <tr key={row.rowId}>
                              <td className="projects-check-cell">
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  onChange={() => handleNewsRowSelection(row.rowId)}
                                  aria-label={`انتخاب خبر ${row.title}`}
                                />
                              </td>
                              <td>{row.code}</td>
                              <td className="news-tab-title-cell">
                                <span className="news-tab-title-text">
                                  {row.title}
                                </span>
                              </td>
                              {isExtractSection ? (
                                <>
                                  <td>{row.publishedAt}</td>
                                  <td>{row.projectName}</td>
                                  <td>{row.sourceName}</td>
                                  <td>
                                    <span className="news-media-tag">{row.mediaType}</span>
                                  </td>
                                </>
                              ) : (
                                <>
                                  <td>{row.scheduledPublishLabel || "--"}</td>
                                </>
                              )}
                              <td className="projects-edit-cell news-tab-view-cell">
                                <button
                                  type="button"
                                  className="project-edit-btn news-view-open-btn"
                                  onClick={() => handleOpenNewsView(row)}
                                  aria-label={`مشاهده خبر ${row.title}`}
                                  title={`مشاهده خبر ${row.title}`}
                                >
                                  👁
                                </button>
                              </td>
                              <td className="projects-edit-cell">
                                {canEditRow && (
                                  <button
                                    type="button"
                                    className="project-edit-btn"
                                    onClick={() => handleEditNewsRow(row)}
                                    aria-label={`ویرایش خبر ${row.title}`}
                                  >
                                    ✎
                                  </button>
                                )}
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>

                {sectionTotalCount > 0 && (
                  <div className="news-table-pagination dashboard-activity-pagination">
                    <button
                      type="button"
                      onClick={() => setNewsSectionControls((prev) => ({
                        ...prev,
                        [normalizedRevisionType]: {
                          ...prev[normalizedRevisionType],
                          page: Math.max(1, (prev[normalizedRevisionType]?.page || 1) - 1),
                        },
                      }))}
                      disabled={sectionCurrentPage <= 1}
                      aria-label="صفحه قبل"
                    >
                      ‹
                    </button>
                    <span className="projects-pagination-current dashboard-activity-page-current">
                      {formatFaNumber(sectionCurrentPage)}
                    </span>
                    <span className="projects-pagination-total dashboard-activity-page-size">
                      {formatFaNumber(sectionTotalPages)} صفحه
                    </span>
                    <button
                      type="button"
                      onClick={() => setNewsSectionControls((prev) => ({
                        ...prev,
                        [normalizedRevisionType]: {
                          ...prev[normalizedRevisionType],
                          page: Math.min(sectionTotalPages, (prev[normalizedRevisionType]?.page || 1) + 1),
                        },
                      }))}
                      disabled={sectionCurrentPage >= sectionTotalPages}
                      aria-label="صفحه بعد"
                    >
                      ›
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
          <div
            id={`news-empty-card-spacer-${normalizedRevisionType}`}
            className="project-create-empty-card news-empty-card-spacer"
            aria-hidden="true"
          />
        </>
      );
    };

    return (
      <div className="news-tab-layout">
        {renderNewsSection("extract", "خبرهای دریافت شده")}
        {renderNewsSection("make", "خبرهای ایجاد شده")}

        {newsMsg.text && (
          <div className={`project-list-message ${newsMsg.type}`}>{newsMsg.text}</div>
        )}

        {newsError && (
          <div className="project-list-message error">{newsError}</div>
        )}

        {(loadingProjects || loadingNewsRows) && (
          <div className="dev-box small">در حال دریافت اخبار...</div>
        )}

        {!loadingProjects && !loadingNewsRows && !newsError && isNewsDeleteModalOpen && (
          <div className="confirm-modal-overlay" onClick={closeNewsDeleteModal}>
            <div
              className="confirm-modal"
              role="dialog"
              aria-modal="true"
              aria-label="حذف خبر"
              onClick={(event) => event.stopPropagation()}
            >
              <h4>حذف خبر</h4>
              <p>
                آیا از حذف{" "}
                <strong>{formatFaNumber(newsDeleteTargetIds.length || selectedNewsIds.length)}</strong>{" "}
                خبر انتخاب‌شده مطمئن هستید؟
              </p>
              <div className="confirm-modal-actions">
                <button
                  type="button"
                  className="confirm-cancel-btn"
                  onClick={closeNewsDeleteModal}
                  disabled={deletingNews}
                >
                  انصراف
                </button>
                <button
                  type="button"
                  className="confirm-delete-btn"
                  onClick={confirmDeleteSelectedNews}
                  disabled={deletingNews}
                >
                  {deletingNews ? "در حال حذف..." : "بله، حذف شود"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderUsersFormTab = () => {
    const hasCreateProjectOptions = createUserProjectOptions.length > 0;
    const isUserFormOpen = userFormMode === "create" || userFormMode === "edit";
    const isUserEditMode = userFormMode === "edit";
    const userFormTitle = isUserEditMode ? "ویرایش کاربر" : "ایجاد کاربر جدید";
    const userFormSubmitLabel = submittingUserCreate
      ? "در حال ذخیره..."
      : (isUserEditMode ? "ذخیره تغییرات" : "ذخیره کاربر");
    const hasSelectedAccess = userCreateForm.projects.length > 0 || userCreateForm.systemSections.length > 0;

    return (
      <div className="users-form-tab">
        <div className="users-form-workspace">
          <div className="projects-create-head users-form-head">
            <div>
              <h3>{userFormTitle}</h3>
            </div>
            <button
              type="button"
              className="projects-create-back-btn"
              onClick={handleCloseUserFormSection}
            >
              بازگشت به لیست کاربران
            </button>
          </div>

          {!isUserFormOpen && (
            <div className="project-list-message error">
              فرم کاربر برای نمایش آماده نیست.
            </div>
          )}

          {usersMsg.text && (
            <div className={`project-list-message ${usersMsg.type}`}>{usersMsg.text}</div>
          )}

          {isUserFormOpen && (
            <div className="users-create-form-card">
              <form className="project-create-form users-create-form" onSubmit={handleSubmitCreateUser}>
                <div className="project-create-card users-create-section-card">
                  <h4>اطلاعات هویتی</h4>
                  <div className="project-create-grid project-create-grid-4">
                    <div className="project-create-field">
                      <label htmlFor="user-create-first-name">نام کاربر</label>
                      <input
                        id="user-create-first-name"
                        type="text"
                        placeholder="نام کاربر"
                        value={userCreateForm.firstName}
                        onChange={(event) => handleUserCreateFieldChange("firstName", event.target.value)}
                      />
                    </div>

                    <div className="project-create-field">
                      <label htmlFor="user-create-last-name">نام خانوادگی کاربر</label>
                      <input
                        id="user-create-last-name"
                        type="text"
                        placeholder="نام خانوادگی کاربر"
                        value={userCreateForm.lastName}
                        onChange={(event) => handleUserCreateFieldChange("lastName", event.target.value)}
                      />
                    </div>

                    <div className="project-create-field">
                      <label htmlFor="user-create-phone">شماره همراه کاربر</label>
                      <input
                        id="user-create-phone"
                        type="tel"
                        placeholder="شماره همراه کاربر"
                        value={userCreateForm.phone}
                        onChange={(event) => handleUserCreateFieldChange("phone", event.target.value)}
                      />
                    </div>

                    <div className="project-create-field">
                      <label htmlFor="user-create-email">ایمیل کاربر</label>
                      <input
                        id="user-create-email"
                        type="email"
                        placeholder="ایمیل کاربر"
                        value={userCreateForm.email}
                        onChange={(event) => handleUserCreateFieldChange("email", event.target.value)}
                      />
                    </div>

                    <div className="project-create-field users-profile-upload-field">
                      <label htmlFor="user-create-profile-image">تصویر پروفایل</label>
                      <input
                        id="user-create-profile-image"
                        type="file"
                        accept="image/*"
                        onChange={handleUserProfileImageUpload}
                      />
                      {userCreateForm.profileImageUrl && (
                        <div className="users-profile-preview-row">
                          <img
                            src={userCreateForm.profileImageUrl}
                            alt="پیش نمایش پروفایل"
                            className="users-profile-preview-image"
                          />
                          <div className="users-profile-preview-meta">
                            <span className="project-file-selected-name">
                              {userCreateForm.profileImageName || "تصویر انتخاب شده"}
                            </span>
                            <button
                              type="button"
                              className="project-file-clear-btn"
                              onClick={handleClearUserProfileImage}
                            >
                              حذف تصویر
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="project-create-card users-create-section-card">
                  <h4>حساب کاربری و امنیت</h4>
                  <div className="project-create-grid project-create-grid-4">
                    <div className="project-create-field">
                      <label htmlFor="user-create-username">نام کاربری</label>
                      <input
                        id="user-create-username"
                        type="text"
                        placeholder="نام کاربری"
                        value={userCreateForm.username}
                        onChange={(event) => handleUserCreateFieldChange("username", event.target.value)}
                      />
                    </div>

                    <div className="project-create-field">
                      <label htmlFor="user-create-manager-name">نام مدیر کاربر</label>
                      <input
                        id="user-create-manager-name"
                        type="text"
                        placeholder="نام مدیر کاربر"
                        value={userCreateForm.managerName}
                        onChange={(event) => handleUserCreateFieldChange("managerName", event.target.value)}
                      />
                    </div>

                    <div className="project-create-field">
                      <label htmlFor="user-create-password">رمز عبور</label>
                      <div className="users-password-input-wrap">
                        <input
                          id="user-create-password"
                          type={showUserCreatePassword ? "text" : "password"}
                          placeholder="رمز عبور"
                          value={userCreateForm.password}
                          onChange={(event) => handleUserCreateFieldChange("password", event.target.value)}
                        />
                        <button
                          type="button"
                          className="users-password-toggle-btn"
                          onClick={() => setShowUserCreatePassword((prevValue) => !prevValue)}
                          aria-label={showUserCreatePassword ? "مخفی کردن رمز عبور" : "نمایش رمز عبور"}
                        >
                          {showUserCreatePassword ? "پنهان" : "نمایش"}
                        </button>
                      </div>
                    </div>

                    <div className="project-create-field">
                      <label htmlFor="user-create-repeat-password">تکرار رمز</label>
                      <div className="users-password-input-wrap">
                        <input
                          id="user-create-repeat-password"
                          type={showUserCreateRepeatPassword ? "text" : "password"}
                          placeholder="تکرار رمز"
                          value={userCreateForm.repeatPassword}
                          onChange={(event) => handleUserCreateFieldChange("repeatPassword", event.target.value)}
                        />
                        <button
                          type="button"
                          className="users-password-toggle-btn"
                          onClick={() => setShowUserCreateRepeatPassword((prevValue) => !prevValue)}
                          aria-label={showUserCreateRepeatPassword ? "مخفی کردن تکرار رمز" : "نمایش تکرار رمز"}
                        >
                          {showUserCreateRepeatPassword ? "پنهان" : "نمایش"}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="project-create-card users-create-section-card">
                  <h4>دسترسی و وضعیت</h4>
                  <div className="project-create-grid project-create-grid-2 users-create-access-grid">
                    <div className="project-create-field">
                      <label htmlFor="user-create-role">نقش کاربر</label>
                      <select
                        id="user-create-role"
                        value={userCreateForm.role}
                        onChange={(event) => handleUserCreateFieldChange("role", event.target.value)}
                      >
                        {userFormRoleOptions.map((role) => (
                          <option key={role} value={role}>{role}</option>
                        ))}
                      </select>
                    </div>

                    <div className="project-create-field">
                      <label htmlFor="user-create-status">وضعیت کاربر</label>
                      <select
                        id="user-create-status"
                        value={userCreateForm.status}
                        onChange={(event) => handleUserCreateFieldChange("status", event.target.value)}
                      >
                        {userFormStatusOptions.map((statusOption) => (
                          <option key={statusOption.value} value={statusOption.value}>
                            {statusOption.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="users-create-project-access users-access-block">
                    <div className="users-access-block-head">
                      <span>پروژه های مجاز</span>
                      <small>یک یا چند پروژه را برای این کاربر انتخاب کنید.</small>
                    </div>
                    <div className="users-create-project-picker">
                      <select
                        value={userCreateSelectedProject}
                        onChange={(event) => setUserCreateSelectedProject(event.target.value)}
                        disabled={!hasCreateProjectOptions}
                      >
                        {!hasCreateProjectOptions && (
                          <option value="">پروژه ای برای انتخاب وجود ندارد.</option>
                        )}
                        {createUserProjectOptions.map((projectTitle) => (
                          <option key={projectTitle} value={projectTitle}>{projectTitle}</option>
                        ))}
                      </select>
                      <button
                        type="button"
                        className="users-add-project-btn"
                        onClick={handleAddUserCreateProject}
                        disabled={!hasCreateProjectOptions || !userCreateSelectedProject}
                      >
                        + افزودن پروژه
                      </button>
                    </div>

                    <div className="users-create-project-list">
                      {userCreateForm.projects.length ? (
                        userCreateForm.projects.map((projectTitle) => (
                          <button
                            key={projectTitle}
                            type="button"
                            className="users-project-chip"
                            onClick={() => handleRemoveUserCreateProject(projectTitle)}
                            aria-label={`حذف دسترسی پروژه ${projectTitle}`}
                          >
                            <span>{projectTitle}</span>
                            <span aria-hidden="true">×</span>
                          </button>
                        ))
                      ) : (
                        <span className="users-project-empty">پروژه ای برای کاربر انتخاب نشده است.</span>
                      )}
                    </div>
                  </div>

                  <div className="users-create-project-access users-access-block">
                    <div className="users-access-block-head">
                      <span>بخش های مجاز سامانه</span>
                      <small>هر بخش انتخاب شده برای کاربر در منوی سامانه فعال می شود.</small>
                    </div>
                    <div className="users-access-section-grid">
                      {USER_ACCESS_SECTION_OPTIONS.map((sectionLabel) => {
                        const isSelected = userCreateForm.systemSections.includes(sectionLabel);

                        return (
                          <button
                            key={sectionLabel}
                            type="button"
                            className={`users-access-section-chip ${isSelected ? "active" : ""}`}
                            onClick={() => handleUserCreateSectionToggle(sectionLabel)}
                            aria-pressed={isSelected}
                          >
                            {sectionLabel}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="users-access-summary">
                    {hasSelectedAccess ? (
                      <>
                        <span className="users-access-summary-item">
                          {`پروژه های انتخاب شده: ${formatFaNumber(userCreateForm.projects.length)}`}
                        </span>
                        <span className="users-access-summary-item">
                          {`بخش های انتخاب شده: ${formatFaNumber(userCreateForm.systemSections.length)}`}
                        </span>
                      </>
                    ) : (
                      <span>حداقل یک پروژه یا یک بخش سامانه برای این کاربر انتخاب کنید.</span>
                    )}
                  </div>
                </div>

                <div className="project-create-actions users-create-form-actions">
                  <button
                    type="button"
                    className="project-create-cancel-btn"
                    onClick={handleCloseUserFormSection}
                  >
                    انصراف
                  </button>
                  <button
                    type="submit"
                    className="projects-create-btn project-create-save-btn users-save-btn"
                    disabled={submittingUserCreate}
                  >
                    {userFormSubmitLabel}
                  </button>
                </div>
                <div id="users-form-actions-empty-card-spacer" className="project-create-empty-card users-form-actions-empty-card-spacer" aria-hidden="true" />
              </form>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderUsersTab = () => {
    const emptyUsersMessage = loadingUsers
      ? "در حال دریافت لیست کاربران..."
      : (usersRows.length === 0
      ? "هنوز کاربری ثبت نشده است."
      : "کاربری مطابق فیلترهای انتخابی پیدا نشد.");

    return (
      <div className="users-tab-layout">
        <div className="users-card">
          <div className="users-toolbar">
            <button
              type="button"
              className="projects-create-btn users-create-btn"
              onClick={handleCreateUser}
            >
              ایجاد کاربر جدید
            </button>

            <div className="users-toolbar-main">
              <div className="projects-search-box users-search-box">
                <input
                  type="search"
                  placeholder="جستجو"
                  value={usersSearchTerm}
                  onChange={(event) => setUsersSearchTerm(event.target.value)}
                />
                <span className="projects-search-icon" aria-hidden="true">⌕</span>
              </div>
            </div>

            <button
              type="button"
              className="projects-bulk-btn users-delete-btn"
              onClick={openUsersDeleteModal}
              disabled={selectedUserIds.length === 0}
            >
              حذف
            </button>
          </div>

          <div className="users-filters-row">
            <select
              value={usersManagerFilter}
              onChange={(event) => setUsersManagerFilter(event.target.value)}
            >
              <option value="all">یک مدیر ارشد انتخاب کنید</option>
              {usersManagerOptions.map((managerName) => (
                <option key={managerName} value={managerName}>
                  {managerName}
                </option>
              ))}
            </select>

            <select
              value={usersRowsPerPage}
              onChange={(event) => setUsersRowsPerPage(Number(event.target.value))}
            >
              {USER_ROWS_PER_PAGE_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  ردیف {formatFaNumber(option)} تایی
                </option>
              ))}
            </select>

            <select
              value={usersProjectFilter}
              onChange={(event) => setUsersProjectFilter(event.target.value)}
            >
              <option value="all">یک پروژه انتخاب کنید</option>
              {usersProjectOptions.map((projectTitle) => (
                <option key={projectTitle} value={projectTitle}>
                  {projectTitle}
                </option>
              ))}
            </select>

            <select
              value={usersRoleFilter}
              onChange={(event) => setUsersRoleFilter(event.target.value)}
            >
              <option value="all">یک نقش انتخاب کنید</option>
              {usersRoleOptions.map((roleValue) => (
                <option key={roleValue} value={roleValue}>
                  {roleValue}
                </option>
              ))}
            </select>

            <select
              value={usersStatusFilter}
              onChange={(event) => setUsersStatusFilter(event.target.value)}
            >
              {USER_STATUS_FILTER_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {usersMsg.text && (
            <div className={`project-list-message ${usersMsg.type}`}>{usersMsg.text}</div>
          )}

          <div className="users-table-card">
            <div className="projects-table-shell dashboard-activity-table-wrap">
              <table className="users-table dashboard-activity-table">
                <thead>
                  <tr>
                    <th className="projects-check-cell">
                      <input
                        type="checkbox"
                        checked={areAllVisibleUsersSelected}
                        onChange={handleSelectAllVisibleUsers}
                        aria-label="انتخاب همه کاربران صفحه"
                      />
                    </th>
                    <th>کد</th>
                    <th>نقش</th>
                    <th>تاریخ ایجاد کاربر</th>
                    <th>نام کاربر</th>
                    <th>نام مدیر کاربر</th>
                    <th>شماره تماس کاربر</th>
                    <th>سطح دسترسی</th>
                    <th>وضعیت</th>
                    <th>ویرایش</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedUsersRows.length === 0 ? (
                    <tr>
                      <td className="dashboard-activity-empty-row users-empty-row" colSpan={10}>
                        {emptyUsersMessage}
                      </td>
                    </tr>
                  ) : (
                    paginatedUsersRows.map((user) => {
                      const userStatus = getUserStatusInfo(user.status);
                      const isChecked = selectedUserIds.includes(user.id);
                      const userProjects = Array.isArray(user.projects) && user.projects.length
                        ? user.projects
                        : [user.projectTitle].filter(Boolean);
                      const userSections = Array.isArray(user.systemSections)
                        ? user.systemSections.filter(Boolean)
                        : [];

                      return (
                        <tr key={user.id}>
                          <td className="projects-check-cell">
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => handleUserRowSelection(user.id)}
                              aria-label={`انتخاب کاربر ${user.name}`}
                            />
                          </td>
                          <td>{user.code}</td>
                          <td>{user.role}</td>
                          <td>{user.createdAt}</td>
                          <td className="users-name-cell">
                            <div className="users-name-cell-content">
                              {user.profileImageUrl ? (
                                <img
                                  src={user.profileImageUrl}
                                  alt={`پروفایل ${user.name}`}
                                  className="users-name-avatar"
                                />
                              ) : (
                                <span className="users-name-avatar users-name-avatar-fallback" aria-hidden="true">
                                  {String(user.name || "?").trim().charAt(0)}
                                </span>
                              )}
                              <span>{user.name}</span>
                            </div>
                          </td>
                          <td>{user.managerName}</td>
                          <td className="users-phone-cell">{user.phone}</td>
                          <td className="users-access-cell">
                            <div className="users-access-summary-cell">
                              <span>{`پروژه: ${userProjects.length ? userProjects.join("، ") : "—"}`}</span>
                              <span>{`بخش: ${userSections.length ? userSections.join("، ") : "—"}`}</span>
                            </div>
                          </td>
                          <td>
                            <span className={`project-status-tag ${userStatus.key}`}>
                              {userStatus.label}
                            </span>
                          </td>
                          <td className="projects-edit-cell">
                            <button
                              type="button"
                              className="project-edit-btn"
                              onClick={() => handleEditUser(user)}
                              aria-label={`ویرایش کاربر ${user.name}`}
                            >
                              ✎
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {filteredUsersRows.length > 0 && (
              <div className="users-table-pagination dashboard-activity-pagination">
                <button
                  type="button"
                  onClick={() => handleUsersPageChange("prev")}
                  disabled={currentUsersPage <= 1}
                  aria-label="صفحه قبل"
                >
                  ‹
                </button>
                <span className="projects-pagination-current dashboard-activity-page-current">
                  {formatFaNumber(currentUsersPage)}
                </span>
                <span className="projects-pagination-total dashboard-activity-page-size">
                  {formatFaNumber(totalUsersPages)} صفحه
                </span>
                <button
                  type="button"
                  onClick={() => handleUsersPageChange("next")}
                  disabled={currentUsersPage >= totalUsersPages}
                  aria-label="صفحه بعد"
                >
                  ›
                </button>
              </div>
            )}
          </div>

          {isUsersDeleteModalOpen && (
            <div className="confirm-modal-overlay" onClick={closeUsersDeleteModal}>
              <div
                className="confirm-modal"
                role="dialog"
                aria-modal="true"
                aria-label="حذف کاربر"
                onClick={(event) => event.stopPropagation()}
              >
                <h4>حذف کاربر</h4>
                <p>
                  آیا از حذف{" "}
                  <strong>{formatFaNumber(selectedUserIds.length)}</strong>{" "}
                  کاربر انتخاب‌شده مطمئن هستید؟
                </p>
                <div className="confirm-modal-actions">
                  <button
                    type="button"
                    className="confirm-cancel-btn"
                    onClick={closeUsersDeleteModal}
                    disabled={deletingUsers}
                  >
                    انصراف
                  </button>
                  <button
                    type="button"
                    className="confirm-delete-btn"
                    onClick={confirmDeleteSelectedUsers}
                    disabled={deletingUsers}
                  >
                    {deletingUsers ? "در حال حذف..." : "بله، حذف شود"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="dashboard-activity-card">
          <div className="dashboard-activity-header">
            <h4>فعالیت اخیر کاربران</h4>
            <div className="dashboard-activity-filters">
              <div className="dashboard-filter-group">
                <div className="dashboard-filter-row">
                  <JalaliDatePicker
                    id="users-activity-from-date-filter"
                    label="از تاریخ"
                    value={usersActivityFromDate}
                    maxDate={usersActivityToDate}
                    onChange={handleUsersActivityFromDateChange}
                  />
                  <JalaliDatePicker
                    id="users-activity-to-date-filter"
                    label="تا تاریخ"
                    value={usersActivityToDate}
                    minDate={usersActivityFromDate}
                    onChange={handleUsersActivityToDateChange}
                  />
                </div>
              </div>
              <div className="dashboard-filter-row dashboard-filter-row-right">
                <div className="dashboard-filter-control">
                  <label htmlFor="users-activity-role-filter">نقش</label>
                  <select
                    id="users-activity-role-filter"
                    className="dashboard-filter-select"
                    value={usersActivityRoleFilter}
                    onChange={(event) => setUsersActivityRoleFilter(event.target.value)}
                    onMouseDown={(event) => (
                      scrollBySelectHeight(event, usersActivityRoleOptions.length + 1)
                    )}
                  >
                    <option value="all">همه نقش‌ها</option>
                    {usersActivityRoleOptions.map((roleValue) => (
                      <option key={roleValue} value={roleValue}>
                        {roleValue}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="dashboard-filter-control">
                  <label htmlFor="users-activity-module-filter">ماژول</label>
                  <select
                    id="users-activity-module-filter"
                    className="dashboard-filter-select"
                    value={usersActivityModuleFilter}
                    onChange={(event) => setUsersActivityModuleFilter(event.target.value)}
                    onMouseDown={(event) => (
                      scrollBySelectHeight(event, usersActivityModuleOptions.length + 1)
                    )}
                  >
                    <option value="all">همه ماژول‌ها</option>
                    {usersActivityModuleOptions.map((moduleValue) => (
                      <option key={moduleValue} value={moduleValue}>
                        {moduleValue}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          <div className="dashboard-activity-table-wrap">
            <table className="dashboard-activity-table">
              <thead>
                <tr>
                  <th>ردیف</th>
                  <th>نقش</th>
                  <th>تاریخ فعالیت</th>
                  <th>نام کاربر</th>
                  <th>ماژول</th>
                  <th>IP</th>
                  <th>رویداد</th>
                </tr>
              </thead>
              <tbody>
                {loadingUsersActivity ? (
                  <tr>
                    <td className="dashboard-activity-empty-row" colSpan={7}>
                      در حال دریافت فعالیت‌های کاربران...
                    </td>
                  </tr>
                ) : filteredUsersActivityRows.length === 0 ? (
                  <tr>
                    <td className="dashboard-activity-empty-row" colSpan={7}>
                      موردی با فیلترهای انتخابی پیدا نشد.
                    </td>
                  </tr>
                ) : (
                  paginatedUsersActivityRows.map((row, index) => (
                    <tr key={row.id}>
                      <td>{formatFaNumber(usersActivityStartIndex + index + 1)}</td>
                      <td>{row.role}</td>
                      <td>{row.activityDate}</td>
                      <td>{row.userName}</td>
                      <td>{row.module}</td>
                      <td>{row.ip}</td>
                      <td>{row.event}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="dashboard-activity-pagination">
            <button
              type="button"
              onClick={() => handleUsersActivityPageChange("prev")}
              disabled={currentUsersActivityPage <= 1}
              aria-label="صفحه قبل"
            >
              ‹
            </button>
            <span className="dashboard-activity-page-current">
              {formatFaNumber(currentUsersActivityPage)}
            </span>
            <span className="dashboard-activity-page-size">
              {formatFaNumber(totalUsersActivityPages)} صفحه
            </span>
            <button
              type="button"
              onClick={() => handleUsersActivityPageChange("next")}
              disabled={currentUsersActivityPage >= totalUsersActivityPages}
              aria-label="صفحه بعد"
            >
              ›
            </button>
          </div>
        </div>

        <div id="users-dashboard-empty-card-spacer" className="project-create-empty-card users-dashboard-empty-card-spacer" aria-hidden="true" />

      </div>
    );
  };

  const fetchSettingsRubikaAccounts = async () => {
    setLoadingSettingsAccounts(true);
    try {
      const result = await settingsApi.listRubikaAccounts();
      const items = Array.isArray(result?.items) ? result.items : [];
      setSettingsAccountsRows(items.map((item, index) => ({
        id: String(item.id),
        row: index + 1,
        username: item.phone || "--",
        accountReference: item.phone || "--",
        platform: item.platform || "روبیکا",
        connectedReceiveSources: Array.isArray(item.connected_receive_sources)
          ? item.connected_receive_sources.map((sourceItem) => ({
              id: String(sourceItem?.id || ""),
              name: String(sourceItem?.name || "").trim(),
            }))
          : [],
        updatedAt: item.updated_at || "",
        status: item.status || "active",
      })));
    } catch (error) {
      setSettingsMsg({ type: "error", text: error.message || "خطا در دریافت اکانت‌های روبیکا." });
    } finally {
      setLoadingSettingsAccounts(false);
    }
  };


  const fetchSettingsEitaAccounts = async () => {
    setLoadingSettingsEitaAccounts(true);
    try {
      const result = await settingsApi.listEitaAccounts();
      const items = Array.isArray(result?.items) ? result.items : [];
      setSettingsEitaAccountsRows(items.map((item, index) => ({
        id: String(item.id),
        row: index + 1,
        username: item.phone || "--",
        accountReference: item.username ? `@${item.username}` : (item.phone || "--"),
        platform: item.platform || "ایتا",
        connectedReceiveSources: Array.isArray(item.connected_receive_sources)
          ? item.connected_receive_sources.map((sourceItem) => ({
              id: String(sourceItem?.id || ""),
              name: String(sourceItem?.name || "").trim(),
            }))
          : [],
        displayName: [item.first_name, item.last_name].filter(Boolean).join(" "),
        updatedAt: item.updated_at || "",
        status: item.status || "active",
      })));
    } catch (error) {
      setSettingsMsg({ type: "error", text: error.message || "خطا در دریافت اکانت‌های ایتا." });
    } finally {
      setLoadingSettingsEitaAccounts(false);
    }
  };

  const handleToggleSettingsAccountSelection = (accountId) => {
    setSelectedSettingsAccountIds((prevSelectedIds) => (
      prevSelectedIds.includes(accountId)
        ? prevSelectedIds.filter((selectedId) => selectedId !== accountId)
        : [...prevSelectedIds, accountId]
    ));
  };

  const handleSelectAllSettingsAccounts = () => {
    if (!settingsAccountsRows.length) {
      return;
    }

    const allRowIds = settingsAccountsRows.map((row) => row.id);
    const areAllSelected = allRowIds.every((rowId) => (
      selectedSettingsAccountIds.includes(rowId)
    ));

    setSelectedSettingsAccountIds(areAllSelected ? [] : allRowIds);
  };

  const openCreateSettingsAccountSection = (provider = "rubika") => {
    setSettingsAccountForm({
      ...createInitialSettingsAccountForm(),
      provider,
      platform: provider === "eita" ? "ایتا" : SETTINGS_ACCOUNT_PLATFORM_OPTIONS[0],
    });
    setSettingsGeneralView("create");
    setSettingsMsg({ type: "", text: "" });
  };

  const handleSettingsAccountFormChange = (field, value) => {
    setSettingsAccountForm((prevForm) => ({
      ...prevForm,
      [field]: value,
    }));
  };

  const handleCancelCreateSettingsAccount = () => {
    setSettingsGeneralView("list");
    setSettingsAccountForm(createInitialSettingsAccountForm());
  };

  const openSettingsBotGuideSection = (botRow) => {
    setActiveSettingsBotGuide(botRow || null);
    setSettingsGeneralView("bot-guide");
    setSettingsMsg({ type: "", text: "" });
  };

  const handleBackToSettingsFromBotGuide = () => {
    setSettingsGeneralView("list");
    setActiveSettingsBotGuide(null);
  };

  const handleSubmitCreateSettingsAccount = async (event) => {
    event.preventDefault();
    const provider = settingsAccountForm.provider === "eita" ? "eita" : "rubika";
    const platformLabel = provider === "eita" ? "ایتا" : "روبیکا";
    const phone = String(settingsAccountForm.phone || settingsAccountForm.accountName || "").trim();
    const code = String(settingsAccountForm.code || "").trim();
    const firstName = String(settingsAccountForm.firstName || "").trim();
    const lastName = String(settingsAccountForm.lastName || "").trim();

    if (!phone) {
      setSettingsMsg({ type: "error", text: `شماره تماس ${platformLabel} را وارد کنید.` });
      return;
    }

    if (settingsAccountForm.signupRequired && !firstName) {
      setSettingsMsg({ type: "error", text: "نام برای ثبت‌نام ایتا الزامی است." });
      return;
    }

    setSubmittingRubikaAccount(true);
    try {
      if (!settingsAccountForm.codeSent) {
        if (provider === "eita") {
          await settingsApi.sendEitaCode({ phone });
        } else {
          await settingsApi.sendRubikaCode({ phone });
        }
        setSettingsAccountForm((prevForm) => ({
          ...prevForm,
          phone,
          accountName: phone,
          codeSent: true,
        }));
        setSettingsMsg({ type: "success", text: `کد تایید ${platformLabel} ارسال شد. کد را وارد و تایید کنید.` });
        return;
      }

      if (!code) {
        setSettingsMsg({ type: "error", text: `کد تایید ${platformLabel} را وارد کنید.` });
        return;
      }

      if (provider === "eita") {
        const result = await settingsApi.verifyEitaCode({ phone, code, firstName, lastName });
        if (result?.signup_required) {
          setSettingsAccountForm((prevForm) => ({
            ...prevForm,
            signupRequired: true,
          }));
          setSettingsMsg({ type: "error", text: "این شماره در ایتا ثبت‌نام نشده است؛ نام و نام خانوادگی را وارد کنید." });
          return;
        }
        await fetchSettingsEitaAccounts();
      } else {
        await settingsApi.verifyRubikaCode({ phone, code });
        await fetchSettingsRubikaAccounts();
      }
      setSettingsGeneralView("list");
      setSettingsAccountForm(createInitialSettingsAccountForm());
      setSettingsMsg({ type: "success", text: `اکانت ${platformLabel} با موفقیت متصل شد.` });
    } catch (error) {
      setSettingsMsg({ type: "error", text: error.message || `اتصال اکانت ${platformLabel} ناموفق بود.` });
    } finally {
      setSubmittingRubikaAccount(false);
    }
  };

  const handleReactivateRubikaAccount = async (accountId) => {
    const normalizedId = String(accountId || "").trim();
    if (!normalizedId) {
      return;
    }
    setReactivatingRubikaAccountId(normalizedId);
    try {
      await settingsApi.reactivateRubikaAccount({ accountId: normalizedId });
      await fetchSettingsRubikaAccounts();
      setSettingsMsg({ type: "success", text: "اکانت روبیکا با موفقیت مجددا فعال شد." });
    } catch (error) {
      setSettingsMsg({ type: "error", text: error.message || "فعال‌سازی مجدد اکانت روبیکا ناموفق بود." });
    } finally {
      setReactivatingRubikaAccountId("");
    }
  };


  const handleReactivateEitaAccount = async (accountId) => {
    const normalizedId = String(accountId || "").trim();
    if (!normalizedId) {
      return;
    }
    setReactivatingEitaAccountId(normalizedId);
    try {
      await settingsApi.reactivateEitaAccount({ accountId: normalizedId });
      await fetchSettingsEitaAccounts();
      setSettingsMsg({ type: "success", text: "اکانت ایتا با موفقیت مجددا فعال شد." });
    } catch (error) {
      setSettingsMsg({ type: "error", text: error.message || "فعال‌سازی مجدد اکانت ایتا ناموفق بود." });
    } finally {
      setReactivatingEitaAccountId("");
    }
  };

  const handleDeleteSelectedSettingsAccounts = () => {
    if (!selectedSettingsAccountIds.length) {
      setSettingsMsg({
        type: "error",
        text: "برای حذف، حداقل یک اکانت را انتخاب کنید.",
      });
      return;
    }

    setSettingsAccountsRows((prevRows) => (
      prevRows.filter((row) => !selectedSettingsAccountIds.includes(row.id))
    ));

    setSettingsMsg({
      type: "success",
      text: `${formatFaNumber(selectedSettingsAccountIds.length)} اکانت حذف شد.`,
    });
    setSelectedSettingsAccountIds([]);
  };

  const openSettingsDeleteModal = () => {
    if (!selectedSettingsAccountIds.length) {
      setSettingsMsg({
        type: "error",
        text: "برای حذف، حداقل یک اکانت را انتخاب کنید.",
      });
      return;
    }

    setSettingsMsg({ type: "", text: "" });
    setIsSettingsDeleteModalOpen(true);
  };

  const closeSettingsDeleteModal = () => {
    if (deletingSettingsAccounts) {
      return;
    }

    setIsSettingsDeleteModalOpen(false);
  };

  const confirmDeleteSelectedSettingsAccounts = () => {
    setDeletingSettingsAccounts(true);
    handleDeleteSelectedSettingsAccounts();
    setIsSettingsDeleteModalOpen(false);
    setDeletingSettingsAccounts(false);
  };

  const handleCopySelectedSettingsAccounts = () => {
    if (!selectedSettingsAccountIds.length) {
      setSettingsMsg({
        type: "error",
        text: "برای کپی، حداقل یک اکانت را انتخاب کنید.",
      });
      return;
    }

    const selectedRows = settingsAccountsRows.filter((row) => (
      selectedSettingsAccountIds.includes(row.id)
    ));
    if (!selectedRows.length) {
      setSettingsMsg({
        type: "error",
        text: "کپی اکانت انجام نشد. دوباره تلاش کنید.",
      });
      return;
    }

    const maxRowNumber = settingsAccountsRows.reduce((maxValue, row) => (
      Math.max(maxValue, Number(row.row) || 0)
    ), 0);
    const copiedRows = selectedRows.map((row, index) => ({
      ...row,
      id: `account-copy-${Date.now()}-${index + 1}`,
      row: maxRowNumber + index + 1,
      username: `${row.username}-copy`,
      accountReference: row.accountReference && row.accountReference !== "--"
        ? `${row.accountReference}-copy`
        : "--",
    }));

    setSettingsAccountsRows((prevRows) => [...prevRows, ...copiedRows]);
    setSettingsMsg({
      type: "success",
      text: `${formatFaNumber(copiedRows.length)} اکانت کپی شد و به لیست اضافه شد.`,
    });
    setSelectedSettingsAccountIds([]);
  };

  const handleCreateSettingsBackup = async () => {
    try {
      await settingsApi.createBackup();
      await fetchSettingsBackups();
      setSettingsMsg({
        type: "success",
        text: "بکاپ جدید با موفقیت ایجاد شد.",
      });
    } catch (error) {
      if (isSqliteOnlyBackupError(error)) {
        setSettingsMsg({ type: "", text: "" });
        return;
      }
      setSettingsMsg({
        type: "error",
        text: error.message || "خطا در ایجاد بکاپ.",
      });
    }
  };

  const handleDownloadSettingsBackup = async (backupCode) => {
    try {
      const payload = await settingsApi.downloadBackup({ backupCode });
      const blob = payload?.blob;
      const filename = sanitizeBackupFilename(payload?.filename, backupCode);
      if (!blob) {
        throw new Error("پاسخ دانلود نامعتبر است.");
      }
      const objectUrl = URL.createObjectURL(blob);
      const tempLink = document.createElement("a");
      tempLink.href = objectUrl;
      tempLink.download = filename;
      document.body.appendChild(tempLink);
      tempLink.click();
      tempLink.remove();
      URL.revokeObjectURL(objectUrl);
      setSettingsMsg({
        type: "success",
        text: `دانلود بکاپ ${backupCode} آغاز شد.`,
      });
    } catch (error) {
      setSettingsMsg({
        type: "error",
        text: error.message || "خطا در دانلود بکاپ.",
      });
    }
  };

  const handleRestoreSettingsBackup = async (backupCode) => {
    try {
      await settingsApi.restoreBackup({ backupCode });
      await fetchSettingsBackups();
      setSettingsMsg({
        type: "success",
        text: `بازگردانی از بکاپ ${backupCode} با موفقیت انجام شد.`,
      });
    } catch (error) {
      setSettingsMsg({
        type: "error",
        text: error.message || "خطا در بازگردانی بکاپ.",
      });
    }
  };

  const openBackupRestoreModal = (backupCode) => {
    setBackupCodeToRestore(String(backupCode || ""));
    setIsBackupRestoreModalOpen(true);
  };

  const closeBackupRestoreModal = () => {
    setIsBackupRestoreModalOpen(false);
    setBackupCodeToRestore("");
  };

  const confirmRestoreSettingsBackup = async () => {
    if (!backupCodeToRestore) {
      return;
    }
    await handleRestoreSettingsBackup(backupCodeToRestore);
    closeBackupRestoreModal();
  };

  const handleChangeSettingsBackupSchedule = async (value) => {
    setSettingsBackupSchedule(value);
    try {
      await settingsApi.updateBackupConfig({ backup_duration: value });
      setSettingsMsg({
        type: "success",
        text: `زمان‌بندی خودکار بکاپ روی ${SETTINGS_BACKUP_SCHEDULE_LABELS[value]} تنظیم شد.`,
      });
    } catch (error) {
      if (isSqliteOnlyBackupError(error)) {
        setSettingsMsg({ type: "", text: "" });
        return;
      }
      setSettingsMsg({
        type: "error",
        text: error.message || "خطا در ذخیره زمان‌بندی بکاپ.",
      });
    }
  };

  const handleSaveSettingsRateLimit = async () => {
    if (!settingsRateLimit) {
      setSettingsMsg({
        type: "error",
        text: "مقدار نرخ محدودیت را وارد کنید.",
      });
      return;
    }

    try {
      await settingsApi.updateGeneralConfig({
        limit_rate: Number(settingsRateLimit),
      });
      setSettingsMsg({
        type: "success",
        text: `نرخ محدودیت روی ${formatFaNumber(settingsRateLimit)} درخواست در دقیقه ذخیره شد.`,
      });
    } catch (error) {
      setSettingsMsg({
        type: "error",
        text: error.message || "خطا در ذخیره نرخ محدودیت.",
      });
    }
  };

  const handleSaveSettingsWindow = async () => {
    if (!settingsSystemCheckIntervalSeconds) {
      setSettingsMsg({
        type: "error",
        text: "بازه زمانی پردازش را وارد کنید.",
      });
      return;
    }

    try {
      await settingsApi.updateGeneralConfig({
        system_check_interval: Number(settingsSystemCheckIntervalSeconds),
      });
      setSettingsMsg({
        type: "success",
        text: `بازه زمانی پردازش با موفقیت ذخیره شد.`,
      });
    } catch (error) {
      setSettingsMsg({
        type: "error",
        text: error.message || "خطا در ذخیره بازه پردازش.",
      });
    }
  };

  const handleSaveSettingsCleanupNewsInterval = async () => {
    if (!settingsCleanupNewsIntervalHours) {
      setSettingsMsg({
        type: "error",
        text: "مقدار دوره پاکسازی خبرها را وارد کنید.",
      });
      return;
    }

    try {
      await settingsApi.updateGeneralConfig({
        cleanup_news_interval_hours: Number(settingsCleanupNewsIntervalHours),
      });
      setSettingsMsg({
        type: "success",
        text: "دوره پاکسازی خبرها با موفقیت ذخیره شد.",
      });
    } catch (error) {
      setSettingsMsg({
        type: "error",
        text: error.message || "خطا در ذخیره دوره پاکسازی خبرها.",
      });
    }
  };

  const handleOpenSystemRestartModal = () => {
    setIsSystemRestartModalOpen(true);
  };

  const handleCloseSystemRestartModal = () => {
    if (restartingSystem) {
      return;
    }
    setIsSystemRestartModalOpen(false);
  };

  const handleConfirmSystemRestart = async () => {
    setRestartingSystem(true);
    try {
      await processApi.restart({ cleanup_mode: "tasks_only" });
      setSettingsMsg({ type: "success", text: "ری استارت پردازشی سیستم انجام شد." });
      setIsSystemRestartModalOpen(false);
    } catch (error) {
      setSettingsMsg({
        type: "error",
        text: error.message || "ری استارت پردازشی سیستم ناموفق بود.",
      });
    } finally {
      setRestartingSystem(false);
    }
  };

  const handleOpenSystemRestartWithNewsCleanupModal = () => {
    setIsSystemRestartWithNewsCleanupModalOpen(true);
  };

  const handleCloseSystemRestartWithNewsCleanupModal = () => {
    if (restartingSystem) {
      return;
    }
    setIsSystemRestartWithNewsCleanupModalOpen(false);
  };

  const handleConfirmSystemRestartWithNewsCleanup = async () => {
    setRestartingSystem(true);
    try {
      await processApi.restart({ cleanup_mode: "tasks_and_news" });
      setSettingsMsg({ type: "success", text: "ری استارت کلی سیستم همراه پاکسازی خبرها انجام شد." });
      setIsSystemRestartWithNewsCleanupModalOpen(false);
    } catch (error) {
      setSettingsMsg({
        type: "error",
        text: error.message || "ری استارت کلی سیستم ناموفق بود.",
      });
    } finally {
      setRestartingSystem(false);
    }
  };

  const handleHomepageSettingsFieldChange = (field, value) => {
    setSettingsHomepageConfig((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleHomepageLogoChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    const previewUrl = URL.createObjectURL(file);
    setSettingsHomepageConfig((prev) => ({
      ...prev,
      homeLogoFile: file,
      homeLogoUrl: previewUrl,
    }));
  };

  const handleSaveHomepageSettings = async () => {
    try {
      await settingsApi.updateHomepageConfig({
        project_title: settingsHomepageConfig.projectTitle,
        home_description: settingsHomepageConfig.homeDescription,
        terms_and_conditions: settingsHomepageConfig.termsAndConditions,
        home_logo: settingsHomepageConfig.homeLogoFile,
      });
      setSettingsMsg({
        type: "success",
        text: "تنظیمات صفحه اصلی با موفقیت ذخیره شد.",
      });
      fetchSettingsHomepageConfig();
    } catch (error) {
      setSettingsMsg({
        type: "error",
        text: error.message || "خطا در ذخیره تنظیمات صفحه اصلی.",
      });
    }
  };

  const handleSecurityIpFormChange = (field, value) => {
    setSecurityIpForm((prevForm) => ({
      ...prevForm,
      [field]: value,
    }));
  };

  const handleAddSecurityWhiteIp = async () => {
    const trimmedIp = securityIpForm.ip.trim();
    const selectedUserId = securityIpForm.userId ? Number(securityIpForm.userId) : null;
    if (!trimmedIp) {
      setSettingsMsg({
        type: "error",
        text: "برای افزودن آی پی، فیلد آی پی را تکمیل کنید.",
      });
      return;
    }

    try {
      await settingsApi.createWhitelistIp({ ip: trimmedIp, user_id: selectedUserId });
      await fetchSecurityWhitelistIps();
      setSecurityIpForm({
        userId: "",
        ip: "",
      });
      setSettingsMsg({
        type: "success",
        text: "آی پی جدید با موفقیت به لیست سفید اضافه شد.",
      });
    } catch (error) {
      setSettingsMsg({
        type: "error",
        text: error.message || "خطا در افزودن آی پی به لیست سفید.",
      });
    }
  };

  const handleDeleteSecurityWhiteIp = async (rowId) => {
    const normalizedRowId = String(rowId || "");
    if (!normalizedRowId) {
      return;
    }
    const numericRowId = Number(normalizedRowId);
    if (!Number.isInteger(numericRowId) || numericRowId <= 0) {
      setSettingsMsg({
        type: "error",
        text: "شناسه رکورد آی پی معتبر نیست.",
      });
      return;
    }
    try {
      setDeletingSecurityWhiteIpId(normalizedRowId);
      await settingsApi.deleteWhitelistIp({ whitelist_ip_id: numericRowId });
      await fetchSecurityWhitelistIps();
      setSettingsMsg({
        type: "success",
        text: "آی پی از لیست سفید حذف شد.",
      });
    } catch (error) {
      setSettingsMsg({
        type: "error",
        text: error.message || "خطا در حذف آی پی از لیست سفید.",
      });
    } finally {
      setDeletingSecurityWhiteIpId("");
    }
  };

  const handleTestSettingsDbConnection = () => {
    if (!settingsDbConfig.host || !settingsDbConfig.port || !settingsDbConfig.dbName) {
      setSettingsDbTestMsg({
        type: "error",
        text: "برای تست اتصال، همه فیلدهای دیتابیس را تکمیل کنید.",
      });
      scrollToElementById("settings-db-test-msg");
      return;
    }

    setSettingsDbTestMsg({
      type: "success",
      text: "اتصال دیتابیس با موفقیت بررسی شد.",
    });
    scrollToElementById("settings-db-test-msg");
  };

  const handleSettingsTopTabChange = (nextTab) => {
    setSettingsTopTab(nextTab);
    setSettingsMsg({ type: "", text: "" });
  };

  const handleSidebarTabChange = (nextTab) => {
    setActiveTab(nextTab);
    setMenuOpen(false);
  };

  const handleRequestSecurityModeChange = (nextMode) => {
    if (nextMode === securityApiRestrictionMode) {
      return;
    }
    setPendingSecurityModeValue(nextMode);
    setIsSecurityModeConfirmModalOpen(true);
  };

  const handleCancelSecurityModeChange = () => {
    if (savingSecurityMode) {
      return;
    }
    setIsSecurityModeConfirmModalOpen(false);
    setPendingSecurityModeValue("");
  };

  const handleConfirmSecurityModeChange = async () => {
    if (!pendingSecurityModeValue) {
      return;
    }
    setSavingSecurityMode(true);
    try {
      await settingsApi.updateGeneralConfig({
        ip_limit_activity_status: pendingSecurityModeValue === "enabled",
      });
      setSecurityApiRestrictionMode(pendingSecurityModeValue);
      setSavedSecuritySnapshot((prev) => ({
        ...prev,
        apiRestrictionMode: pendingSecurityModeValue,
      }));
      setSettingsMsg({
        type: "success",
        text: `محدودیت بر اساس آی پی روی ${pendingSecurityModeValue === "enabled" ? "فعال" : "غیرفعال"} تنظیم شد.`,
      });
      setIsSecurityModeConfirmModalOpen(false);
      setPendingSecurityModeValue("");
    } catch (error) {
      setSettingsMsg({
        type: "error",
        text: error.message || "خطا در به‌روزرسانی محدودیت بر اساس آی پی.",
      });
    } finally {
      setSavingSecurityMode(false);
    }
  };

  const renderSettingsTab = () => {
    const allSettingsAccountIds = settingsAccountsRows.map((row) => row.id);
    const areAllSettingsAccountsSelected = (
      allSettingsAccountIds.length > 0
      && allSettingsAccountIds.every((rowId) => selectedSettingsAccountIds.includes(rowId))
    );
    const securityModeOptions = [
      { value: "enabled", label: "فعال" },
      { value: "disabled", label: "غیرفعال" },
    ];
    const shouldShowSettingsTopTabs = !(
      settingsTopTab === "settings" && (settingsGeneralView === "create" || settingsGeneralView === "bot-guide")
    );

    const renderGeneralSettingsContent = () => {
      if (settingsGeneralView === "create") {
        return (
          <div className="settings-section-card settings-account-create-card">
            <div className="settings-account-create-head">
              <h4>ایجاد اکانت جدید</h4>
              <button type="button" className="settings-primary-btn" onClick={handleCancelCreateSettingsAccount}>
                بازگشت به تنظیمات
              </button>
            </div>

            <form className="settings-account-create-form" onSubmit={handleSubmitCreateSettingsAccount}>
              <div className="settings-account-create-field">
                <label htmlFor="settings-account-phone">
                  شماره تماس {settingsAccountForm.provider === "eita" ? "ایتا" : "روبیکا"}
                </label>
                <input
                  id="settings-account-phone"
                  type="tel"
                  value={settingsAccountForm.phone}
                  onChange={(event) => handleSettingsAccountFormChange("phone", event.target.value)}
                  placeholder="09123456789"
                  disabled={settingsAccountForm.codeSent || submittingRubikaAccount}
                />
              </div>

              {settingsAccountForm.codeSent && (
                <div className="settings-account-create-field">
                  <label htmlFor="settings-account-code">
                    کد تایید {settingsAccountForm.provider === "eita" ? "ایتا" : "روبیکا"}
                  </label>
                  <input
                    id="settings-account-code"
                    type="text"
                    value={settingsAccountForm.code}
                    onChange={(event) => handleSettingsAccountFormChange("code", event.target.value)}
                    placeholder={`کد ارسال‌شده از ${settingsAccountForm.provider === "eita" ? "ایتا" : "روبیکا"}`}
                    disabled={submittingRubikaAccount}
                  />
                </div>
              )}

              {settingsAccountForm.provider === "eita" && settingsAccountForm.signupRequired && (
                <>
                  <div className="settings-account-create-field">
                    <label htmlFor="settings-eita-first-name">نام</label>
                    <input
                      id="settings-eita-first-name"
                      type="text"
                      value={settingsAccountForm.firstName}
                      onChange={(event) => handleSettingsAccountFormChange("firstName", event.target.value)}
                      placeholder="نام کاربر در ایتا"
                      disabled={submittingRubikaAccount}
                    />
                  </div>
                  <div className="settings-account-create-field">
                    <label htmlFor="settings-eita-last-name">نام خانوادگی</label>
                    <input
                      id="settings-eita-last-name"
                      type="text"
                      value={settingsAccountForm.lastName}
                      onChange={(event) => handleSettingsAccountFormChange("lastName", event.target.value)}
                      placeholder="اختیاری"
                      disabled={submittingRubikaAccount}
                    />
                  </div>
                </>
              )}

              <div className="project-create-actions settings-account-create-actions">
                <button type="button" className="project-create-cancel-btn" onClick={handleCancelCreateSettingsAccount}>
                  انصراف
                </button>
                <button type="submit" className="save-btn project-create-save-btn" disabled={submittingRubikaAccount}>
                  {submittingRubikaAccount ? "در حال انجام..." : (settingsAccountForm.codeSent ? "تایید کد" : "ارسال کد تایید")}
                </button>
              </div>
              <div id="settings-account-create-actions-empty-card-spacer" className="project-create-empty-card" aria-hidden="true" />
            </form>
          </div>
        );
      }

      if (settingsGeneralView === "bot-guide") {
        const platformLabel = activeSettingsBotGuide?.platform || "پلتفرم";
        const botIdLabel = activeSettingsBotGuide?.botName || "شناسه ربات";

        return (
          <div className="settings-section-card settings-account-create-card settings-bot-guide-card">
            <div className="settings-account-create-head">
              <h4>راهنمای اتصال "{platformLabel}"</h4>
              <button type="button" className="settings-primary-btn" onClick={handleBackToSettingsFromBotGuide}>
                بازگشت به تنظیمات
              </button>
            </div>

            <div className="settings-bot-guide-body">
              <p>برای انتشار خودکار خبر ها در گروه یا کانال خود</p>
              <p>
                باید ربات "{botIdLabel}" رو به گروه یا کانال خود بصورت ممبر اضافه کنید و سپس به این ربات
                دسترسی ادمین بدهید
              </p>
              {activeSettingsBotGuide?.helpDescription && (
                <p>{activeSettingsBotGuide.helpDescription}</p>
              )}
              {Array.isArray(activeSettingsBotGuide?.helpImages) && activeSettingsBotGuide.helpImages.length > 0 ? (
                <div className="settings-bot-guide-images-list">
                  {activeSettingsBotGuide.helpImages.map((imageItem) => (
                    <div
                      key={imageItem.id}
                      className="settings-section-card settings-account-create-card settings-bot-guide-card settings-bot-guide-image-card"
                    >
                      <img src={imageItem.url} alt={`راهنمای تصویری ${imageItem.priority}`} />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="settings-bot-guide-empty">راهنمای تصویری ثبت نشده است.</div>
              )}
            </div>
          </div>
        );
      }

      return (
        <>
        <div className="settings-section-card">
          <div className="settings-section-head">
            <h4>اکانت‌های روبیکا</h4>
            <button type="button" className="settings-primary-btn" onClick={() => openCreateSettingsAccountSection("rubika")}>
              اتصال اکانت روبیکا
            </button>
          </div>

          <div className="settings-table-wrap">
            <table className="settings-table">
              <thead>
                <tr>
                  <th>ردیف</th>
                  <th>پلتفرم</th>
                  <th>شماره تماس</th>
                  <th>منابع متصل</th>
                  <th>وضعیت</th>
                  <th>آخرین بروزرسانی</th>
                  <th>عملیات</th>
                </tr>
              </thead>
              <tbody>
                {settingsAccountsRows.map((row) => (
                  <tr key={row.id}>
                    <td>{formatFaNumber(row.row)}</td>
                    <td>{row.platform}</td>
                    <td>{row.username}</td>
                    <td>
                      {Array.isArray(row.connectedReceiveSources) && row.connectedReceiveSources.length > 0
                        ? row.connectedReceiveSources
                          .map((sourceItem) => sourceItem.name || `منبع ${sourceItem.id}`)
                          .join("، ")
                        : "--"}
                    </td>
                    <td>
                      <span className={`settings-status-chip ${row.status}`}>
                        {row.status === "active" ? "فعال" : "غیرفعال"}
                      </span>
                    </td>
                    <td>{row.updatedAt || "--"}</td>
                    <td>
                      {row.status !== "active" ? (
                        <button
                          type="button"
                          className="settings-action-btn"
                          aria-label="فعال‌سازی مجدد اکانت"
                          disabled={reactivatingRubikaAccountId === row.id}
                          onClick={() => handleReactivateRubikaAccount(row.id)}
                        >
                          {reactivatingRubikaAccountId === row.id ? "..." : "↻"}
                        </button>
                      ) : "--"}
                    </td>
                  </tr>
                ))}
                {!loadingSettingsAccounts && settingsAccountsRows.length === 0 && (
                  <tr>
                    <td colSpan={7}>اکانت روبیکا ثبت نشده است.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {loadingSettingsAccounts && (
            <div className="settings-msg">در حال دریافت اکانت‌های روبیکا...</div>
          )}
        </div>

        <div className="settings-section-card">
          <div className="settings-section-head">
            <h4>اکانت‌های ایتا</h4>
            <button type="button" className="settings-primary-btn" onClick={() => openCreateSettingsAccountSection("eita")}>
              اتصال اکانت ایتا
            </button>
          </div>

          <div className="settings-table-wrap">
            <table className="settings-table">
              <thead>
                <tr>
                  <th>ردیف</th>
                  <th>پلتفرم</th>
                  <th>شماره تماس</th>
                  <th>منابع متصل</th>
                  <th>وضعیت</th>
                  <th>آخرین بروزرسانی</th>
                  <th>عملیات</th>
                </tr>
              </thead>
              <tbody>
                {settingsEitaAccountsRows.map((row) => (
                  <tr key={row.id}>
                    <td>{formatFaNumber(row.row)}</td>
                    <td>{row.platform}</td>
                    <td>{row.username}</td>
                    <td>
                      {Array.isArray(row.connectedReceiveSources) && row.connectedReceiveSources.length > 0
                        ? row.connectedReceiveSources
                          .map((sourceItem) => sourceItem.name || `منبع ${sourceItem.id}`)
                          .join("، ")
                        : "--"}
                    </td>
                    <td>
                      <span className={`settings-status-chip ${row.status}`}>
                        {row.status === "active" ? "فعال" : "غیرفعال"}
                      </span>
                    </td>
                    <td>{row.updatedAt || "--"}</td>
                    <td>
                      {row.status !== "active" ? (
                        <button
                          type="button"
                          className="settings-action-btn"
                          aria-label="فعال‌سازی مجدد اکانت ایتا"
                          disabled={reactivatingEitaAccountId === row.id}
                          onClick={() => handleReactivateEitaAccount(row.id)}
                        >
                          {reactivatingEitaAccountId === row.id ? "..." : "↻"}
                        </button>
                      ) : "--"}
                    </td>
                  </tr>
                ))}
                {!loadingSettingsEitaAccounts && settingsEitaAccountsRows.length === 0 && (
                  <tr>
                    <td colSpan={7}>اکانت ایتا ثبت نشده است.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {loadingSettingsEitaAccounts && (
            <div className="settings-msg">در حال دریافت اکانت‌های ایتا...</div>
          )}
        </div>

        <div className="settings-section-card">
          <div className="settings-section-head">
            <h4>ربات ها</h4>
          </div>

          <div className="settings-table-wrap">
            <table className="settings-table settings-bots-table">
              <thead>
                <tr>
                  <th>ردیف</th>
                  <th>پلتفرم</th>
                  <th>بات/نام کاربری</th>
                  <th className="settings-link-cell">لینک/شناسه</th>
                  <th>وضعیت</th>
                  <th>راهنمای اتصال</th>
                </tr>
              </thead>
              <tbody>
                {settingsBotsRows.map((row) => (
                  <tr key={row.id}>
                    <td>{formatFaNumber(row.row)}</td>
                    <td>{row.platform}</td>
                    <td>{row.botName}</td>
                    <td className="settings-link-cell">{row.link || "--"}</td>
                    <td>{row.status}</td>
                    <td>
                      <button
                        type="button"
                        className="settings-help-btn"
                        aria-label={row.guide}
                        onClick={() => openSettingsBotGuideSection(row)}
                      >
                        ؟
                      </button>
                    </td>
                  </tr>
                ))}
                {!loadingSettingsBots && settingsBotsRows.length === 0 && (
                  <tr>
                    <td colSpan={6}>رباتی ثبت نشده است.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {loadingSettingsBots && (
            <div className="settings-msg">در حال دریافت ربات‌ها...</div>
          )}
        </div>

        {isSettingsDeleteModalOpen && (
          <div className="confirm-modal-overlay" onClick={closeSettingsDeleteModal}>
            <div
              className="confirm-modal"
              role="dialog"
              aria-modal="true"
              aria-label="حذف اکانت"
              onClick={(event) => event.stopPropagation()}
            >
              <h4>حذف اکانت</h4>
              <p>
                آیا از حذف{" "}
                <strong>{formatFaNumber(selectedSettingsAccountIds.length)}</strong>{" "}
                اکانت انتخاب‌شده مطمئن هستید؟
              </p>
              <div className="confirm-modal-actions">
                <button
                  type="button"
                  className="confirm-cancel-btn"
                  onClick={closeSettingsDeleteModal}
                  disabled={deletingSettingsAccounts}
                >
                  انصراف
                </button>
                <button
                  type="button"
                  className="confirm-delete-btn"
                  onClick={confirmDeleteSelectedSettingsAccounts}
                  disabled={deletingSettingsAccounts}
                >
                  {deletingSettingsAccounts ? "در حال حذف..." : "بله، حذف شود"}
                </button>
              </div>
            </div>
          </div>
        )}

        {isSystemRestartModalOpen && (
          <div className="confirm-modal-overlay" onClick={handleCloseSystemRestartModal}>
            <div
              className="confirm-modal"
              role="dialog"
              aria-modal="true"
              aria-label="ری استارت پردازش سیستم"
              onClick={(event) => event.stopPropagation()}
            >
              <h4>ری استارت پردازش سیستم</h4>
              <p>آیا از انجام ری استارت مطمئنی</p>
              <div className="confirm-modal-actions">
                <button
                  type="button"
                  className="confirm-cancel-btn"
                  onClick={handleCloseSystemRestartModal}
                  disabled={restartingSystem}
                >
                  انصراف
                </button>
                <button
                  type="button"
                  className="confirm-delete-btn"
                  onClick={handleConfirmSystemRestart}
                  disabled={restartingSystem}
                >
                  {restartingSystem ? "در حال انجام..." : "ری استارت"}
                </button>
              </div>
            </div>
          </div>
        )}

        {isSystemRestartWithNewsCleanupModalOpen && (
          <div className="confirm-modal-overlay" onClick={handleCloseSystemRestartWithNewsCleanupModal}>
            <div
              className="confirm-modal"
              role="dialog"
              aria-modal="true"
              aria-label="ری استارت کلی سیستم"
              onClick={(event) => event.stopPropagation()}
            >
              <h4>ری استارت کلی سیستم</h4>
              <p>آیا از انجام ری استارت مطمئنی</p>
              <div className="confirm-modal-actions">
                <button
                  type="button"
                  className="confirm-cancel-btn"
                  onClick={handleCloseSystemRestartWithNewsCleanupModal}
                  disabled={restartingSystem}
                >
                  انصراف
                </button>
                <button
                  type="button"
                  className="confirm-delete-btn"
                  onClick={handleConfirmSystemRestartWithNewsCleanup}
                  disabled={restartingSystem}
                >
                  {restartingSystem ? "در حال انجام..." : "ری استارت"}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="settings-inline-grid">
          <div className="settings-section-card settings-inline-card">
            <h4>ری استارت پردازش سیستم</h4>
            <p className="settings-inline-desc">با این دکمه تمام وظایف پردازشی در حال اجرا متوقف و سیستم پردازشی مجددا شروع به فعالیت میکند</p>
            <button
              type="button"
              className="settings-primary-btn settings-save-btn"
              onClick={handleOpenSystemRestartModal}
              disabled={restartingSystem}
            >
              {restartingSystem ? "در حال انجام..." : "ری استارت"}
            </button>
          </div>

          <div className="settings-section-card settings-inline-card">
            <h4>ری استارت کلی سیستم</h4>
            <p className="settings-inline-desc">با این دکمه تمام وظایف پردازشی در حال اجرا متوقف و سیستم پردازشی مجددا شروع به فعالیت میکند و نیز تمام خبر ها پاک میشوند</p>
            <button
              type="button"
              className="settings-primary-btn settings-save-btn"
              onClick={handleOpenSystemRestartWithNewsCleanupModal}
              disabled={restartingSystem}
            >
              {restartingSystem ? "در حال انجام..." : "ری استارت"}
            </button>
          </div>

          <div className="settings-section-card settings-inline-card">
            <h4>بررسی پروژه ها</h4>
            <div className="settings-inline-form settings-inline-form-left">
              <div className="settings-inline-input-group">
                <input
                  id="settings-system-check-interval-seconds"
                  type="number"
                  min="1"
                  value={settingsSystemCheckIntervalSeconds}
                  onChange={(event) => setSettingsSystemCheckIntervalSeconds(event.target.value)}
                />
                <span>ثانیه</span>
              </div>
            </div>
            <button type="button" className="settings-primary-btn settings-save-btn" onClick={handleSaveSettingsWindow}>
              ذخیره
            </button>
          </div>

          <div className="settings-section-card settings-inline-card">
            <h4>پاک کردن دوره ای خبر ها</h4>
            <div className="settings-inline-form settings-inline-form-left">
              <div className="settings-inline-input-group">
                <input
                  id="settings-cleanup-news-interval-hours"
                  type="number"
                  min="1"
                  value={settingsCleanupNewsIntervalHours}
                  onChange={(event) => setSettingsCleanupNewsIntervalHours(event.target.value)}
                />
                <span>ساعت</span>
              </div>
            </div>
            <button type="button" className="settings-primary-btn settings-save-btn" onClick={handleSaveSettingsCleanupNewsInterval}>
              ذخیره
            </button>
          </div>
        </div>

        <div id="settings-inline-empty-card-spacer" className="project-create-empty-card settings-inline-empty-card-spacer" aria-hidden="true" />

        </>
      );
    };

    const renderSecuritySettingsContent = () => {
      const totalSecurityLoginAttemptsPages = Math.max(
        1,
        Math.ceil(securityLoginAttemptsRows.length / SECURITY_LOGIN_ATTEMPTS_ROWS_PER_PAGE)
      );
      const currentSecurityLoginAttemptsPage = Math.min(
        securityLoginAttemptsPage,
        totalSecurityLoginAttemptsPages
      );
      const securityLoginAttemptsStartIndex = (
        (currentSecurityLoginAttemptsPage - 1) * SECURITY_LOGIN_ATTEMPTS_ROWS_PER_PAGE
      );
      const paginatedSecurityLoginAttemptsRows = securityLoginAttemptsRows.slice(
        securityLoginAttemptsStartIndex,
        securityLoginAttemptsStartIndex + SECURITY_LOGIN_ATTEMPTS_ROWS_PER_PAGE
      );

      return (
      <div className="settings-security-layout">
        <div className="settings-section-card settings-security-card">
          <div className="settings-security-head">
            <h4>محدودیت براساس آی پی</h4>
            <div className="settings-security-control settings-security-head-control">
              <select
                id="security-api-restriction-mode"
                className="settings-security-select"
                value={securityApiRestrictionMode}
                onChange={(event) => handleRequestSecurityModeChange(event.target.value)}
                aria-label="حالت محدودیت دسترسی بر اساس آی پی"
                disabled={savingSecurityMode}
              >
                {securityModeOptions.map((option) => (
                  <option key={`security-api-restriction-${option.value}`} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <h5 className="settings-security-title">لیست آی پی های سفید</h5>
          <div className="settings-table-wrap">
            <table className="settings-table settings-security-table">
              <thead>
                <tr>
                  <th>نام کاربر</th>
                  <th>نقش</th>
                  <th>آی پی</th>
                  <th>وضعیت</th>
                  <th>زمان</th>
                  <th>عملیات</th>
                </tr>
              </thead>
              <tbody>
                {securityWhiteIpRows.length === 0 ? (
                  <tr>
                    <td colSpan={6}>آی پی سفیدی ثبت نشده است.</td>
                  </tr>
                ) : (
                  securityWhiteIpRows.map((row) => (
                    <tr key={row.id}>
                      <td>{row.userName}</td>
                      <td>{row.role}</td>
                      <td>{row.ip}</td>
                      <td>
                        <span className={`settings-status-chip ${row.status}`}>{row.statusLabel}</span>
                      </td>
                      <td>{row.time}</td>
                      <td>
                        <button
                          type="button"
                          className="settings-action-btn"
                          onClick={() => handleDeleteSecurityWhiteIp(row.id)}
                          disabled={deletingSecurityWhiteIpId === row.id}
                        >
                          {deletingSecurityWhiteIpId === row.id ? "در حال حذف..." : "حذف"}
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <h5 className="settings-security-title">فرم افزودن آی پی</h5>
          <div className="settings-security-form">
            <select
              value={securityIpForm.userId}
              onChange={(event) => handleSecurityIpFormChange("userId", event.target.value)}
              aria-label="انتخاب کاربر"
            >
              <option value="">انتخاب کاربر</option>
              {securityUsersOptions.map((user) => (
                <option key={`security-user-${user.id}`} value={user.id}>
                  {user.label}
                </option>
              ))}
            </select>
            <input
              type="text"
              placeholder="IP Address"
              value={securityIpForm.ip}
              onChange={(event) => handleSecurityIpFormChange("ip", event.target.value)}
              aria-label="آی پی"
            />
            <button type="button" className="settings-primary-btn" onClick={handleAddSecurityWhiteIp}>
              افزودن آی پی
            </button>
          </div>
        </div>
        <div id="settings-security-empty-card-spacer-1" className="project-create-empty-card settings-security-empty-card-spacer" aria-hidden="true" />

        <div className="settings-section-card settings-security-card">
          <h4>ثبت تلاش ورود کاربران</h4>
          <div className="settings-table-wrap">
            <table className="settings-table settings-security-table">
              <thead>
                <tr>
                  <th>نام کاربری موفق</th>
                  <th>نام کاربری وارد شده (ناموفق)</th>
                  <th>پسورد وارد شده (ناموفق)</th>
                  <th>آی پی</th>
                  <th>وضعیت</th>
                  <th>علت عدم موفقیت</th>
                  <th>تاریخ و ساعت تلاش</th>
                </tr>
              </thead>
              <tbody>
                {paginatedSecurityLoginAttemptsRows.length === 0 ? (
                  <tr>
                    <td colSpan={7}>پیامی موجود نیست</td>
                  </tr>
                ) : (
                  paginatedSecurityLoginAttemptsRows.map((row) => (
                    <tr key={row.id}>
                      <td>{row.successfulUsername || "--"}</td>
                      <td>{row.failedUsernameInput || "--"}</td>
                      <td>{row.failedPasswordInput || "--"}</td>
                      <td>{row.ip}</td>
                      <td>
                        <span className={`settings-status-chip ${row.status === "success" ? "active" : "inactive"}`}>
                          {row.statusLabel}
                        </span>
                      </td>
                      <td>{row.failureReason || "--"}</td>
                      <td>{row.attemptedAt}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="projects-table-pagination dashboard-activity-pagination settings-security-pagination">
            <button
              type="button"
              onClick={() => setSecurityLoginAttemptsPage((prevPage) => Math.max(1, prevPage - 1))}
              disabled={currentSecurityLoginAttemptsPage <= 1}
              aria-label="صفحه قبل"
            >
              ‹
            </button>
            <span className="projects-pagination-current dashboard-activity-page-current">
              {formatFaNumber(currentSecurityLoginAttemptsPage)}
            </span>
            <span className="projects-pagination-total dashboard-activity-page-size">
              {formatFaNumber(totalSecurityLoginAttemptsPages)} صفحه
            </span>
            <button
              type="button"
              onClick={() => setSecurityLoginAttemptsPage((prevPage) => Math.min(totalSecurityLoginAttemptsPages, prevPage + 1))}
              disabled={currentSecurityLoginAttemptsPage >= totalSecurityLoginAttemptsPages}
              aria-label="صفحه بعد"
            >
              ›
            </button>
          </div>
          {loadingSecurityLoginAttempts && (
            <div className="settings-msg">در حال دریافت تلاش های ورود...</div>
          )}
        </div>
        <div id="settings-security-empty-card-spacer-2" className="project-create-empty-card settings-security-empty-card-spacer" aria-hidden="true" />
      </div>
      );
    };

    const renderBackupSettingsContent = () => {
      const totalSettingsBackupPages = Math.max(
        1,
        Math.ceil(settingsBackupRows.length / SETTINGS_BACKUP_ROWS_PER_PAGE)
      );
      const currentSettingsBackupPage = Math.min(settingsBackupPage, totalSettingsBackupPages);
      const settingsBackupStartIndex = (currentSettingsBackupPage - 1) * SETTINGS_BACKUP_ROWS_PER_PAGE;
      const paginatedSettingsBackupRows = settingsBackupRows.slice(
        settingsBackupStartIndex,
        settingsBackupStartIndex + SETTINGS_BACKUP_ROWS_PER_PAGE
      );

      return (
      <>
      <div className="settings-section-card settings-backup-card">
        <div className="settings-backup-toolbar">
          <div className="settings-backup-schedule">
            <label htmlFor="settings-backup-schedule">زمان بندی خودکار بکاپ</label>
            <select
              id="settings-backup-schedule"
              className="settings-security-select settings-backup-select"
              value={settingsBackupSchedule}
              onChange={(event) => handleChangeSettingsBackupSchedule(event.target.value)}
            >
              {SETTINGS_BACKUP_SCHEDULE_OPTIONS.map((option) => (
                <option key={`settings-backup-${option.value}`} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <button type="button" className="settings-primary-btn" onClick={handleCreateSettingsBackup}>
            ایجاد بکاپ جدید
          </button>
        </div>

        <div className="settings-table-wrap">
          <table className="settings-table settings-backup-table">
            <thead>
              <tr>
                <th>ردیف</th>
                <th>کد بکاپ</th>
                <th>تاریخ ایجاد بکاپ</th>
                <th>وضعیت</th>
                <th>حجم</th>
                <th>عملیات</th>
              </tr>
            </thead>
            <tbody>
              {paginatedSettingsBackupRows.length === 0 ? (
                <tr>
                  <td className="settings-backup-empty-row" colSpan={6}>
                    هنوز بکاپی ثبت نشده است.
                  </td>
                </tr>
              ) : (
                paginatedSettingsBackupRows.map((row, index) => (
                  <tr key={row.id}>
                    <td>{formatFaNumber(settingsBackupStartIndex + index + 1)}</td>
                    <td>{row.code}</td>
                    <td>{row.createdAt}</td>
                    <td>
                      <span className={`settings-status-chip settings-backup-status ${row.status}`}>
                        {row.statusLabel}
                      </span>
                      {row.isCurrentVersion ? (
                        <span className="settings-backup-current-badge">نسخه فعلی</span>
                      ) : null}
                    </td>
                    <td>{row.size}</td>
                    <td>
                      <div className="settings-backup-actions">
                        <button
                          type="button"
                          className="settings-action-btn settings-backup-row-btn"
                          onClick={() => handleDownloadSettingsBackup(row.code)}
                          disabled={!canUseBackupFileActions(row.status)}
                        >
                          دانلود
                        </button>
                        <button
                          type="button"
                          className="settings-action-btn settings-backup-row-btn settings-backup-restore-btn"
                          onClick={() => openBackupRestoreModal(row.code)}
                          disabled={!canUseBackupFileActions(row.status)}
                        >
                          بازگردانی
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="projects-table-pagination dashboard-activity-pagination settings-security-pagination">
          <button
            type="button"
            onClick={() => setSettingsBackupPage((prevPage) => Math.max(1, prevPage - 1))}
            disabled={currentSettingsBackupPage <= 1}
            aria-label="صفحه قبل"
          >
            ‹
          </button>
          <span className="projects-pagination-current dashboard-activity-page-current">
            {formatFaNumber(currentSettingsBackupPage)}
          </span>
          <span className="projects-pagination-total dashboard-activity-page-size">
            {formatFaNumber(totalSettingsBackupPages)} صفحه
          </span>
          <button
            type="button"
            onClick={() => setSettingsBackupPage((prevPage) => Math.min(totalSettingsBackupPages, prevPage + 1))}
            disabled={currentSettingsBackupPage >= totalSettingsBackupPages}
            aria-label="صفحه بعد"
          >
            ›
          </button>
        </div>
        {loadingSettingsBackups && (
          <div className="settings-msg">در حال دریافت بکاپ‌ها...</div>
        )}
        {isBackupRestoreModalOpen && (
          <div className="confirm-modal-overlay" onClick={closeBackupRestoreModal}>
            <div
              className="confirm-modal"
              role="dialog"
              aria-modal="true"
              aria-label="تایید بازگردانی بکاپ"
              onClick={(event) => event.stopPropagation()}
            >
              <h4>بازگردانی بکاپ</h4>
              <p>
                آیا از بازگردانی سیستم با بکاپ
                {" "}
                <strong>{backupCodeToRestore || "--"}</strong>
                {" "}
                مطمئن هستید؟
              </p>
              <p>با انجام این عملیات، سیستم به صورت کامل ری‌استارت می‌شود و خبرهای فعلی پاک می‌شوند. کل تنظیمات به این بک آپ برمی گردد.</p>
              <div className="confirm-modal-actions">
                <button
                  type="button"
                  className="confirm-cancel-btn"
                  onClick={closeBackupRestoreModal}
                >
                  انصراف
                </button>
                <button
                  type="button"
                  className="confirm-delete-btn"
                  onClick={confirmRestoreSettingsBackup}
                >
                  بله، بازگردانی شود
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
      <div id="settings-backup-empty-card-spacer" className="project-create-empty-card settings-backup-empty-card-spacer" aria-hidden="true" />
      </>
      );
    };

    const renderHomepageManagementSettingsContent = () => (
      <>
        <div className="settings-section-card settings-homepage-card">
          <div className="settings-section-head">
            <h4>مدیریت محتوای صفحه اصلی</h4>
          </div>
          <div className="settings-homepage-form">
            <label htmlFor="settings-homepage-title">عنوان پروژه</label>
            <input
              id="settings-homepage-title"
              type="text"
              value={settingsHomepageConfig.projectTitle}
              onChange={(event) => handleHomepageSettingsFieldChange("projectTitle", event.target.value)}
              placeholder="مثال: خبرخوان"
            />

            <label htmlFor="settings-homepage-description">توضیحات صفحه اصلی</label>
            <textarea
              id="settings-homepage-description"
              rows={4}
              value={settingsHomepageConfig.homeDescription}
              onChange={(event) => handleHomepageSettingsFieldChange("homeDescription", event.target.value)}
              placeholder="توضیح کوتاه برای صفحه اصلی"
            />

            <label htmlFor="settings-homepage-logo">بارگذاری لوگو</label>
            <input
              id="settings-homepage-logo"
              type="file"
              accept="image/*"
              onChange={handleHomepageLogoChange}
            />
            {settingsHomepageConfig.homeLogoUrl && (
              <img
                src={settingsHomepageConfig.homeLogoUrl}
                alt="لوگوی پروژه"
                className="settings-homepage-logo-preview"
              />
            )}

            <label htmlFor="settings-homepage-terms">قوانین و مقررات</label>
            <textarea
              id="settings-homepage-terms"
              rows={8}
              value={settingsHomepageConfig.termsAndConditions}
              onChange={(event) => handleHomepageSettingsFieldChange("termsAndConditions", event.target.value)}
              placeholder="متن قوانین و مقررات"
            />

            <button
              type="button"
              className="settings-primary-btn settings-save-btn"
              onClick={handleSaveHomepageSettings}
            >
              ذخیره تنظیمات صفحه اصلی
            </button>
          </div>
          {loadingSettingsHomepageConfig && (
            <div className="settings-msg">در حال دریافت تنظیمات صفحه اصلی...</div>
          )}
        </div>
        <div id="settings-homepage-empty-card-spacer" className="project-create-empty-card settings-homepage-empty-card-spacer" aria-hidden="true" />
      </>
    );

    return (
      <div className="settings-page">
        {shouldShowSettingsTopTabs && (
          <div className="settings-top-tabs" role="tablist" aria-label="تب های تنظیمات">
            {SETTINGS_TOP_TABS.map((tabItem) => (
              <button
                key={tabItem.key}
                type="button"
                className={settingsTopTab === tabItem.key ? "active" : ""}
                onClick={() => handleSettingsTopTabChange(tabItem.key)}
              >
                {tabItem.label}
              </button>
            ))}
          </div>
        )}

        {settingsMsg.text && (
          <div className={`settings-msg ${settingsMsg.type}`}>
            {settingsMsg.text}
          </div>
        )}
        {loadingSettingsGeneralConfig && (
          <div className="settings-msg">در حال دریافت تنظیمات...</div>
        )}

        {settingsTopTab === "settings" && renderGeneralSettingsContent()}

        {settingsTopTab === "security" && renderSecuritySettingsContent()}

        {settingsTopTab === "backup" && renderBackupSettingsContent()}

        {settingsTopTab === "homepage-management" && renderHomepageManagementSettingsContent()}

        {isSecurityModeConfirmModalOpen && (
          <div className="confirm-modal-overlay" onClick={handleCancelSecurityModeChange}>
            <div
              className="confirm-modal"
              role="dialog"
              aria-modal="true"
              aria-label="تایید تغییر محدودیت آی پی"
              onClick={(event) => event.stopPropagation()}
            >
              <h4>تغییر محدودیت آی پی</h4>
              <p>
                آیا از {pendingSecurityModeValue === "enabled" ? "فعال" : "غیرفعال"} کردن محدودیت براساس آی پی مطمئنی؟
              </p>
              <div className="confirm-modal-actions">
                <button
                  type="button"
                  className="confirm-cancel-btn"
                  onClick={handleCancelSecurityModeChange}
                  disabled={savingSecurityMode}
                >
                  انصراف
                </button>
                <button
                  type="button"
                  className="confirm-delete-btn"
                  onClick={handleConfirmSecurityModeChange}
                  disabled={savingSecurityMode}
                >
                  {savingSecurityMode ? "در حال ثبت..." : "تایید"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderReportsTab = () => {
    const reportProjectOptions = projects.reduce((options, project) => {
      const projectId = project?.id;

      if (projectId === undefined || projectId === null) {
        return options;
      }

      const optionValue = String(projectId);

      if (options.some((option) => option.value === optionValue)) {
        return options;
      }

      const optionLabel = (
        project?.title
        || project?.name
        || project?.project_name
        || `پروژه ${optionValue}`
      );
      options.push({ value: optionValue, label: optionLabel });
      return options;
    }, []);
    const availableProjectIdSet = new Set(reportProjectOptions.map((project) => project.value));
    const hasAnyProjectInDatabase = reportProjectOptions.length > 0;
    const receiveSourceOptions = receiveSourcesOptions.reduce((options, source) => {
      const sourceId = source?.id;
      if (sourceId === undefined || sourceId === null) {
        return options;
      }
      const optionValue = String(sourceId);
      if (options.some((option) => option.value === optionValue)) {
        return options;
      }
      const optionLabel = String(
        source?.name
        || source?.source_name
        || source?.receive_source
        || `منبع ${optionValue}`
      ).trim();
      options.push({ value: optionValue, label: optionLabel });
      return options;
    }, []);
    const availableReceiveSourceIdSet = new Set(receiveSourceOptions.map((source) => source.value));
    const hasAnyReceiveSourceInDatabase = receiveSourceOptions.length > 0;
    const filteredTopProjects = reportsTopProjectsData.filter((item) => {
      const normalizedProjectId = String(item?.projectId || "").trim();
      if (normalizedProjectId) {
        return availableProjectIdSet.has(normalizedProjectId);
      }
      return true;
    });
    const filteredTopSources = reportsTopSourcesData.filter((item) => {
      const normalizedReceiveSourceId = String(item?.receiveSourceId || "").trim();
      if (normalizedReceiveSourceId) {
        return availableReceiveSourceIdSet.has(normalizedReceiveSourceId);
      }
      return true;
    });

    const effectiveTopProjects = filteredTopProjects;
    const effectiveTopSources = filteredTopSources;

    const reportColumnMax = Math.max(
      ...effectiveTopProjects.map((item) => Number(item?.value || 0)),
      1
    );
    const reportSourcesMax = Math.max(
      ...effectiveTopSources.map((source) => Number(source?.value || 0)),
      1
    );

    const getReportCardFilter = (cardKey) => (
      reportsCardFilters[cardKey] || REPORT_DEFAULT_CARD_FILTER
    );

    const handleReportCardFilterChange = (cardKey, updates) => {
      setReportsCardFilters((prevFilters) => ({
        ...prevFilters,
        [cardKey]: {
          ...(prevFilters[cardKey] || REPORT_DEFAULT_CARD_FILTER),
          ...updates,
        },
      }));
    };

    const renderCardProjectSelect = (cardKey) => (
      <div className="reports-card-filter">
        <label className="reports-visually-hidden" htmlFor={`report-card-${cardKey}-project`}>
          انتخاب پروژه
        </label>
        <select
          id={`report-card-${cardKey}-project`}
          value={getReportCardFilter(cardKey).projectFilter}
          onChange={(event) => handleReportCardFilterChange(cardKey, { projectFilter: event.target.value })}
        >
          <option value="all">همه پروژه ها</option>
          {reportProjectOptions.map((project) => (
            <option key={project.value} value={project.value}>
              {project.label}
            </option>
          ))}
        </select>
      </div>
    );
    const publishedTrend = dashboardTrendCharts.published || {
      labels: REPORT_WEEKDAY_LABELS,
      points: REPORT_PUBLISHED_NEWS_POINTS,
    };
    const receivedTrend = dashboardTrendCharts.received || {
      labels: REPORT_WEEKDAY_LABELS,
      points: REPORT_RECEIVED_NEWS_POINTS,
    };
    const publishedPeriod = REPORT_PERIOD_TO_API[getReportCardFilter("published-news").period] || "monthly";
    const receivedPeriod = REPORT_PERIOD_TO_API[getReportCardFilter("received-news").period] || "monthly";

    return (
      <div className="reports-page">
        <div className="reports-toolbar">
          <label className="reports-visually-hidden" htmlFor="reports-manager-filter">
            انتخاب مدیر ارشد گزارش
          </label>
          <select
            id="reports-manager-filter"
            className="reports-main-filter"
            value={reportsManagerFilter}
            onChange={(event) => setReportsManagerFilter(event.target.value)}
          >
            <option value="all">یک مدیر ارشد انتخاب کنید</option>
            {projectManagerOptions.map((managerName) => (
              <option key={managerName} value={managerName}>
                {managerName}
              </option>
            ))}
          </select>
          <div className="reports-toolbar-actions">
            <button
              type="button"
              className="reports-realtime-btn"
              onClick={() => setActiveTab("reports-realtime")}
            >
              گزارش لحظه‌ای سیستم
            </button>
            <button
              type="button"
              className="reports-realtime-btn reports-system-messages-btn"
              onClick={openSystemMessagesTab}
            >
              پیام های سیستم
            </button>
          </div>
        </div>

        <div className="reports-grid">
          <article className="reports-card">
            <div className="reports-card-header">
              <h4>پروژه های برتر در انتشار</h4>
            </div>

            <div className="reports-chart-shell">
              {reportsTopChartsError && (
                <div className="dashboard-trend-error-message">{reportsTopChartsError}</div>
              )}
              {!hasAnyProjectInDatabase ? (
                <div className="reports-empty-state-message">پروژه ای نیست</div>
              ) : (
                <div className="reports-column-chart">
                  <div className="reports-column-grid">
                    {[0, 1, 2, 3, 4].map((lineIndex) => (
                      <span key={lineIndex} className="reports-column-grid-line" />
                    ))}
                  </div>
                  <div className="reports-column-bars">
                    {effectiveTopProjects.map((item, index) => (
                      <div key={`${item.label}-${item.value}-${index}`} className="reports-column-item">
                        <span className="reports-column-value">
                          {formatFaNumber(Number(item.value || 0))}
                        </span>
                        <span
                          className="reports-column-bar"
                          style={{ height: `${Math.max(10, (Number(item.value || 0) / reportColumnMax) * 100)}%` }}
                        />
                        <span className="reports-column-label">{item.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {loadingReportsTopCharts && (
              <div className="dashboard-trend-loading">در حال دریافت اطلاعات...</div>
            )}
          </article>

          <article className="reports-card">
            <div className="reports-card-header">
              <h4>منابع برتر در دریافت</h4>
            </div>

            <div className="reports-chart-shell">
              {reportsTopChartsError && (
                <div className="dashboard-trend-error-message">{reportsTopChartsError}</div>
              )}
              {!hasAnyReceiveSourceInDatabase ? (
                <div className="reports-empty-state-message">منبعی نیست</div>
              ) : (
                <ul className="reports-source-list">
                  {effectiveTopSources.map((source, index) => (
                    <li key={`${source.label}-${source.value}-${index}`}>
                      <span className="reports-source-label">{source.label}</span>
                      <div className="reports-source-track">
                        <span
                          className="reports-source-fill"
                          style={{ width: `${Math.max(8, (Number(source.value || 0) / reportSourcesMax) * 100)}%` }}
                        />
                      </div>
                      <span className="reports-source-value">{formatFaNumber(Number(source.value || 0))}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {loadingReportsTopCharts && (
              <div className="dashboard-trend-loading">در حال دریافت اطلاعات...</div>
            )}
          </article>

          <article className="reports-card">
            <div className="reports-card-header">
              <h4>خبر های منتشر شده</h4>
              <div className="reports-period-tabs">
                {REPORT_PERIOD_TABS.map((periodLabel) => (
                  <button
                    key={`reports-published-${periodLabel}`}
                    type="button"
                    className={periodLabel === getReportCardFilter("published-news").period ? "active" : ""}
                    onClick={() => handleReportCardFilterChange("published-news", { period: periodLabel })}
                  >
                    {periodLabel}
                  </button>
                ))}
              </div>
            </div>

            {renderCardProjectSelect("published-news")}

            <div className="reports-chart-shell">
              {dashboardTrendsError && (
                <div className="dashboard-trend-error-message">{dashboardTrendsError}</div>
              )}
              <DashboardNewsTrendChart
                labels={publishedTrend.labels}
                points={publishedTrend.points}
                period={publishedPeriod}
                yAxisTitle="تعداد خبر"
                ariaLabel="نمودار خبر های منتشر شده"
                tooltipValueLabel="تعداد خبر"
              />
              {loadingDashboardTrends && (
                <div className="dashboard-trend-loading">در حال دریافت اطلاعات...</div>
              )}
            </div>
          </article>

          <article className="reports-card">
            <div className="reports-card-header">
              <h4>خبر های دریافت شده</h4>
              <div className="reports-period-tabs">
                {REPORT_PERIOD_TABS.map((periodLabel) => (
                  <button
                    key={`reports-received-${periodLabel}`}
                    type="button"
                    className={periodLabel === getReportCardFilter("received-news").period ? "active" : ""}
                    onClick={() => handleReportCardFilterChange("received-news", { period: periodLabel })}
                  >
                    {periodLabel}
                  </button>
                ))}
              </div>
            </div>

            {renderCardProjectSelect("received-news")}

            <div className="reports-chart-shell">
              {dashboardTrendsError && (
                <div className="dashboard-trend-error-message">{dashboardTrendsError}</div>
              )}
              <DashboardNewsTrendChart
                labels={receivedTrend.labels}
                points={receivedTrend.points}
                period={receivedPeriod}
                yAxisTitle="تعداد خبر دریافت‌شده"
                ariaLabel="نمودار خبر های دریافت شده"
                tooltipValueLabel="تعداد خبر دریافت‌شده"
              />
              {loadingDashboardTrends && (
                <div className="dashboard-trend-loading">در حال دریافت اطلاعات...</div>
              )}
            </div>
          </article>
        </div>

        <div id="reports-empty-card-spacer" className="project-create-empty-card reports-empty-card-spacer" aria-hidden="true" />
      </div>
    );
  };

  const renderReportsRealtimeTab = () => {
    const shouldLimitRealtimeLogBoardHeight = (
      !loadingReportsRealtimeLogs
      && !reportsRealtimeError
      && reportsRealtimeLogs.length > 10
    );

    return (
    <div className="reports-realtime-page">
      <div className="reports-realtime-head">
        <h3>گزارش لحظه ای سیستم</h3>
        <button
          type="button"
          className="projects-create-back-btn"
          onClick={() => setActiveTab("reports")}
        >
          بازگشت به گزارشات
        </button>
      </div>

      <div className="reports-realtime-toolbar">
        <div className="reports-system-status-chip">
          <span className="reports-system-status-dot" aria-hidden="true" />
          وضعیت سیستم
        </div>

        <div className="reports-realtime-actions">
          <label className="reports-visually-hidden" htmlFor="reports-realtime-range">
            بازه زمانی گزارش
          </label>
          <select
            id="reports-realtime-range"
            className="reports-realtime-range-filter"
            value={reportsRealtimeRange}
            onChange={(event) => setReportsRealtimeRange(event.target.value)}
          >
            {REPORT_REALTIME_RANGE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          <button
            type="button"
            className="reports-download-btn reports-realtime-download-btn"
            onClick={() => handleDownloadReportsRealtime("xlsx")}
            disabled={downloadingReportFormat !== ""}
          >
            <span aria-hidden="true">⇩</span>
            {downloadingReportFormat === "xlsx" ? "در حال دانلود..." : "دانلود اکسل"}
          </button>
          <button
            type="button"
            className="reports-download-btn reports-realtime-download-btn"
            onClick={() => handleDownloadReportsRealtime("pdf")}
            disabled={downloadingReportFormat !== ""}
          >
            <span aria-hidden="true">⇩</span>
            {downloadingReportFormat === "pdf" ? "در حال دانلود..." : "دانلود PDF"}
          </button>
        </div>
      </div>

      <div className="reports-realtime-log-shell">
        <div
          className={`reports-realtime-log-board${shouldLimitRealtimeLogBoardHeight ? " reports-realtime-log-board-scroll" : ""}`}
        >
          {loadingReportsRealtimeLogs && <p>در حال دریافت لاگ ها...</p>}
          {!loadingReportsRealtimeLogs && reportsRealtimeError && <p>{reportsRealtimeError}</p>}
          {!loadingReportsRealtimeLogs && !reportsRealtimeError && reportsRealtimeLogs.length === 0 && (
            <p>لاگی برای این بازه زمانی وجود ندارد.</p>
          )}
          {!loadingReportsRealtimeLogs && !reportsRealtimeError && reportsRealtimeLogs.map((logItem) => (
            <p key={`report-log-${logItem.id}`}>
              <span>{formatTimeFromDateTime(logItem.created_at)}</span>
              <span aria-hidden="true">&gt;&gt;&gt;&gt;</span>
              <strong>
                [{String(logItem.level || "").toUpperCase()}] {logItem.message}
              </strong>
            </p>
          ))}
        </div>
      </div>
      <div id="reports-realtime-empty-card-spacer" className="project-create-empty-card reports-realtime-empty-card-spacer" aria-hidden="true" />

    </div>
    );
  };

  const renderSystemMessagesTab = () => (
    <div className="system-messages-page">
      <div className="system-messages-head">
        <h3>پیام های سیستم</h3>
        <button
          type="button"
          className="projects-create-back-btn"
          onClick={() => setActiveTab(systemMessagesReturnTab || "dashboard")}
        >
          بازگشت
        </button>
      </div>

      <div className="system-messages-card">
        <div className="system-messages-card-head">
          <span>لیست کامل پیام ها</span>
          <strong>{formatFaNumber(topbarSystemMessages.length)} پیام</strong>
        </div>

        {topbarSystemMessages.length === 0 ? (
          <div className="system-messages-empty">در حال حاضر پیامی وجود ندارد.</div>
        ) : (
          <ul className="system-messages-list">
            {topbarSystemMessages.map((messageRow, index) => (
              <li key={`system-message-row-${index}`} className="system-messages-item">
                <span className="system-messages-index">{formatFaNumber(index + 1)}</span>
                <div className="system-messages-content">
                  <p>{messageRow.message}</p>
                  <span className="system-messages-datetime">
                    {messageRow.createdAt ? formatNewsDate(messageRow.createdAt) : "--"}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div id="system-messages-empty-card-spacer" className="project-create-empty-card system-messages-empty-card-spacer" aria-hidden="true" />
    </div>
  );

  const summarizeSystemMessage = (text, maxLength = 64) => {
    const normalized = String(text || "").trim();
    if (normalized.length <= maxLength) {
      return normalized;
    }
    return `${normalized.slice(0, maxLength).trim()}...`;
  };

  const renderDashboardTab = () => {
    const reportProjectOptions = projects.reduce((options, project) => {
      const projectId = project?.id;

      if (projectId === undefined || projectId === null) {
        return options;
      }

      const optionValue = String(projectId);

      if (options.some((option) => option.value === optionValue)) {
        return options;
      }

      const optionLabel = (
        project?.title
        || project?.name
        || project?.project_name
        || `پروژه ${optionValue}`
      );
      options.push({ value: optionValue, label: optionLabel });
      return options;
    }, []);

    const getReportCardFilter = (cardKey) => (
      reportsCardFilters[cardKey] || REPORT_DEFAULT_CARD_FILTER
    );

    const handleReportCardFilterChange = (cardKey, updates) => {
      setReportsCardFilters((prevFilters) => ({
        ...prevFilters,
        [cardKey]: {
          ...(prevFilters[cardKey] || REPORT_DEFAULT_CARD_FILTER),
          ...updates,
        },
      }));
    };

    const renderCardProjectSelect = (cardKey) => (
      <div className="reports-card-filter">
        <label className="reports-visually-hidden" htmlFor={`dashboard-report-card-${cardKey}-project`}>
          انتخاب پروژه
        </label>
        <select
          id={`dashboard-report-card-${cardKey}-project`}
          value={getReportCardFilter(cardKey).projectFilter}
          onChange={(event) => handleReportCardFilterChange(cardKey, { projectFilter: event.target.value })}
        >
          <option value="all">همه پروژه ها</option>
          {reportProjectOptions.map((project) => (
            <option key={`${cardKey}-${project.value}`} value={project.value}>
              {project.label}
            </option>
          ))}
        </select>
      </div>
    );

    const statCards = [
      {
        key: "sources",
        title: "تعداد منابع دریافت",
        value: loadingStats ? "..." : dashboardStats.totalReceiveSources,
        icon: resourcesStatIcon,
      },
      {
        key: "projects",
        title: "تعداد پروژه ها",
        value: loadingStats ? "..." : dashboardStats.totalProjects,
        icon: allProjectsStatIcon,
      },
      {
        key: "published",
        title: "خبر های منتشر شده",
        value: loadingStats ? "..." : dashboardStats.totalPublishedNews,
        icon: sendStatIcon,
      },
      {
        key: "received",
        title: "خبر های دریافت شده",
        value: loadingStats ? "..." : dashboardStats.totalReceivedNews,
        icon: receiveStatIcon,
      },
    ];
    const publishedTrend = dashboardTrendCharts.published || {
      labels: REPORT_WEEKDAY_LABELS,
      points: REPORT_PUBLISHED_NEWS_POINTS,
    };
    const receivedTrend = dashboardTrendCharts.received || {
      labels: REPORT_WEEKDAY_LABELS,
      points: REPORT_RECEIVED_NEWS_POINTS,
    };
    const publishedPeriod = REPORT_PERIOD_TO_API[getReportCardFilter("published-news").period] || "monthly";
    const receivedPeriod = REPORT_PERIOD_TO_API[getReportCardFilter("received-news").period] || "monthly";
    const hasCriticalEvents = Boolean(dashboardStats.hasCriticalEvents);
    const criticalEventsCount = Number(dashboardStats.criticalEventsCount || 0);
    const dashboardErrorsCardClassName = hasCriticalEvents
      ? "dashboard-status-card dashboard-errors-box dashboard-errors-link"
      : "dashboard-status-card dashboard-errors-box dashboard-errors-link dashboard-errors-box-ok";
    const dashboardErrorsLabel = hasCriticalEvents
      ? `${formatFaNumber(criticalEventsCount)} مورد`
      : "وضعیت عادی";

    return (
      <div className="dashboard-overview">
        <div className="dashboard-stat-grid">
          {statCards.map((card) => (
            <div className="dashboard-stat-card" key={card.key}>
              <div className="dashboard-stat-content">
                <span className="dashboard-stat-title">{card.title}</span>
                <strong className="dashboard-stat-number">{card.value}</strong>
              </div>
              <div className="dashboard-stat-icon" aria-hidden="true">
                <img src={card.icon} alt="" />
              </div>
            </div>
          ))}
        </div>

        <div className="dashboard-bottom-grid">
          <div className="dashboard-chart-card">
            <div className="reports-card-header">
              <h4>خبر های منتشر شده</h4>
              <div className="reports-period-tabs">
                {REPORT_PERIOD_TABS.map((periodLabel) => (
                  <button
                    key={`dashboard-published-${periodLabel}`}
                    type="button"
                    className={periodLabel === getReportCardFilter("published-news").period ? "active" : ""}
                    onClick={() => handleReportCardFilterChange("published-news", { period: periodLabel })}
                  >
                    {periodLabel}
                  </button>
                ))}
              </div>
            </div>

            {renderCardProjectSelect("published-news")}

            <div className="reports-chart-shell">
              {dashboardTrendsError && (
                <div className="dashboard-trend-error-message">{dashboardTrendsError}</div>
              )}
              <DashboardNewsTrendChart
                labels={publishedTrend.labels}
                points={publishedTrend.points}
                period={publishedPeriod}
                yAxisTitle="تعداد خبر"
                ariaLabel="نمودار خبر های منتشر شده"
                tooltipValueLabel="تعداد خبر"
              />
              {loadingDashboardTrends && (
                <div className="dashboard-trend-loading">در حال دریافت اطلاعات...</div>
              )}
            </div>
          </div>

          <div className="dashboard-chart-card">
            <div className="reports-card-header">
              <h4>خبر های دریافت شده</h4>
              <div className="reports-period-tabs">
                {REPORT_PERIOD_TABS.map((periodLabel) => (
                  <button
                    key={`dashboard-received-${periodLabel}`}
                    type="button"
                    className={periodLabel === getReportCardFilter("received-news").period ? "active" : ""}
                    onClick={() => handleReportCardFilterChange("received-news", { period: periodLabel })}
                  >
                    {periodLabel}
                  </button>
                ))}
              </div>
            </div>

            {renderCardProjectSelect("received-news")}

            <div className="reports-chart-shell">
              {dashboardTrendsError && (
                <div className="dashboard-trend-error-message">{dashboardTrendsError}</div>
              )}
              <DashboardNewsTrendChart
                labels={receivedTrend.labels}
                points={receivedTrend.points}
                period={receivedPeriod}
                yAxisTitle="تعداد خبر دریافت‌شده"
                ariaLabel="نمودار خبر های دریافت شده"
                tooltipValueLabel="تعداد خبر دریافت‌شده"
              />
              {loadingDashboardTrends && (
                <div className="dashboard-trend-loading">در حال دریافت اطلاعات...</div>
              )}
            </div>
          </div>

          <div className="dashboard-status-stack">
            <div className="dashboard-status-card dashboard-status-panel">
              <div className="dashboard-status-head">
                <div className="dashboard-status-meta">
                  <span className="dashboard-status-icon">▣</span>
                  <h4>RAM Usage</h4>
                </div>
                <div className="dashboard-status-value dashboard-status-value-percent">
                  <strong>
                    {loadingSystemUsage ? "..." : formatFaNumber(Math.round(systemUsage.ramPercent))}
                  </strong>
                  <span>%</span>
                </div>
              </div>
              <div className="dashboard-progress-track">
                <span
                  className="dashboard-progress-fill dashboard-progress-fill-ram"
                  style={{ width: `${Math.max(0, Math.min(100, systemUsage.ramPercent))}%` }}
                />
              </div>
            </div>

            <div className="dashboard-status-card dashboard-status-panel">
              <div className="dashboard-status-head">
                <div className="dashboard-status-meta">
                  <span className="dashboard-status-icon">◔</span>
                  <h4>CPU Usage</h4>
                </div>
                <div className="dashboard-status-value dashboard-status-value-percent">
                  <strong>
                    {loadingSystemUsage ? "..." : formatFaNumber(Math.round(systemUsage.cpuPercent))}
                  </strong>
                  <span>%</span>
                </div>
              </div>
              <div className="dashboard-progress-track">
                <span
                  className="dashboard-progress-fill dashboard-progress-fill-cpu"
                  style={{ width: `${Math.max(0, Math.min(100, systemUsage.cpuPercent))}%` }}
                />
              </div>
            </div>

            <button
              type="button"
              className={dashboardErrorsCardClassName}
              onClick={() => setActiveTab("reports-realtime")}
            >
              <div className="dashboard-errors-head">
                <span className="dashboard-error-icon">⚠</span>
                <h4>لاگ خطا ها</h4>
              </div>
              <div className="dashboard-errors-count">
                {loadingStats ? "..." : dashboardErrorsLabel}
              </div>
            </button>
          </div>
        </div>

        <div className="dashboard-activity-card">
          <div className="dashboard-activity-header">
            <h4>رویداد های اخیر</h4>
            <div className="dashboard-activity-filters">
              <div className="dashboard-filter-group">
                <div className="dashboard-filter-row">
                  <JalaliDatePicker
                    id="activity-from-date-filter"
                    label="از تاریخ"
                    value={dashboardActivityFromDate}
                    maxDate={dashboardActivityToDate}
                    onChange={handleDashboardActivityFromDateChange}
                  />
                  <JalaliDatePicker
                    id="activity-to-date-filter"
                    label="تا تاریخ"
                    value={dashboardActivityToDate}
                    minDate={dashboardActivityFromDate}
                    onChange={handleDashboardActivityToDateChange}
                  />
                </div>
              </div>
              <div className="dashboard-filter-row dashboard-filter-row-right">
                <div className="dashboard-filter-control">
                  <label htmlFor="activity-role-filter">نقش</label>
                  <select
                    id="activity-role-filter"
                    className="dashboard-filter-select"
                    value={dashboardActivityRoleFilter}
                    onChange={(event) => setDashboardActivityRoleFilter(event.target.value)}
                    onMouseDown={(event) => (
                      scrollBySelectHeight(event, usersActivityRoleOptions.length + 1)
                    )}
                  >
                    <option value="all">همه نقش‌ها</option>
                    {usersActivityRoleOptions.map((roleValue) => (
                      <option key={roleValue} value={roleValue}>
                        {roleValue}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="dashboard-filter-control">
                  <label htmlFor="activity-module-filter">ماژول</label>
                  <select
                    id="activity-module-filter"
                    className="dashboard-filter-select"
                    value={dashboardActivityModuleFilter}
                    onChange={(event) => setDashboardActivityModuleFilter(event.target.value)}
                    onMouseDown={(event) => (
                      scrollBySelectHeight(event, usersActivityModuleOptions.length + 1)
                    )}
                  >
                    <option value="all">همه ماژول‌ها</option>
                    {usersActivityModuleOptions.map((moduleValue) => (
                      <option key={moduleValue} value={moduleValue}>
                        {moduleValue}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          <div className="dashboard-activity-table-wrap">
            <table className="dashboard-activity-table">
              <thead>
                <tr>
                  <th>ردیف</th>
                  <th>نقش</th>
                  <th>تاریخ فعالیت</th>
                  <th>نام کاربر</th>
                  <th>ماژول</th>
                  <th>IP</th>
                  <th>رویداد</th>
                </tr>
              </thead>
              <tbody>
                {loadingUsersActivity ? (
                  <tr>
                    <td className="dashboard-activity-empty-row" colSpan={7}>
                      در حال دریافت فعالیت‌های کاربران...
                    </td>
                  </tr>
                ) : filteredDashboardActivityRows.length === 0 ? (
                  <tr>
                    <td className="dashboard-activity-empty-row" colSpan={7}>
                      موردی با فیلترهای انتخابی پیدا نشد.
                    </td>
                  </tr>
                ) : (
                  paginatedDashboardActivityRows.map((row, index) => (
                    <tr key={row.id}>
                      <td>{formatFaNumber(dashboardActivityPageStart + index + 1)}</td>
                      <td>{row.role}</td>
                      <td>{row.activityDate}</td>
                      <td>{row.userName}</td>
                      <td>{row.module}</td>
                      <td>{row.ip}</td>
                      <td>{row.event}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="dashboard-activity-pagination">
            <button
              type="button"
              onClick={() => handleDashboardActivityPageChange("prev")}
              disabled={currentDashboardActivityPage <= 1}
              aria-label="صفحه قبل"
            >
              ‹
            </button>
            <span className="dashboard-activity-page-current">
              {formatFaNumber(currentDashboardActivityPage)}
            </span>
            <span className="dashboard-activity-page-size">
              {formatFaNumber(totalDashboardActivityPages)} صفحه
            </span>
            <button
              type="button"
              onClick={() => handleDashboardActivityPageChange("next")}
              disabled={currentDashboardActivityPage >= totalDashboardActivityPages}
              aria-label="صفحه بعد"
            >
              ›
            </button>
          </div>
        </div>

        <div id="dashboard-empty-card-spacer" className="project-create-empty-card dashboard-empty-card-spacer" aria-hidden="true" />
      </div>
    );
  };

  if (authLoading) {
    return <div className="dashboard">در حال بارگذاری داشبورد...</div>;
  }

  return (
    <div className="dashboard">
      {timePickerPopup.isOpen && (
        <div className="time-picker-backdrop" onMouseDown={() => closeTimePickerPopup(timePickerPopup.inputId)}>
          <div
            className="time-picker-popover"
            style={{
              top: `${timePickerPopup.top}px`,
              left: `${timePickerPopup.left}px`,
            }}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="time-picker-header">انتخاب ساعت</div>
            <div className="time-picker-row">
              <label className="time-picker-control">
                <span>ساعت</span>
                <select
                  value={timePickerPopup.hour}
                  onChange={(event) => {
                    const nextHour = String(event.target.value || "00").padStart(2, "0");
                    setTimePickerPopup((prev) => ({
                      ...prev,
                      hour: nextHour,
                    }));
                  }}
                >
                  {TIME_PICKER_HOURS.map((hour) => (
                    <option key={hour} value={hour}>
                      {hour}
                    </option>
                  ))}
                </select>
              </label>
              <label className="time-picker-control">
                <span>دقیقه</span>
                <select
                  value={timePickerPopup.minute}
                  onChange={(event) => {
                    const nextMinute = String(event.target.value || "00").padStart(2, "0");
                    setTimePickerPopup((prev) => ({
                      ...prev,
                      minute: nextMinute,
                    }));
                  }}
                >
                  {TIME_PICKER_MINUTES.map((minute) => (
                    <option key={minute} value={minute}>
                      {minute}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div className="time-picker-actions">
              <button
                type="button"
                className="time-picker-apply-btn"
                onClick={() => {
                  const nextValue = `${timePickerPopup.hour}:${timePickerPopup.minute}`;
                  const inputElement = document.getElementById(timePickerPopup.inputId);
                  if (inputElement) {
                    inputElement.value = nextValue;
                    inputElement.dispatchEvent(new Event("input", { bubbles: true }));
                    inputElement.dispatchEvent(new Event("change", { bubbles: true }));
                    inputElement.focus();
                  }
                  if (openNewsTimePickerInputId === timePickerPopup.inputId) {
                    setOpenNewsTimePickerInputId("");
                  }
                  closeTimePickerPopup(timePickerPopup.inputId);
                }}
              >
                ثبت
              </button>
              <button
                type="button"
                className="time-picker-cancel-btn"
                onClick={() => closeTimePickerPopup(timePickerPopup.inputId)}
              >
                انصراف
              </button>
            </div>
          </div>
        </div>
      )}

      <div ref={sidebarRef} className={`sidebar ${menuOpen ? "open" : ""}`}>

        <div className="logo">پنل مدیریت</div>

        <ul>

          {filteredSidebarItems.map((item) => (
            <li
              key={item.key}
              className={
                (
                  item.key === "users"
                    ? isUsersSectionActive
                    : item.key === "projects"
                      ? isProjectsSectionActive
                    : item.key === "news"
                      ? isNewsSectionActive
                    : item.key === "ads"
                      ? isAdsSectionActive
                    : item.key === "reports"
                      ? isReportsSectionActive
                      : activeTab === item.key
                )
                  ? "active"
                  : ""
              }
              onClick={() => {
                handleSidebarTabChange(item.key);
              }}
            >
              <img src={item.icon} alt="" className="sidebar-icon" aria-hidden="true" />
              <span>{item.label}</span>
            </li>
          ))}

          <li
            className="logout"
            onClick={openLogoutModal}
          >
            <img src={logoutIcon} alt="" className="sidebar-icon" aria-hidden="true" />
            <span>خروج</span>
          </li>

        </ul>

        <div className="sidebar-version">2 . 4 . 0</div>

      </div>

      <div ref={contentRef} className="content">

        <div className="topbar">
          <h2>{getTitle()}</h2>

          <button
            className="menu-btn"
            onClick={()=>setMenuOpen(!menuOpen)}
          >
            ☰
          </button>

          <div className="topbar-circles">
            <div className="topbar-circle topbar-avatar-circle">
              <img
                src={topbarUserInfo.profileImage || topbarUserIcon}
                alt="عکس کاربر"
                className="topbar-avatar-image"
              />
            </div>
            <div className="topbar-notification-wrap" ref={topbarNotificationsRef}>
              <button
                type="button"
                className={`topbar-circle topbar-notification-circle ${isTopbarNotificationsOpen ? "active" : ""}`}
                aria-label="پیام های سیستم"
                aria-expanded={isTopbarNotificationsOpen}
                onClick={() => setIsTopbarNotificationsOpen((prev) => !prev)}
              >
                <img
                  src={alertIcon}
                  alt=""
                  className="topbar-notification-icon"
                />
              </button>

              {isTopbarNotificationsOpen && (
                <div className="topbar-notification-dropdown">
                  <div className="topbar-notification-dropdown-header">پیام های سیستم</div>
                  {topbarSystemMessages.length > 0 ? (
                    <ul className="topbar-notification-list">
                      {topbarSystemMessages.slice(0, 3).map((messageRow, index) => (
                        <li key={`topbar-message-${messageRow.id || index}`}>
                          <button
                            type="button"
                            className="topbar-notification-item topbar-notification-item-link"
                            onClick={() => {
                              setIsTopbarNotificationsOpen(false);
                              openSystemMessagesTab();
                            }}
                            title={messageRow.message}
                          >
                            {summarizeSystemMessage(messageRow.message)}
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="topbar-notification-empty">پیامی موجود نیست</div>
                  )}
                  <button
                    type="button"
                    className="topbar-notification-view-all-btn"
                    onClick={() => {
                      setIsTopbarNotificationsOpen(false);
                      openSystemMessagesTab();
                    }}
                  >
                    مشاهده همه
                  </button>
                </div>
              )}
            </div>
            <div className="topbar-circle topbar-circle-rect topbar-user-card">
              <span className="topbar-user-role">{topbarUserInfo.role}</span>
              <span className="topbar-user-name">{topbarUserInfo.name}</span>
            </div>
          </div>

        </div>

        <div className="page">

          {activeTab === "dashboard" && renderDashboardTab()}

          {activeTab === "projects" && renderProjectsTab()}
          {activeTab === "projects-view" && renderProjectViewTab()}

          {activeTab === "sources" && renderSourcesTab()}

          {activeTab === "ads" && renderAdsTab()}
          {activeTab === "ads-view" && renderAdsViewTab()}
          {activeTab === "ads-create" && renderAdsCreateTab()}

          {activeTab === "news" && renderNewsTab()}
          {activeTab === "news-view" && renderNewsViewTab()}
          {activeTab === "news-create" && renderNewsCreateTab()}

          {activeTab === "users" && renderUsersTab()}
          {(activeTab === "users-create" || activeTab === "users-edit") && renderUsersFormTab()}

          {activeTab === "settings" && renderSettingsTab()}

          {activeTab === "reports" && renderReportsTab()}
          {activeTab === "reports-realtime" && renderReportsRealtimeTab()}
          {activeTab === "system-messages" && renderSystemMessagesTab()}

          {activeTab !== "dashboard"
            && activeTab !== "projects"
            && activeTab !== "projects-view"
            && activeTab !== "sources"
            && activeTab !== "ads"
            && activeTab !== "ads-view"
            && activeTab !== "ads-create"
            && activeTab !== "news"
            && activeTab !== "news-view"
            && activeTab !== "news-create"
            && activeTab !== "users"
            && activeTab !== "users-create"
            && activeTab !== "users-edit"
            && activeTab !== "settings"
            && activeTab !== "reports"
            && activeTab !== "reports-realtime"
            && activeTab !== "system-messages"
            && (
            <div className="dev-box">در حال توسعه</div>
          )}

          {isLogoutModalOpen && (
            <div className="confirm-modal-overlay" onClick={closeLogoutModal}>
              <div
                className="confirm-modal logout-confirm-modal"
                role="dialog"
                aria-modal="true"
                aria-label="تایید خروج"
                onClick={(event) => event.stopPropagation()}
              >
                <h4>خروج از حساب کاربری</h4>
                <p>آیا از خروج از پنل مدیریت مطمئن هستید؟</p>
                <div className="confirm-modal-actions">
                  <button
                    type="button"
                    className="confirm-cancel-btn"
                    onClick={closeLogoutModal}
                  >
                    انصراف
                  </button>
                  <button
                    type="button"
                    className="confirm-delete-btn logout-confirm-btn"
                    onClick={handleConfirmLogout}
                  >
                    بله، خارج می‌شوم
                  </button>
                </div>
              </div>
            </div>
          )}

        </div>

      </div>

    </div>
  );
}

export default Dashboard;
